import { AbortError, EMPTY_TRANSFER, EngineError, EngineErrorCode, NO_ANNOTATION_FLAGS, PDF_SUBTYPE_TO_CODE, PdfAnnotationSubtypeCode, UNKNOWN_WEAK_ANNOTATION_STATE, appearanceImpactOf, buildSnippet, foldOptionsFor, foldText, invalidatesWeakIndexRefs, isValidPageObjectNumber, knownWeakAnnotationState, matchLiteral, matchRegex, normalizePdfRect, revisionTokensEqual, searchRectsForRange, serializeError, shiftsExistingAnnotationIndices, sniffBinaryMetadata, subtypeFromCode, validateSearchQuery, wirePack } from "@embedpdf/engine-core/runtime";
import { NULL_PTR } from "@embedpdf/engine-runtime";
//#region src/runtime/lifecycle/bootstrap.ts
const initialized = /* @__PURE__ */ new WeakSet();
/**
* Initialize the thread-confined PDFium runtime for the calling thread. Each
* worker thread owns its own PDFium state, so this must run on every thread
* that uses the runtime. Safe to call multiple times.
*/
function ensureInitialized(runtime) {
	if (initialized.has(runtime)) return;
	runtime.fn.EPDF_InitThread();
	initialized.add(runtime);
}
/**
* Tear down PDFium for the calling thread. Lifecycle-strict: every PDFium
* handle created on this thread must already be closed.
*/
function destroyLibrary(runtime) {
	if (!initialized.has(runtime)) return;
	runtime.fn.EPDF_ShutdownThread();
	initialized.delete(runtime);
}
//#endregion
//#region src/shared/abort.ts
/**
* Throw if the AbortSignal has been aborted. Service code peppers calls to
* this between PDFium calls so cooperative cancellation works without
* needing async/await.
*/
function throwIfAborted(signal) {
	if (signal.aborted) {
		const reason = signal.reason;
		if (reason instanceof Error) throw reason;
		throw new AbortError(reason);
	}
}
//#endregion
//#region src/shared/uuid.ts
/**
* Generate an RFC 4122 v4 UUID using the Web Crypto API surface that's
* available in every realm we ship to: Node 19+ (global), Web Workers,
* browsers (https), browsers (http/intranet/file:), and Edge runtimes.
*
* Why this implementation, exactly:
*   - `crypto.randomUUID()` was historically gated to *secure contexts*
*     in older browsers and we shipped that bug to production once.
*     `getRandomValues()` has no such restriction.
*   - `import { randomUUID } from 'crypto'` makes Vite externalize the
*     `crypto` module for browser builds and emit a build-time warning;
*     we then crash at runtime in the rare path that hits it.
*   - 16 bytes + RFC 4122 bit-twiddling is six lines and correct.
*
* Output is the canonical hex form: `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`,
* where `y` is one of `8/9/a/b` (variant nibble = 10xx).
*/
function generateUuid() {
	const bytes = globalThis.crypto.getRandomValues(/* @__PURE__ */ new Uint8Array(16));
	bytes[6] = bytes[6] & 15 | 64;
	bytes[8] = bytes[8] & 63 | 128;
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
//#endregion
//#region src/shared/pdf-date.ts
/**
* PDF date string format conversion utilities.
*
* PDF stores timestamps as ASCII strings shaped like
* `D:YYYYMMDDHHmmSSOHH'mm'`, where `O` is `+` / `-` / `Z` (Z means
* UTC and omits the offset). See ISO 32000 §7.9.4.
*
* The wire format used by the engine DTOs is always ISO 8601;
* readers convert PDF → ISO via {@link pdfDateToIso}, writers convert
* the other way via {@link formatPdfDate}.
*/
/**
* Parse a PDF date string `D:YYYYMMDDHHmmSSOHH'mm'` to an ISO 8601
* string. Returns null if the input is malformed.
*
* When the timezone is omitted, PDF defines the relationship to UTC as
* unknown. Since the engine DTO wire format requires an absolute ISO 8601
* timestamp, this converter retains the pre-existing fallback of treating
* an omitted timezone as UTC.
*/
function pdfDateToIso(pdf) {
	const match = /^D:(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:(Z)|([+-])(\d{2})'(\d{2})')?$/.exec(pdf);
	if (!match) return null;
	const [, yText, moText, dText, HText, MText, SText, , sign, tzHText, tzMText] = match;
	const y = Number(yText);
	const mo = Number(moText) - 1;
	const d = Number(dText);
	const H = Number(HText);
	const M = Number(MText);
	const S = Number(SText);
	const wallClock = /* @__PURE__ */ new Date(0);
	wallClock.setUTCFullYear(y, mo, d);
	wallClock.setUTCHours(H, M, S, 0);
	if (wallClock.getUTCFullYear() !== y || wallClock.getUTCMonth() !== mo || wallClock.getUTCDate() !== d || wallClock.getUTCHours() !== H || wallClock.getUTCMinutes() !== M || wallClock.getUTCSeconds() !== S) return null;
	let offsetMinutes = 0;
	if (sign) {
		const tzH = Number(tzHText);
		const tzM = Number(tzMText);
		if (tzH > 23 || tzM > 59) return null;
		offsetMinutes = (tzH * 60 + tzM) * (sign === "+" ? 1 : -1);
	}
	return (/* @__PURE__ */ new Date(wallClock.getTime() - offsetMinutes * 6e4)).toISOString();
}
/**
* Format a JS Date as a PDF date string `D:YYYYMMDDHHmmSSOHH'mm'`.
*
* Emits the offset in PDF's funny `OHH'mm'` form, where the apostrophe
* after the hour is literal and the offset always carries a sign. UTC
* is emitted as `+00'00'` (not `Z`) for maximum reader compatibility —
* the `Z` shorthand is allowed by the spec but not universally
* supported.
*
* Always uses the supplied Date's local fields and offset. For
* server-side stamps callers typically pass `new Date()` (default).
*/
function formatPdfDate(d = /* @__PURE__ */ new Date()) {
	if (Number.isNaN(d.getTime())) throw new RangeError("Cannot format an invalid Date");
	const pad = (n, w = 2) => String(n).padStart(w, "0");
	const year = d.getFullYear();
	if (year < 0 || year > 9999) throw new RangeError("PDF dates require a four-digit year");
	const tzMinutes = -d.getTimezoneOffset();
	const sign = tzMinutes >= 0 ? "+" : "-";
	const tzH = pad(Math.floor(Math.abs(tzMinutes) / 60));
	const tzM = pad(Math.abs(tzMinutes) % 60);
	return `D:${pad(year, 4)}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}${sign}${tzH}'${tzM}'`;
}
//#endregion
//#region src/runtime/memory/bits.ts
/**
* Coerce a value to an unsigned 32-bit integer. PDFium permission words
* arrive as signed JS numbers (the high bit makes them negative); reading
* them as `u32` restores the bit pattern the PDF spec describes.
*/
function u32(value) {
	return value >>> 0;
}
//#endregion
//#region src/shared/securityPermissions.ts
/**
* PDF Standard security handlers reserve bits 1-2 as 0, and PDFium masks
* even owner permissions through that convention — so "all permissions"
* for an encrypted document is `0xFFFFFFFC`, not `0xFFFFFFFF`.
*/
const ALL_STANDARD_SECURITY_PERMISSIONS = 4294967292;
/** Coerce PDFium's signed permission word to its unsigned 32-bit value. */
const normalizeU32 = u32;
/** True when `bits` grants every standard security permission. */
function hasAllStandardSecurityPermissions(bits) {
	return (u32(bits) & ALL_STANDARD_SECURITY_PERMISSIONS) >>> 0 === ALL_STANDARD_SECURITY_PERMISSIONS;
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/typeof.js
function _typeof(o) {
	"@babel/helpers - typeof";
	return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o) {
		return typeof o;
	} : function(o) {
		return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
	}, _typeof(o);
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/toPrimitive.js
function toPrimitive(t, r) {
	if ("object" != _typeof(t) || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != _typeof(i)) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/toPropertyKey.js
function toPropertyKey(t) {
	var i = toPrimitive(t, "string");
	return "symbol" == _typeof(i) ? i : i + "";
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/defineProperty.js
function _defineProperty(e, r, t) {
	return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		value: t,
		enumerable: !0,
		configurable: !0,
		writable: !0
	}) : e[r] = t, e;
}
//#endregion
//#region src/events/EventHub.ts
/**
* The in-process implementation of `DocumentEventStream`, shared by both
* engine shells (one hub per open `DocumentHandle`). The engine that
* performs a mutation publishes here at confirmation time; the cloud
* engine's remote channel will also publish here for OTHER sessions'
* mutations — listeners never learn which transport delivered an event.
*
* Delivery contract:
*   - synchronous fan-out in subscription order;
*   - error isolation: a throwing listener is re-thrown on a microtask
*     (so it surfaces to the host's unhandled-error reporting) without
*     blocking other listeners or failing the mutation path;
*   - `lastServerId` tracks the highest remote cursor seen, for the
*     remote channel's reconnect/backfill.
*/
var EventHub = class {
	constructor(onListenerError = (err) => {
		queueMicrotask(() => {
			throw err;
		});
	}) {
		this.onListenerError = onListenerError;
		_defineProperty(this, "listeners", /* @__PURE__ */ new Set());
		_defineProperty(this, "serverId", null);
	}
	subscribe(listener) {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}
	publish(event) {
		const { serverId } = event.origin;
		if (serverId !== null) this.serverId = this.serverId === null ? serverId : Math.max(this.serverId, serverId);
		for (const listener of [...this.listeners]) try {
			listener(event);
		} catch (err) {
			this.onListenerError(err);
		}
	}
	lastServerId() {
		return this.serverId;
	}
	/** Drop every listener (handle close). Idempotent. */
	clear() {
		this.listeners.clear();
	}
};
/**
* Stamps provenance onto events a mutation path produces. One per open
* handle: the engine shell constructs it with the instance's `sessionId`
* (and, on cloud, the authenticated `sub` when known) and hands it to the
* mutation services — they publish results, never build origins.
*/
var SessionEventPublisher = class {
	constructor(hub, sessionId, sub = null) {
		this.hub = hub;
		this.sessionId = sessionId;
		this.sub = sub;
	}
	/** Publish a mutation THIS engine instance just confirmed. */
	publishLocal(event) {
		this.hub.publish({
			...event,
			origin: {
				kind: "local",
				sessionId: this.sessionId,
				sub: this.sub,
				ts: Date.now(),
				serverId: null
			}
		});
	}
};
//#endregion
//#region src/document-session/lifecycle/PdfDocumentOpener.ts
const FPDF_ERR_PASSWORD$1 = 4;
var CloseStack = class {
	constructor() {
		_defineProperty(this, "actions", []);
		_defineProperty(this, "closed", false);
	}
	push(action) {
		if (this.closed) {
			action();
			return;
		}
		this.actions.push(action);
	}
	close() {
		if (this.closed) return;
		this.closed = true;
		let firstError = null;
		for (let i = this.actions.length - 1; i >= 0; i--) try {
			this.actions[i]?.();
		} catch (error) {
			firstError ?? (firstError = error);
		}
		this.actions.length = 0;
		if (firstError) throw firstError;
	}
};
function openFatMemoryDocument(runtime, bytes, password = null) {
	const { mem, fn } = runtime;
	const stack = new CloseStack();
	const dataPtr = mem.alloc(bytes.byteLength);
	stack.push(() => mem.free(dataPtr));
	try {
		mem.writeBytes(dataPtr, bytes);
		const docPtr = fn.FPDF_LoadMemDocument64(dataPtr, bytes.byteLength, password ?? "");
		if (!docPtr) {
			if (fn.FPDF_GetLastError() === FPDF_ERR_PASSWORD$1) throw password ? new EngineError(EngineErrorCode.DocPasswordIncorrect, "incorrect document password") : new EngineError(EngineErrorCode.DocPasswordRequired, "document requires a password");
			throw new EngineError(EngineErrorCode.DocOpenFailed, "failed to open document");
		}
		setRuntimeOwnerPermissionsIfEncrypted(runtime, docPtr);
		stack.push(() => fn.FPDF_CloseDocument(docPtr));
		return {
			kind: "fat-memory",
			docPtr,
			close: () => stack.close()
		};
	} catch (error) {
		stack.close();
		throw error;
	}
}
function openLayerDocument(runtime, base, layer = { kind: "fresh" }, password = null) {
	const { mem, fn } = runtime;
	const stack = new CloseStack();
	stack.push(() => base.release());
	let layerAccess = null;
	const statusPtr = mem.alloc(4);
	try {
		mem.poke(statusPtr, "i32", -1);
		let docPtr;
		if (layer.kind === "fresh") docPtr = fn.EPDFLayer_OpenLayer(base.basePtr, NULL_PTR, password ?? "", statusPtr);
		else {
			layerAccess = layer.kind === "artifact-file" ? runtime.fileAccess.fromNodeFile(layer.path) : runtime.fileAccess.fromMemory(layer.bytes);
			stack.push(() => layerAccess?.close());
			docPtr = layer.kind === "artifact" || layer.kind === "artifact-file" ? fn.EPDFLayer_OpenLayerArtifact(base.basePtr, layerAccess.ptr, password ?? "", statusPtr) : fn.EPDFLayer_OpenLayer(base.basePtr, layerAccess.ptr, password ?? "", statusPtr);
		}
		const status = Number(mem.peek(statusPtr, "i32"));
		if (!docPtr || status !== 0) throw layerOpenError(status);
		setRuntimeOwnerPermissionsIfEncrypted(runtime, docPtr);
		stack.push(() => fn.FPDF_CloseDocument(docPtr));
		return {
			kind: "layer",
			docPtr,
			close: () => stack.close()
		};
	} catch (error) {
		stack.close();
		throw error;
	} finally {
		mem.free(statusPtr);
	}
}
function setRuntimeOwnerPermissionsIfEncrypted(runtime, docPtr) {
	const { fn } = runtime;
	if (fn.EPDF_IsEncrypted && fn.EPDF_SetRuntimeOwnerPermissions && fn.EPDF_IsEncrypted(docPtr)) fn.EPDF_SetRuntimeOwnerPermissions(docPtr, true);
}
function layerOpenError(status) {
	switch (status) {
		case 1: return new EngineError(EngineErrorCode.DocPasswordRequired, "layer requires a password");
		case 2: return new EngineError(EngineErrorCode.MalformedPdf, "layer delta is malformed");
		case 3: return new EngineError(EngineErrorCode.MalformedPdf, "layer artifact does not match the supplied base document");
		default: return new EngineError(EngineErrorCode.DocOpenFailed, "failed to open layer document");
	}
}
//#endregion
//#region src/document-session/pages/PagePtrPool.ts
/**
* Manages pagePtr lifetime for a single open `DocumentSession`.
*
* `acquire(pon)` returns a loaded pagePtr for the given PDF object number,
* loading it via `EPDFDoc_LoadPageByObjectNumberNormalized` (rotation forced
* to 0deg so render/text/geometry/annotation coordinates all share one
* normalized space) if not already in the pool. `release(pon)` decrements the
* refcount and closes when it reaches
* zero; `closeAll()` is called by `DocumentSession.close()`.
*
* While at least one holder is active the pagePtr is shared (refcounted),
* so overlapping `acquire(pon)` calls for the same page reuse a single
* load. Once the refcount reaches zero the page is closed; a later
* `acquire` reloads it — there is no LRU retention across the zero-ref
* boundary. A future slice could add that without changing callers.
*/
var PagePtrPool = class {
	constructor(runtime, docPtr) {
		this.runtime = runtime;
		this.docPtr = docPtr;
		_defineProperty(this, "counts", /* @__PURE__ */ new Map());
	}
	acquire(pageObjectNumber) {
		const { fn } = this.runtime;
		const existing = this.counts.get(pageObjectNumber);
		if (existing) {
			existing.refs++;
			return existing.ptr;
		}
		const ptr = fn.EPDFDoc_LoadPageByObjectNumberNormalized(this.docPtr, pageObjectNumber);
		if (!ptr) throw new EngineError(EngineErrorCode.NotFound, `no page with object number ${pageObjectNumber}`);
		this.counts.set(pageObjectNumber, {
			ptr,
			refs: 1
		});
		return ptr;
	}
	/**
	* True while at least one holder has the page open. Page-STRUCTURE
	* mutations (delete) assert on this: thread confinement means no other
	* job can be mid-flight, so a held pagePtr during a structural mutation
	* is a leaked `acquire` (an internal bug) — the mutator fails loudly
	* instead of mutating under a live handle.
	*/
	isHeld(pageObjectNumber) {
		return this.counts.has(pageObjectNumber);
	}
	release(pageObjectNumber) {
		const entry = this.counts.get(pageObjectNumber);
		if (!entry) return;
		entry.refs--;
		if (entry.refs <= 0) {
			this.runtime.fn.FPDF_ClosePage(entry.ptr);
			this.counts.delete(pageObjectNumber);
		}
	}
	closeAll() {
		const { fn } = this.runtime;
		for (const { ptr } of this.counts.values()) fn.FPDF_ClosePage(ptr);
		this.counts.clear();
	}
};
//#endregion
//#region src/document-session/revisions/RevisionAuthority.ts
/**
* Per-page generation counter. Backs `RevisionToken` so weak
* `AnnotationRef.kind === 'index'` references can be validated against the
* exact page state they were minted from.
*
* `bump()` is the only mutator; mutation services call it after a
* structural change to the page (annotation create/delete/reorder).
* Read paths never mutate the store.
*/
var LocalRevisionAuthority = class {
	constructor(docSessionId) {
		this.docSessionId = docSessionId;
		_defineProperty(this, "generations", /* @__PURE__ */ new Map());
		_defineProperty(this, "weakAnnotationStates", /* @__PURE__ */ new Map());
	}
	/** Returns the current generation, defaulting to 0 for first read. */
	current(pageObjectNumber) {
		return this.generations.get(pageObjectNumber) ?? 0;
	}
	/** Mints a fresh `RevisionToken` for the page's current generation. */
	token(pageObjectNumber) {
		return {
			docSessionId: this.docSessionId,
			pageObjectNumber,
			generation: this.current(pageObjectNumber)
		};
	}
	/**
	* Increments the generation counter for a page. Returns the new token.
	* Called by mutation services after every structural change.
	*/
	bump(pageObjectNumber) {
		const next = this.current(pageObjectNumber) + 1;
		this.generations.set(pageObjectNumber, next);
		return this.token(pageObjectNumber);
	}
	/**
	* Strict equality check. Throws `EngineError(InvalidReference)` when the
	* caller's token does not match what the store currently holds.
	*/
	validate(token) {
		if (token.docSessionId !== this.docSessionId) throw new EngineError(EngineErrorCode.InvalidReference, "revision token belongs to a different document session", { details: { token } });
		const current = this.token(token.pageObjectNumber);
		if (!revisionTokensEqual(current, token)) throw new EngineError(EngineErrorCode.InvalidReference, "revision token is stale", { details: {
			provided: token,
			current
		} });
	}
	weakAnnotationState(pageObjectNumber) {
		return this.weakAnnotationStates.get(pageObjectNumber) ?? UNKNOWN_WEAK_ANNOTATION_STATE;
	}
	recordWeakAnnotationState(pageObjectNumber, state) {
		this.weakAnnotationStates.set(pageObjectNumber, state);
	}
	drop(pageObjectNumber) {
		this.generations.delete(pageObjectNumber);
		this.weakAnnotationStates.delete(pageObjectNumber);
	}
	clear() {
		this.generations.clear();
		this.weakAnnotationStates.clear();
	}
};
//#endregion
//#region src/document-session/DocumentSession.ts
/**
* Owns the lifecycle of a single open PDFium document and the v3
* identity machinery: page registry (pageObjectNumber <-> pageIndex),
* `RevisionAuthority` (per-page generation counters), and `PagePtrPool`
* (refcounted pagePtr access).
*
* Both the local browser Worker and the server worker_thread instantiate
* this exactly the same way; the only thing that differs is the
* underlying PdfRuntimeModule (WASM vs native).
*/
var DocumentSession = class {
	constructor(runtime, sessionId) {
		this.runtime = runtime;
		_defineProperty(this, "docPtr", null);
		_defineProperty(this, "closeDocument", null);
		_defineProperty(this, "_kind", null);
		_defineProperty(this, "_sessionId", void 0);
		_defineProperty(this, "recordsByObjectNumber", /* @__PURE__ */ new Map());
		_defineProperty(this, "recordsByIndex", /* @__PURE__ */ new Map());
		_defineProperty(this, "fullyEnumerated", false);
		_defineProperty(this, "revisions", null);
		_defineProperty(this, "mutationSeqCounter", 0);
		_defineProperty(this, "pages", null);
		_defineProperty(this, "parkedBytes", null);
		_defineProperty(this, "retained", []);
		this._sessionId = sessionId ?? generateSessionId();
	}
	get sessionId() {
		return this._sessionId;
	}
	get kind() {
		return this._kind;
	}
	isOpen() {
		return this.docPtr !== null;
	}
	open(bytes, password = null) {
		this.openFromHandle(openFatMemoryDocument(this.runtime, bytes, password));
	}
	openFromHandle(handle) {
		if (this.docPtr) {
			handle.close();
			throw new EngineError(EngineErrorCode.InvalidArg, "document already open");
		}
		this.docPtr = handle.docPtr;
		this.closeDocument = () => handle.close();
		this._kind = handle.kind;
		this.revisions = new LocalRevisionAuthority(this._sessionId);
		this.pages = new PagePtrPool(this.runtime, handle.docPtr);
		this.parkedBytes = null;
	}
	/**
	* Park this session in the password-locked state: the document could not
	* be loaded because a (correct) password is missing, so the session keeps
	* the already-transferred bytes and waits for an unlock attempt to load
	* them. A locked session occupies its docId key like an open one — every
	* operation except the password check rejects with DocPasswordRequired.
	*/
	parkLocked(bytes) {
		if (this.docPtr) throw new EngineError(EngineErrorCode.InvalidArg, "document already open");
		this.parkedBytes = bytes;
	}
	isLocked() {
		return this.docPtr === null && this.parkedBytes !== null;
	}
	/** The bytes retained for a later unlock attempt. Locked sessions only. */
	lockedBytes() {
		if (!this.parkedBytes) throw new EngineError(EngineErrorCode.DocNotOpen, "document session is not locked");
		return this.parkedBytes;
	}
	/** Number of pages in the document. */
	pageCount() {
		return this.runtime.fn.FPDF_GetPageCount(this.requireDocPtr());
	}
	/**
	* Lazily enumerate every page and cache (pageObjectNumber, pageIndex).
	* Necessary before `listRawAll()` and any pon -> pageIndex resolution.
	*/
	ensureFullPageRegistry() {
		if (this.fullyEnumerated) return;
		const { fn } = this.runtime;
		const docPtr = this.requireDocPtr();
		const count = fn.FPDF_GetPageCount(docPtr);
		for (let i = 0; i < count; i++) {
			if (this.recordsByIndex.has(i)) continue;
			const pon = fn.EPDFDoc_GetPageObjectNumberByIndex(docPtr, i);
			if (!isValidPageObjectNumber(pon)) throw new EngineError(EngineErrorCode.MalformedPdf, `page at index ${i} is a direct (non-indirect) PDF object; the engine requires every page to have a stable indirect object number`, { details: {
				pageIndex: i,
				pon
			} });
			const record = {
				pageObjectNumber: pon,
				pageIndex: i
			};
			this.recordsByIndex.set(i, record);
			this.recordsByObjectNumber.set(pon, record);
		}
		this.fullyEnumerated = true;
	}
	/** Lookup; populates the cache for one page only on cache miss. */
	recordByObjectNumber(pageObjectNumber) {
		const cached = this.recordsByObjectNumber.get(pageObjectNumber);
		if (cached) return cached;
		const { fn } = this.runtime;
		const docPtr = this.requireDocPtr();
		const probePtr = fn.EPDFDoc_LoadPageByObjectNumber(docPtr, pageObjectNumber);
		if (!probePtr) throw new EngineError(EngineErrorCode.NotFound, `no page with object number ${pageObjectNumber}`);
		fn.FPDF_ClosePage(probePtr);
		this.ensureFullPageRegistry();
		const found = this.recordsByObjectNumber.get(pageObjectNumber);
		if (!found) throw new EngineError(EngineErrorCode.NotFound, `page with object number ${pageObjectNumber} present but unindexable`);
		return found;
	}
	/** All page records, in display order. Forces full enumeration. */
	allRecords() {
		this.ensureFullPageRegistry();
		return Array.from(this.recordsByIndex.entries()).sort(([a], [b]) => a - b).map(([, r]) => r);
	}
	/**
	* Drop the cached `pageIndex <-> pageObjectNumber` mapping and force a
	* fresh enumeration on next access. Called by `PagesMutator`
	* after `FPDF_MovePages` shuffles page positions; we keep
	* weak-annotation knowledge and per-page revision counters intact, both of
	* which are keyed by durable `pageObjectNumber` and survive a page reorder.
	*/
	refreshPageRegistry() {
		this.recordsByIndex.clear();
		this.recordsByObjectNumber.clear();
		this.fullyEnumerated = false;
		this.ensureFullPageRegistry();
	}
	/** Per-page liveness envelope used by annotation read/mutation results. */
	pageState(pageObjectNumber) {
		this.recordByObjectNumber(pageObjectNumber);
		const weakAnnotationState = this.requireRevisions().weakAnnotationState(pageObjectNumber);
		return {
			pageObjectNumber,
			revision: this.requireRevisions().token(pageObjectNumber),
			weakAnnotationState
		};
	}
	/** Set by readers as they discover whether a page has weak annotations. */
	recordWeakFlag(pageObjectNumber, hasWeak) {
		this.recordWeakAnnotationState(pageObjectNumber, knownWeakAnnotationState(hasWeak));
	}
	recordWeakAnnotationState(pageObjectNumber, state) {
		this.requireRevisions().recordWeakAnnotationState(pageObjectNumber, state);
	}
	weakAnnotationState(pageObjectNumber) {
		return this.requireRevisions().weakAnnotationState(pageObjectNumber);
	}
	/** Bump and return the new revision token; called by mutation paths. */
	bumpRevision(pageObjectNumber) {
		return this.requireRevisions().bump(pageObjectNumber);
	}
	/**
	* Monotonic count of successful document mutations in this session.
	* Version key for detached-snapshot caches (e.g. the forms model):
	* a cache entry built at sequence N is exactly valid while the
	* sequence is still N. Coarse on purpose — widgets are annotations
	* and page ops move widgets, so ANY mutation may affect derived
	* form state; per-domain counters are a later optimization.
	*/
	mutationSeq() {
		return this.mutationSeqCounter;
	}
	/** Record one successful mutation; called by mutation paths. */
	noteMutation() {
		this.mutationSeqCounter++;
	}
	/**
	* Forget a page's per-session state (revision generation + weak-annotation
	* flag). Called by `pages.delete` after the page object is retired; the
	* PON is never recycled, so this is hygiene, not correctness.
	*/
	dropPageState(pageObjectNumber) {
		this.requireRevisions().drop(pageObjectNumber);
	}
	validateRevision(token) {
		this.requireRevisions().validate(token);
	}
	pagePool() {
		if (!this.pages) throw new EngineError(EngineErrorCode.DocNotOpen, "document is not open");
		return this.pages;
	}
	requireDocPtr() {
		if (!this.docPtr) throw new EngineError(EngineErrorCode.DocNotOpen, "document is not open");
		return this.docPtr;
	}
	/**
	* Park a disposer to run when THIS session closes. Used by operations
	* whose native side leaves the session document referencing another
	* resource — e.g. `pages.insert`: `FPDF_ImportPagesByIndex` does not
	* fully detach imported objects from their source document, so the
	* source doc + its byte buffer must stay alive until the destination
	* can no longer be saved (i.e. until this session closes).
	*/
	retainUntilClose(dispose) {
		this.retained.push(dispose);
	}
	close() {
		let firstError = null;
		try {
			this.pages?.closeAll();
		} catch (error) {
			firstError = error;
		} finally {
			this.pages = null;
		}
		try {
			this.closeDocument?.();
		} catch (error) {
			firstError ?? (firstError = error);
		} finally {
			this.closeDocument = null;
			this.docPtr = null;
			this._kind = null;
			this.parkedBytes = null;
			this.revisions?.clear();
			this.revisions = null;
			this.recordsByIndex.clear();
			this.recordsByObjectNumber.clear();
			this.fullyEnumerated = false;
		}
		for (let i = this.retained.length - 1; i >= 0; i--) try {
			this.retained[i]();
		} catch (error) {
			firstError ?? (firstError = error);
		}
		this.retained.length = 0;
		if (firstError) throw firstError;
	}
	requireRevisions() {
		if (!this.revisions) throw new EngineError(EngineErrorCode.DocNotOpen, "document is not open");
		return this.revisions;
	}
};
let sessionCounter = 0;
function generateSessionId() {
	return `sess_${(++sessionCounter).toString(36)}_${Date.now().toString(36)}`;
}
//#endregion
//#region src/document-session/lifecycle/BaseDocumentRegistry.ts
var BaseDocumentRegistry = class {
	constructor(runtime) {
		this.runtime = runtime;
		_defineProperty(this, "entries", /* @__PURE__ */ new Map());
	}
	acquireMemoryBase(opts) {
		const existing = this.retain(opts.key);
		if (existing) return existing;
		const { mem, fn } = this.runtime;
		const stack = new CloseStack();
		const dataPtr = mem.alloc(opts.bytes.byteLength);
		stack.push(() => mem.free(dataPtr));
		try {
			mem.writeBytes(dataPtr, opts.bytes);
			const basePtr = fn.EPDF_LoadMemBaseDocument64(dataPtr, opts.bytes.byteLength, opts.password ?? "");
			if (!basePtr) throw new EngineError(EngineErrorCode.DocOpenFailed, "failed to open base document");
			setRuntimeOwnerPermissionsIfEncrypted(this.runtime, basePtr);
			stack.push(() => fn.EPDF_ReleaseBaseDocument(basePtr));
			return this.insert(opts.key, basePtr, () => stack.close());
		} catch (error) {
			stack.close();
			throw error;
		}
	}
	acquireFileBase(opts) {
		const existing = this.retain(opts.key);
		if (existing) return existing;
		const { fn } = this.runtime;
		const stack = new CloseStack();
		let access = null;
		try {
			access = this.runtime.fileAccess.fromNodeFile(opts.path);
			stack.push(() => access?.close());
			const basePtr = fn.EPDF_LoadBaseDocument(access.ptr, opts.password ?? "");
			if (!basePtr) throw new EngineError(EngineErrorCode.DocOpenFailed, "failed to open base document");
			setRuntimeOwnerPermissionsIfEncrypted(this.runtime, basePtr);
			stack.push(() => fn.EPDF_ReleaseBaseDocument(basePtr));
			return this.insert(opts.key, basePtr, () => stack.close());
		} catch (error) {
			stack.close();
			throw error;
		}
	}
	releaseAll() {
		const entries = Array.from(this.entries.values());
		this.entries.clear();
		let firstError = null;
		for (const entry of entries) try {
			entry.close();
		} catch (error) {
			firstError ?? (firstError = error);
		}
		if (firstError) throw firstError;
	}
	/** @internal Test-only probe of the per-key share refcount. */
	getRefCountForTesting(key) {
		return this.entries.get(key)?.refs ?? 0;
	}
	retain(key) {
		const entry = this.entries.get(key);
		if (!entry) return null;
		entry.refs++;
		return this.handleFor(entry);
	}
	insert(key, basePtr, close) {
		const entry = {
			key,
			basePtr,
			refs: 1,
			close
		};
		this.entries.set(key, entry);
		return this.handleFor(entry);
	}
	handleFor(entry) {
		let released = false;
		return {
			key: entry.key,
			basePtr: entry.basePtr,
			release: () => {
				if (released) return;
				released = true;
				const live = this.entries.get(entry.key);
				if (!live) return;
				live.refs--;
				if (live.refs > 0) return;
				this.entries.delete(entry.key);
				live.close();
			}
		};
	}
};
//#endregion
//#region src/runtime/memory/scratch.ts
/**
* Allocate a scratch buffer, run `body` with it, and always free it.
*
* Every PDFium out-parameter call follows the same shape: malloc a
* buffer, hand its pointer to the native function, read the result back,
* free the buffer. Hand-rolling that `try/finally` at each call site is
* how leaks and copy-paste bugs creep in, so it lives here once.
*/
function withScratch(mem, bytes, body) {
	const ptr = mem.alloc(bytes);
	try {
		return body(ptr);
	} finally {
		mem.free(ptr);
	}
}
/**
* Like {@link withScratch} but for native calls that take several
* out-parameters at once (e.g. `FPDFAnnot_GetColor` with one pointer per
* channel). Buffers are freed in reverse allocation order.
*/
function withScratchN(mem, sizes, body) {
	const ptrs = sizes.map((size) => mem.alloc(size));
	try {
		return body(ptrs);
	} finally {
		for (let i = ptrs.length - 1; i >= 0; i--) mem.free(ptrs[i]);
	}
}
//#endregion
//#region src/runtime/memory/strings.ts
/**
* Read a PDFium UTF-16LE string getter that uses the standard
* (probe length, allocate, read) ABI. `call(buf, capacity)` must invoke
* the native function and return the length in BYTES including the
* trailing NUL (the value PDFium reports when `capacity` is 0).
*
* `emptyAs` decides what a present-but-empty value (`len === 2`, a lone
* UTF-16 NUL) decodes to. This is the one knob different callers actually
* disagree on: document Info strings treat `''` as a real value, whereas
* a page label of `''` is indistinguishable from "absent" and reads as
* `null`. Making it a parameter keeps that decision explicit instead of
* forking the buffer dance.
*
* Returns `null` when the value is absent (`len <= 0`).
*/
function readUtf16String(mem, call, emptyAs = "") {
	const len = call(NULL_PTR, 0);
	if (len <= 0) return null;
	if (len === 2) return emptyAs;
	return withScratch(mem, len, (buf) => {
		if (call(buf, len) <= 0) return null;
		return mem.readU16String(buf);
	});
}
/**
* UTF-8 sibling of {@link readUtf16String} for the handful of native
* getters that return UTF-8 (e.g. `EPDF_GetMetaKeyName`). UTF-8 has no
* fixed empty-NUL width to special-case, so an empty value decodes to
* `''`; absent (`len <= 0`) is `null`.
*/
function readUtf8String(mem, call) {
	const len = call(NULL_PTR, 0);
	if (len <= 0) return null;
	return withScratch(mem, len, (buf) => {
		if (call(buf, len) <= 0) return null;
		return mem.readU8String(buf);
	});
}
/**
* Write side of the UTF-16 convention: encode `value` into a scratch
* buffer, hand the pointer to `set`, and always free it. `set` returns
* the native call's success flag, which is propagated.
*/
function writeUtf16String(mem, value, set) {
	const ptr = mem.writeU16String(value);
	try {
		return set(ptr);
	} finally {
		mem.free(ptr);
	}
}
//#endregion
//#region src/features/metadata/internal/readMetadataText.ts
/**
* Read a key from the document's Info dictionary. Returns null if the key
* does not exist, '' if it exists but is empty, otherwise the UTF-16 string.
*
* `EPDF_HasMetaText` distinguishes an absent key (null) from a present
* empty one (''); the value itself uses the standard UTF-16 getter ABI
* (see `readUtf16String`, which keeps `''` as a real value here).
*/
function readMetaText(fn, mem, docPtr, key) {
	if (!fn.EPDF_HasMetaText(docPtr, key)) return null;
	return readUtf16String(mem, (buf, capacity) => fn.FPDF_GetMetaText(docPtr, key, buf, capacity), "");
}
//#endregion
//#region src/features/metadata/internal/readCustomMetadata.ts
/**
* Read the name of one key in the document's Info dictionary.
* customOnly = true returns only non-reserved (custom) keys, matching the
* behavior of `getMetaKeyName` in the legacy engine. Key names come back
* as UTF-8.
*/
function readMetaKeyName(fn, mem, docPtr, index, customOnly) {
	return readUtf8String(mem, (buf, capacity) => fn.EPDF_GetMetaKeyName(docPtr, index, customOnly, buf, capacity));
}
/**
* Read all custom metadata key/value pairs from the document's Info
* dictionary. The wire format guarantees `string` values, so absent keys
* are dropped (rather than represented as null).
*
* Mirrors the legacy `readAllMeta` (engine.ts:4698) but tightens the type
* to Record<string, string> for the wire format.
*/
function readAllCustomMeta(fn, mem, docPtr) {
	const count = Number(fn.EPDF_GetMetaKeyCount(docPtr, true)) | 0;
	const out = {};
	for (let i = 0; i < count; i++) {
		const key = readMetaKeyName(fn, mem, docPtr, i, true);
		if (!key) continue;
		const value = readMetaText(fn, mem, docPtr, key);
		if (value == null) continue;
		out[key] = value;
	}
	return out;
}
//#endregion
//#region src/features/metadata/internal/readTrappedStatus.ts
const TRAPPED_TRUE$1 = 1;
const TRAPPED_FALSE$1 = 2;
function readTrapped(fn, docPtr) {
	const raw = Number(fn.EPDF_GetMetaTrapped(docPtr));
	if (raw === TRAPPED_TRUE$1) return "true";
	if (raw === TRAPPED_FALSE$1) return "false";
	return "unknown";
}
//#endregion
//#region src/features/metadata/MetadataReader.ts
/**
* Synchronous, runtime-agnostic implementation of the metadata read.
*
* Lives in @embedpdf/engine-services so it can be reused unchanged by:
*   - @embedpdf/engine (inside a browser Worker, WASM runtime)
*   - @cloudpdf/server       (inside a Node worker_thread, native runtime)
*
* Async-ness lives at the worker boundary, not here.
*/
var MetadataReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	read(signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const doc = this.session.requireDocPtr();
		const title = readMetaText(fn, mem, doc, "Title");
		throwIfAborted(signal);
		const author = readMetaText(fn, mem, doc, "Author");
		throwIfAborted(signal);
		const subject = readMetaText(fn, mem, doc, "Subject");
		throwIfAborted(signal);
		const keywords = readMetaText(fn, mem, doc, "Keywords");
		throwIfAborted(signal);
		const producer = readMetaText(fn, mem, doc, "Producer");
		throwIfAborted(signal);
		const creator = readMetaText(fn, mem, doc, "Creator");
		throwIfAborted(signal);
		const creationRaw = readMetaText(fn, mem, doc, "CreationDate");
		throwIfAborted(signal);
		const modRaw = readMetaText(fn, mem, doc, "ModDate");
		throwIfAborted(signal);
		const trapped = readTrapped(fn, doc);
		throwIfAborted(signal);
		const custom = readAllCustomMeta(fn, mem, doc);
		return {
			title,
			author,
			subject,
			keywords,
			producer,
			creator,
			created: creationRaw ? pdfDateToIso(creationRaw) : null,
			modified: modRaw ? pdfDateToIso(modRaw) : null,
			trapped,
			custom
		};
	}
};
//#endregion
//#region src/features/metadata/internal/write/writeMetaText.ts
/**
* Write (or clear) one key in the document's Info dictionary.
*
*   value = null  -> remove the key (pass NULL_PTR; the fork's
*                    `EPDF_SetMetaText` deletes the entry on a null value)
*   value = "..."  -> set the key to the UTF-16 string (an empty string is
*                    a real, present-but-empty value, matching the reader's
*                    `emptyAs = ''` decode)
*
* Returns the native success flag. Mirrors v2 `setMetaText` (engine.ts),
* minus v2's "empty string removes" quirk — three-state patch semantics
* keep `null` (clear) and `''` (set empty) distinct.
*/
function writeMetaText(fn, mem, docPtr, key, value) {
	if (value === null) return fn.EPDF_SetMetaText(docPtr, key, NULL_PTR);
	return writeUtf16String(mem, value, (ptr) => fn.EPDF_SetMetaText(docPtr, key, ptr));
}
//#endregion
//#region src/features/metadata/internal/write/writeMetaTrapped.ts
const TRAPPED_TRUE = 1;
const TRAPPED_FALSE = 2;
const TRAPPED_UNKNOWN = 3;
/**
* Set the document's /Trapped Info entry from the wire enum. The v3 DTO
* only carries `true | false | unknown`, so there is no "clear" form here
* (unlike v2, which folded null into NotSet/removal); omit `trapped` from
* the patch to leave it untouched.
*/
function writeMetaTrapped(fn, docPtr, status) {
	const code = status === "true" ? TRAPPED_TRUE : status === "false" ? TRAPPED_FALSE : TRAPPED_UNKNOWN;
	return fn.EPDF_SetMetaTrapped(docPtr, code);
}
//#endregion
//#region src/features/metadata/internal/write/applyMetadataPatch.ts
/**
* Standard Info-dict keys the patch maps to explicitly. Custom keys may
* not collide with these (a custom write to `Title` would shadow the
* structured field), so they are skipped with the date keys and /Trapped.
*/
const RESERVED_INFO_KEYS = /* @__PURE__ */ new Set([
	"Title",
	"Author",
	"Subject",
	"Keywords",
	"Producer",
	"Creator",
	"CreationDate",
	"ModDate",
	"Trapped"
]);
/**
* Standard string field -> PDF Info key. Date fields and /Trapped are
* handled separately because they need format conversion / a dedicated
* native setter.
*/
const STRING_FIELDS = [
	["title", "Title"],
	["author", "Author"],
	["subject", "Subject"],
	["keywords", "Keywords"],
	["producer", "Producer"],
	["creator", "Creator"]
];
/**
* A custom key must be writable as a PDF Name and must not shadow a
* reserved standard key. Mirrors v2 `isValidCustomKey` (helper.ts):
* non-empty ASCII printable, <= 127 chars, no leading slash.
*/
function isValidCustomKey(key) {
	if (!key || key.length > 127) return false;
	if (RESERVED_INFO_KEYS.has(key)) return false;
	if (key[0] === "/") return false;
	for (let i = 0; i < key.length; i++) {
		const c = key.charCodeAt(i);
		if (c < 32 || c > 126) return false;
	}
	return true;
}
/**
* Apply a three-state {@link MetadataPatch} to the document's Info dict
* in place. `undefined` leaves a field, `null` clears it, a value sets
* it (dates are formatted to PDF date syntax). Custom keys are set/cleared
* per-key; reserved or malformed keys are skipped.
*
* Throws {@link EngineError} `Unknown` if any native write fails, so the
* caller never reports a partially-applied edit as success.
*/
function applyMetadataPatch(fn, mem, docPtr, patch) {
	const fail = (what) => {
		throw new EngineError(EngineErrorCode.Unknown, `failed to write metadata ${what}`);
	};
	for (const [field, key] of STRING_FIELDS) {
		const value = patch[field];
		if (value === void 0) continue;
		if (!writeMetaText(fn, mem, docPtr, key, value)) fail(key);
	}
	if (patch.created !== void 0) {
		if (!writeMetaText(fn, mem, docPtr, "CreationDate", patch.created === null ? null : formatPdfDate(new Date(patch.created)))) fail("CreationDate");
	}
	if (patch.modified !== void 0) {
		if (!writeMetaText(fn, mem, docPtr, "ModDate", patch.modified === null ? null : formatPdfDate(new Date(patch.modified)))) fail("ModDate");
	}
	if (patch.trapped !== void 0) {
		if (!writeMetaTrapped(fn, docPtr, patch.trapped)) fail("Trapped");
	}
	if (patch.custom !== void 0) for (const [key, value] of Object.entries(patch.custom)) {
		if (!isValidCustomKey(key)) continue;
		if (!writeMetaText(fn, mem, docPtr, key, value)) fail(key);
	}
}
//#endregion
//#region src/features/metadata/MetadataMutator.ts
/**
* Synchronous orchestrator for document metadata writes. Lives next to
* `MetadataReader` (one orchestrator per feature) so both worker hosts
* (browser Web Worker and Node `worker_thread`) share the same code path.
*
* A metadata write rewrites the document Info dictionary in place. Like
* `PagesMutator.move`, it is a structural layer edit: it returns the
* re-read metadata and a `null` cache (the worker never fills cloud
* coherence pins — the server does, after persisting the artifact and
* bumping `metadataVersion`).
*/
var MetadataMutator = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	update(patch, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		applyMetadataPatch(fn, mem, this.session.requireDocPtr(), patch);
		throwIfAborted(signal);
		return {
			metadata: new MetadataReader(this.runtime, this.session).read(signal),
			cache: null
		};
	}
};
//#endregion
//#region src/runtime/memory/structs.ts
/** Decode an `FS_RECTF` struct that a native call wrote into `ptr`. */
function readRectF(mem, ptr) {
	return {
		left: readF32(mem, ptr, 0),
		top: readF32(mem, ptr, 4),
		right: readF32(mem, ptr, 8),
		bottom: readF32(mem, ptr, 12)
	};
}
/** Decode an `FS_SIZEF` struct that a native call wrote into `ptr`. */
function readSizeF(mem, ptr) {
	return {
		width: readF32(mem, ptr, 0),
		height: readF32(mem, ptr, 4)
	};
}
/** Read a single 32-bit float at `byteOffset` within `ptr`. */
function readF32(mem, ptr, byteOffset = 0) {
	return Number(mem.peek(ptr, "f32", byteOffset));
}
/** Read a single 32-bit int at `byteOffset` within `ptr`. */
function readI32(mem, ptr, byteOffset = 0) {
	return Number(mem.peek(ptr, "i32", byteOffset));
}
//#endregion
//#region src/features/actions/ActionModelReader.ts
const DEFAULT_ACTION_READ_BUDGET = {
	maxModels: 512,
	maxNodes: 16384,
	maxScriptCodeUnits: 16e6
};
const ACTION_TYPE_BY_CODE = {
	0: "unknown",
	1: "goto",
	2: "goto-remote",
	3: "goto-embedded",
	4: "launch",
	5: "thread",
	6: "uri",
	7: "sound",
	8: "movie",
	9: "hide",
	10: "named",
	11: "submit-form",
	12: "reset-form",
	13: "import-data",
	14: "javascript",
	15: "set-ocg-state",
	16: "rendition",
	17: "transition",
	18: "goto-3d-view"
};
const WARNING_CYCLE_DROPPED = 1;
const WARNING_MALFORMED_NEXT = 2;
const WARNING_INCOMPLETE = 4;
const INVALID_NODE_U32 = 4294967295;
/** Decode one `EPDF_ACTION_TYPE_*` code — the shared vocabulary consumers
*  (the link-target projection) use so the two surfaces cannot drift. */
function actionTypeFromCode(code) {
	return ACTION_TYPE_BY_CODE[code] ?? "unknown";
}
/** Is this `EPDFAction_GetRootNode`/`GetNextAt` result a real node id?
*  (`EPDF_ACTION_NODE_INVALID` may surface as -1 or u32 max via cwrap.) */
function isValidActionNodeId(raw) {
	return raw !== -1 && raw >>> 0 !== INVALID_NODE_U32;
}
/** Mutable aggregate budget shared by every action model in one read job. */
var ActionReadBudgetTracker = class {
	constructor(limits = DEFAULT_ACTION_READ_BUDGET) {
		this.limits = limits;
		_defineProperty(this, "models", 0);
		_defineProperty(this, "nodes", 0);
		_defineProperty(this, "scriptCodeUnits", 0);
	}
	addModel(nodeCount) {
		this.models += 1;
		this.nodes += nodeCount;
		this.assertWithinBudget();
	}
	addScript(script) {
		this.scriptCodeUnits += script.length;
		this.assertWithinBudget();
	}
	assertWithinBudget() {
		if (this.models > this.limits.maxModels || this.nodes > this.limits.maxNodes || this.scriptCodeUnits > this.limits.maxScriptCodeUnits) throw new EngineError(EngineErrorCode.MalformedPdf, "aggregate PDF action read budget exceeded", { details: {
			models: this.models,
			nodes: this.nodes,
			scriptCodeUnits: this.scriptCodeUnits,
			limits: this.limits
		} });
	}
};
/**
* Convert and close one detached native action model. This is the only
* native-model walker used by document, page, field, and annotation reads.
*/
function readActionModel(fn, mem, modelPtr, budget) {
	if (modelPtr === NULL_PTR) return null;
	try {
		const nodeCount = fn.EPDFAction_GetNodeCount(modelPtr);
		if (!Number.isInteger(nodeCount) || nodeCount < 0) throw malformedActionModel("invalid node count", { nodeCount });
		budget.addModel(nodeCount);
		const warningFlags = fn.EPDFAction_GetWarningFlags(modelPtr) >>> 0;
		const incomplete = !fn.EPDFAction_IsComplete(modelPtr);
		const rootId = normalizeOptionalNodeId(fn.EPDFAction_GetRootNode(modelPtr), nodeCount);
		const visiting = /* @__PURE__ */ new Set();
		const readNode = (nodeId) => {
			if (visiting.has(nodeId)) throw malformedActionModel("native action model contains a cycle", { nodeId });
			visiting.add(nodeId);
			try {
				const subtype = readUtf8String(mem, (buf, capacity) => fn.EPDFAction_GetNodeSubtype(modelPtr, nodeId, buf, capacity)) ?? "";
				const script = fn.EPDFAction_NodeHasJavaScript(modelPtr, nodeId) ? readUtf16String(mem, (buf, capacity) => fn.EPDFAction_GetNodeJavaScript(modelPtr, nodeId, buf, capacity)) ?? "" : void 0;
				if (script !== void 0) budget.addScript(script);
				const nextCount = fn.EPDFAction_GetNextCount(modelPtr, nodeId);
				if (!Number.isInteger(nextCount) || nextCount < 0) throw malformedActionModel("invalid action child count", {
					nodeId,
					nextCount
				});
				const next = [];
				for (let index = 0; index < nextCount; index++) {
					const childId = normalizeNodeId(fn.EPDFAction_GetNextAt(modelPtr, nodeId, index), nodeCount);
					next.push(readNode(childId));
				}
				return {
					type: ACTION_TYPE_BY_CODE[fn.EPDFAction_GetNodeType(modelPtr, nodeId)] ?? "unknown",
					subtype,
					...script !== void 0 ? { script } : {},
					next
				};
			} finally {
				visiting.delete(nodeId);
			}
		};
		return {
			root: rootId === null ? null : readNode(rootId),
			incomplete,
			warningFlags,
			warnings: warningsFromFlags(warningFlags, incomplete)
		};
	} finally {
		fn.EPDFAction_CloseModel(modelPtr);
	}
}
function normalizeOptionalNodeId(raw, nodeCount) {
	return raw === -1 || raw === INVALID_NODE_U32 || raw >>> 0 === INVALID_NODE_U32 ? null : normalizeNodeId(raw, nodeCount);
}
function normalizeNodeId(raw, nodeCount) {
	if (!Number.isInteger(raw) || raw < 0 || raw >= nodeCount) throw malformedActionModel("native action model returned an invalid node id", {
		raw,
		nodeCount
	});
	return raw;
}
function warningsFromFlags(flags, incomplete) {
	const warnings = [];
	if ((flags & WARNING_CYCLE_DROPPED) !== 0) warnings.push("cycle-dropped");
	if ((flags & WARNING_MALFORMED_NEXT) !== 0) warnings.push("malformed-next");
	if ((flags & WARNING_INCOMPLETE) !== 0 || incomplete) warnings.push("incomplete");
	return warnings;
}
function malformedActionModel(message, details) {
	return new EngineError(EngineErrorCode.MalformedPdf, message, { details });
}
//#endregion
//#region src/features/pages/PagesReader.ts
const BOX_MEDIA = 0;
const BOX_CROP = 1;
const BOX_BLEED = 2;
const BOX_TRIM = 3;
const BOX_ART = 4;
/**
* Runtime-agnostic page geometry reader. Produces the `pages.list()`
* snapshot from the lightweight `...ByIndex` PDFium bindings, none of which
* load or parse a page (no `pagePtr`), so listing stays cheap. Shared
* verbatim by the local WASM worker and the server native worker, so a
* given PDF yields an identical `PageListSnapshot` on both engines.
*/
var PagesReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	read(signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const records = this.session.allRecords();
		const actionBudget = new ActionReadBudgetTracker();
		return withScratchN(mem, [
			16,
			8,
			4
		], ([rectPtr, sizePtr, userUnitPtr]) => {
			const pages = records.map((record) => {
				throwIfAborted(signal);
				const index = record.pageIndex;
				const actions = readPageActions(fn, mem, docPtr, record.pageObjectNumber, actionBudget);
				return {
					index,
					pageObjectNumber: record.pageObjectNumber,
					label: readLabel(fn, mem, docPtr, index),
					size: readSize(fn, mem, docPtr, index, sizePtr),
					rotation: readRotation(fn, docPtr, index),
					userUnit: readUserUnit(fn, mem, docPtr, index, userUnitPtr),
					boxes: readBoxes(fn, mem, docPtr, index, rectPtr),
					...actions ? { actions } : {}
				};
			});
			return {
				pageCount: pages.length,
				pages
			};
		});
	}
};
function readPageActions(fn, mem, docPtr, pageObjectNumber, budget) {
	const actions = {};
	const open = readActionModel(fn, mem, fn.EPDFDoc_GetPageActionModel(docPtr, pageObjectNumber, 0), budget);
	const close = readActionModel(fn, mem, fn.EPDFDoc_GetPageActionModel(docPtr, pageObjectNumber, 1), budget);
	if (open) actions.open = open;
	if (close) actions.close = close;
	return open || close ? actions : void 0;
}
/**
* Read one FS_RECTF box and canonicalize to a y-up `PdfRect`
* (`{ left, bottom, right, top }`, equivalent to `[llx, lly, urx, ury]`) so
* the lower-left/upper-right invariant holds regardless of how the PDF
* ordered the corners. Returns null when the optional box is absent.
*/
function readBox(fn, mem, docPtr, index, boxType, rectPtr) {
	if (!fn.EPDF_GetPageBoxByIndex(docPtr, index, boxType, rectPtr)) return null;
	return normalizePdfRect(readRectF(mem, rectPtr));
}
function readBoxes(fn, mem, docPtr, index, rectPtr) {
	const media = readBox(fn, mem, docPtr, index, BOX_MEDIA, rectPtr) ?? {
		left: 0,
		bottom: 0,
		right: 0,
		top: 0
	};
	const crop = readBox(fn, mem, docPtr, index, BOX_CROP, rectPtr) ?? media;
	const bleed = readBox(fn, mem, docPtr, index, BOX_BLEED, rectPtr);
	const trim = readBox(fn, mem, docPtr, index, BOX_TRIM, rectPtr);
	const art = readBox(fn, mem, docPtr, index, BOX_ART, rectPtr);
	return {
		media,
		crop,
		...bleed ? { bleed } : {},
		...trim ? { trim } : {},
		...art ? { art } : {}
	};
}
function readSize(fn, mem, docPtr, index, sizePtr) {
	if (!fn.EPDF_GetPageSizeByIndexNormalized(docPtr, index, sizePtr)) return {
		width: 0,
		height: 0
	};
	return readSizeF(mem, sizePtr);
}
function readRotation(fn, docPtr, index) {
	switch (fn.EPDF_GetPageRotationByIndex(docPtr, index)) {
		case 1: return 90;
		case 2: return 180;
		case 3: return 270;
		default: return 0;
	}
}
function readUserUnit(fn, mem, docPtr, index, userUnitPtr) {
	if (!fn.EPDF_GetPageUserUnitByIndex(docPtr, index, userUnitPtr)) return 1;
	const value = readF32(mem, userUnitPtr, 0);
	return Number.isFinite(value) && value > 0 ? value : 1;
}
function readLabel(fn, mem, docPtr, index) {
	return readUtf16String(mem, (buf, capacity) => fn.FPDF_GetPageLabel(docPtr, index, buf, capacity), null) || null;
}
//#endregion
//#region src/features/pages/PagesMutator.ts
/**
* Synchronous orchestrator for page-level reads and reorder mutations.
* Lives next to `AnnotationMutator` (one orchestrator per
* mutation domain) so both worker hosts (browser Web Worker and Node
* `worker_thread`) share the same code path.
*
* Architectural anchor — locked with the user, do not loosen without
* re-reading the doc comments on `PageMoveResult` and `RevisionAuthority`:
*
*   - Pages are addressed by their durable `pageObjectNumber`. There is
*     no "weak page ref" model in the engine; therefore there is no
*     document-level revision token, no `DocumentRevisionStore`, and no
*     "doc-level shouldRefetch" semantic. Anything that *is* listed
*     here must remain stable across every reorder permutation.
*
*   - Per-page `RevisionToken`s do NOT bump on `move()`. The /Annots
*     array of each affected page is untouched (PDFium just rewrites
*     pointer entries in the doc-level pages tree), so weak
*     `AnnotationRef.kind === 'index'` references the caller is
*     holding remain valid across a page reorder. This is the right
*     semantic for an editing UI: shuffling pages must NOT silently
*     break a pending highlight edit.
*
*   - Identity strengthening (the opportunistic `/NM` stamping that
*     applies to weak annotations on `update()` / `move()`) is also
*     intentionally absent here. Pages are durable by construction;
*     there is nothing to upgrade.
*/
var PagesMutator = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	/**
	* Reorder pages. Mirrors `FPDF_MovePages`: detach the supplied pages,
	* then re-insert them as a contiguous block at `destIndex` in the
	* post-removal index space, preserving caller order.
	*
	* Atomicity:
	*   - `FPDF_MovePages` rejects atomically: if it returns false, no
	*     change has happened. We surface that as `InvalidArg`.
	*   - On success we refresh the per-session page registry and read the
	*     new layout back, which is what the result returns.
	*
	* Validation done up front (the helper repeats these checks; we do
	* them here for clean error messages):
	*   - non-empty inputs;
	*   - duplicate `pageObjectNumber`s rejected;
	*   - every `pon` resolvable via the session's page registry;
	*   - `destIndex` in `[0, pageCount - len]`.
	*/
	move(pageObjectNumbers, destIndex, signal) {
		throwIfAborted(signal);
		this.requireUniquePons("pages.move", pageObjectNumbers);
		if (destIndex < 0 || !Number.isInteger(destIndex)) throw new EngineError(EngineErrorCode.InvalidArg, `pages.move destIndex must be a non-negative integer (got ${destIndex})`);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const postRemoval = fn.FPDF_GetPageCount(docPtr) - pageObjectNumbers.length;
		if (destIndex > postRemoval) throw new EngineError(EngineErrorCode.InvalidArg, `pages.move destIndex ${destIndex} out of range; post-removal page count is ${postRemoval}`);
		const fromIndices = pageObjectNumbers.map((pon) => {
			throwIfAborted(signal);
			return this.session.recordByObjectNumber(pon).pageIndex;
		});
		const arrPtr = mem.alloc(4 * fromIndices.length);
		let ok;
		try {
			for (let i = 0; i < fromIndices.length; i++) mem.poke(arrPtr, "i32", fromIndices[i], 4 * i);
			ok = fn.FPDF_MovePages(docPtr, arrPtr, fromIndices.length, destIndex);
		} finally {
			mem.free(arrPtr);
		}
		if (!ok) throw new EngineError(EngineErrorCode.InvalidArg, `FPDF_MovePages rejected the request (destIndex=${destIndex}, fromIndices=[${fromIndices.join(",")}])`);
		this.session.refreshPageRegistry();
		return {
			layout: new PagesReader(this.runtime, this.session).read(signal),
			cache: null
		};
	}
	/**
	* Set the ABSOLUTE display rotation of the supplied pages. Rotation is
	* presentation metadata over normalized content (pages always load with
	* rotation forced to 0 — see `PagePtrPool`), so:
	*   - no cached render, text run, or geometry coordinate changes;
	*   - per-page `RevisionToken`s do NOT bump;
	*   - page identity and order are untouched — no registry refresh.
	*
	* Atomicity: every PON is resolved up front (`NotFound` on caller error),
	* so the apply loop below operates on validated pages only and each write
	* is absolute + idempotent — a retry after an unexpected mid-loop engine
	* fault converges to the requested state. Abort is honored BEFORE the
	* loop, never inside it.
	*/
	rotate(pageObjectNumbers, rotation, signal) {
		throwIfAborted(signal);
		this.requireUniquePons("pages.rotate", pageObjectNumbers);
		if (rotation !== 0 && rotation !== 90 && rotation !== 180 && rotation !== 270) throw new EngineError(EngineErrorCode.InvalidArg, `pages.rotate rotation must be 0, 90, 180 or 270 (got ${rotation})`);
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		for (const pon of pageObjectNumbers) this.session.recordByObjectNumber(pon);
		const quarterTurns = rotation / 90;
		for (const pon of pageObjectNumbers) if (!fn.EPDFDoc_SetPageRotationByObjectNumber(docPtr, pon, quarterTurns)) throw new EngineError(EngineErrorCode.Unknown, `EPDFDoc_SetPageRotationByObjectNumber rejected page ${pon} after validation`);
		return {
			layout: new PagesReader(this.runtime, this.session).read(signal),
			cache: null
		};
	}
	/**
	* Delete pages. Deleted object numbers are RETIRED — the engine nulls the
	* page object rather than freeing the number — so per-page state keyed by
	* PON can never silently attach to an unrelated future page; we still drop
	* the session's revision/weak entries as hygiene. Surviving pages keep
	* their identity and `RevisionToken`s.
	*
	* Guards:
	*   - a document must keep at least one page (`InvalidArg`);
	*   - every PON must resolve (`NotFound`);
	*   - no target page may have a live pooled `pagePtr`. Thread confinement
	*     means no other job can be mid-flight, so a held ptr here is a leaked
	*     `acquire` — an internal bug we surface loudly instead of deleting
	*     under a live handle.
	*
	* Abort is honored BEFORE the apply loop, never inside it.
	*/
	delete(pageObjectNumbers, signal) {
		throwIfAborted(signal);
		this.requireUniquePons("pages.delete", pageObjectNumbers);
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const totalPages = fn.FPDF_GetPageCount(docPtr);
		if (pageObjectNumbers.length >= totalPages) throw new EngineError(EngineErrorCode.InvalidArg, `pages.delete would remove every page (${pageObjectNumbers.length} of ${totalPages}); a document must keep at least one`);
		const pool = this.session.pagePool();
		for (const pon of pageObjectNumbers) {
			this.session.recordByObjectNumber(pon);
			if (pool.isHeld(pon)) throw new EngineError(EngineErrorCode.Unknown, `internal invariant violation: pagePtr for page ${pon} is still held during pages.delete`);
		}
		for (const pon of pageObjectNumbers) {
			if (!fn.EPDFDoc_DeletePageByObjectNumber(docPtr, pon)) throw new EngineError(EngineErrorCode.Unknown, `EPDFDoc_DeletePageByObjectNumber rejected page ${pon} after validation`);
			this.session.dropPageState(pon);
		}
		this.session.refreshPageRegistry();
		return {
			layout: new PagesReader(this.runtime, this.session).read(signal),
			cache: null
		};
	}
	/** Shared input check: non-empty, no duplicate PONs. */
	requireUniquePons(op, pageObjectNumbers) {
		if (pageObjectNumbers.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, `${op} requires at least one page`);
		const seen = /* @__PURE__ */ new Set();
		for (const pon of pageObjectNumbers) {
			if (seen.has(pon)) throw new EngineError(EngineErrorCode.InvalidArg, `${op} was given duplicate page object number ${pon}`);
			seen.add(pon);
		}
	}
};
//#endregion
//#region src/features/pages/PagesExtractor.ts
const FPDF_NO_INCREMENTAL$1 = 2;
/**
* Export a subset of pages as a standalone PDF. A READ over the session:
* `FPDF_ImportPagesByIndex` copies pages into a scratch document, so the
* source is untouched — no revision bumps, no registry refresh, no layer
* artifact. Lives next to `PagesReader`/`PagesMutator` (one file per page
* verb family) so both worker hosts share the code path.
*/
var PagesExtractor = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	/**
	* Copy the given pages, in the supplied caller order, into a new
	* document and serialize it. Validation up front: non-empty, no
	* duplicates, every PON resolvable (`NotFound` from the session on a
	* bad one). The scratch document is always closed, success or throw.
	*/
	extract(pageObjectNumbers, signal) {
		throwIfAborted(signal);
		if (pageObjectNumbers.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "pages.extract requires at least one page");
		const seen = /* @__PURE__ */ new Set();
		for (const pon of pageObjectNumbers) {
			if (seen.has(pon)) throw new EngineError(EngineErrorCode.InvalidArg, `pages.extract was given duplicate page object number ${pon}`);
			seen.add(pon);
		}
		const indices = pageObjectNumbers.map((pon) => this.session.recordByObjectNumber(pon).pageIndex);
		const { fn, mem } = this.runtime;
		const srcPtr = this.session.requireDocPtr();
		const destPtr = fn.FPDF_CreateNewDocument();
		if (!destPtr) throw new EngineError(EngineErrorCode.Unknown, "FPDF_CreateNewDocument failed");
		const idxPtr = mem.alloc(4 * indices.length);
		const sizePtr = mem.alloc(4);
		let pdfPtr = null;
		try {
			for (let i = 0; i < indices.length; i++) mem.poke(idxPtr, "i32", indices[i], 4 * i);
			if (!fn.FPDF_ImportPagesByIndex(destPtr, srcPtr, idxPtr, indices.length, 0)) throw new EngineError(EngineErrorCode.Unknown, `FPDF_ImportPagesByIndex rejected the request (indices=[${indices.join(",")}])`);
			mem.poke(sizePtr, "i32", 0);
			pdfPtr = fn.EPDF_SaveDocumentToOwnedBuffer(destPtr, FPDF_NO_INCREMENTAL$1, sizePtr);
			const size = Number(mem.peek(sizePtr, "i32"));
			if (!pdfPtr || size <= 0) throw new EngineError(EngineErrorCode.DocOpenFailed, "failed to save extracted pages");
			const bytes = mem.readBytes(pdfPtr, size);
			const buffer = new ArrayBuffer(bytes.byteLength);
			new Uint8Array(buffer).set(bytes);
			return {
				bytes: buffer,
				size
			};
		} finally {
			if (pdfPtr) fn.EPDF_FreeBuffer(pdfPtr);
			mem.free(sizePtr);
			mem.free(idxPtr);
			fn.FPDF_CloseDocument(destPtr);
		}
	}
};
//#endregion
//#region src/features/pages/PagesInserter.ts
/**
* Insert every page of a standalone PDF into the session document. A
* structural MUTATION (like move/delete): the source bytes are loaded as a
* throwaway PDFium document, `FPDF_ImportPagesByIndex` deep-copies its
* pages in at `destIndex`, and the page registry is rebuilt. Pre-existing
* pages keep their identity and `RevisionToken`s; the inserted copies get
* fresh object numbers, resolved from the post-insert registry.
*/
var PagesInserter = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	insert(bytes, destIndex, signal) {
		throwIfAborted(signal);
		if (bytes.byteLength === 0) throw new EngineError(EngineErrorCode.InvalidArg, "pages.insert requires non-empty bytes");
		const { fn, mem } = this.runtime;
		const destPtr = this.session.requireDocPtr();
		const beforeCount = fn.FPDF_GetPageCount(destPtr);
		const at = destIndex ?? beforeCount;
		if (!Number.isInteger(at) || at < 0 || at > beforeCount) throw new EngineError(EngineErrorCode.InvalidArg, `pages.insert destIndex ${at} out of range [0, ${beforeCount}]`);
		const dataPtr = mem.alloc(bytes.byteLength);
		let srcPtr = null;
		let insertedCount = 0;
		try {
			mem.writeBytes(dataPtr, new Uint8Array(bytes));
			srcPtr = fn.FPDF_LoadMemDocument(dataPtr, bytes.byteLength, "");
			if (!srcPtr) throw new EngineError(EngineErrorCode.MalformedPdf, "pages.insert source PDF could not be opened");
			insertedCount = fn.FPDF_GetPageCount(srcPtr);
			if (insertedCount <= 0) throw new EngineError(EngineErrorCode.InvalidArg, "pages.insert source PDF has no pages");
			if (!fn.FPDF_ImportPagesByIndex(destPtr, srcPtr, NULL_PTR, 0, at)) throw new EngineError(EngineErrorCode.Unknown, `FPDF_ImportPagesByIndex rejected the insert at index ${at}`);
			const retainedSrc = srcPtr;
			this.session.retainUntilClose(() => {
				fn.FPDF_CloseDocument(retainedSrc);
				mem.free(dataPtr);
			});
		} catch (error) {
			if (srcPtr) fn.FPDF_CloseDocument(srcPtr);
			mem.free(dataPtr);
			throw error;
		}
		this.session.refreshPageRegistry();
		const layout = new PagesReader(this.runtime, this.session).read(signal);
		return {
			insertedPageObjectNumbers: layout.pages.slice(at, at + insertedCount).map((page) => page.pageObjectNumber),
			layout,
			cache: null
		};
	}
};
//#endregion
//#region src/features/annotations/internal/read/annotationReadContext.ts
/** Build the read context both read paths (list loop, mutator read-back) use. */
function readContextFor(session) {
	return { docPtr: session.requireDocPtr() };
}
//#endregion
//#region src/features/annotations/internal/annotationFlagBits.ts
/**
* Bit positions of the `/F` (Annotation Flags) entry per ISO 32000 §12.5.3.
* The single source of truth shared by the flag reader and writer so they
* can never drift apart.
*/
const ANNOT_FLAG_BITS = {
	invisible: 1,
	hidden: 2,
	print: 4,
	noZoom: 8,
	noRotate: 16,
	noView: 32,
	readOnly: 64,
	locked: 128,
	toggleNoView: 256,
	lockedContents: 512
};
const FLAG_KEYS = Object.keys(ANNOT_FLAG_BITS);
/** Expand a raw `/F` bitset into the typed boolean object. */
function bitsToFlags(bits) {
	if (bits === 0) return { ...NO_ANNOTATION_FLAGS };
	const flags = { ...NO_ANNOTATION_FLAGS };
	for (const key of FLAG_KEYS) flags[key] = (bits & ANNOT_FLAG_BITS[key]) !== 0;
	return flags;
}
/**
* Fold a partial flag patch onto an existing `/F` bitset. Only keys present
* in `partial` are touched; absent keys preserve their current bit. This is
* what lets `create` start from 0 and `update` merge onto the live value.
*/
function flagsToBits(current, partial) {
	let bits = current;
	for (const key of FLAG_KEYS) {
		const value = partial[key];
		if (value === void 0) continue;
		if (value) bits |= ANNOT_FLAG_BITS[key];
		else bits &= ~ANNOT_FLAG_BITS[key];
	}
	return bits;
}
//#endregion
//#region src/features/annotations/internal/lineEnding.ts
/**
* `FPDF_ANNOT_LINE_END` enum codes from `public/fpdf_annot.h`, mapped to the
* wire-stable kebab-case `LineEnding` strings. We keep this PDFium-specific
* mapping in engine-services so engine-core stays free of any PDFium
* dependency (mirrors `shapeBorderStyle.ts`).
*
*   None=0, Square=1, Circle=2, Diamond=3, OpenArrow=4, ClosedArrow=5,
*   Butt=6, ROpenArrow=7, RClosedArrow=8, Slash=9, Unknown=10
*
* `Unknown` has no wire representation, so it round-trips as `'none'`.
*/
const LE_NONE = 0;
const LE_SQUARE = 1;
const LE_CIRCLE = 2;
const LE_DIAMOND = 3;
const LE_OPEN_ARROW = 4;
const LE_CLOSED_ARROW = 5;
const LE_BUTT = 6;
const LE_R_OPEN_ARROW = 7;
const LE_R_CLOSED_ARROW = 8;
const LE_SLASH = 9;
function lineEndingToCode(ending) {
	switch (ending) {
		case "square": return LE_SQUARE;
		case "circle": return LE_CIRCLE;
		case "diamond": return LE_DIAMOND;
		case "open-arrow": return LE_OPEN_ARROW;
		case "closed-arrow": return LE_CLOSED_ARROW;
		case "butt": return LE_BUTT;
		case "r-open-arrow": return LE_R_OPEN_ARROW;
		case "r-closed-arrow": return LE_R_CLOSED_ARROW;
		case "slash": return LE_SLASH;
		default: return LE_NONE;
	}
}
function lineEndingFromCode(code) {
	switch (code) {
		case LE_SQUARE: return "square";
		case LE_CIRCLE: return "circle";
		case LE_DIAMOND: return "diamond";
		case LE_OPEN_ARROW: return "open-arrow";
		case LE_CLOSED_ARROW: return "closed-arrow";
		case LE_BUTT: return "butt";
		case LE_R_OPEN_ARROW: return "r-open-arrow";
		case LE_R_CLOSED_ARROW: return "r-closed-arrow";
		case LE_SLASH: return "slash";
		case LE_NONE:
		default: return "none";
	}
}
//#endregion
//#region src/features/annotations/internal/read/annotationReadPrimitives.ts
/**
* Reads a UTF-16 string entry from an annotation dictionary. Returns
* `null` if the key is not present, otherwise the (possibly empty) string.
*/
function readAnnotString(fn, mem, annotPtr, key) {
	if (!fn.FPDFAnnot_HasKey(annotPtr, key)) return null;
	return readUtf16String(mem, (buf, capacity) => fn.FPDFAnnot_GetStringValue(annotPtr, key, buf, capacity), "");
}
/**
* Read /Rect from an annot dict via FPDFAnnot_GetRect.
* FS_RECTF layout: { float left, top, right, bottom } -> 16 bytes.
*/
function readAnnotRect(fn, mem, annotPtr) {
	return withScratch(mem, 16, (buf) => {
		if (!fn.FPDFAnnot_GetRect(annotPtr, buf)) return {
			left: 0,
			bottom: 0,
			right: 0,
			top: 0
		};
		return readRectF(mem, buf);
	});
}
function readAnnotFlags(fn, annotPtr) {
	return bitsToFlags(fn.FPDFAnnot_GetFlags(annotPtr));
}
/**
* Read an annotation color via the EmbedPDF `EPDFAnnot_GetColor`
* extension. The `type` arg (FPDFANNOT_COLORTYPE) selects which color to
* read — `0` (default) is the stroke/fill `/C`, `1` is the interior `/IC`
* of square/circle/polygon annotations. Unlike the stock
* `FPDFAnnot_GetColor`, the extension exposes interior color. Returns RGB
* only — annotation opacity lives in `/CA`, not a per-color alpha.
*
* Returns `null` when the requested color entry is absent.
*/
function readAnnotColor(fn, mem, annotPtr, type = 0) {
	return withScratchN(mem, [
		4,
		4,
		4
	], ([r, g, b]) => {
		if (!fn.EPDFAnnot_GetColor(annotPtr, type, r, g, b)) return null;
		return {
			r: readI32(mem, r) & 255,
			g: readI32(mem, g) & 255,
			b: readI32(mem, b) & 255
		};
	});
}
/**
* Read annotation opacity via the EmbedPDF `EPDFAnnot_GetOpacity`
* extension. Returns a 0..1 value (the native alpha is 0..255). Returns
* `null` when the annotation has no opacity entry. This is the path that
* stays consistent across native `EPDFAnnot_GenerateAppearance`, unlike a
* raw `/CA` number read.
*/
function readAnnotOpacity(fn, mem, annotPtr) {
	return withScratch(mem, 4, (buf) => {
		if (!fn.EPDFAnnot_GetOpacity(annotPtr, buf)) return null;
		return (readI32(mem, buf) & 255) / 255;
	});
}
/**
* Read the effective border style and width via
* `EPDFAnnot_GetBorderStyle`, which resolves `/BS`, legacy `/Border`, and
* the ISO defaults. The return value is the raw `FPDF_ANNOT_BORDER_STYLE`
* enum code; the width is written into the scratch out-parameter.
* Style/width string mapping lives in the shape reader (engine-core stays
* PDFium-free).
*/
function readBorderStyle(fn, mem, annotPtr) {
	return withScratch(mem, 4, (buf) => {
		return {
			styleCode: fn.EPDFAnnot_GetBorderStyle(annotPtr, buf),
			width: readF32(mem, buf)
		};
	});
}
/**
* Read the dash pattern of a dashed border. Resolves modern `/BS /D` first,
* then the fourth element of the legacy `/Border` array. Returns an empty
* array when the effective border is not dashed or has no explicit pattern.
*/
function readBorderDashPattern(fn, mem, annotPtr) {
	const count = fn.EPDFAnnot_GetBorderDashPatternCount(annotPtr);
	if (count <= 0) return [];
	return withScratch(mem, count * 4, (buf) => {
		if (!fn.EPDFAnnot_GetBorderDashPattern(annotPtr, buf, count)) return [];
		const out = [];
		for (let i = 0; i < count; i++) out.push(readF32(mem, buf, i * 4));
		return out;
	});
}
/**
* Read the `/BE` cloudy border intensity. Returns `null` when the
* annotation has no cloudy border effect. The DTO domain is positive-or-null,
* so a degenerate foreign `/BE /I 0` (no visible clouds) normalizes to `null`
* rather than leaking an out-of-schema zero; a `/BE` without `/I` reads as 1
* (the runtime's default).
*/
function readBorderEffect(fn, mem, annotPtr) {
	return withScratch(mem, 4, (buf) => {
		if (!fn.EPDFAnnot_GetBorderEffect(annotPtr, buf)) return null;
		const intensity = readF32(mem, buf);
		return intensity > 0 ? intensity : null;
	});
}
/**
* Read the `/RD` rectangle differences. Returns `null` when the
* annotation has no `/RD` entry. PDFium reports `/RD` in
* `[left, bottom, right, top]` order; we surface the wire-stable
* `{ left, top, right, bottom }` shape.
*/
function readRectangleDifferences(fn, mem, annotPtr) {
	return withScratchN(mem, [
		4,
		4,
		4,
		4
	], ([left, bottom, right, top]) => {
		if (!fn.EPDFAnnot_GetRectangleDifferences(annotPtr, left, bottom, right, top)) return null;
		return {
			left: readF32(mem, left),
			bottom: readF32(mem, bottom),
			right: readF32(mem, right),
			top: readF32(mem, top)
		};
	});
}
/**
* Read the `/Vertices` point list of a polygon/polyline annotation via
* the two-call `FPDFAnnot_GetVertices` pattern (probe for the count with
* a NULL buffer, then read into a `count * FS_POINTF` buffer). Returns an
* empty array when the annotation has no vertices.
*/
function readVertices(fn, mem, annotPtr) {
	const count = fn.FPDFAnnot_GetVertices(annotPtr, NULL_PTR, 0);
	if (count <= 0) return [];
	return withScratch(mem, count * 8, (buf) => {
		const got = fn.FPDFAnnot_GetVertices(annotPtr, buf, count);
		if (got <= 0) return [];
		const out = [];
		for (let i = 0; i < got; i++) {
			const off = i * 8;
			out.push({
				x: readF32(mem, buf, off),
				y: readF32(mem, buf, off + 4)
			});
		}
		return out;
	});
}
/**
* Read the `/InkList` of an ink annotation. `FPDFAnnot_GetInkListCount`
* gives the number of strokes; each stroke is sized with a probe call to
* `FPDFAnnot_GetInkListPath` (NULL buffer) and then read into a
* `count * FS_POINTF` buffer. Empty strokes are skipped; the result is an
* array of non-empty point paths.
*/
function readInkList(fn, mem, annotPtr) {
	const pathCount = fn.FPDFAnnot_GetInkListCount(annotPtr);
	if (pathCount <= 0) return [];
	const out = [];
	for (let p = 0; p < pathCount; p++) {
		const count = fn.FPDFAnnot_GetInkListPath(annotPtr, p, NULL_PTR, 0);
		if (count <= 0) continue;
		const stroke = withScratch(mem, count * 8, (buf) => {
			const got = fn.FPDFAnnot_GetInkListPath(annotPtr, p, buf, count);
			if (got <= 0) return [];
			const pts = [];
			for (let i = 0; i < got; i++) {
				const off = i * 8;
				pts.push({
					x: readF32(mem, buf, off),
					y: readF32(mem, buf, off + 4)
				});
			}
			return pts;
		});
		if (stroke.length > 0) out.push(stroke);
	}
	return out;
}
/**
* Read the `/L` endpoints of a line annotation via `FPDFAnnot_GetLine`
* (two `FS_POINTF` out-params). Returns `null` when the annotation is not
* a line or has no `/L` entry.
*/
function readLine$1(fn, mem, annotPtr) {
	return withScratchN(mem, [8, 8], ([start, end]) => {
		if (!fn.FPDFAnnot_GetLine(annotPtr, start, end)) return null;
		return {
			start: {
				x: readF32(mem, start, 0),
				y: readF32(mem, start, 4)
			},
			end: {
				x: readF32(mem, end, 0),
				y: readF32(mem, end, 4)
			}
		};
	});
}
/**
* Read the `/LE` line endings of a line/polyline annotation via the
* EmbedPDF `EPDFAnnot_GetLineEndings` extension (two int out-params).
* Returns `{ start: 'none', end: 'none' }` when the annotation has no
* `/LE` entry, so callers always get a well-formed pair.
*/
function readLineEndings(fn, mem, annotPtr) {
	return withScratchN(mem, [4, 4], ([start, end]) => {
		if (!fn.EPDFAnnot_GetLineEndings(annotPtr, start, end)) return {
			start: "none",
			end: "none"
		};
		return {
			start: lineEndingFromCode(readI32(mem, start)),
			end: lineEndingFromCode(readI32(mem, end))
		};
	});
}
/**
* Read the `/DA` default appearance (font + size + colour) of a free-text
* annotation via `EPDFAnnot_GetDefaultAppearance` (font int, size f32, and
* r/g/b int out-params). `fontCode` is the raw `FPDF_STANDARD_FONT` enum
* value. Returns `null` when the annotation has no `/DA`.
*/
function readDefaultAppearance(fn, mem, annotPtr) {
	return withScratchN(mem, [
		4,
		4,
		4,
		4,
		4
	], ([font, size, r, g, b]) => {
		if (!fn.EPDFAnnot_GetDefaultAppearance(annotPtr, font, size, r, g, b)) return null;
		return {
			fontCode: readI32(mem, font),
			fontSize: readF32(mem, size),
			color: {
				r: readI32(mem, r) & 255,
				g: readI32(mem, g) & 255,
				b: readI32(mem, b) & 255
			}
		};
	});
}
/**
* Read the `/Q` text alignment quadding code of a free-text annotation via
* `EPDFAnnot_GetTextAlignment`. The code<->string mapping lives in
* `textAlignment.ts`; an absent `/Q` reads back as `0` (left).
*/
function readTextAlignment(fn, annotPtr) {
	return fn.EPDFAnnot_GetTextAlignment(annotPtr);
}
/**
* Read `/OverlayText` of a redact annotation via `EPDFAnnot_GetOverlayText`
* (standard UTF-16 byte-length ABI). Returns `null` when the key is absent
* (the redaction has no label), otherwise the — possibly empty — label.
*/
function readOverlayText(fn, mem, annotPtr) {
	if (!fn.FPDFAnnot_HasKey(annotPtr, "OverlayText")) return null;
	return readUtf16String(mem, (buf, capacity) => fn.EPDFAnnot_GetOverlayText(annotPtr, buf, capacity), "");
}
/**
* Read `/Repeat` of a redact annotation via `EPDFAnnot_GetOverlayTextRepeat`
* (an absent key reads as `false`, the PDF default).
*/
function readOverlayTextRepeat(fn, annotPtr) {
	return fn.EPDFAnnot_GetOverlayTextRepeat(annotPtr);
}
/**
* Read the `/IT` intent name via `EPDFAnnot_GetIntent`. Unlike the standard
* `FPDFAnnot_GetStringValue` ABI, this getter reports the count of UTF-16
* code units (excluding the NUL), so we size the buffer as
* `(count + 1) * 2` bytes. Returns `null` when no intent is present.
*/
function readIntent(fn, mem, annotPtr) {
	const codeUnits = fn.EPDFAnnot_GetIntent(annotPtr, NULL_PTR, 0);
	if (codeUnits <= 0) return null;
	const bytes = (codeUnits + 1) * 2;
	return withScratch(mem, bytes, (buf) => {
		fn.EPDFAnnot_GetIntent(annotPtr, buf, bytes);
		return mem.readU16String(buf);
	});
}
/**
* Read the `/CL` callout leader line of a free-text callout via
* `EPDFAnnot_GetCalloutLineCount` + `EPDFAnnot_GetCalloutLine` (the points
* are read into a `count * FS_POINTF` buffer, like {@link readVertices}).
* Returns an empty array when there is no callout line.
*/
function readCalloutLine(fn, mem, annotPtr) {
	const count = fn.EPDFAnnot_GetCalloutLineCount(annotPtr);
	if (count <= 0) return [];
	return withScratch(mem, count * 8, (buf) => {
		const got = fn.EPDFAnnot_GetCalloutLine(annotPtr, buf, count);
		if (got <= 0) return [];
		const out = [];
		for (let i = 0; i < got; i++) {
			const off = i * 8;
			out.push({
				x: readF32(mem, buf, off),
				y: readF32(mem, buf, off + 4)
			});
		}
		return out;
	});
}
/**
* Read attachment points for a text-markup annotation.
* Each `FS_QUADPOINTSF` is 8 floats = 32 bytes.
*/
function readQuadPoints(fn, mem, annotPtr) {
	const count = fn.FPDFAnnot_CountAttachmentPoints(annotPtr);
	if (count <= 0) return [];
	return withScratch(mem, 32, (buf) => {
		const out = [];
		for (let i = 0; i < count; i++) {
			if (!fn.FPDFAnnot_GetAttachmentPoints(annotPtr, i, buf)) continue;
			const f = (off) => readF32(mem, buf, off);
			out.push({
				p1: {
					x: f(0),
					y: f(4)
				},
				p2: {
					x: f(8),
					y: f(12)
				},
				p3: {
					x: f(16),
					y: f(20)
				},
				p4: {
					x: f(24),
					y: f(28)
				}
			});
		}
		return out;
	});
}
//#endregion
//#region src/features/annotations/internal/textEditIntent.ts
/** Adobe-compatible `/IT` names, normalized at the engine-core boundary. */
const IT_REPLACE = "Replace";
const IT_STRIKEOUT_TEXT_EDIT = "StrikeOutTextEdit";
function caretIntentToName(intent) {
	switch (intent) {
		case "replace": return IT_REPLACE;
	}
}
function caretIntentFromName(name) {
	return name === IT_REPLACE ? "replace" : null;
}
function strikeoutIntentToName(intent) {
	switch (intent) {
		case "strikeout-text-edit": return IT_STRIKEOUT_TEXT_EDIT;
	}
}
function strikeoutIntentFromName(name) {
	return name === IT_STRIKEOUT_TEXT_EDIT ? "strikeout-text-edit" : null;
}
//#endregion
//#region src/features/annotations/internal/read/readCaretAnnotation.ts
/** Default `/C` colour when a caret has none (matches the writer default). */
const DEFAULT_CARET_COLOR$1 = {
	r: 255,
	g: 0,
	b: 0
};
function readCaret(fn, mem, annotPtr, base) {
	const color = readAnnotColor(fn, mem, annotPtr) ?? { ...DEFAULT_CARET_COLOR$1 };
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	const opacity = ca == null ? 1 : Math.max(0, Math.min(1, ca));
	const rd = readRectangleDifferences(fn, mem, annotPtr);
	const intent = caretIntentFromName(readIntent(fn, mem, annotPtr));
	return {
		...base,
		subtype: "caret",
		intent,
		color,
		opacity,
		rectDifferences: rd
	};
}
//#endregion
//#region src/features/attachments/internal/attachmentPrimitives.ts
/**
* Shared primitives over an `FPDF_ATTACHMENT` handle (an unretained
* filespec pointer — no close call exists or is needed). Used by both
* homes an embedded file can have: the document-level `/EmbeddedFiles`
* name tree (`AttachmentReader`) and a FileAttachment annotation's `/FS`
* (the annotation reader + `downloadFile`).
*/
/** Read the name-tree KEY at |index| — the durable EmbeddedFileRef address. */
function readAttachmentKey(fn, mem, docPtr, index) {
	return readUtf16String(mem, (buf, cap) => fn.EPDFDoc_GetAttachmentKey(docPtr, index, buf, cap));
}
/** Resolve an EmbeddedFileRef to its CURRENT name-tree index, or -1. */
function resolveAttachmentIndex(fn, mem, docPtr, ref) {
	const keyPtr = mem.writeU16String(ref.key);
	try {
		return fn.EPDFDoc_GetAttachmentIndexByKey(docPtr, keyPtr);
	} finally {
		mem.free(keyPtr);
	}
}
/**
* Write an embedded file's payload onto an existing FPDF_ATTACHMENT:
* bytes via FPDFAttachment_SetFile (which also writes /Params
* Size/CheckSum/CreationDate), declared mime via EPDFAttachment_SetSubtype
* (attachments accept any format — no sniffing), optional /Desc. Shared
* by the file-attachment annotation writer and the document-level
* `attachments.create` mutation — the write half of the one attachment
* vocabulary.
*/
function writeAttachmentFilePayload(fn, mem, attachmentPtr, docPtr, file, resource) {
	const byteLength = resource.bytes.byteLength;
	if (byteLength === 0) {
		if (!fn.FPDFAttachment_SetFile(attachmentPtr, docPtr, NULL_PTR, 0)) throw new EngineError(EngineErrorCode.Unknown, "FPDFAttachment_SetFile returned false");
	} else {
		const dataPtr = mem.alloc(byteLength);
		try {
			mem.writeBytes(dataPtr, new Uint8Array(resource.bytes));
			if (!fn.FPDFAttachment_SetFile(attachmentPtr, docPtr, dataPtr, byteLength)) throw new EngineError(EngineErrorCode.Unknown, "FPDFAttachment_SetFile returned false");
		} finally {
			mem.free(dataPtr);
		}
	}
	if (!fn.EPDFAttachment_SetSubtype(attachmentPtr, file.mimeType ?? "application/octet-stream")) throw new EngineError(EngineErrorCode.Unknown, "EPDFAttachment_SetSubtype returned false");
	if (file.description !== void 0) {
		if (!writeUtf16String(mem, file.description, (ptr) => fn.EPDFAttachment_SetDescription(attachmentPtr, ptr))) throw new EngineError(EngineErrorCode.Unknown, "EPDFAttachment_SetDescription returned false");
	}
}
/** Read the metadata projection of an embedded file (never its bytes). */
function readAttachmentFileInfo(fn, mem, attachmentPtr) {
	const name = readUtf16String(mem, (buf, cap) => fn.FPDFAttachment_GetName(attachmentPtr, buf, cap)) ?? "";
	const mimeType = readUtf16String(mem, (buf, cap) => fn.FPDFAttachment_GetSubtype(attachmentPtr, buf, cap));
	const description = readUtf16String(mem, (buf, cap) => fn.EPDFAttachment_GetDescription(attachmentPtr, buf, cap));
	const creationDate = readAttachmentString(fn, mem, attachmentPtr, "CreationDate");
	const checksum = normalizeChecksum(readAttachmentString(fn, mem, attachmentPtr, "CheckSum"));
	const size = readAttachmentSize(fn, mem, attachmentPtr);
	return {
		name,
		...mimeType ? { mimeType } : {},
		...description ? { description } : {},
		...size !== null ? { size } : {},
		...checksum ? { checksum } : {},
		...creationDate ? { creationDate } : {}
	};
}
/**
* Decode the embedded file into a standalone buffer via the fork's
* single-decode `EPDFAttachment_ExtractFileToOwnedBuffer`. A zero-byte
* embedded file is a valid `{ size: 0 }` result.
*/
function extractAttachmentToBuffer(runtime, attachmentPtr, maxDecodedBytes) {
	const { fn, mem } = runtime;
	const bufPtrSlot = mem.alloc(8);
	const sizePtr = mem.alloc(4);
	const statusPtr = mem.alloc(4);
	let dataPtr = null;
	try {
		mem.poke(bufPtrSlot, "ptr", 0);
		mem.poke(sizePtr, "i32", 0);
		mem.poke(statusPtr, "i32", -1);
		if (!fn.EPDFAttachment_ExtractFileToOwnedBuffer(attachmentPtr, BigInt(maxDecodedBytes ?? 0), bufPtrSlot, sizePtr, statusPtr)) throw extractStatusError(Number(mem.peek(statusPtr, "i32")));
		const size = Number(mem.peek(sizePtr, "i32")) >>> 0;
		dataPtr = mem.peek(bufPtrSlot, "ptr");
		if (size === 0 || !dataPtr) return {
			bytes: /* @__PURE__ */ new ArrayBuffer(0),
			size: 0
		};
		const view = mem.readBytes(dataPtr, size);
		const buffer = new ArrayBuffer(view.byteLength);
		new Uint8Array(buffer).set(view);
		return {
			bytes: buffer,
			size
		};
	} finally {
		if (dataPtr) fn.EPDF_FreeBuffer(dataPtr);
		mem.free(statusPtr);
		mem.free(sizePtr);
		mem.free(bufPtrSlot);
	}
}
/**
* Decode the embedded file straight to `path` via the fork's streaming
* `EPDFAttachment_ExtractFile` + `FPDF_FILEWRITE` — the server mode: the
* decoded payload never crosses the thread boundary and HTTP streams it
* from disk. On failure the destination may hold a partial file (the
* caller owns temp-file cleanup, as with `document.saveFile`).
*/
function extractAttachmentToFile(runtime, attachmentPtr, path, maxDecodedBytes) {
	const { fn, mem } = runtime;
	const sizePtr = mem.alloc(4);
	const statusPtr = mem.alloc(4);
	const writer = runtime.fileWrite.toNodeFile(path);
	try {
		mem.poke(sizePtr, "i32", 0);
		mem.poke(statusPtr, "i32", -1);
		if (!fn.EPDFAttachment_ExtractFile(attachmentPtr, writer.ptr, BigInt(maxDecodedBytes ?? 0), sizePtr, statusPtr)) throw extractStatusError(Number(mem.peek(statusPtr, "i32")));
		return { size: Number(mem.peek(sizePtr, "i32")) >>> 0 };
	} finally {
		writer.close();
		mem.free(statusPtr);
		mem.free(sizePtr);
	}
}
/** `EPDFAttachmentExtractStatus` (public/fpdf_attachment.h) → EngineError. */
function extractStatusError(status) {
	switch (status) {
		case 1: return new EngineError(EngineErrorCode.InvalidArg, "attachment has no embedded file stream (missing /EF)");
		case 2: return new EngineError(EngineErrorCode.MalformedPdf, "embedded file stream failed to decode");
		case 3: return new EngineError(EngineErrorCode.InvalidArg, "embedded file exceeds the configured decoded-size limit");
		case 4: return new EngineError(EngineErrorCode.Unknown, "writing the extracted attachment failed");
		default: return new EngineError(EngineErrorCode.Unknown, `attachment extraction failed (status ${status})`);
	}
}
function readAttachmentString(fn, mem, attachmentPtr, key) {
	if (!fn.FPDFAttachment_HasKey(attachmentPtr, key)) return null;
	return readUtf16String(mem, (buf, cap) => fn.FPDFAttachment_GetStringValue(attachmentPtr, key, buf, cap), null);
}
function readAttachmentSize(fn, mem, attachmentPtr) {
	const out = mem.alloc(4);
	try {
		if (!fn.EPDFAttachment_GetIntegerValue(attachmentPtr, "Size", out)) return null;
		const size = Number(mem.peek(out, "i32"));
		return size >= 0 ? size : null;
	} finally {
		mem.free(out);
	}
}
/** PDFium reports `/CheckSum` hex strings as `<ABCD…>` — normalize to bare lowercase hex. */
function normalizeChecksum(raw) {
	if (!raw) return null;
	const inner = raw.startsWith("<") && raw.endsWith(">") ? raw.slice(1, -1) : raw;
	return /^[0-9a-fA-F]+$/.test(inner) ? inner.toLowerCase() : raw;
}
//#endregion
//#region src/features/annotations/internal/annotationIcon.ts
/**
* `/Name` icon ↔ `FPDF_ANNOT_NAME` enum code mapping for text and
* file-attachment annotations (`EPDFAnnot_SetName` / `EPDFAnnot_GetName`).
* Codes mirror the `FPDF_ANNOT_NAME` enum in `public/fpdf_annot.h` —
* same pattern as `stampName.ts` (whose stamp block starts at 13, right
* after these) keeping engine-core PDFium-free.
*/
const NOTE_ICON_TO_CODE = Object.freeze({
	comment: 0,
	key: 1,
	note: 2,
	help: 3,
	"new-paragraph": 4,
	paragraph: 5,
	insert: 6
});
const NOTE_CODE_TO_ICON = Object.freeze(Object.fromEntries(Object.entries(NOTE_ICON_TO_CODE).map(([icon, code]) => [code, icon])));
const FILE_ICON_TO_CODE = Object.freeze({
	graph: 7,
	"push-pin": 8,
	paperclip: 9,
	tag: 10
});
const FILE_CODE_TO_ICON = Object.freeze(Object.fromEntries(Object.entries(FILE_ICON_TO_CODE).map(([icon, code]) => [code, icon])));
//#endregion
//#region src/features/annotations/internal/read/readFileAttachmentAnnotation.ts
/** Default `/C` — matches the generator's default icon fill and the writer default. */
const DEFAULT_FILE_ATTACHMENT_COLOR$1 = {
	r: 255,
	g: 255,
	b: 0
};
/** An absent or foreign `/Name` reads as 'push-pin' (ISO 32000 §12.5.6.15 default). */
const DEFAULT_FILE_ATTACHMENT_ICON = "push-pin";
/**
* FileAttachment DTO: base + icon presentation + the attached file's
* METADATA (bytes are downloaded explicitly via
* `PageAnnotationsService.downloadFile`). A malformed annotation without
* a readable `/FS` filespec still reads — `file.name` is `''` and the
* download call reports the precise error.
*/
function readFileAttachment(fn, mem, annotPtr, base) {
	const color = readAnnotColor(fn, mem, annotPtr) ?? { ...DEFAULT_FILE_ATTACHMENT_COLOR$1 };
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	const opacity = ca == null ? 1 : Math.max(0, Math.min(1, ca));
	const icon = FILE_CODE_TO_ICON[fn.EPDFAnnot_GetName(annotPtr)] ?? DEFAULT_FILE_ATTACHMENT_ICON;
	const attachmentPtr = fn.FPDFAnnot_GetFileAttachment(annotPtr);
	const file = attachmentPtr ? readAttachmentFileInfo(fn, mem, attachmentPtr) : { name: "" };
	return {
		...base,
		subtype: "file-attachment",
		icon,
		file,
		color,
		opacity
	};
}
//#endregion
//#region src/features/annotations/internal/colorType.ts
/**
* `FPDFANNOT_COLORTYPE` enum codes from `public/fpdf_annot.h`. Shared by
* the annotation read/write primitives and the shape reader/writer so the
* color-selector magic numbers live in exactly one place.
*
*   Color=0 (the stroke/fill `/C`)
*   InteriorColor=1 (the `/IC` of square/circle/polygon)
*   OverlayColor=2 (the redaction overlay)
*   TextColor=3 (the free-text `/DA` color)
*/
const FPDFANNOT_COLORTYPE = {
	Color: 0,
	InteriorColor: 1,
	OverlayColor: 2,
	TextColor: 3
};
//#endregion
//#region src/features/annotations/internal/freeTextIntent.ts
/**
* Free-text `/IT` intent names (ISO 32000 §12.5.6.6) mapped to the
* wire-stable `FreeTextIntent` strings. Kept in engine-services so
* engine-core stays PDFium-free.
*
*   FreeText           -> 'free-text'         (plain text box)
*   FreeTextCallout    -> 'free-text-callout' (with /CL leader line)
*
* `FreeTextTypeWriter` (a typewriter variant) round-trips as the plain
* `'free-text'` intent — we don't author it.
*/
const IT_FREE_TEXT = "FreeText";
const IT_FREE_TEXT_CALLOUT = "FreeTextCallout";
function freeTextIntentToName(intent) {
	return intent === "free-text-callout" ? IT_FREE_TEXT_CALLOUT : IT_FREE_TEXT;
}
function freeTextIntentFromName(name) {
	return name === IT_FREE_TEXT_CALLOUT ? "free-text-callout" : "free-text";
}
//#endregion
//#region src/features/annotations/internal/standardFont.ts
/**
* `FPDF_STANDARD_FONT` enum codes (the 14 standard PDF fonts) mapped to the
* wire-stable kebab-case `StandardFont` strings. We keep this PDFium-specific
* mapping in engine-services so engine-core stays free of any PDFium
* dependency (mirrors `lineEnding.ts` / `shapeBorderStyle.ts`).
*
*   Courier=0, Courier-Bold=1, Courier-BoldOblique=2, Courier-Oblique=3,
*   Helvetica=4, Helvetica-Bold=5, Helvetica-BoldOblique=6,
*   Helvetica-Oblique=7, Times-Roman=8, Times-Bold=9, Times-BoldItalic=10,
*   Times-Italic=11, Symbol=12, ZapfDingbats=13
*/
const FONT_TO_CODE = {
	courier: 0,
	"courier-bold": 1,
	"courier-bold-oblique": 2,
	"courier-oblique": 3,
	helvetica: 4,
	"helvetica-bold": 5,
	"helvetica-bold-oblique": 6,
	"helvetica-oblique": 7,
	"times-roman": 8,
	"times-bold": 9,
	"times-bold-italic": 10,
	"times-italic": 11,
	symbol: 12,
	"zapf-dingbats": 13
};
const CODE_TO_FONT = Object.fromEntries(Object.entries(FONT_TO_CODE).map(([font, code]) => [code, font]));
/** Default when a `/DA` font is missing or unrecognised. */
const DEFAULT_STANDARD_FONT = "helvetica";
function standardFontToCode(font) {
	return FONT_TO_CODE[font] ?? FONT_TO_CODE["helvetica"];
}
/**
* Narrow a FreeText `fontFamily` to one of the 14 standard fonts. A `false`
* result means the value is a registered-font `key`. The standard names are
* therefore reserved — a registered font keyed `'helvetica'` resolves as the
* standard font.
*/
function isStandardFont(font) {
	return Object.prototype.hasOwnProperty.call(FONT_TO_CODE, font);
}
function standardFontFromCode(code) {
	return CODE_TO_FONT[code] ?? "helvetica";
}
//#endregion
//#region src/features/annotations/internal/textAlignment.ts
/**
* `/Q` quadding codes (ISO 32000 §12.7.3.3) mapped to the wire-stable
* `TextAlignment` strings. Kept in engine-services so engine-core stays
* PDFium-free (mirrors `lineEnding.ts`).
*
*   Left=0, Center=1, Right=2
*/
const Q_LEFT = 0;
const Q_CENTER = 1;
const Q_RIGHT = 2;
function textAlignmentToCode(align) {
	switch (align) {
		case "center": return Q_CENTER;
		case "right": return Q_RIGHT;
		default: return Q_LEFT;
	}
}
function textAlignmentFromCode(code) {
	switch (code) {
		case Q_CENTER: return "center";
		case Q_RIGHT: return "right";
		case Q_LEFT:
		default: return "left";
	}
}
//#endregion
//#region src/features/annotations/internal/shapeBorderStyle.ts
/**
* `FPDF_ANNOT_BORDER_STYLE` enum codes from `public/fpdf_annot.h`. We keep
* this PDFium-specific mapping in engine-services so engine-core stays free
* of any PDFium dependency (mirrors `PDF_SUBTYPE_TO_CODE`).
*
*   UNKNOWN=0, SOLID=1, DASHED=2, BEVELED=3, INSET=4, UNDERLINE=5, CLOUDY=6
*
* The wire-stable `AnnotationBorderStyle` union covers the four styles a
* shape annotation realistically authors; cloudy borders are surfaced
* separately via `cloudyIntensity` (the `/BE` border effect).
*/
const BS_SOLID = 1;
const BS_DASHED = 2;
const BS_BEVELED = 3;
const BS_INSET = 4;
function borderStyleToCode(style) {
	switch (style) {
		case "dashed": return BS_DASHED;
		case "beveled": return BS_BEVELED;
		case "inset": return BS_INSET;
		default: return BS_SOLID;
	}
}
function borderStyleFromCode(code) {
	switch (code) {
		case BS_DASHED: return "dashed";
		case BS_BEVELED: return "beveled";
		case BS_INSET: return "inset";
		case BS_SOLID:
		default: return "solid";
	}
}
//#endregion
//#region src/features/annotations/internal/read/readStyle.ts
/**
* Default `/C` colour when an annotation has none. Matches the v2 engine
* (circle/square/polygon/polyline/line/ink all default red).
*/
const DEFAULT_COLOR$1 = {
	r: 255,
	g: 0,
	b: 0
};
/**
* Shared reader for the effective border (style + width + dash), resolving
* `/BS`, legacy `/Border`, and the ISO defaults. The border-only slice of
* {@link readGeometryStyleExtras}, reused by the free-text box border (which
* reads `/C` and `/DA` itself).
*/
function readBorderFields(fn, mem, annotPtr) {
	const { styleCode, width } = readBorderStyle(fn, mem, annotPtr);
	const dashArray = readBorderDashPattern(fn, mem, annotPtr);
	return {
		strokeWidth: width,
		borderStyle: borderStyleFromCode(styleCode),
		...dashArray.length > 0 ? { dashArray } : {}
	};
}
/**
* Shared reader for the geometry styling (`/C`, `/CA`, border, dash) — the
* no-fill layer shared by ink and the filled families. Family readers layer
* their own geometry + extras on top; filled families add `/IC` via
* {@link readFilledStyleExtras}.
*/
function readGeometryStyleExtras(fn, mem, annotPtr) {
	const color = readAnnotColor(fn, mem, annotPtr, FPDFANNOT_COLORTYPE.Color) ?? { ...DEFAULT_COLOR$1 };
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	return {
		color,
		opacity: ca == null ? 1 : Math.max(0, Math.min(1, ca)),
		...readBorderFields(fn, mem, annotPtr)
	};
}
/**
* Shared reader for the full stroke/fill styling (geometry styling + `/IC`).
* Reused by every filled family (shape/vertex/line); each layers its own
* geometry + extras on top.
*/
function readFilledStyleExtras(fn, mem, annotPtr) {
	const interiorColor = readAnnotColor(fn, mem, annotPtr, FPDFANNOT_COLORTYPE.InteriorColor);
	return {
		...readGeometryStyleExtras(fn, mem, annotPtr),
		interiorColor: interiorColor ?? null
	};
}
//#endregion
//#region src/features/annotations/internal/read/readAnnotationTransformMetadata.ts
/**
* Read-side twin of `writers/.../writeAnnotationTransformMetadata.ts`. Decodes
* the EmbedPDF rotation keys from /EMBD_Metadata back onto the DTO:
*
*   - `Rotation`      — degrees, PDF convention. Returned for EVERY rotatable
*     kind (box AND vertex); the plugin converts it back to its CW-content
*     `rot` once at the repository seam.
*   - `UnrotatedRect` — the logical box. BOX kinds only (square/circle/
*     free-text); a vertex annotation never carries one (its points are the
*     visual), so vertex readers don't ask for it.
*
* Both are absent on un-rotated annotations (and on everything authored before
* rotation shipped), so both reads return `undefined` and the DTO field stays
* unset.
*/
const KEY_ROTATION$1 = "Rotation";
const KEY_UNROTATED_RECT$1 = "UnrotatedRect";
/** The advisory/portable rotation scalar (degrees, PDF convention), or
*  `undefined` when absent or zero. */
function readAnnotationRotation(fn, mem, annotPtr) {
	if (!fn.EPDFAnnot_HasEmbedMetadata(annotPtr)) return void 0;
	return withScratch(mem, 4, (buf) => {
		if (!fn.EPDFAnnot_GetEmbedMetadataNumber(annotPtr, KEY_ROTATION$1, buf)) return void 0;
		const v = readF32(mem, buf);
		return v ? v : void 0;
	});
}
/** The logical (unrotated) box of a rotated BOX annotation, or `undefined`. */
function readAnnotationUnrotatedRect(fn, mem, annotPtr) {
	if (!fn.EPDFAnnot_HasEmbedMetadata(annotPtr)) return void 0;
	return withScratch(mem, 16, (buf) => {
		if (!fn.EPDFAnnot_GetEmbedMetadataRect(annotPtr, KEY_UNROTATED_RECT$1, buf)) return void 0;
		const r = readRectF(mem, buf);
		return {
			left: r.left,
			bottom: r.bottom,
			right: r.right,
			top: r.top
		};
	});
}
//#endregion
//#region src/features/annotations/internal/read/readFreeTextAnnotation.ts
/** Default `/DA` colour (black) when an annotation has no default appearance. */
const DEFAULT_FREETEXT_COLOR$1 = {
	r: 0,
	g: 0,
	b: 0
};
/** Default font size when `/DA` has none (or an unusable 0). */
const DEFAULT_FONT_SIZE$2 = 12;
function colorsEqual(a, b) {
	return a.r === b.r && a.g === b.g && a.b === b.b;
}
function readFreeText(fn, mem, annotPtr, base) {
	const da = readDefaultAppearance(fn, mem, annotPtr);
	const color = da?.color ?? { ...DEFAULT_FREETEXT_COLOR$1 };
	const fontFamily = da ? standardFontFromCode(da.fontCode) : DEFAULT_STANDARD_FONT;
	const fontSize = da && da.fontSize > 0 ? da.fontSize : DEFAULT_FONT_SIZE$2;
	const textColor = readAnnotColor(fn, mem, annotPtr, FPDFANNOT_COLORTYPE.TextColor);
	const fontColor = textColor && !colorsEqual(textColor, color) ? textColor : void 0;
	const background = readAnnotColor(fn, mem, annotPtr, FPDFANNOT_COLORTYPE.Color);
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	const opacity = ca == null ? 1 : Math.max(0, Math.min(1, ca));
	const textAlign = textAlignmentFromCode(readTextAlignment(fn, annotPtr));
	const intent = freeTextIntentFromName(readIntent(fn, mem, annotPtr));
	const points = readCalloutLine(fn, mem, annotPtr);
	const calloutLine = points.length === 2 ? [points[0], points[1]] : points.length === 3 ? [
		points[0],
		points[1],
		points[2]
	] : void 0;
	const leaderEnd = readLineEndings(fn, mem, annotPtr).end;
	const rd = readRectangleDifferences(fn, mem, annotPtr);
	const rotation = readAnnotationRotation(fn, mem, annotPtr);
	const unrotatedRect = readAnnotationUnrotatedRect(fn, mem, annotPtr);
	return {
		...base,
		subtype: "free-text",
		intent,
		fontFamily,
		fontSize,
		textAlign,
		color,
		...fontColor !== void 0 ? { fontColor } : {},
		interiorColor: background ?? null,
		opacity,
		...readBorderFields(fn, mem, annotPtr),
		rectDifferences: rd,
		...calloutLine !== void 0 ? { calloutLine } : {},
		...calloutLine !== void 0 && leaderEnd !== "none" ? { lineEnding: leaderEnd } : {},
		...rotation != null ? { rotation } : {},
		...unrotatedRect ? { unrotatedRect } : {}
	};
}
//#endregion
//#region src/features/annotations/internal/inkIntent.ts
function inkIntentToName(intent) {
	switch (intent) {
		case "ink-highlight": return "InkHighlight";
	}
}
function inkIntentFromName(name) {
	switch (name) {
		case "InkHighlight": return "ink-highlight";
		default: return null;
	}
}
//#endregion
//#region src/features/annotations/internal/read/readInkAnnotation.ts
function readInk(fn, mem, annotPtr, base) {
	const rotation = readAnnotationRotation(fn, mem, annotPtr);
	const intent = inkIntentFromName(readIntent(fn, mem, annotPtr));
	return {
		...base,
		subtype: "ink",
		...readGeometryStyleExtras(fn, mem, annotPtr),
		intent,
		inkList: readInkList(fn, mem, annotPtr),
		...rotation != null ? { rotation } : {}
	};
}
//#endregion
//#region src/features/annotations/internal/read/readLineAnnotation.ts
/** Fallback `/L` when the annotation has no line geometry. */
const ZERO_LINE = {
	start: {
		x: 0,
		y: 0
	},
	end: {
		x: 0,
		y: 0
	}
};
function readLine(fn, mem, annotPtr, base) {
	const rotation = readAnnotationRotation(fn, mem, annotPtr);
	return {
		...base,
		subtype: "line",
		...readFilledStyleExtras(fn, mem, annotPtr),
		linePoints: readLine$1(fn, mem, annotPtr) ?? ZERO_LINE,
		lineEndings: readLineEndings(fn, mem, annotPtr),
		...rotation != null ? { rotation } : {}
	};
}
//#endregion
//#region src/features/annotations/internal/destinationViewCodes.ts
/**
* `PDFDEST_VIEW_*` fit-type codes (public/fpdf_doc.h) — the shared
* vocabulary of `FPDFDest_GetView` (read) and `EPDFDest_CreateView`
* (write). XYZ is listed for completeness but never goes through
* CreateView (it has its own null-aware constructor).
*/
const DEST_VIEW = {
	unknown: 0,
	xyz: 1,
	fit: 2,
	fitH: 3,
	fitV: 4,
	fitR: 5,
	fitB: 6,
	fitBH: 7,
	fitBV: 8
};
/** Wire destination kind → PDFDEST_VIEW code, for the non-XYZ kinds. */
const VIEW_CODE_BY_KIND = {
	fit: DEST_VIEW.fit,
	fitH: DEST_VIEW.fitH,
	fitV: DEST_VIEW.fitV,
	fitR: DEST_VIEW.fitR,
	fitB: DEST_VIEW.fitB,
	fitBH: DEST_VIEW.fitBH,
	fitBV: DEST_VIEW.fitBV
};
//#endregion
//#region src/features/annotations/internal/read/readDestination.ts
const MAX_VIEW_PARAMS = 4;
/**
* Materialise one explicit `PdfDestination` from a live `destPtr`. PDFium
* already resolved named destinations before handing us the pointer, so
* only explicit arrays arrive here (the rule `PdfDestination` documents).
*
* Returns `null` when the destination is unreadable: no visible indirect page
* in the document, or an unknown fit type. Coordinates stay raw PDF user
* space — no conversion at this layer.
*
* Shared by the link reader today; the outline/bookmark port reuses it.
*/
function readDestination(fn, mem, docPtr, destPtr) {
	const pageObjectNumber = fn.EPDFDest_GetPageObjectNumber(docPtr, destPtr);
	if (pageObjectNumber === 0) return null;
	const view = withScratchN(mem, [4, MAX_VIEW_PARAMS * 4], ([countPtr, paramsPtr]) => {
		const code = fn.FPDFDest_GetView(destPtr, countPtr, paramsPtr);
		const count = Math.min(readI32(mem, countPtr), MAX_VIEW_PARAMS);
		const params = [];
		for (let i = 0; i < count; i++) params.push(readF32(mem, paramsPtr, i * 4));
		return {
			code,
			params
		};
	});
	const at = (i) => i < view.params.length ? view.params[i] : null;
	switch (view.code) {
		case DEST_VIEW.xyz: return {
			kind: "xyz",
			pageObjectNumber,
			...readXyzLocation(fn, mem, destPtr)
		};
		case DEST_VIEW.fit: return {
			kind: "fit",
			pageObjectNumber
		};
		case DEST_VIEW.fitH: return {
			kind: "fitH",
			pageObjectNumber,
			top: at(0)
		};
		case DEST_VIEW.fitV: return {
			kind: "fitV",
			pageObjectNumber,
			left: at(0)
		};
		case DEST_VIEW.fitR:
			if (view.params.length < 4) return {
				kind: "fit",
				pageObjectNumber
			};
			return {
				kind: "fitR",
				pageObjectNumber,
				left: view.params[0],
				bottom: view.params[1],
				right: view.params[2],
				top: view.params[3]
			};
		case DEST_VIEW.fitB: return {
			kind: "fitB",
			pageObjectNumber
		};
		case DEST_VIEW.fitBH: return {
			kind: "fitBH",
			pageObjectNumber,
			top: at(0)
		};
		case DEST_VIEW.fitBV: return {
			kind: "fitBV",
			pageObjectNumber,
			left: at(0)
		};
		default: return null;
	}
}
/** `/XYZ` null-aware axes via `FPDFDest_GetLocationInPage` (FPDF_BOOL is 4 bytes). */
function readXyzLocation(fn, mem, destPtr) {
	return withScratchN(mem, [
		4,
		4,
		4,
		4,
		4,
		4
	], ([hasLeft, hasTop, hasZoom, left, top, zoom]) => {
		if (!fn.FPDFDest_GetLocationInPage(destPtr, hasLeft, hasTop, hasZoom, left, top, zoom)) return {
			left: null,
			top: null,
			zoom: null
		};
		return {
			left: readI32(mem, hasLeft) ? readF32(mem, left) : null,
			top: readI32(mem, hasTop) ? readF32(mem, top) : null,
			zoom: readI32(mem, hasZoom) ? readF32(mem, zoom) : null
		};
	});
}
//#endregion
//#region src/features/annotations/internal/read/readLinkAnnotation.ts
/**
* Link reader: rect/flags/relationship ride the base (a link grouped to an
* annotation is plain `inReplyTo` + `replyType: 'group'`); this module only
* materialises the normalized target.
*
* ONE truth, two views: the target is projected off the SAME fork action
* model the scripting plane reads (`base.actions`) — root classification
* via the full `EPDF_ACTION_TYPE_*` vocabulary, payloads via the on-demand
* node getters. The classic `FPDFAction_*` surface (5 types, JavaScript-
* and Named-blind) is deliberately not consulted; `FPDFLink_GetDest`
* remains only for the data-only direct `/Dest`, which is not an action.
*
* `/A` wins over `/Dest` — the spec forbids carrying both, and when a
* malformed file has both, Acrobat gives the action precedence. (Our own
* writes can't produce that state: `EPDFAnnot_SetAction` strips `/Dest`.)
*/
function readLink(fn, mem, annotPtr, base, _rawSubtypeCode, ctx) {
	return {
		...base,
		subtype: "link",
		target: readLinkTarget(fn, mem, annotPtr, ctx)
	};
}
function readLinkTarget(fn, mem, annotPtr, ctx) {
	const modelPtr = fn.EPDFAnnot_GetActionModel(annotPtr, 0);
	if (modelPtr !== NULL_PTR) try {
		const root = fn.EPDFAction_GetRootNode(modelPtr);
		if (isValidActionNodeId(root)) return readRootTarget(fn, mem, modelPtr, root, ctx);
	} finally {
		fn.EPDFAction_CloseModel(modelPtr);
	}
	const linkPtr = fn.FPDFAnnot_GetLink(annotPtr);
	if (!linkPtr) return null;
	const destPtr = fn.FPDFLink_GetDest(ctx.docPtr, linkPtr);
	if (!destPtr) return null;
	const destination = readDestination(fn, mem, ctx.docPtr, destPtr);
	return destination ? {
		kind: "goto",
		destination
	} : { kind: "unsupported" };
}
/** Classify the root action node and fetch its payload — the projection. */
function readRootTarget(fn, mem, modelPtr, node, ctx) {
	switch (actionTypeFromCode(fn.EPDFAction_GetNodeType(modelPtr, node))) {
		case "goto": {
			const destPtr = fn.EPDFAction_GetNodeDest(ctx.docPtr, modelPtr, node);
			const destination = destPtr ? readDestination(fn, mem, ctx.docPtr, destPtr) : null;
			return destination ? {
				kind: "goto",
				destination
			} : { kind: "unsupported" };
		}
		case "uri": {
			const uri = readUtf8String(mem, (buf, capacity) => fn.EPDFAction_GetNodeURI(ctx.docPtr, modelPtr, node, buf, capacity));
			return uri == null ? { kind: "unsupported" } : {
				kind: "uri",
				uri
			};
		}
		case "goto-remote": return {
			kind: "goto-remote",
			file: readUtf8String(mem, (buf, capacity) => fn.EPDFAction_GetNodeFilePath(modelPtr, node, buf, capacity)) ?? ""
		};
		case "launch": return {
			kind: "launch",
			path: readUtf8String(mem, (buf, capacity) => fn.EPDFAction_GetNodeFilePath(modelPtr, node, buf, capacity)) ?? ""
		};
		case "javascript": return { kind: "javascript" };
		case "named": {
			const name = readUtf8String(mem, (buf, capacity) => fn.EPDFAction_GetNodeName(modelPtr, node, buf, capacity));
			return name == null ? { kind: "unsupported" } : {
				kind: "named",
				name
			};
		}
		default: return { kind: "unsupported" };
	}
}
//#endregion
//#region src/features/annotations/internal/read/readRedactAnnotation.ts
/** Default `/C` marking outline (red) — the redaction marking convention and
*  the AP generator's default. */
const DEFAULT_REDACT_COLOR$1 = {
	r: 255,
	g: 0,
	b: 0
};
/** Default label colour (black) when a redaction carries no `/DA`. */
const DEFAULT_LABEL_COLOR$1 = {
	r: 0,
	g: 0,
	b: 0
};
/** Default label size when there is no `/DA`. When a `/DA` IS present its
*  size is kept verbatim — including `0`, which means auto-fit for a
*  redaction label (unlike free text, which normalizes 0 away). */
const DEFAULT_FONT_SIZE$1 = 12;
function readRedact(fn, mem, annotPtr, base) {
	const color = readAnnotColor(fn, mem, annotPtr, FPDFANNOT_COLORTYPE.Color) ?? { ...DEFAULT_REDACT_COLOR$1 };
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	const opacity = ca == null ? 1 : Math.max(0, Math.min(1, ca));
	const interiorColor = readAnnotColor(fn, mem, annotPtr, FPDFANNOT_COLORTYPE.InteriorColor) ?? null;
	const da = readDefaultAppearance(fn, mem, annotPtr);
	const fontFamily = da ? standardFontFromCode(da.fontCode) : DEFAULT_STANDARD_FONT;
	const fontSize = da ? da.fontSize : DEFAULT_FONT_SIZE$1;
	const fontColor = da?.color ?? { ...DEFAULT_LABEL_COLOR$1 };
	return {
		...base,
		subtype: "redact",
		quadPoints: readQuadPoints(fn, mem, annotPtr),
		color,
		opacity,
		interiorColor,
		overlayText: readOverlayText(fn, mem, annotPtr),
		repeat: readOverlayTextRepeat(fn, annotPtr),
		fontFamily,
		fontSize,
		fontColor,
		textAlign: textAlignmentFromCode(readTextAlignment(fn, annotPtr))
	};
}
//#endregion
//#region src/features/annotations/internal/read/readShapeAnnotation.ts
/**
* Shared reader for the two shape subtypes. Materialises the common
* stroke/fill styling plus the shape-only cloudy (`/BE`) and rect-diff
* (`/RD`) fields; the caller fills in the `subtype` literal. Absent `/BE`
* and `/RD` read as explicit `null` (never omission), so a read DTO
* compares structurally against a clearing patch.
*/
function readShapeExtras(fn, mem, annotPtr) {
	const cloudyIntensity = readBorderEffect(fn, mem, annotPtr);
	const rectDifferences = readRectangleDifferences(fn, mem, annotPtr);
	const rotation = readAnnotationRotation(fn, mem, annotPtr);
	const unrotatedRect = readAnnotationUnrotatedRect(fn, mem, annotPtr);
	return {
		...readFilledStyleExtras(fn, mem, annotPtr),
		cloudyIntensity,
		rectDifferences,
		...rotation != null ? { rotation } : {},
		...unrotatedRect ? { unrotatedRect } : {}
	};
}
function readCircle(fn, mem, annotPtr, base) {
	return {
		...base,
		subtype: "circle",
		...readShapeExtras(fn, mem, annotPtr)
	};
}
function readSquare(fn, mem, annotPtr, base) {
	return {
		...base,
		subtype: "square",
		...readShapeExtras(fn, mem, annotPtr)
	};
}
//#endregion
//#region src/features/annotations/internal/stampName.ts
/**
* Stamp `/Name` label ↔ `FPDF_ANNOT_NAME` enum code mapping for the
* `EPDFAnnot_SetName` / `EPDFAnnot_GetName` fork helpers. Labels are the
* PDF spec / Adobe literal name values (ISO 32000 §12.5.6.12 table 181
* plus the Adobe SB/SH extended set the fork supports). Codes mirror the
* `FPDF_ANNOT_NAME` enum in `public/fpdf_annot.h` — same pattern as
* `lineEnding.ts` keeping engine-core PDFium-free.
*/
const STAMP_NAME_TO_CODE = Object.freeze({
	Approved: 13,
	Experimental: 14,
	NotApproved: 15,
	AsIs: 16,
	Expired: 17,
	NotForPublicRelease: 18,
	Confidential: 19,
	Final: 20,
	Sold: 21,
	Departmental: 22,
	ForComment: 23,
	TopSecret: 24,
	Draft: 25,
	ForPublicRelease: 26,
	Completed: 27,
	Void: 28,
	PreliminaryResults: 29,
	InformationOnly: 30,
	Rejected: 31,
	Witness: 32,
	InitialHere: 33,
	SignHere: 34,
	Accepted: 35
});
const STAMP_CODE_TO_NAME = Object.freeze(Object.fromEntries(Object.entries(STAMP_NAME_TO_CODE).map(([name, code]) => [code, name])));
//#endregion
//#region src/features/annotations/internal/read/readStampAnnotation.ts
/**
* Stamp DTO: base + `/Name` label + transform metadata. The visual content
* stays in the `/AP` stream — rendered via `renderAppearanceImages()`,
* never surfaced as DTO data.
*/
function readStamp(fn, mem, annotPtr, base) {
	const rotation = readAnnotationRotation(fn, mem, annotPtr);
	const unrotatedRect = readAnnotationUnrotatedRect(fn, mem, annotPtr);
	return {
		...base,
		subtype: "stamp",
		name: readStampName(fn, mem, annotPtr),
		...rotation != null ? { rotation } : {},
		...unrotatedRect != null ? { unrotatedRect } : {}
	};
}
function readStampName(fn, mem, annotPtr) {
	const known = STAMP_CODE_TO_NAME[fn.EPDFAnnot_GetName(annotPtr)];
	if (known !== void 0) return known;
	return readAnnotString(fn, mem, annotPtr, "Name");
}
//#endregion
//#region src/features/annotations/internal/read/readTextAnnotation.ts
/** Default `/C` — matches the generator's yellow note fill and the writer default. */
const DEFAULT_NOTE_COLOR$1 = {
	r: 255,
	g: 255,
	b: 0
};
/** An absent or foreign `/Name` reads as 'note' (ISO 32000 §12.5.6.4 default). */
const DEFAULT_NOTE_ICON = "note";
function readText(fn, mem, annotPtr, base) {
	const color = readAnnotColor(fn, mem, annotPtr) ?? { ...DEFAULT_NOTE_COLOR$1 };
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	const opacity = ca == null ? 1 : Math.max(0, Math.min(1, ca));
	const icon = NOTE_CODE_TO_ICON[fn.EPDFAnnot_GetName(annotPtr)] ?? DEFAULT_NOTE_ICON;
	return {
		...base,
		subtype: "text",
		icon,
		color,
		opacity
	};
}
//#endregion
//#region src/features/annotations/internal/read/readTextMarkupAnnotation.ts
const DEFAULT_HIGHLIGHT_COLOR$1 = {
	r: 255,
	g: 255,
	b: 0
};
const DEFAULT_TEXT_MARKUP_COLOR$1 = {
	r: 0,
	g: 0,
	b: 0
};
/**
* Shared reader for the four text-markup subtypes. Wires color, opacity,
* and quadPoints; the caller fills in the `subtype` literal so the
* result matches the requested DTO shape.
*/
function readTextMarkupExtras(fn, mem, annotPtr, fallbackColor = DEFAULT_TEXT_MARKUP_COLOR$1) {
	const color = readAnnotColor(fn, mem, annotPtr) ?? { ...fallbackColor };
	const ca = readAnnotOpacity(fn, mem, annotPtr);
	return {
		color,
		opacity: ca == null ? 1 : Math.max(0, Math.min(1, ca)),
		quadPoints: readQuadPoints(fn, mem, annotPtr)
	};
}
function readHighlight(fn, mem, annotPtr, base) {
	const extras = readTextMarkupExtras(fn, mem, annotPtr, DEFAULT_HIGHLIGHT_COLOR$1);
	return {
		...base,
		subtype: "highlight",
		...extras
	};
}
function readUnderline(fn, mem, annotPtr, base) {
	const extras = readTextMarkupExtras(fn, mem, annotPtr);
	return {
		...base,
		subtype: "underline",
		...extras
	};
}
function readSquiggly(fn, mem, annotPtr, base) {
	const extras = readTextMarkupExtras(fn, mem, annotPtr);
	return {
		...base,
		subtype: "squiggly",
		...extras
	};
}
function readStrikeout(fn, mem, annotPtr, base) {
	const extras = readTextMarkupExtras(fn, mem, annotPtr);
	const intent = strikeoutIntentFromName(readIntent(fn, mem, annotPtr));
	return {
		...base,
		subtype: "strikeout",
		intent,
		...extras
	};
}
//#endregion
//#region src/features/annotations/internal/read/readUnsupportedAnnotation.ts
/**
* Forward-compat fallback. Captures the raw subtype code (and best-effort
* subtype name from the dict) so debugging information survives across
* the wire even when no per-subtype reader has been wired yet.
*/
function readUnsupported(fn, mem, annotPtr, base, rawSubtypeCode) {
	const rawSubtypeName = readAnnotString(fn, mem, annotPtr, "Subtype");
	return {
		...base,
		subtype: "unsupported",
		rawSubtypeCode,
		rawSubtypeName
	};
}
//#endregion
//#region src/features/annotations/internal/read/readVertexAnnotation.ts
/**
* Shared reader for the two vertex subtypes (polygon/polyline). Reads the
* common stroke/fill styling plus the `/Vertices` point list; each caller
* layers its own subtype-specific extras (polygon: cloudy border; polyline:
* line endings) and the `subtype` literal.
*/
function readVertexExtras(fn, mem, annotPtr) {
	const rotation = readAnnotationRotation(fn, mem, annotPtr);
	return {
		...readFilledStyleExtras(fn, mem, annotPtr),
		vertices: readVertices(fn, mem, annotPtr),
		...rotation != null ? { rotation } : {}
	};
}
function readPolygon(fn, mem, annotPtr, base) {
	return {
		...base,
		subtype: "polygon",
		...readVertexExtras(fn, mem, annotPtr),
		cloudyIntensity: readBorderEffect(fn, mem, annotPtr)
	};
}
function readPolyline(fn, mem, annotPtr, base) {
	return {
		...base,
		subtype: "polyline",
		...readVertexExtras(fn, mem, annotPtr),
		lineEndings: readLineEndings(fn, mem, annotPtr)
	};
}
//#endregion
//#region src/features/annotations/internal/read/readWidgetAnnotation.ts
const MK_BORDER_COLOR$1 = 0;
const MK_BACKGROUND_COLOR$1 = 1;
const I32_BYTES = 4;
function readMKColor(fn, mem, annotPtr, which) {
	return withScratchN(mem, [
		I32_BYTES,
		I32_BYTES,
		I32_BYTES
	], ([r, g, b]) => {
		if (!fn.EPDFAnnot_GetMKColor(annotPtr, which, r, g, b)) return null;
		return {
			r: readI32(mem, r) & 255,
			g: readI32(mem, g) & 255,
			b: readI32(mem, b) & 255
		};
	});
}
/**
* Widget-plane read: /MK colours, /BS border, /DA text defaults, /Q, and
* the field join. Field-plane data (value, options, flags) lives on
* `doc.forms` — join via `fieldObjectNumber`.
*/
function readWidget(fn, mem, annotPtr, base) {
	const border = readBorderFields(fn, mem, annotPtr);
	const da = readDefaultAppearance(fn, mem, annotPtr);
	return {
		...base,
		subtype: "widget",
		color: readMKColor(fn, mem, annotPtr, MK_BORDER_COLOR$1),
		interiorColor: readMKColor(fn, mem, annotPtr, MK_BACKGROUND_COLOR$1),
		strokeWidth: border.strokeWidth,
		borderStyle: border.borderStyle,
		...da ? {
			fontFamily: standardFontFromCode(da.fontCode),
			fontSize: da.fontSize,
			fontColor: da.color
		} : {},
		textAlign: textAlignmentFromCode(readTextAlignment(fn, annotPtr)),
		fieldObjectNumber: 0,
		fieldFamily: "unknown"
	};
}
//#endregion
//#region src/features/annotations/internal/read/annotationReaderRegistry.ts
const READER_BY_SUBTYPE = {
	highlight: readHighlight,
	underline: readUnderline,
	squiggly: readSquiggly,
	strikeout: readStrikeout,
	circle: readCircle,
	square: readSquare,
	polygon: readPolygon,
	polyline: readPolyline,
	line: readLine,
	link: readLink,
	ink: readInk,
	"free-text": readFreeText,
	caret: readCaret,
	text: readText,
	stamp: readStamp,
	"file-attachment": readFileAttachment,
	widget: readWidget,
	redact: readRedact,
	unsupported: readUnsupported
};
/**
* Maps a PDFium subtype code onto the reader that should handle it.
* Returns the `unsupported` reader for any subtype that doesn't have a
* dedicated reader yet.
*/
function pickReader(rawSubtypeCode) {
	const subtype = subtypeFromCode(rawSubtypeCode);
	const reader = READER_BY_SUBTYPE[subtype];
	if (reader) return {
		reader,
		subtype
	};
	return {
		reader: READER_BY_SUBTYPE.unsupported,
		subtype: "unsupported"
	};
}
//#endregion
//#region src/features/forms/internal/formModelCache.ts
/**
* Per-session cache of the native `EPDFForm_LoadModel` snapshot, keyed on
* the session's mutation sequence.
*
* The native model is an immutable, detached snapshot: correct forever,
* stale after any document mutation. Rebuilding it per call would walk the
* field tree and sweep every page's /Annots on each read (widget lists on
* multi-page documents read it constantly); keeping one forever would
* serve stale values. Version-keying gives exact coherence for free:
* `DocumentSession.noteMutation()` bumps the sequence on EVERY successful
* mutation (annotations, pages, metadata, forms — widgets are annotations,
* so all of them can affect the form model), and the next read rebuilds.
*
* The cache also owns the native handle's lifetime: exactly one live model
* per session, closed before replacement and on session teardown via
* {@link disposeFormModel}.
*/
const cache$1 = /* @__PURE__ */ new WeakMap();
function acquireFormModel(runtime, session) {
	const seq = session.mutationSeq();
	const hit = cache$1.get(session);
	if (hit && hit.seq === seq) return hit.ptr;
	if (hit) {
		runtime.fn.EPDFForm_CloseModel(hit.ptr);
		cache$1.delete(session);
	}
	const ptr = runtime.fn.EPDFForm_LoadModel(session.requireDocPtr());
	if (ptr === NULL_PTR) throw new EngineError(EngineErrorCode.Unknown, "failed to build form model");
	cache$1.set(session, {
		seq,
		ptr
	});
	return ptr;
}
/** Close and forget the session's cached model, if any. */
function disposeFormModel(runtime, session) {
	const hit = cache$1.get(session);
	if (!hit) return;
	runtime.fn.EPDFForm_CloseModel(hit.ptr);
	cache$1.delete(session);
}
//#endregion
//#region src/features/forms/internal/readFormSnapshot.ts
const FAMILY_BY_CODE = {
	0: "unknown",
	1: "pushbutton",
	2: "checkbox",
	3: "radio",
	4: "text",
	5: "combobox",
	6: "listbox",
	7: "signature"
};
const FORM_KIND_BY_CODE = {
	0: "none",
	1: "acroform",
	2: "xfa"
};
const FF_READ_ONLY = 1;
const FF_REQUIRED = 2;
const FF_NO_EXPORT = 4;
const FF_MULTILINE = 4096;
const FF_PASSWORD = 8192;
const FF_NO_TOGGLE_TO_OFF = 16384;
const FF_EDIT = 1 << 18;
const FF_MULTI_SELECT = 1 << 21;
const FF_COMB = 1 << 24;
const FF_RADIOS_IN_UNISON = 1 << 25;
function readWide(runtime, call) {
	return readUtf16String(runtime.mem, call) ?? "";
}
function readWideOrNull(runtime, call) {
	return readUtf16String(runtime.mem, call, null);
}
function readToggleWidgets(runtime, model, fieldIndex) {
	const { fn } = runtime;
	const count = fn.EPDFForm_CountFieldWidgets(model, fieldIndex);
	const widgets = [];
	for (let w = 0; w < count; w++) widgets.push({
		annotObjectNumber: fn.EPDFForm_GetFieldWidgetObjNum(model, fieldIndex, w),
		pageObjectNumber: fn.EPDFForm_GetFieldWidgetPageObjNum(model, fieldIndex, w),
		onState: readUtf8String(runtime.mem, (buf, cap) => fn.EPDFForm_GetFieldWidgetOnState(model, fieldIndex, w, buf, cap)) ?? "",
		exportValue: readWide(runtime, (buf, cap) => fn.EPDFForm_GetFieldWidgetExportValue(model, fieldIndex, w, buf, cap)),
		checked: fn.EPDFForm_IsFieldWidgetChecked(model, fieldIndex, w)
	});
	return widgets;
}
function readPlainWidgets(runtime, model, fieldIndex) {
	const { fn } = runtime;
	const count = fn.EPDFForm_CountFieldWidgets(model, fieldIndex);
	const widgets = [];
	for (let w = 0; w < count; w++) widgets.push({
		annotObjectNumber: fn.EPDFForm_GetFieldWidgetObjNum(model, fieldIndex, w),
		pageObjectNumber: fn.EPDFForm_GetFieldWidgetPageObjNum(model, fieldIndex, w)
	});
	return widgets;
}
function readOptions(runtime, model, fieldIndex) {
	const { fn } = runtime;
	const count = fn.EPDFForm_CountFieldOptions(model, fieldIndex);
	const options = [];
	for (let o = 0; o < count; o++) options.push({
		label: readWide(runtime, (buf, cap) => fn.EPDFForm_GetFieldOptionLabel(model, fieldIndex, o, buf, cap)),
		value: readWide(runtime, (buf, cap) => fn.EPDFForm_GetFieldOptionValue(model, fieldIndex, o, buf, cap)),
		selected: fn.EPDFForm_IsFieldOptionSelected(model, fieldIndex, o)
	});
	return options;
}
/** Read one field of the native model into its per-family DTO. */
function readFieldAt(runtime, model, fieldIndex, actionBudget = new ActionReadBudgetTracker()) {
	const { fn } = runtime;
	const fieldObjectNumber = fn.EPDFForm_GetFieldObjNum(model, fieldIndex);
	const name = readWide(runtime, (buf, cap) => fn.EPDFForm_GetFieldName(model, fieldIndex, buf, cap));
	const rawFlags = fn.EPDFForm_GetFieldFlags(model, fieldIndex);
	const family = FAMILY_BY_CODE[fn.EPDFForm_GetFieldFamily(model, fieldIndex)] ?? "unknown";
	const valueEntry = readValueEntry(runtime, model, fieldIndex, false);
	const defaultValueEntry = readValueEntry(runtime, model, fieldIndex, true);
	const actions = readFieldActions(runtime, model, fieldIndex, actionBudget);
	const base = {
		ref: fieldObjectNumber > 0 ? {
			kind: "objectNumber",
			fieldObjectNumber
		} : {
			kind: "fqn",
			name
		},
		fieldObjectNumber,
		name,
		family,
		origin: fn.EPDFForm_GetFieldOrigin(model, fieldIndex) === 1 ? "recovered" : "acroform",
		flags: {
			readOnly: (rawFlags & FF_READ_ONLY) !== 0,
			required: (rawFlags & FF_REQUIRED) !== 0,
			noExport: (rawFlags & FF_NO_EXPORT) !== 0,
			raw: rawFlags
		},
		alternateName: readWideOrNull(runtime, (buf, cap) => fn.EPDFForm_GetFieldAlternateName(model, fieldIndex, buf, cap)),
		mappingName: readWideOrNull(runtime, (buf, cap) => fn.EPDFForm_GetFieldMappingName(model, fieldIndex, buf, cap)),
		valueEntry,
		defaultValueEntry,
		...actions ? { actions } : {},
		widgets: []
	};
	const scalarValue = entryScalar(valueEntry);
	const scalarDefault = entryScalar(defaultValueEntry);
	switch (family) {
		case "text": {
			const maxLen = fn.EPDFForm_GetFieldMaxLen(model, fieldIndex);
			return {
				...base,
				family,
				value: scalarValue,
				defaultValue: scalarDefault,
				maxLength: maxLen > 0 ? maxLen : null,
				multiline: (rawFlags & FF_MULTILINE) !== 0,
				password: (rawFlags & FF_PASSWORD) !== 0,
				comb: (rawFlags & FF_COMB) !== 0,
				widgets: readPlainWidgets(runtime, model, fieldIndex)
			};
		}
		case "checkbox": {
			const widgets = readToggleWidgets(runtime, model, fieldIndex);
			return {
				...base,
				family,
				checked: widgets.some((w) => w.checked),
				exportValue: widgets[0]?.exportValue ?? "Yes",
				widgets
			};
		}
		case "radio": {
			const widgets = readToggleWidgets(runtime, model, fieldIndex);
			return {
				...base,
				family,
				value: widgets.find((widget) => widget.checked)?.exportValue ?? "Off",
				radiosInUnison: (rawFlags & FF_RADIOS_IN_UNISON) !== 0,
				noToggleToOff: (rawFlags & FF_NO_TOGGLE_TO_OFF) !== 0,
				widgets
			};
		}
		case "combobox": return {
			...base,
			family,
			value: scalarValue,
			defaultValue: scalarDefault,
			edit: (rawFlags & FF_EDIT) !== 0,
			options: readOptions(runtime, model, fieldIndex),
			widgets: readPlainWidgets(runtime, model, fieldIndex)
		};
		case "listbox": {
			const options = readOptions(runtime, model, fieldIndex);
			return {
				...base,
				family,
				selectedValues: options.filter((o) => o.selected).map((o) => o.value),
				multiSelect: (rawFlags & FF_MULTI_SELECT) !== 0,
				options,
				widgets: readPlainWidgets(runtime, model, fieldIndex)
			};
		}
		case "pushbutton":
		case "signature": return {
			...base,
			family,
			widgets: readPlainWidgets(runtime, model, fieldIndex)
		};
		default: return {
			...base,
			family: "unknown",
			rawValue: valueEntry.kind === "array" ? valueEntry.values.join(",") : entryScalar(valueEntry),
			widgets: readPlainWidgets(runtime, model, fieldIndex)
		};
	}
}
/** Read the whole native model into a detached {@link FormSnapshot}. */
function readFormSnapshot(runtime, model) {
	const { fn } = runtime;
	const count = fn.EPDFForm_CountFields(model);
	const fields = [];
	const actionBudget = new ActionReadBudgetTracker();
	for (let i = 0; i < count; i++) fields.push(readFieldAt(runtime, model, i, actionBudget));
	const calculationOrder = [];
	const calculationCount = fn.EPDFForm_CountCalculationOrder(model);
	for (let index = 0; index < calculationCount; index++) {
		const fieldIndex = fn.EPDFForm_GetCalculationOrderFieldIndex(model, index);
		calculationOrder.push(fieldIndex >= 0 && fieldIndex < fields.length ? fields[fieldIndex].ref : null);
	}
	return {
		formKind: FORM_KIND_BY_CODE[fn.EPDFForm_GetFormKind(model)] ?? "none",
		needsAppearances: fn.EPDFForm_GetNeedAppearances(model),
		fields,
		calculationOrder
	};
}
function readValueEntry(runtime, model, fieldIndex, isDefault) {
	const { fn } = runtime;
	const kind = isDefault ? fn.EPDFForm_GetFieldDefaultValueKind(model, fieldIndex) : fn.EPDFForm_GetFieldValueKind(model, fieldIndex);
	if (kind === 0) return { kind: "none" };
	if (kind === 3) return { kind: "unsupported" };
	if (kind !== 1 && kind !== 2) return { kind: "unsupported" };
	const count = isDefault ? fn.EPDFForm_CountFieldDefaultValues(model, fieldIndex) : fn.EPDFForm_CountFieldValues(model, fieldIndex);
	const values = [];
	for (let index = 0; index < count; index++) values.push(readWide(runtime, (buf, capacity) => isDefault ? fn.EPDFForm_GetFieldDefaultValueAt(model, fieldIndex, index, buf, capacity) : fn.EPDFForm_GetFieldValueAt(model, fieldIndex, index, buf, capacity)));
	return kind === 1 ? {
		kind: "scalar",
		value: values[0] ?? ""
	} : {
		kind: "array",
		values
	};
}
function entryScalar(entry) {
	if (entry.kind === "scalar") return entry.value;
	if (entry.kind === "array") return entry.values[0] ?? "";
	return "";
}
function readFieldActions(runtime, model, fieldIndex, budget) {
	const { fn, mem } = runtime;
	const entries = [
		["keystroke", 0],
		["format", 1],
		["validate", 2],
		["calculate", 3]
	];
	const actions = {};
	for (const [key, event] of entries) {
		const action = readActionModel(fn, mem, fn.EPDFForm_GetFieldActionModel(model, fieldIndex, event), budget);
		if (action) actions[key] = action;
	}
	return Object.keys(actions).length > 0 ? actions : void 0;
}
//#endregion
//#region src/features/annotations/internal/read/joinWidgetField.ts
/**
* Resolve the owning field's object number for a widget annotation, or 0
* when unattached. (The /Parent of a widget is a FIELD dictionary - not an
* annotation - so FPDFAnnot_GetLinkedAnnot cannot follow it; the reconciled
* form model is the authoritative join.)
*/
function resolveWidgetFieldObjectNumber(runtime, session, annotObjectNumber) {
	if (annotObjectNumber <= 0) return 0;
	const model = acquireFormModel(runtime, session);
	const fieldIndex = runtime.fn.EPDFForm_GetFieldIndexForWidget(model, annotObjectNumber);
	if (fieldIndex < 0) return 0;
	return runtime.fn.EPDFForm_GetFieldObjNum(model, fieldIndex);
}
/** Stamp `fieldObjectNumber` onto every widget DTO in a freshly read list. */
function joinWidgetFieldNumbers(runtime, session, annotations) {
	for (const annotation of annotations) {
		if (annotation.subtype !== "widget") continue;
		if (annotation.ref.kind !== "objectNumber") continue;
		const model = acquireFormModel(runtime, session);
		const fieldIndex = runtime.fn.EPDFForm_GetFieldIndexForWidget(model, annotation.ref.annotObjectNumber);
		if (fieldIndex < 0) {
			annotation.fieldObjectNumber = 0;
			annotation.fieldFamily = "unknown";
			continue;
		}
		annotation.fieldObjectNumber = runtime.fn.EPDFForm_GetFieldObjNum(model, fieldIndex);
		annotation.fieldFamily = FAMILY_BY_CODE[runtime.fn.EPDFForm_GetFieldFamily(model, fieldIndex)] ?? "unknown";
	}
}
//#endregion
//#region src/features/annotations/internal/read/readAnnotationIdentity.ts
/**
* Decides which `AnnotationRef` form to expose on the wire and the
* `identityQuality` flag.
*
* Order:
*   1. `EPDFAnnot_GetObjectNumber(annot)` - if `> 0`, use `objectNumber`.
*      Always durable.
*   2. /NM - if present and non-empty, use `nm`. Always durable.
*   3. Fallback: `index + revision`. Always weak. The revision must be
*      provided by the caller (sourced from the live `RevisionAuthority`).
*/
function readAnnotationIdentity(fn, mem, annotPtr, pageObjectNumber, index, revision) {
	const objectNumber = fn.EPDFAnnot_GetObjectNumber(annotPtr);
	const nm = readAnnotString(fn, mem, annotPtr, "NM");
	if (objectNumber > 0) return {
		ref: {
			kind: "objectNumber",
			pageObjectNumber,
			annotObjectNumber: objectNumber
		},
		identityQuality: "durable",
		nm: nm ?? null
	};
	if (nm && nm.length > 0) return {
		ref: {
			kind: "nm",
			pageObjectNumber,
			nm
		},
		identityQuality: "durable",
		nm
	};
	return {
		ref: {
			kind: "index",
			pageObjectNumber,
			index,
			revision
		},
		identityQuality: "weak",
		nm: nm ?? null
	};
}
/**
* Map a PDFium reply-type code to the wire string, GATED on the presence
* of `/IRT`. PDFium's `EPDFAnnot_GetReplyType` returns `RT_REPLY` even
* when there is no `/IRT` (it reports the `/RT` default in isolation), so
* callers must only invoke this for annotations that actually carry an
* `/IRT` link. `RT_GROUP` -> `'group'`; everything else -> `'reply'`.
*/
function replyTypeFromCode(code) {
	return code === 2 ? "group" : "reply";
}
/** Map the wire string to the PDFium code written via `EPDFAnnot_SetReplyType`. */
function replyTypeToCode(replyType) {
	return replyType === "group" ? 2 : 1;
}
//#endregion
//#region src/features/annotations/internal/read/readAnnotationRelationship.ts
const NO_RELATIONSHIP = {
	inReplyTo: null,
	replyType: null
};
/**
* Read the `/IRT` + `/RT` relationship edge of an annotation.
*
* `/IRT` is an indirect reference to the parent annotation, which (per ISO
* 32000 §12.5.6.2) lives on the SAME page — so we surface the parent with
* the same `pageObjectNumber` and the same identity precedence the rest of
* the engine uses (objectNumber, then `/NM`). We do NOT fall back to an
* index ref: an `/IRT` target is by construction an indirect object, so its
* object number is always available. In the pathological case where neither
* an object number nor an `/NM` can be read (a malformed direct-object
* target), we report no relationship rather than an unaddressable parent —
* keeping the invariant `replyType === null` iff `inReplyTo === null`.
*
* `/RT` is read via `EPDFAnnot_GetReplyType`, which returns the `/R` default
* when `/RT` is absent; we only consult it when `/IRT` is present, so a
* top-level annotation never spuriously reports `'reply'`.
*/
function readAnnotationRelationship(fn, mem, annotPtr, pageObjectNumber) {
	const parentPtr = fn.FPDFAnnot_GetLinkedAnnot(annotPtr, "IRT");
	if (!parentPtr) return NO_RELATIONSHIP;
	try {
		const inReplyTo = readParentRef(fn, mem, parentPtr, pageObjectNumber);
		if (!inReplyTo) return NO_RELATIONSHIP;
		return {
			inReplyTo,
			replyType: replyTypeFromCode(fn.EPDFAnnot_GetReplyType(annotPtr))
		};
	} finally {
		fn.FPDFPage_CloseAnnot(parentPtr);
	}
}
function readParentRef(fn, mem, parentPtr, pageObjectNumber) {
	const objectNumber = fn.EPDFAnnot_GetObjectNumber(parentPtr);
	if (objectNumber > 0) return {
		kind: "objectNumber",
		pageObjectNumber,
		annotObjectNumber: objectNumber
	};
	const nm = readAnnotString(fn, mem, parentPtr, "NM");
	if (nm && nm.length > 0) return {
		kind: "nm",
		pageObjectNumber,
		nm
	};
	return null;
}
//#endregion
//#region src/features/annotations/internal/read/readEmbedMetadata.ts
/**
* Read /EMBD_Metadata from an annotation, if present. Returns `null`
* (not an empty object) when the annotation has no EMBD_Metadata
* dictionary at all — the caller can distinguish "no metadata" from
* "metadata with only some fields set".
*
* Uses the same UTF-16LE two-pass read pattern as `readAnnotString` in
* `./util.ts` so a future change to the underlying memory helpers
* propagates uniformly.
*/
function readEmbedMetadata(fn, mem, annotPtr) {
	if (!fn.EPDFAnnot_HasEmbedMetadata(annotPtr)) return null;
	const schemaVersion = readMetaNumber(fn, mem, annotPtr, "SchemaVersion");
	const userId = readMetaString(fn, mem, annotPtr, "UserID");
	const groupId = readMetaString(fn, mem, annotPtr, "GroupID");
	const createdBy = readMetaString(fn, mem, annotPtr, "CreatedBy");
	const updatedBy = readMetaString(fn, mem, annotPtr, "UpdatedBy");
	const out = {};
	if (schemaVersion !== void 0) out.schemaVersion = schemaVersion;
	if (userId !== void 0) out.userId = userId;
	if (groupId !== void 0) out.groupId = groupId;
	if (createdBy !== void 0) out.createdBy = createdBy;
	if (updatedBy !== void 0) out.updatedBy = updatedBy;
	return out;
}
function readMetaString(fn, mem, annotPtr, key) {
	const len = fn.EPDFAnnot_GetEmbedMetadataString(annotPtr, key, NULL_PTR, 0);
	if (len <= 0) return void 0;
	if (len === 2) return "";
	const buf = mem.alloc(len);
	try {
		if (fn.EPDFAnnot_GetEmbedMetadataString(annotPtr, key, buf, len) <= 0) return void 0;
		return mem.readU16String(buf);
	} finally {
		mem.free(buf);
	}
}
function readMetaNumber(fn, mem, annotPtr, key) {
	const buf = mem.alloc(4);
	try {
		if (!fn.EPDFAnnot_GetEmbedMetadataNumber(annotPtr, key, buf)) return void 0;
		return Number(mem.peek(buf, "f32"));
	} finally {
		mem.free(buf);
	}
}
//#endregion
//#region src/features/annotations/internal/blendMode.ts
/** Explicit wire-domain ↔ PDFium `FPDF_BLENDMODE` mapping. */
function blendModeToCode(mode) {
	switch (mode) {
		case "normal": return 0;
		case "multiply": return 1;
		case "screen": return 2;
		case "overlay": return 3;
		case "darken": return 4;
		case "lighten": return 5;
		case "color-dodge": return 6;
		case "color-burn": return 7;
		case "hard-light": return 8;
		case "soft-light": return 9;
		case "difference": return 10;
		case "exclusion": return 11;
		case "hue": return 12;
		case "saturation": return 13;
		case "color": return 14;
		case "luminosity": return 15;
	}
}
function blendModeFromCode(code) {
	switch (code) {
		case 1: return "multiply";
		case 2: return "screen";
		case 3: return "overlay";
		case 4: return "darken";
		case 5: return "lighten";
		case 6: return "color-dodge";
		case 7: return "color-burn";
		case 8: return "hard-light";
		case 9: return "soft-light";
		case 10: return "difference";
		case 11: return "exclusion";
		case 12: return "hue";
		case 13: return "saturation";
		case 14: return "color";
		case 15: return "luminosity";
		default: return "normal";
	}
}
//#endregion
//#region src/features/annotations/internal/read/readAnnotationBase.ts
/**
* Reads the wire shell every annotation DTO carries: identity, flags,
* rect, contents, author, dates. No subtype-specific fields. The
* per-subtype reader builds its DTO by extending this with its own
* fields and `subtype: '...'` discriminator.
*/
function readAnnotationBase(fn, mem, annotPtr, pageObjectNumber, index, revision, actionBudget = new ActionReadBudgetTracker()) {
	const identity = readAnnotationIdentity(fn, mem, annotPtr, pageObjectNumber, index, revision);
	const rect = readAnnotRect(fn, mem, annotPtr);
	const flags = readAnnotFlags(fn, annotPtr);
	const contents = readAnnotString(fn, mem, annotPtr, "Contents");
	const author = readAnnotString(fn, mem, annotPtr, "T");
	const createdRaw = readAnnotString(fn, mem, annotPtr, "CreationDate");
	const modifiedRaw = readAnnotString(fn, mem, annotPtr, "M");
	const blendMode = blendModeFromCode(fn.EPDFAnnot_GetBlendMode(annotPtr));
	const embd = readEmbedMetadata(fn, mem, annotPtr);
	const relationship = readAnnotationRelationship(fn, mem, annotPtr, pageObjectNumber);
	const actions = readAnnotationActions(fn, mem, annotPtr, actionBudget);
	return {
		ref: identity.ref,
		pageObjectNumber,
		index,
		identityQuality: identity.identityQuality,
		nm: identity.nm,
		flags,
		rect,
		contents,
		author,
		created: createdRaw ? pdfDateToIso(createdRaw) : null,
		modified: modifiedRaw ? pdfDateToIso(modifiedRaw) : null,
		blendMode,
		inReplyTo: relationship.inReplyTo,
		replyType: relationship.replyType,
		...embd?.userId !== void 0 ? { userId: embd.userId } : {},
		...embd?.groupId !== void 0 ? { groupId: embd.groupId } : {},
		...embd?.createdBy !== void 0 ? { createdBy: embd.createdBy } : {},
		...embd?.updatedBy !== void 0 ? { updatedBy: embd.updatedBy } : {},
		...actions ? { actions } : {}
	};
}
function readAnnotationActions(fn, mem, annotPtr, budget) {
	const events = [
		["activate", 0],
		["cursorEnter", 1],
		["cursorExit", 2],
		["mouseDown", 3],
		["mouseUp", 4],
		["focus", 5],
		["blur", 6],
		["pageOpen", 7],
		["pageClose", 8],
		["pageVisible", 9],
		["pageInvisible", 10]
	];
	const actions = {};
	for (const [key, event] of events) {
		const action = readActionModel(fn, mem, fn.EPDFAnnot_GetActionModel(annotPtr, event), budget);
		if (action) actions[key] = action;
	}
	return Object.keys(actions).length > 0 ? actions : void 0;
}
//#endregion
//#region src/features/annotations/internal/read/collectPageAnnotations.ts
/**
* Shared per-page annotation read loop, used by both read paths. The raw
* path (off `docPtr`, no `pagePtr`) and the full path (off an acquired
* `pagePtr`) differ only in how they obtain the annotation count and each
* `annotPtr`; everything after that — per-subtype dispatch, weak-flag
* accounting, `pageState` decoration — is identical, so it lives here once.
*
* `getAnnotPtrAt(i)` returns the annotation handle at index `i`; this loop
* always closes it via `FPDFPage_CloseAnnot`. The caller owns acquiring and
* releasing any enclosing `pagePtr`.
*/
function collectPageAnnotations(input) {
	const { runtime, session, pageObjectNumber, count, getAnnotPtrAt, signal } = input;
	const { fn, mem } = runtime;
	const annotations = [];
	let hasWeak = false;
	const revision = session.pageState(pageObjectNumber).revision;
	const actionBudget = new ActionReadBudgetTracker();
	const readCtx = readContextFor(session);
	for (let i = 0; i < count; i++) {
		throwIfAborted(signal);
		const annotPtr = getAnnotPtrAt(i);
		if (!annotPtr) continue;
		try {
			const base = readAnnotationBase(fn, mem, annotPtr, pageObjectNumber, i, revision, actionBudget);
			const subtypeCode = fn.FPDFAnnot_GetSubtype(annotPtr);
			const { reader } = pickReader(subtypeCode);
			const dto = reader(fn, mem, annotPtr, base, subtypeCode, readCtx);
			annotations.push(dto);
			if (dto.identityQuality === "weak") hasWeak = true;
		} finally {
			fn.FPDFPage_CloseAnnot(annotPtr);
		}
	}
	joinWidgetFieldNumbers(runtime, session, annotations);
	session.recordWeakFlag(pageObjectNumber, hasWeak);
	return {
		pageState: session.pageState(pageObjectNumber),
		annotations
	};
}
//#endregion
//#region src/features/annotations/AnnotationReader.ts
/**
* Per-page slow-path reader. Acquires a `pagePtr` from the
* `PagePtrPool`, dispatches the same per-subtype catalog as the raw
* reader (via `collectPageAnnotations`), and releases the page on exit.
*
* Currently the dispatched readers do not use the `pagePtr` parameter
* (text-markup is fully readable from the dict). Subtype readers that
* need form/widget access (e.g. widget) will plug into this code path
* as they land - the public DTO surface stays the same.
*/
var AnnotationReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	list(pageObjectNumber, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		try {
			const count = fn.FPDFPage_GetAnnotCount(pagePtr);
			return collectPageAnnotations({
				runtime: this.runtime,
				session: this.session,
				pageObjectNumber,
				count,
				getAnnotPtrAt: (i) => fn.FPDFPage_GetAnnot(pagePtr, i),
				signal
			});
		} finally {
			pool.release(pageObjectNumber);
		}
	}
};
//#endregion
//#region src/features/render/deviceRaster.ts
/**
* The ONE place a PDF user-space region becomes a device raster.
*
* Every PDFium rasterizer — `PageRenderReader`, `AnnotationAppearanceReader`,
* and any future one (thumbnails, stamps, flatten) — composes these three
* pieces and supplies only its own `draw` call. The geometry is a pure affine
* (the engine twin of the viewer's `Mat2D`/`rotateScaleMatrix`); the bitmap
* lifecycle (alloc → fill → draw → read back → free) lives in `rasterize`.
*/
const FPDF_BITMAP_BGRA = 4;
/**
* Map a normalized post-`GetDisplayMatrix()` display-space rect onto an
* `outW × outH` device bitmap, baking in the caller's viewport rotation.
*
* PDFium's page and annotation renderers pre-apply `CPDF_Page::GetDisplayMatrix()`
* before concatenating the caller matrix. That display matrix has already
* converted PDF page coordinates (y-up) into bitmap/display coordinates
* (y-down), so the caller matrix must target that post-display rect.
*/
function displayRectToDeviceMatrix(rect, rotation, outW, outH) {
	const left = rect.left;
	const bottom = rect.bottom;
	const width = rect.right - rect.left;
	const height = rect.top - rect.bottom;
	const sx0 = outW / width;
	const sy0 = outH / height;
	const sx90 = outW / height;
	const sy90 = outH / width;
	switch (rotation) {
		case 90: return [
			0,
			sy90,
			-sx90,
			0,
			sx90 * (bottom + height),
			-sy90 * left
		];
		case 180: return [
			-sx0,
			0,
			0,
			-sy0,
			sx0 * (left + width),
			sy0 * (bottom + height)
		];
		case 270: return [
			0,
			-sy90,
			sx90,
			0,
			-sx90 * bottom,
			sy90 * (left + width)
		];
		case 0: return [
			sx0,
			0,
			0,
			sy0,
			-sx0 * left,
			-sy0 * bottom
		];
	}
}
/**
* Device pixel size for a region under a rotation + viewport. `width` swaps with
* height on quarter-turns. Validates the viewport (the public-API contract).
*/
function deviceSize(rect, rotation, viewport) {
	const rectWidth = rect.right - rect.left;
	const rectHeight = rect.top - rect.bottom;
	const swap = rotation === 90 || rotation === 270;
	const baseWidth = swap ? rectHeight : rectWidth;
	const baseHeight = swap ? rectWidth : rectHeight;
	if (viewport.kind === "width") {
		if (!Number.isFinite(viewport.width) || viewport.width <= 0) throw new EngineError(EngineErrorCode.InvalidArg, "render viewport width must be positive");
		const width = Math.max(1, Math.round(viewport.width));
		return {
			width,
			height: Math.max(1, Math.round(width * baseHeight / baseWidth))
		};
	}
	const scale = viewport.scale ?? 1;
	if (!Number.isFinite(scale) || scale <= 0) throw new EngineError(EngineErrorCode.InvalidArg, "render viewport scale must be positive");
	return {
		width: Math.max(1, Math.round(baseWidth * scale)),
		height: Math.max(1, Math.round(baseHeight * scale))
	};
}
/**
* Owns the whole bitmap lifecycle: allocate the pixel buffer + bitmap + matrix
* (+ clip), fill the background, run the caller's `draw`, read the pixels back
* into a `PageRaster`, and free everything. Returns null on a degenerate rect or
* a failed allocation/draw.
*/
function rasterize(runtime, opts) {
	const { fn, mem } = runtime;
	const { rect, page, rotation, viewport, background, draw } = opts;
	if (rect.right <= rect.left || rect.top <= rect.bottom) return null;
	const displayRect = pdfRectToDisplayRect(rect, page.height);
	const { width, height } = deviceSize(displayRect, rotation, viewport);
	if (opts.maxOutputPixels !== void 0 && width * height > opts.maxOutputPixels) throw new EngineError(EngineErrorCode.InvalidArg, `render output ${width}x${height} exceeds the ${opts.maxOutputPixels}-pixel budget — request a smaller width (or tiles, once available)`);
	const stride = width * 4;
	const bytes = stride * height;
	let pixelPtr = null;
	let bitmapPtr = null;
	let matrixPtr = null;
	let clipPtr = null;
	try {
		pixelPtr = mem.alloc(bytes);
		bitmapPtr = fn.FPDFBitmap_CreateEx(width, height, FPDF_BITMAP_BGRA, pixelPtr, stride);
		if (!bitmapPtr) return null;
		fn.FPDFBitmap_FillRect(bitmapPtr, 0, 0, width, height, background === "transparent" ? 0 : 4294967295);
		matrixPtr = mem.alloc(24);
		pokeMat2D(mem, matrixPtr, displayRectToDeviceMatrix(displayRect, rotation, width, height));
		clipPtr = mem.alloc(16);
		mem.poke(clipPtr, "f32", 0, 0);
		mem.poke(clipPtr, "f32", 0, 4);
		mem.poke(clipPtr, "f32", width, 8);
		mem.poke(clipPtr, "f32", height, 12);
		if (!draw(bitmapPtr, matrixPtr, clipPtr)) return null;
		return {
			width,
			height,
			stride,
			color: "rgba8",
			premultipliedAlpha: false,
			data: toExactArrayBuffer(mem.readBytes(pixelPtr, bytes))
		};
	} finally {
		if (bitmapPtr) fn.FPDFBitmap_Destroy(bitmapPtr);
		if (clipPtr) mem.free(clipPtr);
		if (matrixPtr) mem.free(matrixPtr);
		if (pixelPtr) mem.free(pixelPtr);
	}
}
function pdfRectToDisplayRect(rect, pageHeight) {
	return {
		left: rect.left,
		right: rect.right,
		bottom: pageHeight - rect.top,
		top: pageHeight - rect.bottom
	};
}
function pokeMat2D(mem, ptr, m) {
	for (let i = 0; i < 6; i++) mem.poke(ptr, "f32", m[i], i * 4);
}
function toExactArrayBuffer(bytes) {
	const out = new Uint8Array(bytes.byteLength);
	out.set(bytes);
	return out.buffer;
}
//#endregion
//#region src/features/annotations/AnnotationAppearanceReader.ts
/** `FPDF_ANNOT_WIDGET` — form-field annotation subtype code. */
const ANNOT_SUBTYPE_WIDGET = 20;
/** `FPDF_ANNOT_FREETEXT` — free-text annotation subtype code. */
const ANNOT_SUBTYPE_FREETEXT = 3;
/**
* BOX-family subtypes (free-text 3, square 5, circle 6, stamp 13): the kinds
* whose v3 writers put rotation in the AP `/Matrix` + `/EMBD_Metadata`
* `/UnrotatedRect`. Only these are eligible for rotation-stripped appearance
* rendering — vertex kinds (line/polyline/polygon/ink) pre-rotate their
* geometry, so their rasters must stay on the classic path.
*/
const BOX_FAMILY_SUBTYPES = /* @__PURE__ */ new Set([
	3,
	5,
	6,
	13
]);
/**
* Maps an `AnnotationAppearanceMode` onto the PDFium appearance-mode int and
* the `EPDFAnnot_GetAvailableAppearanceModes` bit it occupies.
*   N -> mode 0 / bit 1, R -> mode 1 / bit 2, D -> mode 2 / bit 4.
*/
const APPEARANCE_MODES = [
	{
		name: "normal",
		modeInt: 0,
		bit: 1
	},
	{
		name: "rollover",
		modeInt: 1,
		bit: 2
	},
	{
		name: "down",
		modeInt: 2,
		bit: 4
	}
];
/**
* Batch-renders the appearance streams (`/AP`) of every annotation on a page,
* one bitmap per requested mode. Ported from the v2 PDFium engine's
* `renderPageAnnotationsRaw` / `renderSingleAnnotAppearance`, but expressed in
* PDF user space and against the `PdfRuntimeModule` (`fn` + `mem`).
*
* Each appearance bitmap is sized to its annotation's `/Rect` scaled by
* `options.scale`. The shared raster helper handles PDFium's display matrix
* convention, so this reader stays in normalized PDF page coordinates.
*/
var AnnotationAppearanceReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	render(pageObjectNumber, options, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		const scale = normalizeScale(options.scale);
		const rotation = options.rotation ?? 0;
		const modes = resolveModes(options.modes);
		const revision = this.session.pageState(pageObjectNumber).revision;
		const appearances = [];
		try {
			const page = {
				width: fn.FPDF_GetPageWidthF(pagePtr),
				height: fn.FPDF_GetPageHeightF(pagePtr)
			};
			const count = fn.FPDFPage_GetAnnotCount(pagePtr);
			for (let i = 0; i < count; i++) {
				throwIfAborted(signal);
				const annotPtr = fn.FPDFPage_GetAnnot(pagePtr, i);
				if (!annotPtr) continue;
				try {
					const available = fn.EPDFAnnot_GetAvailableAppearanceModes(annotPtr);
					if (!available) continue;
					const identity = readAnnotationIdentity(fn, mem, annotPtr, pageObjectNumber, i, revision);
					const subtypeCode = fn.FPDFAnnot_GetSubtype(annotPtr);
					const isCallout = subtypeCode === ANNOT_SUBTYPE_FREETEXT && freeTextIntentFromName(readIntent(fn, mem, annotPtr)) === "free-text-callout";
					const unrotatedRect = BOX_FAMILY_SUBTYPES.has(subtypeCode) && !isCallout && readAnnotationRotation(fn, mem, annotPtr) !== void 0 ? readAnnotationUnrotatedRect(fn, mem, annotPtr) : void 0;
					const rect = normalizePdfRect(unrotatedRect ?? readAnnotRect(fn, mem, annotPtr));
					for (const mode of modes) {
						if (!(available & mode.bit)) continue;
						const raster = this.renderOne(pagePtr, annotPtr, mode.modeInt, rect, page, rotation, scale, unrotatedRect !== void 0, options.maxOutputPixels);
						if (!raster) continue;
						appearances.push({
							ref: identity.ref,
							mode: mode.name,
							rect,
							raster
						});
					}
				} finally {
					fn.FPDFPage_CloseAnnot(annotPtr);
				}
			}
			return {
				pageState: this.session.pageState(pageObjectNumber),
				appearances
			};
		} finally {
			pool.release(pageObjectNumber);
		}
	}
	/**
	* Render a single annotation appearance into its own raster. Returns `null`
	* when the mode has no appearance stream (after an optional form-field AP
	* generation fallback) or the render fails.
	*/
	renderOne(pagePtr, annotPtr, modeInt, rect, page, rotation, scale, stripRotation, maxOutputPixels) {
		const { fn } = this.runtime;
		if (!fn.EPDFAnnot_HasAppearanceStream(annotPtr, modeInt)) if (fn.FPDFAnnot_GetSubtype(annotPtr) === ANNOT_SUBTYPE_WIDGET && !fn.FPDFAnnot_HasKey(annotPtr, "AP")) {
			fn.EPDFAnnot_GenerateFormFieldAP(annotPtr);
			if (!fn.EPDFAnnot_HasAppearanceStream(annotPtr, modeInt)) return null;
		} else return null;
		return rasterize(this.runtime, {
			rect,
			page,
			rotation,
			viewport: {
				kind: "scale",
				scale
			},
			...maxOutputPixels !== void 0 ? { maxOutputPixels } : {},
			background: "transparent",
			draw: (bitmapPtr, matrixPtr) => (stripRotation ? fn.EPDF_RenderAnnotBitmapUnrotated : fn.EPDF_RenderAnnotBitmap)(bitmapPtr, pagePtr, annotPtr, modeInt, matrixPtr, 16)
		});
	}
};
function resolveModes(requested) {
	if (!requested || requested.length === 0) return APPEARANCE_MODES.filter((m) => m.name === "normal");
	const wanted = new Set(requested);
	return APPEARANCE_MODES.filter((m) => wanted.has(m.name));
}
function normalizeScale(scale) {
	if (scale === void 0) return 1;
	if (!Number.isFinite(scale) || scale <= 0) return 1;
	return scale;
}
//#endregion
//#region src/features/annotations/RawAnnotationReader.ts
/**
* Whole-document and per-page raw read paths. Never acquires a pagePtr;
* uses `EPDFPage_GetAnnotCountRaw` / `EPDFPage_GetAnnotRaw` /
* `EPDFAnnot_GetObjectNumber` directly off the docPtr.
*
* Per-subtype dispatch is the same as the full reader (both share
* `collectPageAnnotations`), so the wire shape `AnnotationDTO[]` is
* identical between raw and full read paths for the subtypes that don't
* actually need a pagePtr to materialise their fields.
*/
var RawAnnotationReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	listAll(signal) {
		throwIfAborted(signal);
		this.session.ensureFullPageRegistry();
		const records = this.session.allRecords();
		const pages = [];
		for (const record of records) {
			throwIfAborted(signal);
			pages.push(this.listOne(record.pageObjectNumber, signal));
		}
		return { pages };
	}
	listOne(pageObjectNumber, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const record = this.session.recordByObjectNumber(pageObjectNumber);
		const count = fn.EPDFPage_GetAnnotCountRaw(docPtr, record.pageIndex);
		if (count < 0) throw new EngineError(EngineErrorCode.Unknown, `EPDFPage_GetAnnotCountRaw returned ${count} for page ${pageObjectNumber}`);
		return collectPageAnnotations({
			runtime: this.runtime,
			session: this.session,
			pageObjectNumber,
			count,
			getAnnotPtrAt: (i) => fn.EPDFPage_GetAnnotRaw(docPtr, record.pageIndex, i),
			signal
		});
	}
};
//#endregion
//#region src/features/annotations/internal/write/annotationWritePrimitives.ts
/**
* Write-side twin of `annotationReadPrimitives.ts`. Every annotation
* family (text-markup, shape, and upcoming polygon/polyline/line/free-text)
* composes these low-level setters so the PDFium FFI surface lives in one
* place. All colour/opacity/border writes go through the EmbedPDF
* `EPDFAnnot_*` extensions — the same path v2 used and the read primitives
* read back — so values survive native `EPDFAnnot_GenerateAppearance`.
*/
/**
* Set an annotation colour via the EmbedPDF `EPDFAnnot_SetColor`
* extension. The `type` arg (FPDFANNOT_COLORTYPE) selects which colour to
* write — `Color` is the stroke/fill `/C`, `InteriorColor` is the `/IC`
* of square/circle/polygon. RGB only; annotation opacity lives in `/CA`
* (see {@link setAnnotOpacity}), not a per-colour alpha. The stock
* `FPDFAnnot_SetColor` cannot author interior colour, which is why we use
* the extension uniformly.
*/
function setAnnotColor(fn, annotPtr, color, type = FPDFANNOT_COLORTYPE.Color) {
	if (!fn.EPDFAnnot_SetColor(annotPtr, type, color.r & 255, color.g & 255, color.b & 255)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetColor returned false");
}
/**
* Remove an annotation colour entry via `EPDFAnnot_ClearColor` (e.g. a
* transparent fill clears `/IC`). A false return is benign — there was no
* entry to clear — so we don't treat it as an error.
*/
function clearAnnotColor(fn, annotPtr, type) {
	fn.EPDFAnnot_ClearColor(annotPtr, type);
}
/**
* Set annotation opacity (`/CA`) via the dedicated `EPDFAnnot_SetOpacity`
* extension. Input is 0..1; the native alpha is 0..255. This is the path
* that stores `/CA` in the form native appearance generation expects, so
* opacity survives the bake.
*/
function setAnnotOpacity(fn, annotPtr, opacity) {
	const alpha = Math.max(0, Math.min(255, Math.round(opacity * 255)));
	if (!fn.EPDFAnnot_SetOpacity(annotPtr, alpha)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetOpacity returned false");
}
/**
* Set the `/F` (Annotation Flags) entry via `FPDFAnnot_SetFlags`. Only the
* keys present in `partial` are changed; the rest preserve their current
* value (read back via `FPDFAnnot_GetFlags` first). On create the dict
* starts at 0, so this is equivalently "set exactly these"; on update it
* merges onto the live flags.
*/
function setAnnotFlags(fn, annotPtr, partial) {
	const next = flagsToBits(fn.FPDFAnnot_GetFlags(annotPtr), partial);
	if (!fn.FPDFAnnot_SetFlags(annotPtr, next)) throw new EngineError(EngineErrorCode.Unknown, "FPDFAnnot_SetFlags returned false");
}
/**
* Write `/Rect` via `FPDFAnnot_SetRect`. FS_RECTF layout is
* `{ left, top, right, bottom }` (top > bottom in PDF coords).
*/
function setAnnotRect(fn, mem, annotPtr, rect) {
	const buf = mem.alloc(16);
	try {
		mem.poke(buf, "f32", rect.left, 0);
		mem.poke(buf, "f32", rect.top, 4);
		mem.poke(buf, "f32", rect.right, 8);
		mem.poke(buf, "f32", rect.bottom, 12);
		if (!fn.FPDFAnnot_SetRect(annotPtr, buf)) throw new EngineError(EngineErrorCode.Unknown, "FPDFAnnot_SetRect returned false");
	} finally {
		mem.free(buf);
	}
}
/**
* Set `/BS /S` (border style code) and `/BS /W` (width) in one call via
* `EPDFAnnot_SetBorderStyle`. `styleCode` is a raw
* `FPDF_ANNOT_BORDER_STYLE` enum value — the string<->code mapping lives
* in `shapeBorderStyle.ts` so engine-core stays PDFium-free.
*/
function setBorderStyle(fn, annotPtr, styleCode, width) {
	if (!fn.EPDFAnnot_SetBorderStyle(annotPtr, styleCode, width)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetBorderStyle returned false");
}
/**
* Write the dashed-border pattern (`/BS /D`) via
* `EPDFAnnot_SetBorderDashPattern`. Each entry is clamped to >= 0.
*/
function setBorderDashPattern(fn, mem, annotPtr, dashArray) {
	const buf = mem.alloc(dashArray.length * 4);
	try {
		for (let i = 0; i < dashArray.length; i++) mem.poke(buf, "f32", Math.max(0, dashArray[i]), i * 4);
		if (!fn.EPDFAnnot_SetBorderDashPattern(annotPtr, buf, dashArray.length)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetBorderDashPattern returned false");
	} finally {
		mem.free(buf);
	}
}
/** Set the `/BE` cloudy border effect intensity. */
function setBorderEffect(fn, annotPtr, intensity) {
	if (!fn.EPDFAnnot_SetBorderEffect(annotPtr, intensity)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetBorderEffect returned false");
}
/**
* Remove the `/BE` cloudy border effect. A false return is benign (there
* was no effect to clear), so we don't treat it as an error.
*/
function clearBorderEffect(fn, annotPtr) {
	fn.EPDFAnnot_ClearBorderEffect(annotPtr);
}
/**
* Write `/RD` rectangle differences via
* `EPDFAnnot_SetRectangleDifferences`. PDFium core stores `/RD` as
* `[left, bottom, right, top]`; we accept the wire-stable
* `{ left, top, right, bottom }` shape and reorder at the boundary.
*/
function setRectangleDifferences(fn, annotPtr, rd) {
	if (!fn.EPDFAnnot_SetRectangleDifferences(annotPtr, rd.left, rd.bottom, rd.right, rd.top)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetRectangleDifferences returned false");
}
/**
* Remove the `/RD` rectangle-differences entry. A false return is benign
* (there was no entry to clear), so we don't treat it as an error.
*/
function clearRectangleDifferences(fn, annotPtr) {
	fn.EPDFAnnot_ClearRectangleDifferences(annotPtr);
}
/**
* Replace the `/Vertices` point list of a polygon/polyline annotation via
* the EmbedPDF `EPDFAnnot_SetVertices` extension. Writes the points into a
* contiguous `count * FS_POINTF` buffer.
*/
function setVertices(fn, mem, annotPtr, vertices) {
	const buf = mem.alloc(vertices.length * 8);
	try {
		for (let i = 0; i < vertices.length; i++) {
			const off = i * 8;
			mem.poke(buf, "f32", vertices[i].x, off);
			mem.poke(buf, "f32", vertices[i].y, off + 4);
		}
		if (!fn.EPDFAnnot_SetVertices(annotPtr, buf, vertices.length)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetVertices returned false");
	} finally {
		mem.free(buf);
	}
}
/**
* Write the `/L` endpoints of a line annotation via the EmbedPDF
* `EPDFAnnot_SetLine` extension. Uses one 16-byte buffer holding two
* `FS_POINTF` structs (start at offset 0, end at offset 8).
*/
function setLine(fn, mem, annotPtr, line) {
	const start = mem.alloc(8);
	const end = mem.alloc(8);
	try {
		mem.poke(start, "f32", line.start.x, 0);
		mem.poke(start, "f32", line.start.y, 4);
		mem.poke(end, "f32", line.end.x, 0);
		mem.poke(end, "f32", line.end.y, 4);
		if (!fn.EPDFAnnot_SetLine(annotPtr, start, end)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetLine returned false");
	} finally {
		mem.free(start);
		mem.free(end);
	}
}
/**
* Replace the `/InkList` of an ink annotation. PDFium has no single
* "set ink list" call, so we clear any existing list with
* `FPDFAnnot_RemoveInkList` (a false return is benign on a freshly-created
* annotation that has none) and re-add each non-empty stroke via
* `FPDFAnnot_AddInkStroke`, which takes a contiguous `count * FS_POINTF`
* buffer and returns the new stroke index (`< 0` on failure).
*/
function setInkList(fn, mem, annotPtr, inkList) {
	fn.FPDFAnnot_RemoveInkList(annotPtr);
	for (const stroke of inkList) {
		if (stroke.length === 0) continue;
		const buf = mem.alloc(stroke.length * 8);
		try {
			for (let i = 0; i < stroke.length; i++) {
				const off = i * 8;
				mem.poke(buf, "f32", stroke[i].x, off);
				mem.poke(buf, "f32", stroke[i].y, off + 4);
			}
			if (fn.FPDFAnnot_AddInkStroke(annotPtr, buf, stroke.length) < 0) throw new EngineError(EngineErrorCode.Unknown, "FPDFAnnot_AddInkStroke returned < 0");
		} finally {
			mem.free(buf);
		}
	}
}
/**
* Write the `/LE` line endings of a line/polyline annotation via the
* EmbedPDF `EPDFAnnot_SetLineEndings` extension. The string<->code mapping
* lives in `lineEnding.ts` so engine-core stays PDFium-free.
*/
function setLineEndings(fn, annotPtr, endings) {
	if (!fn.EPDFAnnot_SetLineEndings(annotPtr, lineEndingToCode(endings.start), lineEndingToCode(endings.end))) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetLineEndings returned false");
}
/**
* Write the `/DA` default appearance (font + size + colour) of a free-text
* annotation via `EPDFAnnot_SetDefaultAppearance`. The `/DA` colour paints
* the border and the default text colour; a separate `TextColor` entry (see
* {@link setAnnotColor}) overrides the text only. `fontCode` is the raw
* `FPDF_STANDARD_FONT` enum value — the string<->code mapping lives in
* `standardFont.ts` so engine-core stays PDFium-free.
*/
function setDefaultAppearance(fn, annotPtr, fontCode, fontSize, color) {
	if (!fn.EPDFAnnot_SetDefaultAppearance(annotPtr, fontCode, fontSize, color.r & 255, color.g & 255, color.b & 255)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetDefaultAppearance returned false");
}
/**
* Write the `/DA` default appearance using a runtime font registered through
* `engine.fonts` (via `EPDFAnnot_SetDefaultAppearanceRegisteredFont`). `fontId`
* is the thread-local `CFX_FontRegistry::FontId` resolved from the stable
* `registeredFontKey` at authoring time. The font's glyph subset is embedded
* into the annotation's appearance stream when the document/layer is saved.
*/
function setDefaultAppearanceRegisteredFont(fn, annotPtr, fontId, fontSize, color) {
	if (!fn.EPDFAnnot_SetDefaultAppearanceRegisteredFont(annotPtr, fontId, fontSize, color.r & 255, color.g & 255, color.b & 255)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetDefaultAppearanceRegisteredFont returned false");
}
/**
* Write the `/Q` text alignment of a free-text annotation via
* `EPDFAnnot_SetTextAlignment`. `code` is the raw quadding value — the
* string<->code mapping lives in `textAlignment.ts`.
*/
function setTextAlignment(fn, annotPtr, code) {
	if (!fn.EPDFAnnot_SetTextAlignment(annotPtr, code)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetTextAlignment returned false");
}
/**
* Write the `/IT` intent name via `EPDFAnnot_SetIntent` (expects a UTF-8
* bytestring without the leading slash, e.g. `'FreeTextCallout'`). The
* string<->name mapping lives in `freeTextIntent.ts`.
*/
function setIntent(fn, annotPtr, name) {
	if (!fn.EPDFAnnot_SetIntent(annotPtr, name)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetIntent returned false");
}
/**
* Write the `/CL` callout leader line of a free-text callout via
* `EPDFAnnot_SetCalloutLine`. Accepts 2 points (straight leader) or 3
* (knee-jointed); writes them into a contiguous `count * FS_POINTF` buffer
* (like {@link setVertices}).
*/
/**
* Write `/OverlayText` of a redact annotation via `EPDFAnnot_SetOverlayText`.
* The native setter removes the key for an empty string, so passing `''`
* clears the label.
*/
function setOverlayText(fn, mem, annotPtr, text) {
	const ptr = mem.writeU16String(text);
	try {
		if (!fn.EPDFAnnot_SetOverlayText(annotPtr, ptr)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetOverlayText returned false");
	} finally {
		mem.free(ptr);
	}
}
/**
* Write `/Repeat` of a redact annotation via `EPDFAnnot_SetOverlayTextRepeat`
* (`false` removes the key — absent is the PDF default).
*/
function setOverlayTextRepeat(fn, annotPtr, repeat) {
	if (!fn.EPDFAnnot_SetOverlayTextRepeat(annotPtr, repeat)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetOverlayTextRepeat returned false");
}
function setCalloutLine(fn, mem, annotPtr, points) {
	const buf = mem.alloc(points.length * 8);
	try {
		for (let i = 0; i < points.length; i++) {
			const off = i * 8;
			mem.poke(buf, "f32", points[i].x, off);
			mem.poke(buf, "f32", points[i].y, off + 4);
		}
		if (!fn.EPDFAnnot_SetCalloutLine(annotPtr, buf, points.length)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetCalloutLine returned false");
	} finally {
		mem.free(buf);
	}
}
//#endregion
//#region src/features/annotations/internal/write/writeAnnotationBase.ts
/**
* Write the annotation-wide base fields shared by every Draft
* (contents/nm). The kind-specific writer calls this BEFORE its own
* field writes; order doesn't actually matter at the PDF level, but
* keeping the base first makes the writers symmetric with the readers.
*
* `/T` (author display) is stamped by the mutator from
* `AnnotationActor.displayName` via {@link writeAnnotationAuthor}.
*
* `nm` is written verbatim if the caller supplied one. The mutator
* decides whether to opportunistically stamp a UUID v4 on a weak
* annotation; the writer does not.
*/
function applyAnnotationBaseDraft(fn, mem, annotPtr, draft) {
	if (draft.contents !== void 0) writeStringOrClear(fn, mem, annotPtr, "Contents", draft.contents);
	if (draft.nm !== void 0 && draft.nm.length > 0) writeString(fn, mem, annotPtr, "NM", draft.nm);
	if (draft.flags !== void 0) setAnnotFlags(fn, annotPtr, draft.flags);
}
/**
* Write the annotation-wide base fields shared by every Patch
* (contents). /T is bound at creation and not patchable — see
* `AnnotationPatchBase`. /NM is monotonic per annotation; the mutator
* may stamp /NM opportunistically on a weak annotation via
* `writeAnnotationNm`.
*
* Three-state semantics on string|null fields:
*   undefined -> don't touch the dict
*   null      -> remove the entry
*   "..."     -> set the entry to that value
*/
function applyAnnotationBasePatch(fn, mem, annotPtr, patch) {
	if (patch.contents !== void 0) writeStringOrClear(fn, mem, annotPtr, "Contents", patch.contents);
	if (patch.flags !== void 0) setAnnotFlags(fn, annotPtr, patch.flags);
}
/**
* Stamp /T (the standard PDF "author" display field) on an annotation.
* Called by the mutator on CREATE — /T is bound to the caller's
* `display_name` at creation. No-op when `displayName` is empty so
* callers don't need to gate it.
*/
function writeAnnotationAuthor(fn, mem, annotPtr, displayName) {
	if (displayName.length === 0) return;
	writeString(fn, mem, annotPtr, "T", displayName);
}
/**
* Stamp /NM on an annotation that didn't have one. Used by the mutator
* (not by the writers proper) to upgrade a weak annotation to durable
* identity during update. Idempotent and silent if the value is empty.
*/
function writeAnnotationNm(fn, mem, annotPtr, nm) {
	if (nm.length === 0) return;
	writeString(fn, mem, annotPtr, "NM", nm);
}
/**
* Stamp /M (modification date) on an annotation. /M is a standard
* ISO 32000 base annotation field — the moment of the last edit — so
* it lives here alongside /T / /NM / /Contents rather than in any
* vendor-namespaced extension.
*
* Called by the mutator on every annotation create AND every update;
* the existing base draft/patch writers leave /M alone because the
* value is derived from the moment of the write, not from the
* draft/patch payload. The base reader already extracts /M and
* surfaces it on `AnnotationBase.modified`.
*
* Format: `D:YYYYMMDDHHmmSSOHH'mm'` (see formatPdfDate).
*/
function writeAnnotationModified(fn, mem, annotPtr, now = /* @__PURE__ */ new Date()) {
	writeString(fn, mem, annotPtr, "M", formatPdfDate(now));
}
function writeString(fn, mem, annotPtr, key, value) {
	const ptr = mem.writeU16String(value);
	try {
		fn.FPDFAnnot_SetStringValue(annotPtr, key, ptr);
	} finally {
		mem.free(ptr);
	}
}
function writeStringOrClear(fn, mem, annotPtr, key, value) {
	if (value === null) {
		fn.FPDFAnnot_SetStringValue(annotPtr, key, NULL_PTR);
		return;
	}
	writeString(fn, mem, annotPtr, key, value);
}
//#endregion
//#region src/features/annotations/internal/identity/captureOrStampStableId.ts
/**
* Read an annotation's stable id, opportunistically stamping a fresh
* engine-generated UUID v4 as `/NM` if it is currently weak (no object
* number, no `/NM`). Monotonic `/NM` rule: an already-durable annotation
* is NEVER touched. Caller owns the lifecycle of `annotPtr`.
*
* Shared by the create/update/move mutator paths and the `/IRT`
* relationship writer (which strengthens a weak parent before linking to
* it, so the strengthened id can be reported in `meta.changed`).
*/
function captureOrStampStableId(runtime, annotPtr) {
	const { fn, mem } = runtime;
	const objNum = fn.EPDFAnnot_GetObjectNumber(annotPtr);
	if (objNum > 0) return {
		kind: "objectNumber",
		value: objNum
	};
	const nm = readAnnotString(fn, mem, annotPtr, "NM");
	if (nm !== null && nm.length > 0) return {
		kind: "nm",
		value: nm
	};
	const minted = generateUuid();
	writeAnnotationNm(fn, mem, annotPtr, minted);
	return {
		kind: "nm",
		value: minted
	};
}
//#endregion
//#region src/features/annotations/internal/identity/resolveAnnotationPointer.ts
/**
* Resolve an `AnnotationRef` to a live `annotPtr` on an ALREADY-acquired
* `pagePtr`. This does NOT acquire/release the page and does NOT close the
* returned annot — the caller owns both lifetimes.
*
* Resolution order matches the wire spec:
*   1. `objectNumber` -> `EPDFPage_GetAnnotByObjectNumber`
*   2. `nm`           -> `EPDFPage_GetAnnotByName`
*   3. `index`        -> revision validation, then `FPDFPage_GetAnnot`
*
* Surfaces `InvalidReference` deterministically when the ref doesn't
* resolve, so mutation paths can fail fast before doing any work.
*/
function resolveAnnotPtr(runtime, session, pagePtr, ref) {
	const { fn, mem } = runtime;
	switch (ref.kind) {
		case "objectNumber": {
			const annotPtr = fn.EPDFPage_GetAnnotByObjectNumber(pagePtr, ref.annotObjectNumber);
			if (!annotPtr) throw new EngineError(EngineErrorCode.InvalidReference, `no annotation with object number ${ref.annotObjectNumber} on page ${ref.pageObjectNumber}`);
			return annotPtr;
		}
		case "nm": {
			const namePtr = mem.writeU16String(ref.nm);
			try {
				const annotPtr = fn.EPDFPage_GetAnnotByName(pagePtr, namePtr);
				if (!annotPtr) throw new EngineError(EngineErrorCode.InvalidReference, `no annotation with /NM '${ref.nm}' on page ${ref.pageObjectNumber}`);
				return annotPtr;
			} finally {
				mem.free(namePtr);
			}
		}
		case "index": {
			session.validateRevision(ref.revision);
			const annotPtr = fn.FPDFPage_GetAnnot(pagePtr, ref.index);
			if (!annotPtr) throw new EngineError(EngineErrorCode.InvalidReference, `index ${ref.index} out of range on page ${ref.pageObjectNumber}`);
			return annotPtr;
		}
	}
	throw new EngineError(EngineErrorCode.InvalidArg, `unsupported annotation ref kind`);
}
//#endregion
//#region src/features/annotations/internal/mutations/computeMutationImpact.ts
/**
* Pure helper: turn a mutation outcome into the side-effect envelope
* every result type carries on its `meta` field.
*
* The rules — locked with the user, do not change without re-reading
* the doc comment on `AnnotationListMutationMeta` and the
* `MutationKind` definition above:
*
*   1. `update` and `create` are non-invalidating.
*
*      - `update` doesn't touch the /Annots array; indices on the page
*        do not move, so weak refs the client may be holding stay
*        valid. Opportunistic /NM stamping during an update is also
*        non-structural — it changes the annotation's identity quality
*        but not its position.
*
*      - `create` is append-only. The new annotation goes at
*        `index = previousCount`, so no existing index shifts and no
*        pre-existing weak ref ever becomes stale. We treat `create`
*        the same as `update` for impact purposes: no revision bump,
*        `weakRefsInvalidated = false`, `shouldRefetch = null`.
*
*        The "revisions exist solely for weak-ref authentication"
*        doctrine (see `pages.move()`'s identical reasoning) means we
*        deliberately do NOT bump on `create` — bumping a revision
*        that nobody's weak ref depends on would erode the invariant
*        and turn revisions into a generic "something changed" signal.
*
*   2. `delete` and `move` ARE index-shifting. They genuinely move
*      pre-existing indices:
*        - `delete` removes an annotation, shifting every later index
*          down by one.
*        - `move` detaches a contiguous block and re-inserts it
*          elsewhere, rewriting indices on both ends of the gap.
*      They invalidate weak refs iff the page actually had any weak
*      refs *before* the mutation — if every annotation already had a
*      durable identity, no client could possibly be holding a stale
*      index, and we keep `shouldRefetch = null`.
*
*      (`move` opportunistically stamps /NM on weak refs in the batch
*      BEFORE the move, so the annotations actually being moved end up
*      durable on the way out; but other weak annotations on the page
*      still need a refetch.)
*
*   3. When rule (2) fires, the reason is always `'weakRefsInvalidated'`.
*      `'pageRebuilt'` and `'externalChange'` are reserved for higher-
*      level signals the engine doesn't emit yet (insert/delete-page
*      that rebuilds /Annots, watch-based refresh). Page **reorder**
*      explicitly does NOT bump per-page revisions and does NOT emit
*      `pageRebuilt` — see `PagesMutator`.
*
*   Future note: when an explicit-position `createAt(index)` API
*   ships, add `'createAt'` to `MutationKind` and slot it into rule
*   (2) — it shifts every index >= toIndex and is structural in the
*   same way `delete` and `move` are.
*/
function computeMutationImpact(inputs) {
	const { mutation, pageStateBefore, pageStateAfter, changed } = inputs;
	if (!shiftsExistingAnnotationIndices(mutation)) return {
		affectedPages: [pageStateAfter],
		cacheDelta: null,
		changed,
		weakRefsInvalidated: false,
		shouldRefetch: null
	};
	const weakRefsInvalidated = invalidatesWeakIndexRefs(mutation, pageStateBefore.weakAnnotationState);
	return {
		affectedPages: [pageStateAfter],
		cacheDelta: null,
		changed,
		weakRefsInvalidated,
		shouldRefetch: weakRefsInvalidated ? { reason: "weakRefsInvalidated" } : null
	};
}
//#endregion
//#region src/features/annotations/internal/read/readAnnotationFromPtr.ts
/**
* Materialise a single annotation DTO from a live `annotPtr`, reusing the
* exact same code paths the list readers use. Caller owns the lifecycle of
* `annotPtr` (this function does NOT close it) so the mutator can keep it
* for follow-up writes.
*
* Used by:
*   - `AnnotationMutator.create()` after `EPDFPage_CreateAnnot` to
*     produce `AnnotationCreateResult.created`.
*   - `AnnotationMutator.update()` after writes complete to produce
*     `AnnotationUpdateResult.updated`.
*/
function readAnnotationFromPtr(fn, mem, annotPtr, pageObjectNumber, index, revision, ctx) {
	const base = readAnnotationBase(fn, mem, annotPtr, pageObjectNumber, index, revision);
	const subtypeCode = fn.FPDFAnnot_GetSubtype(annotPtr);
	const { reader } = pickReader(subtypeCode);
	return reader(fn, mem, annotPtr, base, subtypeCode, ctx);
}
//#endregion
//#region src/features/annotations/internal/write/writeCaretAnnotation.ts
/** Default `/C` colour when a caret draft omits it (engine-wide default mark). */
const DEFAULT_CARET_COLOR = {
	r: 255,
	g: 0,
	b: 0
};
/** Default opacity, set explicitly so reads always round-trip the same value. */
const DEFAULT_OPACITY$4 = 1;
/**
* Apply a caret draft to a freshly-created annotation. Caret carries no
* geometry of its own beyond `/Rect`. Order:
*   1. base author-metadata (contents/nm/flags)
*   2. `/Rect` (required — supplied by the caller; never derived)
*   3. `/C` color + `/CA` opacity
*   4. `/RD` rectangle differences (optional)
*/
function applyCaretDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	setAnnotColor(fn, annotPtr, draft.color ?? DEFAULT_CARET_COLOR);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? DEFAULT_OPACITY$4);
	if (draft.intent !== void 0) setIntent(fn, annotPtr, caretIntentToName(draft.intent));
	if (draft.rectDifferences != null) setRectangleDifferences(fn, annotPtr, draft.rectDifferences);
}
/**
* Apply a caret patch to an existing annotation. Only present fields are
* touched.
*/
function applyCaretPatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	if (patch.color !== void 0) setAnnotColor(fn, annotPtr, patch.color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	if (patch.intent !== void 0) setIntent(fn, annotPtr, caretIntentToName(patch.intent));
	if (patch.rectDifferences === null) clearRectangleDifferences(fn, annotPtr);
	else if (patch.rectDifferences !== void 0) setRectangleDifferences(fn, annotPtr, patch.rectDifferences);
}
/**
* Type-narrowing predicate used by the writer registry to pick the caret
* writer for a draft/patch's `subtype`.
*/
function isCaretSubtype(subtype) {
	return subtype === "caret";
}
//#endregion
//#region src/features/annotations/internal/write/writeFileAttachmentAnnotation.ts
/** Default `/C` — the generator's default icon fill, set explicitly so reads round-trip. */
const DEFAULT_FILE_ATTACHMENT_COLOR = {
	r: 255,
	g: 255,
	b: 0
};
const DEFAULT_OPACITY$3 = 1;
/**
* Validate a file-attachment draft before any native write: the wire
* `file.resource` ref must have arrived with the mutation. No format
* sniffing — unlike stamps, ANY bytes are a valid attachment.
*/
function preflightFileAttachmentDraft(draft, ctx) {
	requireFileResource(draft.file, ctx);
	if (ctx?.docPtr === void 0) throw new EngineError(EngineErrorCode.Unknown, "file-attachment writer requires docPtr on the write context");
}
/**
* Apply a file-attachment draft. Order:
*   1. base author-metadata (contents/nm/flags)
*   2. `/Rect` + `/C` + `/CA` + `/Name` icon
*   3. the embedded file: `/FS` filespec via `FPDFAnnot_AddFileAttachment`,
*      bytes via `FPDFAttachment_SetFile` (which also writes `/Params`
*      Size/CheckSum/CreationDate), declared mime via
*      `EPDFAttachment_SetSubtype`, optional `/Desc`.
*
* The icon appearance itself is generator-owned: the mutator's closing
* `regenerateAppearance` bakes it from `/C` + `/Name`
* (GenerateFileAttachmentAP), exactly like the text note.
*/
function applyFileAttachmentDraft(fn, mem, annotPtr, draft, ctx) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	setAnnotColor(fn, annotPtr, draft.color ?? DEFAULT_FILE_ATTACHMENT_COLOR);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? DEFAULT_OPACITY$3);
	setFileAttachmentIcon(fn, annotPtr, draft.icon ?? "paperclip");
	attachFile(fn, mem, annotPtr, draft.file, requireFileResource(draft.file, ctx), ctx.docPtr);
}
/** Patch: presentation only — the attached file is create-only by design. */
function applyFileAttachmentPatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	if (patch.color !== void 0) setAnnotColor(fn, annotPtr, patch.color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	if (patch.icon !== void 0) setFileAttachmentIcon(fn, annotPtr, patch.icon);
}
function isFileAttachmentSubtype(subtype) {
	return subtype === "file-attachment";
}
function setFileAttachmentIcon(fn, annotPtr, icon) {
	if (!fn.EPDFAnnot_SetName(annotPtr, FILE_ICON_TO_CODE[icon])) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetName returned false");
}
function requireFileResource(file, ctx) {
	const resource = ctx?.resources?.[file.resource];
	if (!resource) throw new EngineError(EngineErrorCode.InvalidArg, `attachment file references resource '${file.resource}' but no such binary payload accompanied the mutation`);
	return resource;
}
function attachFile(fn, mem, annotPtr, file, resource, docPtr) {
	const namePtr = mem.writeU16String(file.name);
	let attachmentPtr;
	try {
		attachmentPtr = fn.FPDFAnnot_AddFileAttachment(annotPtr, namePtr);
	} finally {
		mem.free(namePtr);
	}
	if (!attachmentPtr) throw new EngineError(EngineErrorCode.Unknown, "FPDFAnnot_AddFileAttachment returned NULL");
	writeAttachmentFilePayload(fn, mem, attachmentPtr, docPtr, file, resource);
}
//#endregion
//#region src/features/annotations/internal/write/writeDefaultAppearance.ts
/**
* Write `/DA`. A standard font name goes through the native standard-font
* path; anything else is treated as a registered-font `key` and resolved to
* this thread's FontId. A key with no resolver wired (e.g. a host without a
* font registry) is a programming error — fail loud rather than silently
* downgrade to Helvetica and embed the wrong glyphs.
*
* Shared by the free-text and redact writers: `/DA` styles a free-text body
* and a redaction `/OverlayText` label through the exact same triple.
*/
function applyDefaultAppearance(fn, annotPtr, fontFamily, fontSize, color, ctx) {
	if (isStandardFont(fontFamily)) {
		setDefaultAppearance(fn, annotPtr, standardFontToCode(fontFamily), fontSize, color);
		return;
	}
	if (!ctx?.resolveRegisteredFontId) throw new EngineError(EngineErrorCode.InvalidArg, `fontFamily '${fontFamily}' is not a standard font and no font registry is available on this host`);
	setDefaultAppearanceRegisteredFont(fn, annotPtr, ctx.resolveRegisteredFontId(fontFamily), fontSize, color);
}
const DEFAULT_COLOR = {
	r: 255,
	g: 0,
	b: 0
};
/**
* Apply the geometry styling (`/C`, `/CA`, `/BS`, dash) from a draft to a
* freshly-created annotation. This is the no-fill layer shared by ink and
* the filled families; `interiorColor` (`/IC`) is layered on by
* {@link applyFilledStyleDraft}. Geometry (`/Rect`, `/Vertices`, `/L`,
* `/InkList`), cloudy borders, rect-differences, and line endings are
* layered on by the family-specific writer around this call.
*/
function applyGeometryStyleDraft(fn, mem, annotPtr, draft) {
	setAnnotColor(fn, annotPtr, draft.color ?? DEFAULT_COLOR);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? 1);
	applyBorderDraft(fn, mem, annotPtr, draft);
}
/**
* Write the `/BS` border (style + width, then dash) from a draft. This is
* the border-only slice of {@link applyGeometryStyleDraft}, shared with the
* free-text box border (which manages `/C` and `/DA` itself). A missing
* `borderStyle`/`strokeWidth` falls back to a 1pt solid border.
*/
function applyBorderDraft(fn, mem, annotPtr, draft) {
	setBorderStyle(fn, annotPtr, borderStyleToCode(draft.borderStyle ?? "solid"), draft.strokeWidth ?? 1);
	if (draft.dashArray !== void 0 && draft.dashArray.length > 0) setBorderDashPattern(fn, mem, annotPtr, draft.dashArray);
}
/**
* Apply the geometry styling from a patch to an existing annotation. Only
* present fields are touched. Border style + width share the single
* `EPDFAnnot_SetBorderStyle` call, so when only one is patched we read the
* current pair first and preserve the other.
*/
function applyGeometryStylePatch(fn, mem, annotPtr, patch) {
	if (patch.color !== void 0) setAnnotColor(fn, annotPtr, patch.color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	applyBorderPatch(fn, mem, annotPtr, patch);
}
/**
* Write the `/BS` border from a patch, touching only present fields. Border
* style + width share the single `EPDFAnnot_SetBorderStyle` call, so when
* only one is patched we read the current pair first and preserve the other.
* The border-only slice of {@link applyGeometryStylePatch}, shared with the
* free-text box border.
*/
function applyBorderPatch(fn, mem, annotPtr, patch) {
	if (patch.borderStyle !== void 0 || patch.strokeWidth !== void 0) {
		const current = readBorderStyle(fn, mem, annotPtr);
		const style = patch.borderStyle ?? borderStyleFromCode(current.styleCode);
		const width = patch.strokeWidth ?? current.width;
		setBorderStyle(fn, annotPtr, borderStyleToCode(style), width);
	}
	if (patch.dashArray !== void 0 && patch.dashArray.length > 0) setBorderDashPattern(fn, mem, annotPtr, patch.dashArray);
}
/**
* Apply the full stroke/fill styling (geometry styling + `/IC`) from a
* draft. A `null`/omitted fill clears `/IC` so the shape renders unfilled.
* Used by the filled families (circle/square/polygon/polyline/line).
*/
function applyFilledStyleDraft(fn, mem, annotPtr, draft) {
	const interior = draft.interiorColor ?? null;
	if (interior === null) clearAnnotColor(fn, annotPtr, FPDFANNOT_COLORTYPE.InteriorColor);
	else setAnnotColor(fn, annotPtr, interior, FPDFANNOT_COLORTYPE.InteriorColor);
	applyGeometryStyleDraft(fn, mem, annotPtr, draft);
}
/**
* Apply the full stroke/fill styling from a patch. Only present fields are
* touched; `interiorColor: null` clears `/IC`.
*/
function applyFilledStylePatch(fn, mem, annotPtr, patch) {
	if (patch.interiorColor !== void 0) if (patch.interiorColor === null) clearAnnotColor(fn, annotPtr, FPDFANNOT_COLORTYPE.InteriorColor);
	else setAnnotColor(fn, annotPtr, patch.interiorColor, FPDFANNOT_COLORTYPE.InteriorColor);
	applyGeometryStylePatch(fn, mem, annotPtr, patch);
}
//#endregion
//#region src/features/annotations/internal/write/writeEmbedMetadata.ts
/**
* Write the EmbedPDF-namespaced /EMBD_Metadata annotation dictionary.
*
* This module owns ONLY the vendor-extension dictionary. Standard PDF
* base fields (/T, /M, /NM, /Contents) are handled by
* `writers/annotations/base.ts` — /EMBD_Metadata is a separate
* dictionary nested under the annotation that carries cloud-side
* identity bookkeeping which has no PDF-spec equivalent.
*
* Schema (v1):
*   /EMBD_Metadata <<
*     /SchemaVersion 1                       % EmbedPDF metadata schema version
*     /UserID    (acme/alice)                % identity that created the annotation
*     /GroupID   (engineering)               % group the annotation belongs to
*     /CreatedBy (acme/alice)                % same as UserID on create
*     /UpdatedBy (acme/alice)                % last editor; refreshed on every update
*   >>
*
* Read side: see `readers/annotations/embed-metadata.ts` (commit 12).
*
* PDFium bindings used: the `EPDFAnnot_*EmbedMetadata*` extension API
* shipped in the EmbedPDF PDFium fork. Values are written as UTF-16LE
* strings via the same `mem.writeU16String` pattern the base writer
* uses for /T.
*/
const KEY_SCHEMA_VERSION = "SchemaVersion";
const KEY_USER_ID = "UserID";
const KEY_GROUP_ID = "GroupID";
const KEY_CREATED_BY = "CreatedBy";
const KEY_UPDATED_BY = "UpdatedBy";
/**
* Stamp /EMBD_Metadata on a freshly created annotation. No-op if the
* actor has no identity fields — the worker still emits the standard
* /M timestamp via the base writer; only the vendor dictionary is
* skipped.
*
*   actor.userId    → /UserID, /CreatedBy, /UpdatedBy
*   actor.groupId   → /GroupID
*
* /SchemaVersion is written whenever any field is written.
*/
function applyEmbedMetadataOnCreate(fn, mem, annotPtr, actor) {
	if (!actor || !actor.userId && !actor.groupId) return;
	fn.EPDFAnnot_SetEmbedMetadataNumber(annotPtr, KEY_SCHEMA_VERSION, 1);
	if (actor.userId) {
		setMetadataString(fn, mem, annotPtr, KEY_USER_ID, actor.userId);
		setMetadataString(fn, mem, annotPtr, KEY_CREATED_BY, actor.userId);
		setMetadataString(fn, mem, annotPtr, KEY_UPDATED_BY, actor.userId);
	}
	if (actor.groupId) setMetadataString(fn, mem, annotPtr, KEY_GROUP_ID, actor.groupId);
}
/**
* Apply /EMBD_Metadata refreshes on update. Two fields can change on
* update; everything else (UserID, CreatedBy) is immutable.
*
*   actor.userId  → /UpdatedBy (refreshed every update where the
*                   caller has an identity)
*   actor.groupId → /GroupID   (reassigned only when the patch
*                   explicitly contains a new groupId; route runs
*                   `checkSetGroup` first)
*
* If the target annotation has no EMBD_Metadata yet (created before
* the cloud started stamping, or by a non-cloud writer), we seed
* /SchemaVersion so subsequent readers can find the marker.
*
* No-op when the actor has neither userId nor groupId.
*/
function applyEmbedMetadataOnUpdate(fn, mem, annotPtr, actor) {
	if (!actor || !actor.userId && !actor.groupId) return;
	if (!fn.EPDFAnnot_HasEmbedMetadata(annotPtr)) fn.EPDFAnnot_SetEmbedMetadataNumber(annotPtr, KEY_SCHEMA_VERSION, 1);
	if (actor.userId) setMetadataString(fn, mem, annotPtr, KEY_UPDATED_BY, actor.userId);
	if (actor.groupId) setMetadataString(fn, mem, annotPtr, KEY_GROUP_ID, actor.groupId);
}
function setMetadataString(fn, mem, annotPtr, key, value) {
	const ptr = mem.writeU16String(value);
	try {
		fn.EPDFAnnot_SetEmbedMetadataString(annotPtr, key, ptr);
	} finally {
		mem.free(ptr);
	}
}
//#endregion
//#region src/features/annotations/internal/write/writeAnnotationTransformMetadata.ts
/**
* Write the EmbedPDF transform keys under /EMBD_Metadata. This is the seam
* PDFium's native AP generator reads to bake a ROTATED appearance:
*
*   /EMBD_Metadata <<
*     /Rotation      45                    % degrees, PDF convention (CCW)
*     /UnrotatedRect [x0 y0 x1 y1]         % BOX kinds only — the logical box
*   >>
*
* The family split (see the v3 plan):
*   - BOX kinds (square/circle/free-text): `/Rotation` + `/UnrotatedRect`.
*     With BOTH present the AP generator emits an `/AP /Matrix` that rotates a
*     box-sized appearance about the box centre, and `/Rect` is the enclosing
*     AABB. This is the portable, externally-correct rotation.
*   - VERTEX kinds (line/polyline/polygon/ink): `/Rotation` ONLY (advisory).
*     The points are already rotated (they are the visual), so a lone
*     `/Rotation` is INERT for the AP generator (it ignores `/Rotation` with no
*     `/UnrotatedRect`) — it just records the applied angle so EmbedPDF can
*     reconstruct an oriented selection box + offer reset.
*
* Both fields follow the engine-wide tri-state law ("a patch touches what it
* states, preserves what it omits"):
*   - `undefined` → the key is UNTOUCHED (a rect-only move keeps its rotation).
*   - `null` or `0` → CLEAR just that key via `EPDFAnnot_ClearEmbedMetadataKey`
*     (0 ≡ no rotation is the canonical identity, not a sentinel; never clear
*     the whole dict — identity fields UserID/GroupID/CreatedBy/UpdatedBy must
*     survive).
*   - a value → SET the key. Setting a nonzero rotation with no
*     `/UnrotatedRect` (neither in the patch nor already on the annotation)
*     is an unsatisfiable state for the AP generator and throws `InvalidArg`.
*
* MUST run BEFORE `EPDFAnnot_GenerateAppearance` so the bake sees the rotation.
* `/SchemaVersion` is seeded (stays 1) if this is the first key in the dict.
*/
const KEY_ROTATION = "Rotation";
const KEY_UNROTATED_RECT = "UnrotatedRect";
/** Seed `/SchemaVersion` when we are about to create the dict by writing the
*  first transform key, so the marker readers look for is always present. */
function ensureSchemaVersion(fn, annotPtr) {
	if (!fn.EPDFAnnot_HasEmbedMetadata(annotPtr)) fn.EPDFAnnot_SetEmbedMetadataNumber(annotPtr, "SchemaVersion", 1);
}
function setRotation(fn, annotPtr, rotation) {
	ensureSchemaVersion(fn, annotPtr);
	fn.EPDFAnnot_SetEmbedMetadataNumber(annotPtr, KEY_ROTATION, rotation);
}
function setUnrotatedRect(fn, mem, annotPtr, rect) {
	ensureSchemaVersion(fn, annotPtr);
	const buf = mem.alloc(16);
	try {
		mem.poke(buf, "f32", rect.left, 0);
		mem.poke(buf, "f32", rect.top, 4);
		mem.poke(buf, "f32", rect.right, 8);
		mem.poke(buf, "f32", rect.bottom, 12);
		fn.EPDFAnnot_SetEmbedMetadataRect(annotPtr, KEY_UNROTATED_RECT, buf);
	} finally {
		mem.free(buf);
	}
}
/**
* Write transform metadata for a BOX kind (square/circle/free-text/stamp),
* tri-state per field: `undefined` never touches the document (safe to call on
* every patch), `null`/`0` clears, a value sets. A nonzero rotation with no
* unrotated box anywhere (patch or annotation) is unsatisfiable — the AP
* generator needs the pair — and throws instead of writing a broken state.
*/
function writeBoxTransformMetadata(fn, mem, annotPtr, t) {
	if (t.rotation !== void 0) if (t.rotation !== null && t.rotation !== 0) {
		if (!(t.unrotatedRect != null || t.unrotatedRect === void 0 && readAnnotationUnrotatedRect(fn, mem, annotPtr) != null)) throw new EngineError(EngineErrorCode.InvalidArg, "rotation requires unrotatedRect: supply the pair together (or rely on an existing /EMBD_Metadata/UnrotatedRect)");
		setRotation(fn, annotPtr, t.rotation);
	} else fn.EPDFAnnot_ClearEmbedMetadataKey(annotPtr, KEY_ROTATION);
	if (t.unrotatedRect !== void 0) if (t.unrotatedRect !== null) setUnrotatedRect(fn, mem, annotPtr, t.unrotatedRect);
	else fn.EPDFAnnot_ClearEmbedMetadataKey(annotPtr, KEY_UNROTATED_RECT);
}
/**
* Write the advisory `/Rotation` scalar for a VERTEX kind
* (line/polyline/polygon/ink), tri-state: `undefined` never touches the
* document, `null`/`0` clears, a value sets. Whenever it does write, any
* `/UnrotatedRect` is defensively cleared — that key is an impossible state on
* a vertex kind and must never accidentally drive the AP generator.
*/
function writeVertexTransformMetadata(fn, annotPtr, t) {
	if (t.rotation === void 0) return;
	if (t.rotation !== null && t.rotation !== 0) setRotation(fn, annotPtr, t.rotation);
	else fn.EPDFAnnot_ClearEmbedMetadataKey(annotPtr, KEY_ROTATION);
	fn.EPDFAnnot_ClearEmbedMetadataKey(annotPtr, KEY_UNROTATED_RECT);
}
//#endregion
//#region src/features/annotations/internal/write/writeFreeTextAnnotation.ts
/**
* Default `/DA` colour for free text: black (border + default text). Unlike
* the geometric families (which default to red `/C`), a text box reads best
* with a black mark.
*/
const DEFAULT_FREETEXT_COLOR = {
	r: 0,
	g: 0,
	b: 0
};
/**
* Apply a free-text draft to a freshly-created annotation. Colour model:
*   - `color` -> `/DA` colour = border + default text colour.
*   - `fontColor` (optional) -> `TextColor` channel, overriding text only;
*     written AFTER `/DA` so the override wins.
*   - `interiorColor` -> `/C` box background (`null`/omitted clears it).
*
* Order:
*   1. base author-metadata (contents/nm/flags)
*   2. `/Rect` (required — supplied by the caller; never derived)
*   3. `/C` background (set or clear) + `/CA` opacity
*   4. `/BS` border (style + width + dash)
*   5. `/DA` default appearance (font + size + `color`)
*   6. `TextColor` override (only when `fontColor` is given)
*   7. `/Q` text alignment
*   8. `/IT` intent
*   9. `/RD` rectangle differences (optional)
*  10. callout `/CL` + leader `/LE` ending (only for callouts with geometry)
*/
function applyFreeTextDraft(fn, mem, annotPtr, draft, ctx) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	const background = draft.interiorColor ?? null;
	if (background === null) clearAnnotColor(fn, annotPtr, FPDFANNOT_COLORTYPE.Color);
	else setAnnotColor(fn, annotPtr, background, FPDFANNOT_COLORTYPE.Color);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? 1);
	applyBorderDraft(fn, mem, annotPtr, draft);
	const daColor = draft.color ?? DEFAULT_FREETEXT_COLOR;
	applyDefaultAppearance(fn, annotPtr, draft.fontFamily, draft.fontSize, daColor, ctx);
	if (draft.fontColor !== void 0) setAnnotColor(fn, annotPtr, draft.fontColor, FPDFANNOT_COLORTYPE.TextColor);
	setTextAlignment(fn, annotPtr, textAlignmentToCode(draft.textAlign));
	setIntent(fn, annotPtr, freeTextIntentToName(draft.intent));
	if (draft.rectDifferences != null) setRectangleDifferences(fn, annotPtr, draft.rectDifferences);
	if (draft.calloutLine !== void 0) setCalloutLine(fn, mem, annotPtr, draft.calloutLine);
	if (draft.lineEnding !== void 0) setLineEndings(fn, annotPtr, {
		start: "none",
		end: draft.lineEnding
	});
	writeBoxTransformMetadata(fn, mem, annotPtr, {
		rotation: draft.rotation,
		unrotatedRect: draft.unrotatedRect
	});
}
/**
* Apply a free-text patch to an existing annotation. Only present fields are
* touched. `/DA` packs the font, size, and `color` into ONE string, so a
* partial patch preserves the unpatched members by READING the current triple
* first (the same read-modify-write as {@link applyBorderPatch}'s shared
* `/BS` call) — a `{fontSize}` patch must never reset the font or colour.
* Registered (embedded) fonts are the one caveat: the current `/DA` reads
* back as a font CODE, so preserving a registered family requires the patch
* to restate `fontFamily` (an unknown code falls back to the standard-font
* default).
*
* `fontColor` here only sets an override; clearing it back to "follow
* `color`" is out of scope this iteration.
*/
function applyFreeTextPatch(fn, mem, annotPtr, patch, ctx) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	writeBoxTransformMetadata(fn, mem, annotPtr, {
		rotation: patch.rotation,
		unrotatedRect: patch.unrotatedRect
	});
	if (patch.interiorColor !== void 0) if (patch.interiorColor === null) clearAnnotColor(fn, annotPtr, FPDFANNOT_COLORTYPE.Color);
	else setAnnotColor(fn, annotPtr, patch.interiorColor, FPDFANNOT_COLORTYPE.Color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	applyBorderPatch(fn, mem, annotPtr, patch);
	if (patch.fontFamily !== void 0 || patch.fontSize !== void 0 || patch.color !== void 0) {
		const cur = readDefaultAppearance(fn, mem, annotPtr);
		applyDefaultAppearance(fn, annotPtr, patch.fontFamily ?? (cur ? standardFontFromCode(cur.fontCode) : "helvetica"), patch.fontSize ?? (cur && cur.fontSize > 0 ? cur.fontSize : 12), patch.color ?? cur?.color ?? DEFAULT_FREETEXT_COLOR, ctx);
	}
	if (patch.fontColor !== void 0) setAnnotColor(fn, annotPtr, patch.fontColor, FPDFANNOT_COLORTYPE.TextColor);
	if (patch.textAlign !== void 0) setTextAlignment(fn, annotPtr, textAlignmentToCode(patch.textAlign));
	if (patch.intent !== void 0) setIntent(fn, annotPtr, freeTextIntentToName(patch.intent));
	if (patch.rectDifferences === null) clearRectangleDifferences(fn, annotPtr);
	else if (patch.rectDifferences !== void 0) setRectangleDifferences(fn, annotPtr, patch.rectDifferences);
	if (patch.calloutLine !== void 0) setCalloutLine(fn, mem, annotPtr, patch.calloutLine);
	if (patch.lineEnding !== void 0) setLineEndings(fn, annotPtr, {
		start: "none",
		end: patch.lineEnding
	});
}
/**
* Type-narrowing predicate used by the writer registry to pick the
* free-text writer for a draft/patch's `subtype`.
*/
function isFreeTextSubtype(subtype) {
	return subtype === "free-text";
}
//#endregion
//#region src/features/annotations/internal/write/writeInkAnnotation.ts
/**
* Apply an ink draft to a freshly-created annotation. Ink has a stroke but
* no `/IC`, so it uses the geometry styling layer (not the filled one).
* Order:
*   1. base author-metadata (contents/nm)
*   2. /Rect (required — supplied by the plugin; the engine never derives it)
*   3. geometry styling (/C, /CA, /BS, dash)
*   4. /InkList freehand strokes
*/
function applyInkDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	if (draft.intent !== void 0) setIntent(fn, annotPtr, inkIntentToName(draft.intent));
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	applyGeometryStyleDraft(fn, mem, annotPtr, draft);
	setInkList(fn, mem, annotPtr, draft.inkList);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: draft.rotation });
}
function applyInkPatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.intent !== void 0) setIntent(fn, annotPtr, inkIntentToName(patch.intent));
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	applyGeometryStylePatch(fn, mem, annotPtr, patch);
	if (patch.inkList !== void 0) setInkList(fn, mem, annotPtr, patch.inkList);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: patch.rotation });
}
/**
* Type-narrowing predicate used by the writer registry to pick the ink
* writer for a draft/patch's `subtype`.
*/
function isInkSubtype(subtype) {
	return subtype === "ink";
}
//#endregion
//#region src/features/annotations/internal/write/writeLineAnnotation.ts
/** Default line endings when a line draft omits them. */
const DEFAULT_LINE_ENDINGS$1 = {
	start: "none",
	end: "none"
};
/**
* Apply a line draft to a freshly-created annotation. Order:
*   1. base author-metadata (contents/nm)
*   2. /Rect (required — supplied by the plugin; the engine never derives it)
*   3. shared stroke/fill styling (/C, /CA, /BS, dash)
*   4. /L line geometry
*   5. /LE line endings (default none/none)
*/
function applyLineDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	applyFilledStyleDraft(fn, mem, annotPtr, draft);
	setLine(fn, mem, annotPtr, draft.linePoints);
	setLineEndings(fn, annotPtr, draft.lineEndings ?? DEFAULT_LINE_ENDINGS$1);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: draft.rotation });
}
function applyLinePatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	applyFilledStylePatch(fn, mem, annotPtr, patch);
	if (patch.linePoints !== void 0) setLine(fn, mem, annotPtr, patch.linePoints);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: patch.rotation });
	if (patch.lineEndings !== void 0) setLineEndings(fn, annotPtr, patch.lineEndings);
}
/**
* Type-narrowing predicate used by the writer registry to pick the line
* writer for a draft/patch's `subtype`.
*/
function isLineSubtype(subtype) {
	return subtype === "line";
}
//#endregion
//#region src/features/annotations/internal/write/writeLinkAnnotation.ts
/**
* Link writer: rect + the `/A` action. Only `goto`/`uri` targets are
* writable (the draft/patch types enforce it; `goto-remote`/`launch` are
* read-only by design). Relationship (`/IRT` + `/RT` — v2's grouped links)
* is written by the mutator's kind-agnostic relationship pass, never here.
*
* A retarget REPLACES `/A`; it cannot remove a pre-existing direct `/Dest`
* (no dict-entry removal primitive in the runtime), which is why the link
* READER gives `/A` precedence — see readLinkAnnotation.ts.
*/
function applyLinkDraft(fn, mem, annotPtr, draft, ctx) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	if (draft.target) applyLinkTarget(fn, mem, annotPtr, draft.target, ctx);
}
function applyLinkPatch(fn, mem, annotPtr, patch, ctx) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	if (patch.target === null) clearLinkTarget(fn, annotPtr);
	else if (patch.target !== void 0) applyLinkTarget(fn, mem, annotPtr, patch.target, ctx);
}
/**
* `target: null` → a dead link. The model treats the target as ONE concept
* with two spellings, so THIS layer composes the two single-purpose
* removal primitives — removing only `/A` would resurrect a stale direct
* `/Dest` as the live target.
*/
function clearLinkTarget(fn, annotPtr) {
	if (!fn.EPDFAnnot_RemoveAction(annotPtr) || !fn.EPDFAnnot_RemoveDest(annotPtr)) throw new EngineError(EngineErrorCode.Unknown, "failed to clear link target");
}
function isLinkSubtype(subtype) {
	return subtype === "link";
}
function applyLinkTarget(fn, mem, annotPtr, target, ctx) {
	const docPtr = ctx?.docPtr;
	if (!docPtr) throw new EngineError(EngineErrorCode.InvalidArg, "writing a link target requires a document pointer on the write context");
	const actionPtr = target.kind === "uri" ? fn.EPDFAction_CreateURI(docPtr, target.uri) : fn.EPDFAction_CreateGoTo(docPtr, createDestination(fn, mem, docPtr, target.destination));
	if (!actionPtr) throw new EngineError(EngineErrorCode.Unknown, `failed to create '${target.kind}' action`);
	if (!fn.EPDFAnnot_SetAction(annotPtr, actionPtr)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetAction failed for link");
}
/**
* Build an INDIRECT explicit-destination array for `dest`. The target page
* is loaded just to reference its dictionary and closed immediately —
* destinations routinely point at pages the mutator's pool never touches.
*/
function createDestination(fn, mem, docPtr, dest) {
	const pagePtr = fn.EPDFDoc_LoadPageByObjectNumber(docPtr, dest.pageObjectNumber);
	if (!pagePtr) throw new EngineError(EngineErrorCode.NotFound, `link destination page not found: pageObjectNumber=${dest.pageObjectNumber}`);
	try {
		const destPtr = dest.kind === "xyz" ? fn.EPDFDest_CreateXYZ(pagePtr, dest.left != null, dest.left ?? 0, dest.top != null, dest.top ?? 0, dest.zoom != null, dest.zoom ?? 0) : createViewDestination(fn, mem, pagePtr, dest);
		if (!destPtr) throw new EngineError(EngineErrorCode.Unknown, `failed to create '${dest.kind}' destination`);
		return destPtr;
	} finally {
		fn.FPDF_ClosePage(pagePtr);
	}
}
function createViewDestination(fn, mem, pagePtr, dest) {
	const params = (() => {
		switch (dest.kind) {
			case "fitH":
			case "fitBH": return dest.top != null ? [dest.top] : [];
			case "fitV":
			case "fitBV": return dest.left != null ? [dest.left] : [];
			case "fitR": return [
				dest.left,
				dest.bottom,
				dest.right,
				dest.top
			];
			default: return [];
		}
	})();
	const view = VIEW_CODE_BY_KIND[dest.kind];
	if (!params.length) return fn.EPDFDest_CreateView(pagePtr, view, NULL_PTR, 0);
	return withScratch(mem, params.length * 4, (buf) => {
		for (let i = 0; i < params.length; i++) mem.poke(buf, "f32", params[i], i * 4);
		return fn.EPDFDest_CreateView(pagePtr, view, buf, params.length);
	});
}
//#endregion
//#region src/features/annotations/internal/write/writeTextMarkupAnnotation.ts
/**
* Default opacity when a draft omits `opacity`. PDFium's /CA defaults to
* 1.0 if absent from the dict, but we set it explicitly so reads always
* round-trip the same value.
*/
const DEFAULT_OPACITY$1 = 1;
/**
* Default fill colour per text-markup subtype. Matches the read-side
* fallback in `readers/annotations/text-markup.ts`.
*/
const DEFAULT_HIGHLIGHT_COLOR = {
	r: 255,
	g: 255,
	b: 0
};
const DEFAULT_TEXT_MARKUP_COLOR = {
	r: 0,
	g: 0,
	b: 0
};
/**
* Apply a text-markup draft to a freshly-created annotation. Caller is
* responsible for `EPDFPage_CreateAnnot`; this function only writes
* fields. Order:
*   1. base author-metadata (contents/author/nm)
*   2. quadPoints (required for text-markup; 0-length is a typed error)
*   3. rect (computed from quadPoints when not supplied — text-markup
*      annotations require /Rect to be the smallest enclosing box)
*   4. color (with subtype-aware fallback)
*   5. opacity (/CA)
*/
function applyTextMarkupDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	const quadPoints = draft.quadPoints;
	if (!Array.isArray(quadPoints) || quadPoints.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, `text-markup draft (${draft.subtype}) requires at least one quadPoint`);
	appendQuadPoints(fn, mem, annotPtr, quadPoints);
	setRectFromQuadPoints(fn, mem, annotPtr, quadPoints);
	const fallback = draft.subtype === "highlight" ? DEFAULT_HIGHLIGHT_COLOR : DEFAULT_TEXT_MARKUP_COLOR;
	setAnnotColor(fn, annotPtr, draft.color ?? fallback);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? DEFAULT_OPACITY$1);
	if (draft.subtype === "strikeout" && draft.intent !== void 0) setIntent(fn, annotPtr, strikeoutIntentToName(draft.intent));
}
/**
* Apply a text-markup patch to an existing annotation. Order:
*   1. base author-metadata (contents/author; never /NM)
*   2. color (only if present)
*   3. opacity (only if present)
*   4. quadPoints (only if present, AND non-empty — the schema accepts an
*      empty array but PDFium would treat it as a deletion of the
*      attachment-point list, which is never the user's intent. We
*      explicitly reject the empty-array case).
*
* `rect` is recomputed only when quadPoints change, to keep /Rect in sync
* with the smallest enclosing box.
*/
function applyTextMarkupPatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.color !== void 0) setAnnotColor(fn, annotPtr, patch.color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	if (patch.subtype === "strikeout" && patch.intent !== void 0) setIntent(fn, annotPtr, strikeoutIntentToName(patch.intent));
	if (patch.quadPoints !== void 0) {
		if (patch.quadPoints.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, `text-markup patch quadPoints, when present, must be non-empty`);
		replaceQuadPoints(fn, mem, annotPtr, patch.quadPoints);
		setRectFromQuadPoints(fn, mem, annotPtr, patch.quadPoints);
	}
}
/**
* Type-narrowing predicate. Mirrors the reader-side dispatch. Used by
* the writer registry to pick the right writer for a draft/patch's
* `subtype` discriminator.
*/
function isTextMarkupSubtype(subtype) {
	return subtype === "highlight" || subtype === "underline" || subtype === "squiggly" || subtype === "strikeout";
}
function appendQuadPoints(fn, mem, annotPtr, quadPoints) {
	const buf = mem.alloc(32);
	try {
		for (const qp of quadPoints) {
			writeQuadPointStruct(mem, buf, qp);
			if (!fn.FPDFAnnot_AppendAttachmentPoints(annotPtr, buf)) throw new EngineError(EngineErrorCode.Unknown, "FPDFAnnot_AppendAttachmentPoints returned false");
		}
	} finally {
		mem.free(buf);
	}
}
/**
* Replace the existing quadPoints with the supplied list. PDFium has
* `FPDFAnnot_SetAttachmentPoints` which writes at index, but no truncate
* helper, so we overwrite as many existing slots as we can and append
* the rest. PDFium will GROW the list via append, but it cannot SHRINK
* it; for that reason `applyTextMarkupPatch` rejects an empty patch and
* the conformance suite covers the "patch must not shrink quadPoints"
* rule (`patch.quadPoints.length >= existingCount`).
*/
function replaceQuadPoints(fn, mem, annotPtr, quadPoints) {
	const existing = fn.FPDFAnnot_CountAttachmentPoints(annotPtr);
	if (quadPoints.length < existing) throw new EngineError(EngineErrorCode.InvalidArg, `text-markup patch cannot shrink quadPoints (have ${existing}, patch supplies ${quadPoints.length})`);
	const buf = mem.alloc(32);
	try {
		for (let i = 0; i < existing; i++) {
			writeQuadPointStruct(mem, buf, quadPoints[i]);
			if (!fn.FPDFAnnot_SetAttachmentPoints(annotPtr, i, buf)) throw new EngineError(EngineErrorCode.Unknown, `FPDFAnnot_SetAttachmentPoints failed at index ${i}`);
		}
		for (let i = existing; i < quadPoints.length; i++) {
			writeQuadPointStruct(mem, buf, quadPoints[i]);
			if (!fn.FPDFAnnot_AppendAttachmentPoints(annotPtr, buf)) throw new EngineError(EngineErrorCode.Unknown, `FPDFAnnot_AppendAttachmentPoints failed at index ${i}`);
		}
	} finally {
		mem.free(buf);
	}
}
function writeQuadPointStruct(mem, buf, qp) {
	mem.poke(buf, "f32", qp.p1.x, 0);
	mem.poke(buf, "f32", qp.p1.y, 4);
	mem.poke(buf, "f32", qp.p2.x, 8);
	mem.poke(buf, "f32", qp.p2.y, 12);
	mem.poke(buf, "f32", qp.p3.x, 16);
	mem.poke(buf, "f32", qp.p3.y, 20);
	mem.poke(buf, "f32", qp.p4.x, 24);
	mem.poke(buf, "f32", qp.p4.y, 28);
}
function setRectFromQuadPoints(fn, mem, annotPtr, quadPoints) {
	let minX = Number.POSITIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	for (const qp of quadPoints) for (const p of [
		qp.p1,
		qp.p2,
		qp.p3,
		qp.p4
	]) {
		if (p.x < minX) minX = p.x;
		if (p.x > maxX) maxX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.y > maxY) maxY = p.y;
	}
	setAnnotRect(fn, mem, annotPtr, {
		left: minX,
		top: maxY,
		right: maxX,
		bottom: minY
	});
}
//#endregion
//#region src/features/annotations/internal/write/writeRedactAnnotation.ts
/** Default `/C` marking outline: red — the redaction marking convention (and
*  the AP generator's default). */
const DEFAULT_REDACT_COLOR = {
	r: 255,
	g: 0,
	b: 0
};
/** Default `/DA` label colour: black, mirroring free text. Tools that pair a
*  label with a dark `interiorColor` should set a light `fontColor`
*  explicitly. */
const DEFAULT_LABEL_COLOR = {
	r: 0,
	g: 0,
	b: 0
};
const DEFAULT_FONT_SIZE = 12;
/**
* True when the draft/patch carries the label or any `/DA` member — i.e. the
* `/DA` must be (re)written. ISO 32000-2 requires a `/DA` alongside
* `/OverlayText`, so setting a label always emits one (defaults filling any
* omitted triple member). Clearing the label (`overlayText: null`) does not.
*/
function touchesLabelStyle(p) {
	return p.overlayText !== void 0 && p.overlayText !== null || p.fontFamily !== void 0 || p.fontSize !== void 0 || p.fontColor !== void 0;
}
/**
* Apply a redact draft to a freshly-created annotation. Colour model:
*   - `color` -> `/C` marking-stage outline.
*   - `interiorColor` -> `/IC` post-apply fill (`null`/omitted = the key is
*     never written; ISO leaves the region transparent).
*   - `fontColor` -> the `/DA` colour (label text).
*
* Order:
*   1. base author-metadata (contents/nm/flags)
*   2. `/Rect` (required — supplied by the caller; never derived)
*   3. `/QuadPoints` (text redactions only)
*   4. `/C` outline + `/CA` opacity + `/IC` fill
*   5. `/OverlayText` + `/Repeat`
*   6. `/DA` (only when a label field is present)
*   7. `/Q` label alignment
*/
function applyRedactDraft(fn, mem, annotPtr, draft, ctx) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	if (draft.quadPoints && draft.quadPoints.length > 0) appendQuadPoints(fn, mem, annotPtr, draft.quadPoints);
	setAnnotColor(fn, annotPtr, draft.color ?? DEFAULT_REDACT_COLOR, FPDFANNOT_COLORTYPE.Color);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? 1);
	const fill = draft.interiorColor ?? null;
	if (fill !== null) setAnnotColor(fn, annotPtr, fill, FPDFANNOT_COLORTYPE.InteriorColor);
	if (draft.overlayText !== void 0 && draft.overlayText.length > 0) setOverlayText(fn, mem, annotPtr, draft.overlayText);
	if (draft.repeat) setOverlayTextRepeat(fn, annotPtr, true);
	if (touchesLabelStyle(draft)) applyDefaultAppearance(fn, annotPtr, draft.fontFamily ?? "helvetica", draft.fontSize ?? DEFAULT_FONT_SIZE, draft.fontColor ?? DEFAULT_LABEL_COLOR, ctx);
	if (draft.textAlign !== void 0) setTextAlignment(fn, annotPtr, textAlignmentToCode(draft.textAlign));
}
/**
* Apply a redact patch to an existing annotation. Only present fields are
* touched. `/DA` follows the free-text triple rule: send `fontFamily` +
* `fontSize` + `fontColor` together when changing any of them. `quadPoints`
* shares the text-markup no-shrink constraint (PDFium can grow but not
* shrink the attachment-points list); patching quads re-derives `/Rect` from
* their bounds unless the patch also carries an explicit `rect`.
*/
function applyRedactPatch(fn, mem, annotPtr, patch, ctx) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	if (patch.quadPoints !== void 0) {
		replaceQuadPoints(fn, mem, annotPtr, patch.quadPoints);
		if (patch.rect === void 0) setRectFromQuadPoints(fn, mem, annotPtr, patch.quadPoints);
	}
	if (patch.color !== void 0) setAnnotColor(fn, annotPtr, patch.color, FPDFANNOT_COLORTYPE.Color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	if (patch.interiorColor !== void 0) if (patch.interiorColor === null) clearAnnotColor(fn, annotPtr, FPDFANNOT_COLORTYPE.InteriorColor);
	else setAnnotColor(fn, annotPtr, patch.interiorColor, FPDFANNOT_COLORTYPE.InteriorColor);
	if (patch.overlayText !== void 0) setOverlayText(fn, mem, annotPtr, patch.overlayText ?? "");
	if (patch.repeat !== void 0) setOverlayTextRepeat(fn, annotPtr, patch.repeat);
	if (touchesLabelStyle(patch)) applyDefaultAppearance(fn, annotPtr, patch.fontFamily ?? "helvetica", patch.fontSize ?? DEFAULT_FONT_SIZE, patch.fontColor ?? DEFAULT_LABEL_COLOR, ctx);
	if (patch.textAlign !== void 0) setTextAlignment(fn, annotPtr, textAlignmentToCode(patch.textAlign));
}
/**
* Type-narrowing predicate used by the writer registry to pick the redact
* writer for a draft/patch's `subtype`.
*/
function isRedactSubtype(subtype) {
	return subtype === "redact";
}
//#endregion
//#region src/features/annotations/internal/write/writeShapeAnnotation.ts
/**
* Apply a shape draft to a freshly-created annotation. Caller is
* responsible for `EPDFPage_CreateAnnot`; this function only writes
* fields. Order:
*   1. base author-metadata (contents/nm)
*   2. /Rect (required — shapes carry their geometry as /Rect, not quads)
*   3. shared stroke/fill styling (/IC, /C, /CA, /BS, dash)
*   4. optional cloudy (/BE), rect-diff (/RD)
*/
function applyShapeDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	applyFilledStyleDraft(fn, mem, annotPtr, draft);
	if (draft.cloudyIntensity != null && draft.cloudyIntensity > 0) setBorderEffect(fn, annotPtr, draft.cloudyIntensity);
	if (draft.rectDifferences != null) setRectangleDifferences(fn, annotPtr, draft.rectDifferences);
	writeBoxTransformMetadata(fn, mem, annotPtr, {
		rotation: draft.rotation,
		unrotatedRect: draft.unrotatedRect
	});
}
/**
* Apply a shape patch to an existing annotation. Only fields present on
* the patch are touched; `cloudyIntensity` and `rectDifferences` are
* tri-state (as `interiorColor`): a value sets the entry, `null` removes it.
*/
function applyShapePatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	writeBoxTransformMetadata(fn, mem, annotPtr, {
		rotation: patch.rotation,
		unrotatedRect: patch.unrotatedRect
	});
	applyFilledStylePatch(fn, mem, annotPtr, patch);
	if (patch.cloudyIntensity !== void 0) if (patch.cloudyIntensity !== null && patch.cloudyIntensity > 0) setBorderEffect(fn, annotPtr, patch.cloudyIntensity);
	else clearBorderEffect(fn, annotPtr);
	if (patch.rectDifferences === null) clearRectangleDifferences(fn, annotPtr);
	else if (patch.rectDifferences !== void 0) setRectangleDifferences(fn, annotPtr, patch.rectDifferences);
}
/**
* Type-narrowing predicate. Mirrors the reader-side dispatch. Used by the
* writer registry to pick the shape writer for a draft/patch's `subtype`.
*/
function isShapeSubtype(subtype) {
	return subtype === "circle" || subtype === "square";
}
//#endregion
//#region src/features/annotations/internal/write/writeStampAnnotation.ts
/** `EPDF_STAMP_FIT` codes from `public/fpdf_annot.h` (CSS `object-fit` naming on the wire). */
const STAMP_FIT_TO_CODE = {
	contain: 0,
	cover: 1,
	fill: 2
};
/**
* Validate every caller-controlled stamp input before the mutation owner
* performs its first native write. AnnotationMutator invokes these before
* creating an annotation or strengthening a weak annotation id.
*/
function preflightStampDraft(draft, ctx) {
	if (draft.name !== void 0) requireStampNameCode(draft.name);
	requireStampFit(draft.fit ?? "contain");
	requireStampContent(draft.source, ctx);
}
function preflightStampPatch(patch, ctx) {
	if (patch.name !== void 0) requireStampNameCode(patch.name);
	requireStampFit(patch.fit ?? "contain");
	if (patch.source !== void 0) requireStampContent(patch.source, ctx);
}
/**
* Apply a stamp draft. Order mirrors the other writers (base → subtype
* fields), then the appearance pipeline via {@link authorStampAppearance}:
*   1. resolve the `{ resource }` ref against the mutation's binary payloads
*   2. sniff the bytes (PNG/JPEG → image object, PDF → cloned form XObject)
*   3. `EPDFAnnot_UpdateAppearanceToRect` fits the appearance into the box
*      honouring `fit` and any `/EMBD_Metadata` rotation.
* The mutator's `EPDFAnnot_GenerateAppearance` pass afterwards is a no-op
* for stamps (CPDF_GenerateAP has no stamp arm), so the appearance built
* here is what ships.
*/
function applyStampDraft(fn, mem, annotPtr, draft, ctx) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	if (draft.name !== void 0) setStampName(fn, annotPtr, draft.name);
	authorStampAppearance(fn, mem, annotPtr, draft.source, draft.fit ?? "contain", {
		rect: draft.rect,
		unrotatedRect: draft.unrotatedRect ?? void 0,
		rotation: draft.rotation ?? void 0
	}, ctx);
}
function applyStampPatch(fn, mem, annotPtr, patch, ctx) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.name !== void 0) setStampName(fn, annotPtr, patch.name);
	if (patch.source !== void 0) {
		const rect = patch.rect ?? readAnnotRect(fn, mem, annotPtr);
		authorStampAppearance(fn, mem, annotPtr, patch.source, patch.fit ?? "contain", {
			rect,
			unrotatedRect: patch.unrotatedRect ?? void 0,
			rotation: patch.rotation ?? void 0
		}, ctx);
		return;
	}
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	writeBoxTransformMetadata(fn, mem, annotPtr, {
		rotation: patch.rotation,
		unrotatedRect: patch.unrotatedRect
	});
	if (patch.rect !== void 0 || patch.fit !== void 0 || patch.rotation !== void 0 || patch.unrotatedRect !== void 0) refitAppearance(fn, annotPtr, patch.fit ?? "contain");
}
function isStampSubtype(subtype) {
	return subtype === "stamp";
}
function setStampName(fn, annotPtr, name) {
	const code = STAMP_NAME_TO_CODE[name];
	if (code === void 0) throw new EngineError(EngineErrorCode.Unknown, "stamp name preflight invariant broken");
	if (!fn.EPDFAnnot_SetName(annotPtr, code)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetName returned false");
}
function requireStampNameCode(name) {
	const code = STAMP_NAME_TO_CODE[name];
	if (code === void 0) throw new EngineError(EngineErrorCode.InvalidArg, `unknown stamp name '${name}'; supported names: ${Object.keys(STAMP_NAME_TO_CODE).join(", ")}`);
	return code;
}
function requireStampFit(fit) {
	if (STAMP_FIT_TO_CODE[fit] === void 0) throw new EngineError(EngineErrorCode.InvalidArg, `unknown stamp fit '${String(fit)}'; expected contain, cover, or fill`);
}
function requireStampResource(ref, ctx) {
	const resource = ctx?.resources?.[ref.resource];
	if (!resource) throw new EngineError(EngineErrorCode.InvalidArg, `stamp source references resource '${ref.resource}' but no such binary payload accompanied the mutation`);
	return resource;
}
function requireStampContent(ref, ctx) {
	const resource = requireStampResource(ref, ctx);
	if (!(resource.bytes instanceof ArrayBuffer) || !sniffBinaryMetadata(resource.bytes)) throw new EngineError(EngineErrorCode.InvalidArg, "stamp source must be PNG, JPEG, or single-page PDF bytes");
	if (ctx?.docPtr === void 0 || ctx.pagePtr === void 0) throw new EngineError(EngineErrorCode.Unknown, "stamp writer requires docPtr/pagePtr on the write context");
}
/**
* Author a stamp's appearance from `source`, correct under rotation.
*
* The appearance MUST be built in the UNROTATED frame and rotated by an
* `/AP /Matrix` afterwards — the "FreeText pattern" the stamp draft documents.
* If instead the image is fit into the rotated AABB `/Rect` (a portrait box for
* a landscape image, say), the letterboxed bands get baked into the appearance,
* and the closing native re-fit — which records `EPDFOrigContentRect` from that
* padded box and then fits it into the unrotated box — shrinks the image by the
* aspect ratio a SECOND time. The result is a small image adrift in white
* padding, and only when rotated (at 0° the two frames coincide, so it fills).
*
* So for a rotated stamp we: author the image into the UNROTATED box with no
* rotation metadata active (its recorded `EPDFOrigContentRect` is the
* image-filled logical box), then write the rotation metadata, set the real
* AABB `/Rect`, and re-fit once — which bakes the `/Matrix` and leaves the
* appearance filling the box exactly as the 0° case does. The unrotated path is
* unchanged: author straight into `/Rect`.
*/
function authorStampAppearance(fn, mem, annotPtr, source, fit, box, ctx) {
	if (!(!!box.rotation && box.unrotatedRect !== void 0)) {
		setAnnotRect(fn, mem, annotPtr, box.rect);
		setStampContent(fn, mem, annotPtr, source, fit, box.rect, ctx);
		return;
	}
	const unrotated = box.unrotatedRect;
	writeBoxTransformMetadata(fn, mem, annotPtr, {});
	setAnnotRect(fn, mem, annotPtr, unrotated);
	setStampContent(fn, mem, annotPtr, source, fit, unrotated, ctx);
	writeBoxTransformMetadata(fn, mem, annotPtr, {
		rotation: box.rotation,
		unrotatedRect: unrotated
	});
	setAnnotRect(fn, mem, annotPtr, box.rect);
	refitAppearance(fn, annotPtr, fit);
}
function setStampContent(fn, mem, annotPtr, ref, fit, rect, ctx) {
	const resource = ctx?.resources?.[ref.resource];
	const meta = resource ? sniffBinaryMetadata(resource.bytes) : null;
	if (!resource || !meta || ctx?.docPtr === void 0 || ctx.pagePtr === void 0) throw new EngineError(EngineErrorCode.Unknown, "stamp content preflight invariant broken");
	for (let i = fn.FPDFAnnot_GetObjectCount(annotPtr) - 1; i >= 0; i--) fn.FPDFAnnot_RemoveObject(annotPtr, i);
	if (meta.mimeType === "application/pdf") setAppearanceFromPdfBytes(fn, mem, annotPtr, resource.bytes);
	else appendImageObject(fn, mem, annotPtr, ctx.docPtr, ctx.pagePtr, resource.bytes, meta, rect, fit);
	refitAppearance(fn, annotPtr, fit);
}
function refitAppearance(fn, annotPtr, fit) {
	if (!fn.EPDFAnnot_UpdateAppearanceToRect(annotPtr, STAMP_FIT_TO_CODE[fit])) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_UpdateAppearanceToRect returned false");
}
/**
* PNG/JPEG → image object appended to the appearance.
*
* The AP's coordinate space aligns with page space (its BBox is the annot
* `/Rect`, same as v2), so the image must be painted INSIDE `/Rect` or it
* falls outside the form's clip and renders blank. The `fit` placement is
* computed here from the sniffed intrinsic dimensions; the closing
* `EPDFAnnot_UpdateAppearanceToRect` pass then normalises the BBox and
* records `EPDFOrigContentRect` so later geometry-only patches can re-fit
* natively.
*/
function appendImageObject(fn, mem, annotPtr, docPtr, pagePtr, bytes, meta, rect, fit) {
	const imageObjPtr = fn.FPDFPageObj_NewImageObj(docPtr);
	if (!imageObjPtr) throw new EngineError(EngineErrorCode.Unknown, "FPDFPageObj_NewImageObj returned NULL");
	let appended = false;
	try {
		const dataPtr = mem.alloc(bytes.byteLength);
		try {
			mem.writeBytes(dataPtr, new Uint8Array(bytes));
			if (!(meta.mimeType === "image/png" ? fn.EPDFImageObj_SetPng(pagePtr, 0, imageObjPtr, dataPtr, bytes.byteLength) : fn.EPDFImageObj_SetJpeg(pagePtr, 0, imageObjPtr, dataPtr, bytes.byteLength))) throw new EngineError(EngineErrorCode.InvalidArg, `${meta.mimeType === "image/png" ? "EPDFImageObj_SetPng" : "EPDFImageObj_SetJpeg"} rejected the image data`);
		} finally {
			mem.free(dataPtr);
		}
		const placement = fitIntoRect(meta.width, meta.height, rect, fit);
		setImageMatrix(fn, mem, imageObjPtr, placement.width, placement.height);
		fn.FPDFPageObj_Transform(imageObjPtr, 1, 0, 0, 1, placement.left, placement.bottom);
		if (!fn.FPDFAnnot_AppendObject(annotPtr, imageObjPtr)) throw new EngineError(EngineErrorCode.Unknown, "FPDFAnnot_AppendObject returned false");
		appended = true;
	} finally {
		if (!appended) fn.FPDFPageObj_Destroy(imageObjPtr);
	}
}
/** Fit-policy placement of `w×h` content inside `rect` (centered for the aspect-preserving fits). */
function fitIntoRect(w, h, rect, fit) {
	const boxW = Math.max(0, rect.right - rect.left);
	const boxH = Math.max(0, rect.top - rect.bottom);
	if (fit === "fill" || w <= 0 || h <= 0) return {
		left: rect.left,
		bottom: rect.bottom,
		width: boxW,
		height: boxH
	};
	const scale = fit === "contain" ? Math.min(boxW / w, boxH / h) : Math.max(boxW / w, boxH / h);
	const width = w * scale;
	const height = h * scale;
	return {
		left: rect.left + (boxW - width) / 2,
		bottom: rect.bottom + (boxH - height) / 2,
		width,
		height
	};
}
/** FS_MATRIX { a, b, c, d, e, f } — six f32s. */
function setImageMatrix(fn, mem, imageObjPtr, width, height) {
	const buf = mem.alloc(24);
	try {
		mem.poke(buf, "f32", width, 0);
		mem.poke(buf, "f32", 0, 4);
		mem.poke(buf, "f32", 0, 8);
		mem.poke(buf, "f32", height, 12);
		mem.poke(buf, "f32", 0, 16);
		mem.poke(buf, "f32", 0, 20);
		if (!fn.FPDFPageObj_SetMatrix(imageObjPtr, buf)) throw new EngineError(EngineErrorCode.Unknown, "FPDFPageObj_SetMatrix returned false");
	} finally {
		mem.free(buf);
	}
}
/**
* Single-page PDF → deep-cloned Form XObject as AP/N (the v2 rubber-stamp
* path). The source buffer must stay alive until the temp document closes.
*/
function setAppearanceFromPdfBytes(fn, mem, annotPtr, bytes) {
	const dataPtr = mem.alloc(bytes.byteLength);
	try {
		mem.writeBytes(dataPtr, new Uint8Array(bytes));
		const tempDocPtr = fn.FPDF_LoadMemDocument(dataPtr, bytes.byteLength, "");
		if (!tempDocPtr) throw new EngineError(EngineErrorCode.MalformedPdf, "stamp source PDF could not be opened");
		try {
			if (!fn.EPDFAnnot_SetAppearanceFromPage(annotPtr, tempDocPtr, 0)) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetAppearanceFromPage returned false");
		} finally {
			fn.FPDF_CloseDocument(tempDocPtr);
		}
	} finally {
		mem.free(dataPtr);
	}
}
//#endregion
//#region src/features/annotations/internal/write/writeTextAnnotation.ts
/** Default `/C` — the generator's yellow note fill, set explicitly so reads round-trip. */
const DEFAULT_NOTE_COLOR = {
	r: 255,
	g: 255,
	b: 0
};
const DEFAULT_OPACITY = 1;
/**
* Apply a text (sticky-note) draft. The visual is entirely generator-owned:
* the mutator's closing `regenerateAppearance` bakes the 20×20 note icon
* from `/C` + `/Name` (GenerateTextAP), so this writer only records state.
*/
function applyTextDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	setAnnotColor(fn, annotPtr, draft.color ?? DEFAULT_NOTE_COLOR);
	setAnnotOpacity(fn, annotPtr, draft.opacity ?? DEFAULT_OPACITY);
	setNoteIcon(fn, annotPtr, draft.icon ?? "note");
}
function applyTextPatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	if (patch.color !== void 0) setAnnotColor(fn, annotPtr, patch.color);
	if (patch.opacity !== void 0) setAnnotOpacity(fn, annotPtr, patch.opacity);
	if (patch.icon !== void 0) setNoteIcon(fn, annotPtr, patch.icon);
}
function isTextSubtype(subtype) {
	return subtype === "text";
}
function setNoteIcon(fn, annotPtr, icon) {
	if (!fn.EPDFAnnot_SetName(annotPtr, NOTE_ICON_TO_CODE[icon])) throw new EngineError(EngineErrorCode.Unknown, "EPDFAnnot_SetName returned false");
}
//#endregion
//#region src/features/annotations/internal/write/writeVertexAnnotation.ts
/** Default line endings when a polyline draft omits them. */
const DEFAULT_LINE_ENDINGS = {
	start: "none",
	end: "none"
};
/**
* Apply a polygon draft to a freshly-created annotation. Order:
*   1. base author-metadata (contents/nm)
*   2. /Rect (required — supplied by the plugin; the engine never derives it)
*   3. shared stroke/fill styling (/IC, /C, /CA, /BS, dash)
*   4. /Vertices geometry
*   5. optional cloudy (/BE)
*
* Note: polygon does NOT use /RD (rectangle differences) — its geometry is
* fully described by /Vertices + /Rect, so /RD is redundant. Per ISO 32000
* /RD applies to Square/Circle (and FreeText/Caret), not Polygon.
*/
function applyPolygonDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	applyFilledStyleDraft(fn, mem, annotPtr, draft);
	setVertices(fn, mem, annotPtr, draft.vertices);
	if (draft.cloudyIntensity != null && draft.cloudyIntensity > 0) setBorderEffect(fn, annotPtr, draft.cloudyIntensity);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: draft.rotation });
}
function applyPolygonPatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	applyFilledStylePatch(fn, mem, annotPtr, patch);
	if (patch.vertices !== void 0) setVertices(fn, mem, annotPtr, patch.vertices);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: patch.rotation });
	if (patch.cloudyIntensity !== void 0) if (patch.cloudyIntensity !== null && patch.cloudyIntensity > 0) setBorderEffect(fn, annotPtr, patch.cloudyIntensity);
	else clearBorderEffect(fn, annotPtr);
}
/**
* Apply a polyline draft to a freshly-created annotation. Order:
*   1. base author-metadata (contents/nm)
*   2. /Rect (required — supplied by the plugin)
*   3. shared stroke/fill styling (/IC, /C, /CA, /BS, dash)
*   4. /Vertices geometry
*   5. /LE line endings (default none/none)
*/
function applyPolylineDraft(fn, mem, annotPtr, draft) {
	applyAnnotationBaseDraft(fn, mem, annotPtr, draft);
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	applyFilledStyleDraft(fn, mem, annotPtr, draft);
	setVertices(fn, mem, annotPtr, draft.vertices);
	setLineEndings(fn, annotPtr, draft.lineEndings ?? DEFAULT_LINE_ENDINGS);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: draft.rotation });
}
function applyPolylinePatch(fn, mem, annotPtr, patch) {
	applyAnnotationBasePatch(fn, mem, annotPtr, patch);
	if (patch.rect !== void 0) setAnnotRect(fn, mem, annotPtr, patch.rect);
	applyFilledStylePatch(fn, mem, annotPtr, patch);
	if (patch.vertices !== void 0) setVertices(fn, mem, annotPtr, patch.vertices);
	writeVertexTransformMetadata(fn, annotPtr, { rotation: patch.rotation });
	if (patch.lineEndings !== void 0) setLineEndings(fn, annotPtr, patch.lineEndings);
}
/**
* Type-narrowing predicate used by the writer registry to pick the vertex
* writer for a draft/patch's `subtype`.
*/
function isVertexSubtype(subtype) {
	return subtype === "polygon" || subtype === "polyline";
}
//#endregion
//#region src/features/annotations/internal/write/writeWidgetAnnotation.ts
const MK_BORDER_COLOR = 0;
const MK_BACKGROUND_COLOR = 1;
function isWidgetSubtype(subtype) {
	return subtype === "widget";
}
/**
* THE widget-plane style writer: /MK colours, /BS, /DA, /Q. Both entry
* points funnel here — the widget annotation kind (create/patch) and
* `doc.forms.createField`'s inline placements — so creation-time and
* edit-time styling can never drift apart.
*/
function applyWidgetStyle(fn, mem, annotPtr, style) {
	if (style.color === null) fn.EPDFAnnot_ClearMKColor(annotPtr, MK_BORDER_COLOR);
	else if (style.color) fn.EPDFAnnot_SetMKColor(annotPtr, MK_BORDER_COLOR, style.color.r, style.color.g, style.color.b);
	if (style.interiorColor === null) fn.EPDFAnnot_ClearMKColor(annotPtr, MK_BACKGROUND_COLOR);
	else if (style.interiorColor) fn.EPDFAnnot_SetMKColor(annotPtr, MK_BACKGROUND_COLOR, style.interiorColor.r, style.interiorColor.g, style.interiorColor.b);
	if (style.strokeWidth !== void 0 || style.borderStyle !== void 0) fn.EPDFAnnot_SetBorderStyle(annotPtr, borderStyleToCode(style.borderStyle ?? "solid"), style.strokeWidth ?? 1);
	if (style.fontFamily !== void 0 || style.fontSize !== void 0 || style.fontColor !== void 0) {
		const color = style.fontColor ?? {
			r: 0,
			g: 0,
			b: 0
		};
		fn.EPDFAnnot_SetDefaultAppearance(annotPtr, standardFontToCode(style.fontFamily ?? "helvetica"), style.fontSize ?? 12, color.r, color.g, color.b);
	}
	if (style.textAlign !== void 0) fn.EPDFAnnot_SetTextAlignment(annotPtr, textAlignmentToCode(style.textAlign));
}
/** Create an INERT widget: placement + style. Adoption is a forms concern. */
function applyWidgetDraft(fn, mem, annotPtr, draft) {
	setAnnotRect(fn, mem, annotPtr, draft.rect);
	applyWidgetStyle(fn, mem, annotPtr, draft);
}
/**
* Move/restyle a widget. When the widget is attached to a field the
* family-correct appearance is re-baked afterwards; on inert widgets the
* regenerator is a no-op (no /FT context) and that is fine.
*/
function applyWidgetPatch(fn, mem, annotPtr, patch) {
	if (patch.rect) setAnnotRect(fn, mem, annotPtr, patch.rect);
	applyWidgetStyle(fn, mem, annotPtr, patch);
	fn.EPDFAnnot_GenerateFormFieldAP(annotPtr);
}
//#endregion
//#region src/features/annotations/internal/write/annotationWriterRegistry.ts
/** Validate subtype inputs before AnnotationMutator performs any native write. */
function preflightDraft(draft, ctx) {
	if (isStampSubtype(draft.subtype)) preflightStampDraft(draft, ctx);
	if (isFileAttachmentSubtype(draft.subtype)) preflightFileAttachmentDraft(draft, ctx);
}
/** Validate subtype inputs before AnnotationMutator performs any native write. */
function preflightPatch(patch, ctx) {
	if (isStampSubtype(patch.subtype)) preflightStampPatch(patch, ctx);
}
/**
* Per-subtype write dispatch, mirroring the read-side registry. Adding a
* new subtype is one extra arm here plus its writer module — no other
* file in this package needs to change.
*
* The mutator calls `applyDraft` or `applyPatch` once per mutation; the
* actual `EPDFPage_CreateAnnot` / identity resolution happens around
* these calls in `AnnotationMutator`.
*/
function applyDraft(fn, mem, annotPtr, draft, ctx) {
	if (isTextMarkupSubtype(draft.subtype)) {
		applyTextMarkupDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isShapeSubtype(draft.subtype)) {
		applyShapeDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isVertexSubtype(draft.subtype)) {
		if (draft.subtype === "polygon") applyPolygonDraft(fn, mem, annotPtr, draft);
		else applyPolylineDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isLineSubtype(draft.subtype)) {
		applyLineDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isLinkSubtype(draft.subtype)) {
		applyLinkDraft(fn, mem, annotPtr, draft, ctx);
		return;
	}
	if (isInkSubtype(draft.subtype)) {
		applyInkDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isFreeTextSubtype(draft.subtype)) {
		applyFreeTextDraft(fn, mem, annotPtr, draft, ctx);
		return;
	}
	if (isCaretSubtype(draft.subtype)) {
		applyCaretDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isTextSubtype(draft.subtype)) {
		applyTextDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isStampSubtype(draft.subtype)) {
		applyStampDraft(fn, mem, annotPtr, draft, ctx);
		return;
	}
	if (isFileAttachmentSubtype(draft.subtype)) {
		applyFileAttachmentDraft(fn, mem, annotPtr, draft, ctx);
		return;
	}
	if (isWidgetSubtype(draft.subtype)) {
		applyWidgetDraft(fn, mem, annotPtr, draft);
		return;
	}
	if (isRedactSubtype(draft.subtype)) {
		applyRedactDraft(fn, mem, annotPtr, draft, ctx);
		return;
	}
	throw new EngineError(EngineErrorCode.NotImplemented, `no writer registered for draft.subtype='${draft.subtype}'`);
}
function applyPatch(fn, mem, annotPtr, patch, ctx) {
	if (isTextMarkupSubtype(patch.subtype)) {
		applyTextMarkupPatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isShapeSubtype(patch.subtype)) {
		applyShapePatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isVertexSubtype(patch.subtype)) {
		if (patch.subtype === "polygon") applyPolygonPatch(fn, mem, annotPtr, patch);
		else applyPolylinePatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isLineSubtype(patch.subtype)) {
		applyLinePatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isLinkSubtype(patch.subtype)) {
		applyLinkPatch(fn, mem, annotPtr, patch, ctx);
		return;
	}
	if (isInkSubtype(patch.subtype)) {
		applyInkPatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isFreeTextSubtype(patch.subtype)) {
		applyFreeTextPatch(fn, mem, annotPtr, patch, ctx);
		return;
	}
	if (isCaretSubtype(patch.subtype)) {
		applyCaretPatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isTextSubtype(patch.subtype)) {
		applyTextPatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isStampSubtype(patch.subtype)) {
		applyStampPatch(fn, mem, annotPtr, patch, ctx);
		return;
	}
	if (isFileAttachmentSubtype(patch.subtype)) {
		applyFileAttachmentPatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isWidgetSubtype(patch.subtype)) {
		applyWidgetPatch(fn, mem, annotPtr, patch);
		return;
	}
	if (isRedactSubtype(patch.subtype)) {
		applyRedactPatch(fn, mem, annotPtr, patch, ctx);
		return;
	}
	throw new EngineError(EngineErrorCode.NotImplemented, `no writer registered for patch.subtype='${patch.subtype}'`);
}
//#endregion
//#region src/features/annotations/internal/write/writeAnnotationRelationship.ts
/**
* Apply the `/IRT` + `/RT` relationship to `annotPtr` on an
* already-acquired `pagePtr`. Returns the parent's (possibly newly
* strengthened) {@link AnnotationStableId} when a link was SET, so the
* mutator can report the side-effecting promotion in `meta.changed`;
* `null` for the clear / RT-only / no-op cases.
*
* Linking to a parent that is a weak/direct object promotes it to an
* indirect object (`/IRT` must be an indirect reference). This is
* non-structural — it assigns the parent an object number in place
* without shifting any `/Annots` index — so it does not invalidate weak
* refs or warrant a revision bump. We strengthen the parent with a
* durable `/NM` BEFORE linking (via {@link captureOrStampStableId}) so its
* identity survives a full save/reload, and report that id upstream.
*
* ISO 32000 §12.5.6.2 requires the reply and its parent to share a page;
* we enforce that here with a fast `InvalidArg` (PDFium itself only checks
* same-document).
*/
function writeAnnotationRelationship(runtime, session, pagePtr, annotPtr, pageObjectNumber, rel) {
	const { fn } = runtime;
	if (rel.inReplyTo === null) {
		fn.EPDFAnnot_SetLinkedAnnot(annotPtr, "IRT", NULL_PTR);
		fn.EPDFAnnot_SetReplyType(annotPtr, 0);
		return null;
	}
	if (rel.inReplyTo) {
		if (rel.inReplyTo.pageObjectNumber !== pageObjectNumber) throw new EngineError(EngineErrorCode.InvalidArg, `/IRT parent must be on the same page as the reply (parent page ${rel.inReplyTo.pageObjectNumber}, reply page ${pageObjectNumber})`);
		const parentPtr = resolveAnnotPtr(runtime, session, pagePtr, rel.inReplyTo);
		try {
			const parentStableId = captureOrStampStableId(runtime, parentPtr);
			if (!fn.EPDFAnnot_SetLinkedAnnot(annotPtr, "IRT", parentPtr)) throw new EngineError(EngineErrorCode.Unknown, "failed to set /IRT linked annotation");
			fn.EPDFAnnot_SetReplyType(annotPtr, replyTypeToCode(rel.replyType ?? "reply"));
			return parentStableId;
		} finally {
			fn.FPDFPage_CloseAnnot(parentPtr);
		}
	}
	if (rel.replyType !== void 0) {
		const linkedPtr = fn.FPDFAnnot_GetLinkedAnnot(annotPtr, "IRT");
		if (linkedPtr) try {
			fn.EPDFAnnot_SetReplyType(annotPtr, replyTypeToCode(rel.replyType));
		} finally {
			fn.FPDFPage_CloseAnnot(linkedPtr);
		}
	}
	return null;
}
//#endregion
//#region src/features/annotations/AnnotationMutator.ts
/** `FPDF_ANNOT_APPEARANCEMODE_NORMAL` — the `/AP /N` stream. */
const APPEARANCE_MODE_NORMAL = 0;
/**
* Synchronous orchestrator for `create` / `update` / `delete` annotation
* mutations. Owns the dance between PDFium calls, identity bookkeeping,
* revision bumping (only for structural ops), and the
* `AnnotationListMutationMeta` envelope every result type carries.
*
* Lives in `engine-services` (not the worker hosts) so the local Web
* Worker, the Node `worker_thread` server, and any future direct-thread
* embedding share the exact same code path. The only difference between
* them is the underlying `PdfRuntimeModule` (WASM vs native).
*
* Identity rules enforced here, locked with the user:
*   - `create` always uses `EPDFPage_CreateAnnot` (the fork helper that
*     creates an INDIRECT object) so new annotations are born durable.
*     If the fork helper ever returns a direct object, we throw — never
*     silently produce a weak annotation.
*   - `update` is non-structural. /NM is monotonic per annotation:
*       * already durable (objectNumber > 0 OR /NM present) -> NEVER touched.
*       * weak (no objectNumber, no /NM) -> stamp engine-generated UUID v4.
*     The patch type has no `nm` field, so the writer surface enforces
*     "callers cannot rename a stable id" at the type level. Updates do
*     not bump the revision.
*   - `delete` is subtype-agnostic. Three native fork helpers handle the
*     three ref kinds without round-tripping through index. For weak
*     deletes (`AnnotationStableId | null`-shaped result), we set
*     `deleted: null` so callers can detect that no durable id was
*     reportable.
*/
var AnnotationMutator = class {
	constructor(runtime, session, fonts) {
		this.runtime = runtime;
		this.session = session;
		this.fonts = fonts;
	}
	/** Build the per-write context handed to subtype writers: font resolver
	*  (FreeText `/DA`), doc/page pointers and binary resources (stamp). */
	writeContext(pagePtr, resources) {
		const fonts = this.fonts;
		return {
			...fonts ? { resolveRegisteredFontId: (key) => fonts.idFor(key) } : {},
			docPtr: this.session.requireDocPtr(),
			pagePtr,
			...resources ? { resources } : {}
		};
	}
	create(pageObjectNumber, draft, signal, actor, resources) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const subtypeCode = PDF_SUBTYPE_TO_CODE[draft.subtype];
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		try {
			this.ensureKnownWeakStateFromPage(pageObjectNumber, pagePtr);
			const writeCtx = this.writeContext(pagePtr, resources);
			preflightDraft(draft, writeCtx);
			const pageStateBefore = this.session.pageState(pageObjectNumber);
			throwIfAborted(signal);
			const annotPtr = fn.EPDFPage_CreateAnnot(pagePtr, subtypeCode);
			if (!annotPtr) throw new EngineError(EngineErrorCode.Unknown, `EPDFPage_CreateAnnot returned NULL for subtype '${draft.subtype}'`);
			let dto;
			let newObjNum;
			let newIndex;
			let linkedParentId = null;
			try {
				applyDraft(fn, mem, annotPtr, draft, writeCtx);
				if (draft.inReplyTo !== void 0 || draft.replyType !== void 0) linkedParentId = writeAnnotationRelationship(this.runtime, this.session, pagePtr, annotPtr, pageObjectNumber, {
					inReplyTo: draft.inReplyTo,
					replyType: draft.replyType
				});
				if (actor?.displayName) writeAnnotationAuthor(fn, mem, annotPtr, actor.displayName);
				writeAnnotationModified(fn, mem, annotPtr);
				applyEmbedMetadataOnCreate(fn, mem, annotPtr, actor);
				this.regenerateAppearance(annotPtr, pagePtr, draft.blendMode);
				newObjNum = fn.EPDFAnnot_GetObjectNumber(annotPtr);
				if (newObjNum <= 0) throw new EngineError(EngineErrorCode.Unknown, `EPDFPage_CreateAnnot produced a direct object (no objectNumber); fork helper invariant broken`);
				newIndex = fn.FPDFPage_GetAnnotIndex(pagePtr, annotPtr);
				if (newIndex < 0) throw new EngineError(EngineErrorCode.Unknown, `FPDFPage_GetAnnotIndex returned ${newIndex} for freshly created annotation`);
				dto = readAnnotationFromPtr(fn, mem, annotPtr, pageObjectNumber, newIndex, pageStateBefore.revision, readContextFor(this.session));
				joinWidgetFieldNumbers(this.runtime, this.session, [dto]);
			} finally {
				fn.FPDFPage_CloseAnnot(annotPtr);
			}
			const createdId = {
				kind: "objectNumber",
				value: newObjNum
			};
			if (linkedParentId) this.recordWeakStateFromPage(pageObjectNumber, pagePtr);
			const meta = computeMutationImpact({
				mutation: "create",
				pageStateBefore,
				pageStateAfter: this.session.pageState(pageObjectNumber),
				changed: linkedParentId ? [createdId, linkedParentId] : [createdId]
			});
			return {
				created: dto,
				meta
			};
		} finally {
			pool.release(pageObjectNumber);
		}
	}
	update(ref, patch, signal, actor, resources) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(ref.pageObjectNumber);
		let annotPtr = null;
		try {
			annotPtr = resolveAnnotPtr(this.runtime, this.session, pagePtr, ref);
			throwIfAborted(signal);
			const writeCtx = this.writeContext(pagePtr, resources);
			preflightPatch(patch, writeCtx);
			this.ensureKnownWeakStateFromPage(ref.pageObjectNumber, pagePtr);
			const pageStateBefore = this.session.pageState(ref.pageObjectNumber);
			throwIfAborted(signal);
			const stableId = this.captureOrStampStableId(annotPtr);
			const previousBlendMode = blendModeFromCode(fn.EPDFAnnot_GetBlendMode(annotPtr));
			const preIndex = fn.FPDFPage_GetAnnotIndex(pagePtr, annotPtr);
			if (preIndex < 0) throw new EngineError(EngineErrorCode.Unknown, `FPDFPage_GetAnnotIndex returned ${preIndex} before update`);
			const currentDto = readAnnotationFromPtr(fn, mem, annotPtr, ref.pageObjectNumber, preIndex, pageStateBefore.revision, readContextFor(this.session));
			applyPatch(fn, mem, annotPtr, patch, writeCtx);
			let linkedParentId = null;
			if (patch.inReplyTo !== void 0 || patch.replyType !== void 0) linkedParentId = writeAnnotationRelationship(this.runtime, this.session, pagePtr, annotPtr, ref.pageObjectNumber, {
				inReplyTo: patch.inReplyTo,
				replyType: patch.replyType
			});
			writeAnnotationModified(fn, mem, annotPtr);
			applyEmbedMetadataOnUpdate(fn, mem, annotPtr, actor);
			const impact = appearanceImpactOf(currentDto, patch);
			let appearance;
			if (impact === "inert" || impact === "translation" && fn.EPDFAnnot_HasAppearanceStream(annotPtr, APPEARANCE_MODE_NORMAL)) appearance = {
				action: "preserved",
				changed: false
			};
			else appearance = this.regenerateAppearance(annotPtr, pagePtr, patch.blendMode ?? previousBlendMode) ? {
				action: "regenerated",
				changed: true
			} : {
				action: "generation-unavailable",
				changed: impact === "regenerate"
			};
			fn.FPDFPage_CloseAnnot(annotPtr);
			annotPtr = null;
			annotPtr = resolveAnnotPtr(this.runtime, this.session, pagePtr, ref);
			const newIndex = fn.FPDFPage_GetAnnotIndex(pagePtr, annotPtr);
			if (newIndex < 0) throw new EngineError(EngineErrorCode.Unknown, `FPDFPage_GetAnnotIndex returned ${newIndex} after update`);
			const dto = readAnnotationFromPtr(fn, mem, annotPtr, ref.pageObjectNumber, newIndex, pageStateBefore.revision, readContextFor(this.session));
			joinWidgetFieldNumbers(this.runtime, this.session, [dto]);
			this.recordWeakStateFromPage(ref.pageObjectNumber, pagePtr);
			const meta = computeMutationImpact({
				mutation: "update",
				pageStateBefore,
				pageStateAfter: this.session.pageState(ref.pageObjectNumber),
				changed: linkedParentId ? [stableId, linkedParentId] : [stableId]
			});
			return {
				updated: dto,
				appearance,
				meta
			};
		} finally {
			if (annotPtr !== null) fn.FPDFPage_CloseAnnot(annotPtr);
			pool.release(ref.pageObjectNumber);
		}
	}
	/**
	* Attached widgets are views of a form field: deleting one here would
	* orphan the field's /Kids (the corruption class doc.forms.repair
	* exists to fix). Detach first, or use the forms-plane cascade.
	*/
	assertNotAttachedWidget(annotPtr) {
		const { fn } = this.runtime;
		if (fn.FPDFAnnot_GetSubtype(annotPtr) !== PdfAnnotationSubtypeCode.WIDGET) return;
		const annotObjectNumber = fn.EPDFAnnot_GetObjectNumber(annotPtr);
		if (resolveWidgetFieldObjectNumber(this.runtime, this.session, annotObjectNumber) === 0) return;
		throw new EngineError(EngineErrorCode.InvalidArg, "widget is attached to a form field - use doc.forms.detachWidget or doc.forms.deleteField");
	}
	delete(ref, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(ref.pageObjectNumber);
		let bumpRequested = false;
		try {
			this.ensureKnownWeakStateFromPage(ref.pageObjectNumber, pagePtr);
			const pageStateBefore = this.session.pageState(ref.pageObjectNumber);
			throwIfAborted(signal);
			let deleted;
			let ok = false;
			switch (ref.kind) {
				case "objectNumber": {
					const probe = fn.EPDFPage_GetAnnotByObjectNumber(pagePtr, ref.annotObjectNumber);
					if (!probe) throw new EngineError(EngineErrorCode.InvalidReference, `no annotation with object number ${ref.annotObjectNumber} on page ${ref.pageObjectNumber}`);
					try {
						this.assertNotAttachedWidget(probe);
					} finally {
						fn.FPDFPage_CloseAnnot(probe);
					}
					bumpRequested = true;
					ok = fn.EPDFPage_RemoveAnnotByObjectNumber(pagePtr, ref.annotObjectNumber);
					deleted = {
						kind: "objectNumber",
						value: ref.annotObjectNumber
					};
					break;
				}
				case "nm": {
					const namePtr = mem.writeU16String(ref.nm);
					try {
						const probe = fn.EPDFPage_GetAnnotByName(pagePtr, namePtr);
						if (!probe) throw new EngineError(EngineErrorCode.InvalidReference, `no annotation with /NM '${ref.nm}' on page ${ref.pageObjectNumber}`);
						try {
							this.assertNotAttachedWidget(probe);
						} finally {
							fn.FPDFPage_CloseAnnot(probe);
						}
						bumpRequested = true;
						ok = fn.EPDFPage_RemoveAnnotByName(pagePtr, namePtr);
					} finally {
						mem.free(namePtr);
					}
					deleted = {
						kind: "nm",
						value: ref.nm
					};
					break;
				}
				case "index": {
					this.session.validateRevision(ref.revision);
					const annotPtr = fn.FPDFPage_GetAnnot(pagePtr, ref.index);
					if (!annotPtr) throw new EngineError(EngineErrorCode.InvalidReference, `index ${ref.index} out of range on page ${ref.pageObjectNumber}`);
					let probedObjNum;
					let probedNm;
					try {
						probedObjNum = fn.EPDFAnnot_GetObjectNumber(annotPtr);
						probedNm = readAnnotString(fn, mem, annotPtr, "NM");
					} finally {
						fn.FPDFPage_CloseAnnot(annotPtr);
					}
					deleted = probedObjNum > 0 ? {
						kind: "objectNumber",
						value: probedObjNum
					} : probedNm !== null && probedNm.length > 0 ? {
						kind: "nm",
						value: probedNm
					} : null;
					bumpRequested = true;
					ok = fn.EPDFPage_RemoveAnnot(pagePtr, ref.index);
					break;
				}
			}
			if (!ok) throw new EngineError(EngineErrorCode.Unknown, `failed to remove annotation: ${ref.kind}`);
			this.session.bumpRevision(ref.pageObjectNumber);
			bumpRequested = false;
			this.recordWeakStateFromPage(ref.pageObjectNumber, pagePtr);
			const meta = computeMutationImpact({
				mutation: "delete",
				pageStateBefore,
				pageStateAfter: this.session.pageState(ref.pageObjectNumber),
				changed: deleted ? [deleted] : []
			});
			return {
				deleted,
				meta
			};
		} finally {
			if (bumpRequested) this.session.bumpRevision(ref.pageObjectNumber);
			pool.release(ref.pageObjectNumber);
		}
	}
	/**
	* Batch reorder of a contiguous block of annotations within a single
	* page's /Annots array. Symmetric with `pages.move()` for pages.
	*
	* Semantics (locked with the user, mirrors `EPDFPage_MoveAnnots`):
	*   - Each ref in `refs` is resolved to its current /Annots index.
	*     The block is detached, then re-inserted at `toIndex` in the
	*     post-removal index space, preserving caller-supplied order.
	*   - Single-annotation case is `move([ref], toIndex)`. There is no
	*     separate single-move path; one batch primitive serves both.
	*   - Atomic from the caller's perspective:
	*       * one revision bump per batch, regardless of `refs.length`.
	*       * one `AnnotationListMutationMeta` envelope.
	*       * if `EPDFPage_MoveAnnots` rejects (returns false) the page is
	*         untouched and we throw `InvalidArg` without bumping.
	*   - Identity strengthening: each weak ref in the batch (no
	*     `objectNumber`, no `/NM`) is opportunistically stamped with a
	*     fresh engine-generated UUID v4 BEFORE the move. So
	*     `meta.changed` always lists durable stable ids, and the moved
	*     DTOs come out durable. Same monotonic `/NM` rule as `update()`.
	*
	* Validation rules applied here BEFORE calling the helper, so callers
	* get clean errors instead of an opaque `false` return code:
	*   - `refs.length >= 1`.
	*   - All refs target the page identified by `pageObjectNumber`.
	*   - `toIndex >= 0` and `toIndex <= count - refs.length` (count is
	*     captured AFTER ref resolution, so the helper sees the same view).
	*   - Resolved indices have no duplicates.
	*
	* The `EPDFPage_MoveAnnots` helper itself enforces the same rules; the
	* up-front validation is purely for a usable error surface.
	*/
	move(pageObjectNumber, refs, toIndex, signal) {
		throwIfAborted(signal);
		if (refs.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "move requires at least one ref");
		if (toIndex < 0 || !Number.isInteger(toIndex)) throw new EngineError(EngineErrorCode.InvalidArg, `move toIndex must be a non-negative integer (got ${toIndex})`);
		for (const r of refs) if (r.pageObjectNumber !== pageObjectNumber) throw new EngineError(EngineErrorCode.InvalidArg, `move refs must all target page ${pageObjectNumber}; got ref on page ${r.pageObjectNumber}`);
		const { fn, mem } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		let bumpRequested = false;
		try {
			this.ensureKnownWeakStateFromPage(pageObjectNumber, pagePtr);
			const pageStateBefore = this.session.pageState(pageObjectNumber);
			throwIfAborted(signal);
			const fromIndices = new Array(refs.length);
			const stableIds = new Array(refs.length);
			for (let i = 0; i < refs.length; i++) {
				throwIfAborted(signal);
				const annotPtr = resolveAnnotPtr(this.runtime, this.session, pagePtr, refs[i]);
				try {
					const idx = fn.FPDFPage_GetAnnotIndex(pagePtr, annotPtr);
					if (idx < 0) throw new EngineError(EngineErrorCode.Unknown, `FPDFPage_GetAnnotIndex returned ${idx} during move resolution`);
					fromIndices[i] = idx;
					stableIds[i] = this.captureOrStampStableId(annotPtr);
				} finally {
					fn.FPDFPage_CloseAnnot(annotPtr);
				}
			}
			const seen = /* @__PURE__ */ new Set();
			for (const idx of fromIndices) {
				if (seen.has(idx)) throw new EngineError(EngineErrorCode.InvalidArg, `move refs resolve to duplicate /Annots index ${idx}`);
				seen.add(idx);
			}
			const postRemovalCount = fn.FPDFPage_GetAnnotCount(pagePtr) - fromIndices.length;
			if (toIndex > postRemovalCount) throw new EngineError(EngineErrorCode.InvalidArg, `move toIndex ${toIndex} out of range; post-removal count is ${postRemovalCount}`);
			const arrBytes = 4 * fromIndices.length;
			const arrPtr = mem.alloc(arrBytes);
			let ok;
			try {
				for (let i = 0; i < fromIndices.length; i++) mem.poke(arrPtr, "i32", fromIndices[i], 4 * i);
				bumpRequested = true;
				ok = fn.EPDFPage_MoveAnnots(pagePtr, arrPtr, fromIndices.length, toIndex);
			} finally {
				mem.free(arrPtr);
			}
			if (!ok) {
				bumpRequested = false;
				throw new EngineError(EngineErrorCode.InvalidArg, `EPDFPage_MoveAnnots rejected the request (toIndex=${toIndex}, fromIndices=[${fromIndices.join(",")}])`);
			}
			const bumpedRev = this.session.bumpRevision(pageObjectNumber);
			bumpRequested = false;
			const moved = new Array(fromIndices.length);
			for (let i = 0; i < fromIndices.length; i++) {
				throwIfAborted(signal);
				const newIdx = toIndex + i;
				const annotPtr = fn.FPDFPage_GetAnnot(pagePtr, newIdx);
				if (!annotPtr) throw new EngineError(EngineErrorCode.Unknown, `failed to re-read moved annotation at index ${newIdx}`);
				try {
					moved[i] = readAnnotationFromPtr(fn, mem, annotPtr, pageObjectNumber, newIdx, bumpedRev, readContextFor(this.session));
				} finally {
					fn.FPDFPage_CloseAnnot(annotPtr);
				}
			}
			this.recordWeakStateFromPage(pageObjectNumber, pagePtr);
			return {
				moved,
				meta: computeMutationImpact({
					mutation: "move",
					pageStateBefore,
					pageStateAfter: this.session.pageState(pageObjectNumber),
					changed: stableIds
				})
			};
		} finally {
			if (bumpRequested) this.session.bumpRevision(pageObjectNumber);
			pool.release(pageObjectNumber);
		}
	}
	/**
	* Bake (or re-bake) an annotation's `/AP` normal appearance stream from
	* its current dictionary properties using PDFium's native AP generator,
	* then flush the page content so the result is persisted into the
	* page/object tree. Same path the v2 engine used after every
	* create/update; runs for every writable subtype (shapes + text-markup).
	*
	* Best-effort: `EPDFAnnot_GenerateAppearance` returns false for subtypes
	* PDFium has no generator for (e.g. widgets), in which case the
	* annotation simply ships without a baked `/AP` and viewers synthesize
	* one — so a false return is not a hard error; it is surfaced to `update`
	* callers as the `generation-unavailable` appearance outcome. This step is
	* non-structural: it never shifts annotation indices or bumps revisions.
	*/
	regenerateAppearance(annotPtr, pagePtr, blendMode) {
		const { fn } = this.runtime;
		const ok = blendMode === void 0 ? fn.EPDFAnnot_GenerateAppearance(annotPtr) : fn.EPDFAnnot_GenerateAppearanceWithBlend(annotPtr, blendModeToCode(blendMode));
		fn.FPDFPage_GenerateContent(pagePtr);
		return ok;
	}
	captureOrStampStableId(annotPtr) {
		return captureOrStampStableId(this.runtime, annotPtr);
	}
	ensureKnownWeakStateFromPage(pageObjectNumber, pagePtr) {
		if (this.session.weakAnnotationState(pageObjectNumber).kind === "known") return;
		this.recordWeakStateFromPage(pageObjectNumber, pagePtr);
	}
	recordWeakStateFromPage(pageObjectNumber, pagePtr) {
		this.session.recordWeakFlag(pageObjectNumber, this.computeHasWeakAnnotations(pagePtr));
	}
	computeHasWeakAnnotations(pagePtr) {
		const { fn, mem } = this.runtime;
		const count = fn.FPDFPage_GetAnnotCount(pagePtr);
		if (count < 0) throw new EngineError(EngineErrorCode.Unknown, `FPDFPage_GetAnnotCount returned ${count} while computing weak annotations`);
		for (let i = 0; i < count; i++) {
			const annotPtr = fn.FPDFPage_GetAnnot(pagePtr, i);
			if (!annotPtr) continue;
			try {
				if (fn.EPDFAnnot_GetObjectNumber(annotPtr) > 0) continue;
				const nm = readAnnotString(fn, mem, annotPtr, "NM");
				if (nm === null || nm.length === 0) return true;
			} finally {
				fn.FPDFPage_CloseAnnot(annotPtr);
			}
		}
		return false;
	}
};
//#endregion
//#region src/features/pages/PagesFlattener.ts
const FLATTEN_FAIL = 0;
const FLATTEN_SUCCESS = 1;
const FLATTEN_NOTHING_TO_DO = 2;
/** Layer-safe page flattening. Content and annotation liveness change together. */
var PagesFlattener = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	flatten(pageObjectNumbers, usage, signal) {
		throwIfAborted(signal);
		requireUniquePages(pageObjectNumbers);
		for (const pageObjectNumber of pageObjectNumbers) this.session.recordByObjectNumber(pageObjectNumber);
		const results = [];
		const affected = /* @__PURE__ */ new Set();
		let stop = false;
		for (const pageObjectNumber of pageObjectNumbers) {
			if (stop || signal.aborted && affected.size > 0) {
				stop = true;
				results.push({
					pageObjectNumber,
					status: "skipped"
				});
				continue;
			}
			if (signal.aborted) throwIfAborted(signal);
			const pool = this.session.pagePool();
			const pagePtr = pool.acquire(pageObjectNumber);
			let code;
			try {
				code = this.runtime.fn.EPDFPage_Flatten(pagePtr, usage === "print" ? 1 : 0);
			} catch (error) {
				affected.add(pageObjectNumber);
				results.push({
					pageObjectNumber,
					status: "failed",
					error: serializeError(error)
				});
				stop = true;
				continue;
			} finally {
				pool.release(pageObjectNumber);
			}
			if (code === FLATTEN_NOTHING_TO_DO) results.push({
				pageObjectNumber,
				status: "unchanged"
			});
			else if (code === FLATTEN_SUCCESS) {
				affected.add(pageObjectNumber);
				results.push({
					pageObjectNumber,
					status: "applied"
				});
			} else {
				affected.add(pageObjectNumber);
				results.push({
					pageObjectNumber,
					status: "failed",
					error: serializeError(new EngineError(EngineErrorCode.Unknown, code === FLATTEN_FAIL ? "native page flatten failed after preflight" : `native page flatten returned unexpected code ${code}`))
				});
				stop = true;
			}
		}
		if (affected.size === 0) return {
			pageObjectNumbers,
			usage,
			results,
			meta: null
		};
		this.session.noteMutation();
		for (const pageObjectNumber of affected) {
			this.session.bumpRevision(pageObjectNumber);
			try {
				new AnnotationReader(this.runtime, this.session).list(pageObjectNumber, signal);
			} catch {}
		}
		return {
			pageObjectNumbers,
			usage,
			results,
			meta: {
				affectedPages: [...affected].map((pageObjectNumber) => this.session.pageState(pageObjectNumber)),
				cacheDelta: null
			}
		};
	}
};
function requireUniquePages(pageObjectNumbers) {
	if (pageObjectNumbers.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "pages.flatten requires at least one page");
	const seen = /* @__PURE__ */ new Set();
	for (const pageObjectNumber of pageObjectNumbers) {
		if (seen.has(pageObjectNumber)) throw new EngineError(EngineErrorCode.InvalidArg, `pages.flatten was given duplicate page object number ${pageObjectNumber}`);
		seen.add(pageObjectNumber);
	}
}
//#endregion
//#region src/features/redaction/RedactionApplier.ts
/**
* The destructive half of redaction (see `DocumentRedactionService` for the
* model and the layer trust boundary). Content and annotation liveness
* change together, exactly like {@link PagesFlattener} — this class mirrors
* its validate-then-apply boundary and ordered-batch semantics.
*
* Scope semantics:
*   - `pages`: every REDACT annotation on each listed page is applied; a
*     page with none is `unchanged`.
*   - `annotations`: exactly the referenced REDACT annotations are applied.
*     Every ref is resolved and subtype-checked BEFORE the first native
*     write; a non-REDACT ref rejects the whole call with `InvalidArg`.
*
* The per-page `removedAnnotationCount` is the native collateral count:
* annotations other than REDACT ones removed alongside the apply (popup
* cascades and detached widgets included).
*/
var RedactionApplier = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	apply(scope, signal) {
		throwIfAborted(signal);
		const plan = this.buildPlan(scope);
		for (const pageObjectNumber of plan.keys()) this.session.recordByObjectNumber(pageObjectNumber);
		this.preflight(plan, signal);
		const { fn } = this.runtime;
		const results = [];
		const affected = /* @__PURE__ */ new Set();
		let totalRemoved = 0;
		let stop = false;
		for (const [pageObjectNumber, refs] of plan) {
			if (stop || signal.aborted && affected.size > 0) {
				stop = true;
				results.push({
					pageObjectNumber,
					status: "skipped",
					removedAnnotationCount: 0
				});
				continue;
			}
			if (signal.aborted) throwIfAborted(signal);
			const pool = this.session.pagePool();
			const pagePtr = pool.acquire(pageObjectNumber);
			try {
				const outcome = refs === null ? this.applyAllOnPage(pagePtr) : this.applyRefsOnPage(pagePtr, refs);
				if (outcome.status === "applied") {
					fn.FPDFPage_GenerateContent(pagePtr);
					affected.add(pageObjectNumber);
					totalRemoved += outcome.removed;
				}
				results.push({
					pageObjectNumber,
					status: outcome.status,
					removedAnnotationCount: outcome.removed
				});
			} catch (error) {
				affected.add(pageObjectNumber);
				results.push({
					pageObjectNumber,
					status: "failed",
					removedAnnotationCount: 0,
					error: serializeError(error)
				});
				stop = true;
			} finally {
				pool.release(pageObjectNumber);
			}
		}
		if (affected.size === 0) return {
			scope,
			results,
			removedAnnotationCount: totalRemoved,
			meta: null
		};
		this.session.noteMutation();
		for (const pageObjectNumber of affected) {
			this.session.bumpRevision(pageObjectNumber);
			try {
				new AnnotationReader(this.runtime, this.session).list(pageObjectNumber, signal);
			} catch {}
		}
		const meta = {
			affectedPages: [...affected].map((pageObjectNumber) => this.session.pageState(pageObjectNumber)),
			cacheDelta: null
		};
		return {
			scope,
			results,
			removedAnnotationCount: totalRemoved,
			meta
		};
	}
	/**
	* Normalize the scope into an ordered per-page plan. `null` refs means
	* "every REDACT annotation on the page" (the `pages` scope).
	*/
	buildPlan(scope) {
		const plan = /* @__PURE__ */ new Map();
		if (scope.kind === "pages") {
			if (scope.pageObjectNumbers.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "redaction.apply requires at least one page");
			for (const pageObjectNumber of scope.pageObjectNumbers) {
				if (plan.has(pageObjectNumber)) throw new EngineError(EngineErrorCode.InvalidArg, `redaction.apply was given duplicate page object number ${pageObjectNumber}`);
				plan.set(pageObjectNumber, null);
			}
			return plan;
		}
		if (scope.refs.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "redaction.apply requires at least one annotation ref");
		for (const ref of scope.refs) {
			const existing = plan.get(ref.pageObjectNumber);
			if (existing === null) throw new EngineError(EngineErrorCode.InvalidArg, "mixed redaction scopes on one page");
			if (existing) existing.push(ref);
			else plan.set(ref.pageObjectNumber, [ref]);
		}
		for (const [pageObjectNumber, refs] of plan) if (refs !== null && refs.length > 1 && refs.some((r) => r.kind === "index")) throw new EngineError(EngineErrorCode.InvalidArg, `multiple redactions on page ${pageObjectNumber} cannot be addressed by positional 'index' refs — use objectNumber/nm refs, or one apply call per index ref`);
		return plan;
	}
	/**
	* Validate every ref BEFORE the first native write: it must resolve and
	* it must be a REDACT annotation. After this returns, the apply loop's
	* writes begin and per-page failures are recorded, not thrown.
	*/
	preflight(plan, signal) {
		const { fn } = this.runtime;
		for (const [pageObjectNumber, refs] of plan) {
			if (refs === null) continue;
			throwIfAborted(signal);
			const pool = this.session.pagePool();
			const pagePtr = pool.acquire(pageObjectNumber);
			try {
				for (const ref of refs) {
					const annotPtr = resolveAnnotPtr(this.runtime, this.session, pagePtr, ref);
					try {
						const subtype = subtypeFromCode(fn.FPDFAnnot_GetSubtype(annotPtr));
						if (subtype !== "redact") throw new EngineError(EngineErrorCode.InvalidArg, `redaction.apply ref on page ${pageObjectNumber} resolves to a '${subtype}' annotation, not a redact annotation`);
					} finally {
						fn.FPDFPage_CloseAnnot(annotPtr);
					}
				}
			} finally {
				pool.release(pageObjectNumber);
			}
		}
	}
	applyAllOnPage(pagePtr) {
		const { fn, mem } = this.runtime;
		return withScratch(mem, 4, (countPtr) => {
			if (!fn.EPDFPage_ApplyRedactions(pagePtr, countPtr)) return {
				status: "unchanged",
				removed: 0
			};
			return {
				status: "applied",
				removed: readI32(mem, countPtr) >>> 0
			};
		});
	}
	applyRefsOnPage(pagePtr, refs) {
		const { fn, mem } = this.runtime;
		let removed = 0;
		for (const ref of refs) {
			const annotPtr = resolveAnnotPtr(this.runtime, this.session, pagePtr, ref);
			try {
				if (!withScratch(mem, 4, (countPtr) => {
					const applied = fn.EPDFAnnot_ApplyRedaction(pagePtr, annotPtr, countPtr);
					if (applied) removed += readI32(mem, countPtr) >>> 0;
					return applied;
				})) throw new EngineError(EngineErrorCode.Unknown, "native redaction apply failed after preflight");
			} finally {
				fn.FPDFPage_CloseAnnot(annotPtr);
			}
		}
		return {
			status: "applied",
			removed
		};
	}
};
//#endregion
//#region src/features/text/PageTextReader.ts
/**
* Per-page slow-path text reader. Acquires a `pagePtr` from the
* `PagePtrPool`, opens a PDFium text page (`FPDFText_LoadPage`),
* extracts the full UTF-16 buffer with `FPDFText_GetText`, decodes,
* and releases everything in reverse order.
*
* Memory model:
*   - `count` is in UTF-16 code units (FPDFText's "char").
*   - The buffer needs `(count + 1) * 2` bytes (PDFium writes a
*     trailing NUL terminator).
*   - `mem.readU16String` walks the buffer up to the NUL and decodes
*     to a JS string, faithfully preserving surrogate pairs.
*
* Errors:
*   - `EPDFDoc_LoadPageByObjectNumber` returning 0 is `NotFound`
*     (raised by `PagePtrPool.acquire`).
*   - `FPDFText_LoadPage` returning 0 is `RuntimeUnavailable` — should
*     not happen on a healthy page; treat it as a runtime bug rather
*     than user error.
*/
var PageTextReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	read(pageObjectNumber, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		try {
			throwIfAborted(signal);
			const textPagePtr = fn.FPDFText_LoadPage(pagePtr);
			if (!textPagePtr) throw new EngineError(EngineErrorCode.RuntimeUnavailable, `FPDFText_LoadPage returned null for page object ${pageObjectNumber}`);
			try {
				throwIfAborted(signal);
				const charCount = fn.FPDFText_CountChars(textPagePtr);
				return {
					text: readPageText(fn, mem, textPagePtr, charCount),
					charCount: Math.max(charCount, 0)
				};
			} finally {
				fn.FPDFText_ClosePage(textPagePtr);
			}
		} finally {
			pool.release(pageObjectNumber);
		}
	}
};
function readPageText(fn, mem, textPagePtr, charCount) {
	if (charCount <= 0) return "";
	return withScratch(mem, (charCount + 1) * 2, (buf) => {
		if (fn.FPDFText_GetText(textPagePtr, 0, charCount, buf) <= 0) return "";
		return mem.readU16String(buf);
	});
}
//#endregion
//#region src/features/geometry/PageGeometryReader.ts
/**
* Geometry-only text layout reader.
*
* Emits geometry in PDF user space (y-up edges) — the canonical engine
* geometry. The viewer converts to content/view space via the page geometry
* matrix; this reader applies NO Y-flip or device transform.
*/
var PageGeometryReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	read(pageObjectNumber, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		try {
			throwIfAborted(signal);
			const textPagePtr = fn.FPDFText_LoadPage(pagePtr);
			if (!textPagePtr) throw new EngineError(EngineErrorCode.RuntimeUnavailable, `FPDFText_LoadPage returned null for page object ${pageObjectNumber}`);
			try {
				throwIfAborted(signal);
				const glyphCount = Math.max(fn.FPDFText_CountChars(textPagePtr), 0);
				const glyphs = [];
				for (let i = 0; i < glyphCount; i++) {
					throwIfAborted(signal);
					glyphs.push(this.readGlyphInfo(textPagePtr, i));
				}
				return { runs: this.buildRunsFromGlyphs(glyphs, textPagePtr) };
			} finally {
				fn.FPDFText_ClosePage(textPagePtr);
			}
		} finally {
			pool.release(pageObjectNumber);
		}
	}
	buildRunsFromGlyphs(glyphs, textPagePtr) {
		const { fn } = this.runtime;
		const runs = [];
		let current = null;
		let curObjPtr = null;
		let bounds = null;
		for (let i = 0; i < glyphs.length; i++) {
			const glyph = glyphs[i];
			const objPtr = fn.FPDFText_GetTextObject(textPagePtr, i);
			if (objPtr !== curObjPtr) {
				curObjPtr = objPtr;
				current = {
					rect: { ...glyph.looseBox },
					charStart: i,
					glyphs: [],
					fontSize: fn.FPDFText_GetFontSize(textPagePtr, i)
				};
				bounds = { ...glyph.looseBox };
				runs.push(current);
			}
			current.glyphs.push(toSlimGlyph(glyph));
			if (glyph.isEmpty) continue;
			bounds.left = Math.min(bounds.left, glyph.looseBox.left);
			bounds.bottom = Math.min(bounds.bottom, glyph.looseBox.bottom);
			bounds.right = Math.max(bounds.right, glyph.looseBox.right);
			bounds.top = Math.max(bounds.top, glyph.looseBox.top);
			current.rect = { ...bounds };
		}
		return runs;
	}
	readGlyphInfo(textPagePtr, charIndex) {
		const { fn, mem } = this.runtime;
		const rectPtr = mem.alloc(16);
		const tLeftPtr = mem.alloc(8);
		const tRightPtr = mem.alloc(8);
		const tBottomPtr = mem.alloc(8);
		const tTopPtr = mem.alloc(8);
		const ptrs = [
			rectPtr,
			tLeftPtr,
			tRightPtr,
			tBottomPtr,
			tTopPtr
		];
		try {
			if (!fn.FPDFText_GetLooseCharBox(textPagePtr, charIndex, rectPtr)) return emptyGlyph();
			const left = Number(mem.peek(rectPtr, "f32", 0));
			const top = Number(mem.peek(rectPtr, "f32", 4));
			const right = Number(mem.peek(rectPtr, "f32", 8));
			const bottom = Number(mem.peek(rectPtr, "f32", 12));
			if (left === right || top === bottom) return emptyGlyph();
			const glyph = { looseBox: normalizePdfRect({
				left,
				bottom,
				right,
				top
			}) };
			if (fn.FPDFText_GetCharBox(textPagePtr, charIndex, tLeftPtr, tRightPtr, tBottomPtr, tTopPtr)) {
				const tLeft = Number(mem.peek(tLeftPtr, "f64"));
				const tRight = Number(mem.peek(tRightPtr, "f64"));
				glyph.tightBox = normalizePdfRect({
					left: tLeft,
					bottom: Number(mem.peek(tBottomPtr, "f64")),
					right: tRight,
					top: Number(mem.peek(tTopPtr, "f64"))
				});
			}
			glyph.isSpace = fn.FPDFText_GetUnicode(textPagePtr, charIndex) === 32;
			return glyph;
		} finally {
			for (const ptr of ptrs) mem.free(ptr);
		}
	}
};
function emptyGlyph() {
	return {
		looseBox: {
			left: 0,
			bottom: 0,
			right: 0,
			top: 0
		},
		isEmpty: true
	};
}
function toSlimGlyph(glyph) {
	return {
		looseBox: glyph.looseBox,
		flags: glyph.isEmpty ? 2 : glyph.isSpace ? 1 : 0,
		...glyph.tightBox ? { tightBox: glyph.tightBox } : {}
	};
}
//#endregion
//#region src/features/render/PageRenderReader.ts
const FPDF_RENDER_ANNOT = 1;
var PageRenderReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	render(pageObjectNumber, options, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		try {
			throwIfAborted(signal);
			const pageWidth = fn.FPDF_GetPageWidthF(pagePtr);
			const pageHeight = fn.FPDF_GetPageHeightF(pagePtr);
			const target = resolveTarget(options.target, pageWidth, pageHeight);
			const rotation = options.rotation ?? 0;
			const viewport = options.viewport ?? {
				kind: "scale",
				scale: 1
			};
			let flags = 16;
			if (options.includeAnnotations ?? true) flags |= FPDF_RENDER_ANNOT;
			const raster = rasterize(this.runtime, {
				rect: target,
				page: {
					width: pageWidth,
					height: pageHeight
				},
				rotation,
				viewport,
				...options.maxOutputPixels !== void 0 ? { maxOutputPixels: options.maxOutputPixels } : {},
				background: options.background === "transparent" ? "transparent" : "white",
				draw: (bitmapPtr, matrixPtr, clipPtr) => {
					throwIfAborted(signal);
					fn.FPDF_RenderPageBitmapWithMatrix(bitmapPtr, pagePtr, matrixPtr, clipPtr, flags);
					throwIfAborted(signal);
					return true;
				}
			});
			if (!raster) throw new EngineError(EngineErrorCode.RuntimeUnavailable, `failed to render page object ${pageObjectNumber}`);
			return raster;
		} finally {
			pool.release(pageObjectNumber);
		}
	}
};
/** Resolve the render target to a normalized PDF-space rect (page box, or a sub-rect). */
function resolveTarget(target, pageWidth, pageHeight) {
	if (!target || target.kind === "page") return {
		left: 0,
		bottom: 0,
		right: pageWidth,
		top: pageHeight
	};
	const rect = normalizePdfRect(target.rect);
	if (rect.right <= rect.left || rect.top <= rect.bottom) throw new EngineError(EngineErrorCode.InvalidArg, "render rect must have positive area");
	return rect;
}
//#endregion
//#region src/features/attachments/AttachmentReader.ts
/**
* Read-only access to embedded files, at both of their homes: the
* document catalog's `/EmbeddedFiles` name tree (list / readFile by
* index) and a FileAttachment annotation's `/FS` (readAnnotationFile by
* ref). Pure READS over the session — no revision bumps, no layer
* artifacts (the `PagesExtractor` shape).
*
* Byte delivery mirrors the request's `path?`: absent → a standalone
* buffer for the transfer list (browser); present → the decoded file is
* streamed to `path` via `FPDF_FILEWRITE` (server), and only metadata
* rides the response.
*/
var AttachmentReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	/** Snapshot of the `/EmbeddedFiles` name tree, in tree (key-sorted) order. */
	list(signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const count = fn.FPDFDoc_GetAttachmentCount(docPtr);
		const items = [];
		for (let index = 0; index < count; index++) {
			const attachmentPtr = fn.FPDFDoc_GetAttachment(docPtr, index);
			if (!attachmentPtr) continue;
			const key = readAttachmentKey(fn, mem, docPtr, index) ?? "";
			items.push({
				...readAttachmentFileInfo(fn, mem, attachmentPtr),
				key,
				index
			});
		}
		return items;
	}
	/** Decode one document-level embedded file, addressed by key. */
	readFile(ref, path, maxDecodedBytes, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const index = resolveAttachmentIndex(fn, mem, docPtr, ref);
		if (index < 0) throw new EngineError(EngineErrorCode.NotFound, `no embedded file with key '${ref.key}' in the document`);
		const attachmentPtr = fn.FPDFDoc_GetAttachment(docPtr, index);
		if (!attachmentPtr) throw new EngineError(EngineErrorCode.Unknown, `embedded file with key '${ref.key}' resolved to index ${index} but could not be opened`);
		const info = readAttachmentFileInfo(fn, mem, attachmentPtr);
		return this.extract(attachmentPtr, info.name, info.mimeType, path, maxDecodedBytes);
	}
	/** Decode the file embedded in a FileAttachment annotation's `/FS`. */
	readAnnotationFile(pageObjectNumber, ref, path, maxDecodedBytes, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const pool = this.session.pagePool();
		const pagePtr = pool.acquire(pageObjectNumber);
		let annotPtr = null;
		try {
			annotPtr = resolveAnnotPtr(this.runtime, this.session, pagePtr, ref);
			const attachmentPtr = fn.FPDFAnnot_GetFileAttachment(annotPtr);
			if (!attachmentPtr) throw new EngineError(EngineErrorCode.InvalidArg, "annotation is not a file attachment or carries no embedded file (/FS)");
			const info = readAttachmentFileInfo(fn, mem, attachmentPtr);
			return this.extract(attachmentPtr, info.name, info.mimeType, path, maxDecodedBytes);
		} finally {
			if (annotPtr !== null) fn.FPDFPage_CloseAnnot(annotPtr);
			pool.release(pageObjectNumber);
		}
	}
	extract(attachmentPtr, name, mimeType, path, maxDecodedBytes) {
		if (path !== void 0) {
			const { size } = extractAttachmentToFile(this.runtime, attachmentPtr, path, maxDecodedBytes);
			return {
				name,
				...mimeType ? { mimeType } : {},
				size,
				path
			};
		}
		const { bytes, size } = extractAttachmentToBuffer(this.runtime, attachmentPtr, maxDecodedBytes);
		return {
			name,
			...mimeType ? { mimeType } : {},
			size,
			bytes
		};
	}
};
//#endregion
//#region src/features/attachments/AttachmentMutator.ts
/**
* Create/delete mutations over the document catalog's `/EmbeddedFiles`
* name tree — the doc-level sibling of `AnnotationMutator`, sharing the
* write path (`writeAttachmentFilePayload`) with the file-attachment
* annotation writer.
*
* Identity is the name-tree KEY (unique by construction), so unlike
* annotations there is no weak-ref/revision bookkeeping here. Note the
* tree is key-sorted: both mutations shift OTHER entries' indices; keys
* never move. Layer persistence (`finishMutation`) and event publication
* happen in the callers, exactly like every other mutation family.
*/
var AttachmentMutator = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	/**
	* Create an embedded file. `file.name` becomes the tree key (PDFium
	* writes key = /UF = /F); a duplicate key rejects with `InvalidArg`.
	* The result carries the read-back entry, `checksum`/`size`/dates
	* included.
	*/
	create(file, resources, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const resource = resources?.[file.resource];
		if (!resource) throw new EngineError(EngineErrorCode.InvalidArg, `attachment file references resource '${file.resource}' but no such binary payload accompanied the mutation`);
		if (file.name.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "attachment file requires a name");
		if (resolveAttachmentIndex(fn, mem, docPtr, {
			kind: "key",
			key: file.name
		}) !== -1) throw new EngineError(EngineErrorCode.InvalidArg, `an embedded file with key '${file.name}' already exists — keys are unique; rename and retry`);
		throwIfAborted(signal);
		const namePtr = mem.writeU16String(file.name);
		let attachmentPtr;
		try {
			attachmentPtr = fn.FPDFDoc_AddAttachment(docPtr, namePtr);
		} finally {
			mem.free(namePtr);
		}
		if (!attachmentPtr) throw new EngineError(EngineErrorCode.Unknown, "FPDFDoc_AddAttachment returned NULL");
		writeAttachmentFilePayload(fn, mem, attachmentPtr, docPtr, file, resource);
		const index = resolveAttachmentIndex(fn, mem, docPtr, {
			kind: "key",
			key: file.name
		});
		if (index < 0) throw new EngineError(EngineErrorCode.Unknown, "created attachment did not resolve by key after the write");
		return {
			created: {
				...readAttachmentFileInfo(fn, mem, attachmentPtr),
				key: file.name,
				index
			},
			cache: null
		};
	}
	/** Delete an embedded file by key. Unlinks the tree entry only. */
	delete(ref, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const index = resolveAttachmentIndex(fn, mem, docPtr, ref);
		if (index < 0) throw new EngineError(EngineErrorCode.NotFound, `no embedded file with key '${ref.key}' in the document`);
		if (!fn.FPDFDoc_DeleteAttachment(docPtr, index)) throw new EngineError(EngineErrorCode.Unknown, "FPDFDoc_DeleteAttachment returned false");
		return {
			deleted: ref,
			cache: null
		};
	}
};
//#endregion
//#region src/features/security/internal/buildSecurityInfo.ts
/**
* Single constructor for the "successfully inspected" shape of
* `DocumentSecurityProbeInfo`. Every path that actually resolves a
* permission word — the cold file probe, the live-session snapshot, and
* the password-permission check — funnels through here so the encrypted/
* none distinction, the revision-nulling rule, and the
* `pdfPermissionsAllAllowed` derivation stay defined in exactly one place.
*
* The password-blocked / unsupported / unknown shapes are NOT built here:
* those carry null permission words and only the cold probe can produce
* them, so they stay local to `SecurityReader.probeFile`.
*/
function buildSecurityInfo(input) {
	const encrypted = input.openedAs !== "none";
	return {
		encryptionState: encrypted ? "encrypted" : "none",
		encryptionRequiresPassword: false,
		securityHandlerRevision: encrypted ? input.securityHandlerRevision : null,
		pdfPermissionsBits: input.permissionsBits,
		pdfPermissionsAllAllowed: hasAllStandardSecurityPermissions(input.permissionsBits),
		pdfOpenedAs: input.openedAs,
		securityProbedAt: input.probedAt
	};
}
/**
* Mirrors `EPDF_PASSWORD_PERMISSION_*` in public/fpdfview.h:
* invalid=0, none=1, user=2, owner=3.
*/
function openedAsFromCode(code) {
	if (code === 3) return "owner";
	if (code === 2) return "user";
	return "none";
}
//#endregion
//#region src/features/security/SecurityReader.ts
const FPDF_ERR_PASSWORD = 4;
const FPDF_ERR_SECURITY = 5;
/**
* Reads document security/permission state. Three entry points:
*
*   - `probeFile` is the only doc-less path: it opens, inspects, and
*     closes its own document straight off a file. It is the intentional
*     exception to the package's `(runtime, session)` reader shape — it
*     answers "what is this file's security?" before any session exists.
*   - `readLiveSecurityInfo` / `checkPasswordPermissions` inspect an
*     already-open `DocumentSession`.
*/
var SecurityReader = class {
	constructor(runtime) {
		this.runtime = runtime;
		ensureInitialized(this.runtime);
	}
	probeFile(path, password = null) {
		const now = Date.now();
		let access = null;
		let docPtr = null;
		try {
			access = this.runtime.fileAccess.fromNodeFile(path);
			docPtr = this.runtime.fn.FPDF_LoadCustomDocument(access.ptr, password ?? "");
			if (!docPtr) {
				const err = this.runtime.fn.FPDF_GetLastError();
				if (err === FPDF_ERR_PASSWORD) return {
					encryptionState: "encrypted",
					encryptionRequiresPassword: true,
					securityHandlerRevision: null,
					pdfPermissionsBits: null,
					pdfPermissionsAllAllowed: null,
					pdfOpenedAs: null,
					securityProbedAt: now
				};
				if (err === FPDF_ERR_SECURITY) return {
					encryptionState: "unsupported",
					encryptionRequiresPassword: null,
					securityHandlerRevision: null,
					pdfPermissionsBits: null,
					pdfPermissionsAllAllowed: null,
					pdfOpenedAs: null,
					securityProbedAt: now
				};
				return unknownSecurity(now);
			}
			const encrypted = this.runtime.fn.EPDF_IsEncrypted(docPtr);
			const bits = normalizeU32(this.runtime.fn.FPDF_GetDocPermissions(docPtr));
			return buildSecurityInfo({
				openedAs: !encrypted ? "none" : this.runtime.fn.EPDF_IsOwnerUnlocked(docPtr) ? "owner" : "user",
				permissionsBits: bits,
				securityHandlerRevision: encrypted ? this.runtime.fn.FPDF_GetSecurityHandlerRevision(docPtr) : null,
				probedAt: now
			});
		} finally {
			if (docPtr) this.runtime.fn.FPDF_CloseDocument(docPtr);
			access?.close();
		}
	}
	/**
	* Snapshot of the live session's EFFECTIVE security state.
	* `FPDF_GetDocUserPermissions` reports the permission word in force
	* right now (e.g. after an owner unlock), as opposed to the declared
	* word the cold `probeFile` path reads.
	*/
	readLiveSecurityInfo(session) {
		const { fn } = this.runtime;
		const docPtr = session.requireDocPtr();
		const encrypted = fn.EPDF_IsEncrypted(docPtr);
		const bits = normalizeU32(fn.FPDF_GetDocUserPermissions(docPtr));
		return buildSecurityInfo({
			openedAs: !encrypted ? "none" : fn.EPDF_IsOwnerUnlocked(docPtr) ? "owner" : "user",
			permissionsBits: bits,
			securityHandlerRevision: encrypted ? fn.FPDF_GetSecurityHandlerRevision(docPtr) : null,
			probedAt: Date.now()
		});
	}
	/**
	* Verify a password against the live session and report the resulting
	* effective permissions. Throws `DocPasswordIncorrect` when the
	* password is rejected, or (in `'owner'` mode) when it unlocks only as
	* a user.
	*/
	checkPasswordPermissions(session, password, mode = "any") {
		const docPtr = session.requireDocPtr();
		const { mem, fn } = this.runtime;
		const kindPtr = mem.alloc(4);
		const userPermissionsPtr = mem.alloc(4);
		const effectivePermissionsPtr = mem.alloc(4);
		const revisionPtr = mem.alloc(4);
		try {
			mem.poke(kindPtr, "i32", 0);
			mem.poke(userPermissionsPtr, "i32", 0);
			mem.poke(effectivePermissionsPtr, "i32", 0);
			mem.poke(revisionPtr, "i32", 0);
			if (!fn.EPDF_CheckPasswordPermissions(docPtr, password, kindPtr, userPermissionsPtr, effectivePermissionsPtr, revisionPtr)) throw new EngineError(EngineErrorCode.DocPasswordIncorrect, "incorrect document password");
			const openedAs = openedAsFromCode(Number(mem.peek(kindPtr, "i32")));
			if (mode === "owner" && openedAs !== "owner") throw new EngineError(EngineErrorCode.DocPasswordIncorrect, "owner password required");
			return buildSecurityInfo({
				openedAs,
				permissionsBits: normalizeU32(Number(mem.peek(effectivePermissionsPtr, "i32"))),
				securityHandlerRevision: Number(mem.peek(revisionPtr, "i32")),
				probedAt: Date.now()
			});
		} finally {
			mem.free(revisionPtr);
			mem.free(effectivePermissionsPtr);
			mem.free(userPermissionsPtr);
			mem.free(kindPtr);
		}
	}
};
function unknownSecurity(now) {
	return {
		encryptionState: "unknown",
		encryptionRequiresPassword: null,
		securityHandlerRevision: null,
		pdfPermissionsBits: null,
		pdfPermissionsAllAllowed: null,
		pdfOpenedAs: null,
		securityProbedAt: now
	};
}
//#endregion
//#region src/features/save/internal/pdfSaveMode.ts
const FPDF_INCREMENTAL = 1;
const FPDF_NO_INCREMENTAL = 2;
function pdfSaveModeFlags(mode) {
	return mode === "incremental" ? FPDF_INCREMENTAL : FPDF_NO_INCREMENTAL;
}
function layerSaveError(status) {
	if (status === 1) return new EngineError(EngineErrorCode.DocOpenFailed, "layer artifact cannot be saved because append-only offsets exceed the supported range");
	return new EngineError(EngineErrorCode.DocOpenFailed, "failed to save layer artifact");
}
//#endregion
//#region src/features/save/DocumentSaver.ts
/**
* Owns every "serialize the open document" path for a `DocumentSession`.
* Two distinct outputs:
*
*   - Layer artifacts (`saveLayerArtifact*`) are the storage-optimized
*     `.layer` delta used by server-side persistence. Only valid for a
*     `layer` session.
*   - Standalone saves (`saveStandalone*`) export a self-contained PDF
*     view (base + applied delta) via `FPDF_SaveAsCopy` /
*     `EPDF_SaveDocumentToOwnedBuffer`, valid for any session kind.
*
* Lives in `engine-services` so the browser Worker and the Node
* `worker_thread` server share the exact same save code path.
*/
var DocumentSaver = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	saveLayerArtifact() {
		this.requireLayer();
		const { mem, fn } = this.runtime;
		const sizePtr = mem.alloc(4);
		const statusPtr = mem.alloc(4);
		let artifactPtr = null;
		try {
			mem.poke(sizePtr, "i32", 0);
			mem.poke(statusPtr, "i32", -1);
			artifactPtr = fn.EPDFLayer_SaveLayerArtifactToOwnedBuffer(this.session.requireDocPtr(), sizePtr, statusPtr);
			const status = Number(mem.peek(statusPtr, "i32"));
			const size = Number(mem.peek(sizePtr, "i32"));
			if (!artifactPtr || status !== 0 || size <= 0) throw layerSaveError(status);
			const bytes = mem.readBytes(artifactPtr, size);
			const buffer = new ArrayBuffer(bytes.byteLength);
			new Uint8Array(buffer).set(bytes);
			return {
				bytes: buffer,
				size
			};
		} finally {
			if (artifactPtr) fn.EPDF_FreeBuffer(artifactPtr);
			mem.free(statusPtr);
			mem.free(sizePtr);
		}
	}
	saveLayerArtifactToFile(path) {
		this.requireLayer();
		const { mem, fn } = this.runtime;
		const statusPtr = mem.alloc(4);
		const writer = this.runtime.fileWrite.toNodeFile(path);
		try {
			mem.poke(statusPtr, "i32", -1);
			const ok = fn.EPDFLayer_SaveLayerArtifact(this.session.requireDocPtr(), writer.ptr, statusPtr);
			const status = Number(mem.peek(statusPtr, "i32"));
			if (!ok || status !== 0) throw layerSaveError(status);
			return { path };
		} finally {
			writer.close();
			mem.free(statusPtr);
		}
	}
	saveStandaloneToBuffer(mode) {
		const { mem, fn } = this.runtime;
		const sizePtr = mem.alloc(4);
		let pdfPtr = null;
		try {
			mem.poke(sizePtr, "i32", 0);
			pdfPtr = fn.EPDF_SaveDocumentToOwnedBuffer(this.session.requireDocPtr(), pdfSaveModeFlags(mode), sizePtr);
			const size = Number(mem.peek(sizePtr, "i32"));
			if (!pdfPtr || size <= 0) throw new EngineError(EngineErrorCode.DocOpenFailed, "failed to save document");
			const bytes = mem.readBytes(pdfPtr, size);
			const buffer = new ArrayBuffer(bytes.byteLength);
			new Uint8Array(buffer).set(bytes);
			return {
				bytes: buffer,
				size
			};
		} finally {
			if (pdfPtr) fn.EPDF_FreeBuffer(pdfPtr);
			mem.free(sizePtr);
		}
	}
	saveStandaloneToFile(path, mode) {
		const writer = this.runtime.fileWrite.toNodeFile(path);
		try {
			if (!this.runtime.fn.FPDF_SaveAsCopy(this.session.requireDocPtr(), writer.ptr, pdfSaveModeFlags(mode))) throw new EngineError(EngineErrorCode.DocOpenFailed, "failed to save document");
			return { path };
		} finally {
			writer.close();
		}
	}
	requireLayer() {
		if (this.session.kind !== "layer") throw new EngineError(EngineErrorCode.InvalidArg, "document session is not a layer");
	}
};
//#endregion
//#region src/features/fonts/FontRegistrar.ts
/**
* Thread-confined runtime font registry binding.
*
* Wraps the `EPDFFont_*` C API on a single PDFium thread. PDFium's font
* registry is thread-local: a font registered here is visible only to this
* thread's page rendering and annotation-authoring calls, and the numeric
* `CFX_FontRegistry::FontId` it returns is a per-thread `next_font_id++`.
*
* That id is volatile, so we never let it cross the worker boundary. The host
* owns the `fontKey → id` map (passed in by reference) and every wire message
* references the stable `fontKey`. The FreeText writer resolves a key to the
* current thread's id through {@link idFor} at authoring time.
*
* Lives in `engine-services` (not a worker host) so the browser Web Worker,
* the Node `worker_thread` server, and any future direct-thread embedding
* share the exact same binding — only the `PdfRuntimeModule` (WASM vs native)
* differs.
*/
var FontRegistrar = class {
	constructor(runtime, ids) {
		this.runtime = runtime;
		this.ids = ids;
		_defineProperty(this, "fileHandles", []);
	}
	/**
	* Register a font from its bytes. Copies into the WASM heap, hands the
	* pointer to `EPDFFont_RegisterMemFont64` (which copies the bytes into the
	* native registry), then frees the scratch — so the heap holds exactly one
	* retained copy per registered font.
	*
	* Throws `InvalidArg` when the runtime rejects the font (corrupt, no glyphs).
	*/
	register(fontKey, familyName, weight, italic, bytes) {
		if (bytes.byteLength === 0) throw new EngineError(EngineErrorCode.InvalidArg, `empty font data: ${fontKey}`);
		const { mem, fn } = this.runtime;
		const ptr = mem.alloc(bytes.byteLength);
		try {
			mem.writeBytes(ptr, bytes);
			const id = fn.EPDFFont_RegisterMemFont64(familyName, weight, italic, ptr, bytes.byteLength);
			if (id === 0) throw new EngineError(EngineErrorCode.InvalidArg, `font registration failed (unloadable or no glyphs): ${fontKey}`);
			this.ids.set(fontKey, id);
		} finally {
			mem.free(ptr);
		}
	}
	/**
	* Register a font from a local file via `EPDFFont_RegisterFont`. The runtime
	* range-reads the file on demand (it is not loaded into the JS heap), and the
	* file-access handle is retained until {@link clear}. Native/node only.
	*
	* Throws `InvalidArg` when the runtime rejects the font (corrupt, no glyphs).
	*/
	registerFromNodeFile(fontKey, familyName, weight, italic, path) {
		const access = this.runtime.fileAccess.fromNodeFile(path);
		let id = 0;
		try {
			id = this.runtime.fn.EPDFFont_RegisterFont(familyName, weight, italic, access.ptr);
		} catch (error) {
			access.close();
			throw error;
		}
		if (id === 0) {
			access.close();
			throw new EngineError(EngineErrorCode.InvalidArg, `font registration failed (unloadable or no glyphs): ${fontKey} (${path})`);
		}
		this.ids.set(fontKey, id);
		this.fileHandles.push(access);
	}
	/**
	* Register host-owned startup fonts on this thread, in order, optionally
	* adding each to the fallback chain. Order matters: it fixes both the native
	* FontIds and the fallback precedence, so every worker thread that runs the
	* same list ends up identical.
	*/
	registerStartup(specs) {
		for (const spec of specs) {
			const italic = spec.italic === void 0 ? -1 : spec.italic ? 1 : 0;
			this.registerFromNodeFile(spec.key, spec.familyName ?? "", spec.weight ?? 0, italic, spec.path);
			if (spec.fallback) this.addFallback(spec.key);
		}
	}
	/** Append a registered font to the ordered glyph-fallback chain. */
	addFallback(fontKey) {
		const id = this.requireId(fontKey);
		if (!this.runtime.fn.EPDFFont_AddFallbackFont(id)) throw new EngineError(EngineErrorCode.InvalidArg, `addFallback failed: ${fontKey}`);
	}
	clearFallbacks() {
		this.runtime.fn.EPDFFont_ClearFallbackFonts();
	}
	/** Unregister every font and reset the fallback chain (clears the map too). */
	clear() {
		this.runtime.fn.EPDFFont_ClearRegisteredFonts();
		this.ids.clear();
		for (const access of this.fileHandles) access.close();
		this.fileHandles.length = 0;
	}
	/** Resolve a stable key to this thread's native FontId. Throws if unknown. */
	idFor(fontKey) {
		return this.requireId(fontKey);
	}
	requireId(fontKey) {
		const id = this.ids.get(fontKey);
		if (id === void 0) throw new EngineError(EngineErrorCode.InvalidArg, `font not registered on this thread: ${fontKey}`);
		return id;
	}
};
//#endregion
//#region src/features/forms/internal/resolveFieldRef.ts
/**
* Resolve a caller ref against the native model. `objectNumber` refs use
* the model's reverse index; `fqn` refs compare fully qualified names.
* Throws `NotFound` when nothing matches.
*/
function resolveFieldRef(runtime, model, ref) {
	const { fn } = runtime;
	if (ref.kind === "objectNumber") {
		const fieldIndex = fn.EPDFForm_GetFieldIndexByObjNum(model, ref.fieldObjectNumber);
		if (fieldIndex < 0) throw new EngineError(EngineErrorCode.NotFound, `form field not found: object ${ref.fieldObjectNumber}`);
		return {
			fieldIndex,
			fieldObjectNumber: ref.fieldObjectNumber
		};
	}
	const count = fn.EPDFForm_CountFields(model);
	for (let i = 0; i < count; i++) if ((readUtf16String(runtime.mem, (buf, cap) => fn.EPDFForm_GetFieldName(model, i, buf, cap)) ?? "") === ref.name) return {
		fieldIndex: i,
		fieldObjectNumber: fn.EPDFForm_GetFieldObjNum(model, i)
	};
	throw new EngineError(EngineErrorCode.NotFound, `form field not found: "${ref.name}"`);
}
//#endregion
//#region src/features/forms/FormReader.ts
/**
* Read side of the forms feature. All reads go through the session's
* version-keyed model cache, so repeated reads between mutations reuse
* one native snapshot build.
*/
var FormReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	snapshot(signal) {
		throwIfAborted(signal);
		const model = acquireFormModel(this.runtime, this.session);
		return readFormSnapshot(this.runtime, model);
	}
	field(ref, signal) {
		throwIfAborted(signal);
		const model = acquireFormModel(this.runtime, this.session);
		const { fieldIndex } = resolveFieldRef(this.runtime, model, ref);
		return readFieldAt(this.runtime, model, fieldIndex);
	}
	/**
	* Serialize form data. Exports read the reconciled view (recovered
	* fields included; on layers, promoted values win) straight from the
	* document — the model cache is not involved.
	*/
	exportData(format, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const call = format === "fdf" ? fn.EPDFForm_ExportFDF : fn.EPDFForm_ExportXFDF;
		const length = call(docPtr, NULL_PTR, 0, NULL_PTR, 0);
		if (length <= 0) throw new EngineError(EngineErrorCode.Unknown, `form ${format} export failed`);
		return withScratch(mem, length, (buf) => {
			if (call(docPtr, NULL_PTR, 0, buf, length) !== length) throw new EngineError(EngineErrorCode.Unknown, `form ${format} export failed`);
			const view = mem.readBytes(buf, length);
			const bytes = new ArrayBuffer(view.byteLength);
			new Uint8Array(bytes).set(view);
			return {
				format,
				bytes
			};
		});
	}
};
//#endregion
//#region src/features/forms/internal/authorWidget.ts
const WIDGET_SUBTYPE_CODE = 20;
/**
* Birth a widget through the annotation plane (EPDFPage_CreateAnnot -
* indirect, durable object number), place it, and style it with THE
* widget-plane writer (`applyWidgetStyle` - the same code the widget
* annotation kind uses for create/patch). Returns the widget's object
* number, ready for EPDFForm_AttachWidget adoption.
*/
function createUnattachedWidget(runtime, session, placement) {
	const { fn, mem } = runtime;
	const pool = session.pagePool();
	const pagePtr = pool.acquire(placement.pageObjectNumber);
	try {
		const annotPtr = fn.EPDFPage_CreateAnnot(pagePtr, WIDGET_SUBTYPE_CODE);
		if (annotPtr === NULL_PTR) throw new EngineError(EngineErrorCode.Unknown, "failed to create widget annotation");
		try {
			setAnnotRect(fn, mem, annotPtr, placement.rect);
			if (placement.appearance) applyWidgetStyle(fn, mem, annotPtr, placement.appearance);
			const widgetObjectNumber = fn.EPDFAnnot_GetObjectNumber(annotPtr);
			if (widgetObjectNumber <= 0) throw new EngineError(EngineErrorCode.Unknown, "widget annotation has no object number");
			return widgetObjectNumber;
		} finally {
			fn.FPDFPage_CloseAnnot(annotPtr);
		}
	} finally {
		pool.release(placement.pageObjectNumber);
	}
}
//#endregion
//#region src/features/forms/internal/fieldFlagBits.ts
/**
* /Ff bits writable through drafts and patches (1-based spec bit numbers).
* Family-DEFINING bits (Radio 1<<15, Pushbutton 1<<16, Combo 1<<17) are set
* once by EPDFForm_CreateField and immutable afterwards - the native
* setter rejects them.
*/
const FIELD_FLAG_BITS = {
	readOnly: 1,
	required: 2,
	noExport: 4,
	multiline: 4096,
	password: 8192,
	noToggleToOff: 16384,
	edit: 1 << 18,
	multiSelect: 1 << 21,
	comb: 1 << 24,
	radiosInUnison: 1 << 25
};
/**
* Fold optional booleans into set/clear masks: `true` sets the bit,
* `false` clears it, `undefined` leaves it alone.
*/
function flagMasks(flags) {
	let setBits = 0;
	let clearBits = 0;
	for (const [name, bit] of Object.entries(FIELD_FLAG_BITS)) {
		const value = flags[name];
		if (value === true) setBits |= bit;
		else if (value === false) clearBits |= bit;
	}
	return {
		setBits,
		clearBits
	};
}
//#endregion
//#region src/features/forms/internal/wideStringArray.ts
/**
* Marshal a `const FPDF_WIDESTRING*` array: encode every string into the
* runtime heap, write the pointer table with the runtime's pointer width
* (bigint pointers cannot be poked into the wasm 32-bit heap), run |body|,
* free everything.
*/
function withWideStringArray(runtime, values, body) {
	const { mem } = runtime;
	const stringPtrs = values.map((value) => mem.writeU16String(value));
	const isWasm = runtime.kind === "wasm";
	const ptrSize = isWasm ? 4 : 8;
	try {
		return withScratch(mem, Math.max(1, stringPtrs.length * ptrSize), (arrayPtr) => {
			stringPtrs.forEach((ptr, i) => isWasm ? mem.poke(arrayPtr, "i32", Number(ptr), i * 4) : mem.poke(arrayPtr, "i64", ptr, i * 8));
			return body(arrayPtr, stringPtrs.length);
		});
	} finally {
		for (let i = stringPtrs.length - 1; i >= 0; i--) mem.free(stringPtrs[i]);
	}
}
//#endregion
//#region src/features/forms/FormMutator.ts
const FAMILY_CODE = {
	checkbox: 2,
	radio: 3,
	text: 4,
	combobox: 5,
	listbox: 6
};
/** Widgets a single value write can touch; far above any real form. */
const CHANGED_WIDGETS_CAPACITY$1 = 256;
const REPAIR_BAKE_APPEARANCES = 1;
/**
* Value writes are non-structural: no page-list revision bumps, and the
* cloud layer computes its own cache delta server-side.
*/
const EMPTY_META$1 = {
	affectedPages: [],
	cacheDelta: null
};
const FAMILY_BY_VALUE_TYPE = {
	text: ["text"],
	toggle: ["checkbox", "radio"],
	choice: ["combobox", "listbox"]
};
/**
* Write side of the forms feature. Every method is a validate-then-apply
* transaction over the native EPDFForm_* write API: a failed call leaves
* the document untouched (on layers: nothing promoted), and each success
* bumps the session's mutation sequence so version-keyed caches rebuild.
*/
var FormMutator = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	setValue(ref, value, signal) {
		throwIfAborted(signal);
		const model = acquireFormModel(this.runtime, this.session);
		const resolved = resolveFieldRef(this.runtime, model, ref);
		this.assertWritable(resolved.fieldObjectNumber);
		const before = readFieldAt(this.runtime, model, resolved.fieldIndex);
		if (!FAMILY_BY_VALUE_TYPE[value.type].includes(before.family)) throw new EngineError(EngineErrorCode.InvalidArg, `value type '${value.type}' does not apply to a '${before.family}' field`);
		const changed = this.applyWrite(resolved.fieldObjectNumber, value);
		return this.readBack(resolved.fieldObjectNumber, changed);
	}
	reset(ref, signal) {
		throwIfAborted(signal);
		const model = acquireFormModel(this.runtime, this.session);
		const resolved = resolveFieldRef(this.runtime, model, ref);
		this.assertWritable(resolved.fieldObjectNumber);
		const changed = this.withChangedWidgets((buf, cap, countPtr) => this.runtime.fn.EPDFForm_ResetField(this.session.requireDocPtr(), resolved.fieldObjectNumber, buf, cap, countPtr));
		if (changed === null) throw new EngineError(EngineErrorCode.InvalidArg, "form field cannot be reset");
		return this.readBack(resolved.fieldObjectNumber, changed);
	}
	importData(data, format, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const bytes = new Uint8Array(data);
		if (bytes.byteLength === 0) throw new EngineError(EngineErrorCode.InvalidArg, "empty form data payload");
		const resolvedFormat = format ?? sniffFormat(bytes);
		const call = resolvedFormat === "fdf" ? fn.EPDFForm_ImportFDF : fn.EPDFForm_ImportXFDF;
		const docPtr = this.session.requireDocPtr();
		const counters = withScratchN(mem, [bytes.byteLength, 16], ([dataPtr, resultPtr]) => {
			mem.writeBytes(dataPtr, bytes);
			if (!call(docPtr, dataPtr, bytes.byteLength, resultPtr)) throw new EngineError(EngineErrorCode.InvalidArg, `payload is not valid ${resolvedFormat.toUpperCase()}`);
			return {
				fieldsTotal: Number(mem.peek(resultPtr, "i32", 0)),
				fieldsApplied: Number(mem.peek(resultPtr, "i32", 4)),
				fieldsSkipped: Number(mem.peek(resultPtr, "i32", 8)),
				widgetsChanged: Number(mem.peek(resultPtr, "i32", 12))
			};
		});
		this.session.noteMutation();
		const fresh = acquireFormModel(this.runtime, this.session);
		return {
			...counters,
			snapshot: readFormSnapshot(this.runtime, fresh),
			meta: EMPTY_META$1
		};
	}
	repair(bakeAppearances, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const flags = bakeAppearances ? REPAIR_BAKE_APPEARANCES : 0;
		const report = withScratch(mem, 24, (reportPtr) => {
			if (!fn.EPDFForm_Repair(this.session.requireDocPtr(), flags, reportPtr)) throw new EngineError(EngineErrorCode.Unknown, "form repair failed");
			return {
				acroformCreated: Number(mem.peek(reportPtr, "i32", 0)) !== 0,
				fieldsLinked: Number(mem.peek(reportPtr, "i32", 4)),
				widgetsLinked: Number(mem.peek(reportPtr, "i32", 8)),
				fieldsUnrepairable: Number(mem.peek(reportPtr, "i32", 12)),
				appearancesBaked: Number(mem.peek(reportPtr, "i32", 16)),
				needAppearancesCleared: Number(mem.peek(reportPtr, "i32", 20)) !== 0
			};
		});
		this.session.noteMutation();
		return {
			...report,
			meta: EMPTY_META$1
		};
	}
	/**
	* Create a field and (optionally) its widgets in one composed
	* transaction: native field creation, widget birth through the
	* annotation plane, adoption, then field-plane setters. A failure
	* mid-composition throws; earlier steps stay applied (the document is
	* never inconsistent - at worst a partially configured field exists).
	*/
	createField(draft, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const placements = this.placementsOf(draft);
		if (draft.family === "radio") {
			for (const placement of placements) if (!placement.onState || placement.onState === "Off") throw new EngineError(EngineErrorCode.InvalidArg, "every radio widget needs a non-\"Off\" onState");
		}
		const familyCode = FAMILY_CODE[draft.family];
		const namePtr = mem.writeU16String(draft.name);
		let fieldObjectNumber;
		try {
			fieldObjectNumber = fn.EPDFForm_CreateField(docPtr, familyCode, namePtr);
		} finally {
			mem.free(namePtr);
		}
		if (fieldObjectNumber <= 0) throw new EngineError(EngineErrorCode.InvalidArg, `cannot create field "${draft.name}" (name conflict or invalid)`);
		const { setBits, clearBits } = flagMasks(draft);
		if (setBits !== 0 || clearBits !== 0) fn.EPDFForm_SetFieldFlags(docPtr, fieldObjectNumber, setBits, clearBits);
		if ("options" in draft && draft.options) this.applyOptions(fieldObjectNumber, draft.options);
		if ("defaultValue" in draft && draft.defaultValue !== void 0) this.applyDefaultValues(fieldObjectNumber, [draft.defaultValue]);
		if ("maxLength" in draft && draft.maxLength !== void 0) {
			if (!fn.EPDFForm_SetFieldMaxLen(docPtr, fieldObjectNumber, draft.maxLength)) throw new EngineError(EngineErrorCode.InvalidArg, "maxLength rejected");
		}
		if (draft.alternateName !== void 0) this.applyWideSetter(fn.EPDFForm_SetFieldAlternateName, fieldObjectNumber, draft.alternateName, "alternate name rejected");
		if (draft.mappingName !== void 0) this.applyWideSetter(fn.EPDFForm_SetFieldMappingName, fieldObjectNumber, draft.mappingName, "mapping name rejected");
		for (const placement of placements) {
			const widgetObjectNumber = createUnattachedWidget(this.runtime, this.session, placement);
			const onState = draft.family === "radio" ? placement.onState : draft.family === "checkbox" ? placement.onState ?? "Yes" : "";
			if (!fn.EPDFForm_AttachWidget(docPtr, fieldObjectNumber, widgetObjectNumber, onState)) throw new EngineError(EngineErrorCode.Unknown, "widget adoption failed");
		}
		this.session.noteMutation();
		return { field: this.readBackField(fieldObjectNumber) };
	}
	updateField(ref, patch, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const model = acquireFormModel(this.runtime, this.session);
		const resolved = resolveFieldRef(this.runtime, model, ref);
		this.assertWritable(resolved.fieldObjectNumber);
		const before = readFieldAt(this.runtime, model, resolved.fieldIndex);
		if (before.family !== patch.family) throw new EngineError(EngineErrorCode.InvalidArg, `patch family '${patch.family}' does not match field family '${before.family}'`);
		const fieldObjectNumber = resolved.fieldObjectNumber;
		if (patch.name !== void 0) this.applyWideSetter(fn.EPDFForm_SetFieldName, fieldObjectNumber, patch.name, `cannot rename to "${patch.name}" (sibling conflict or invalid)`);
		const { setBits, clearBits } = flagMasks(patch);
		if (setBits !== 0 || clearBits !== 0) {
			if (!fn.EPDFForm_SetFieldFlags(docPtr, fieldObjectNumber, setBits, clearBits)) throw new EngineError(EngineErrorCode.InvalidArg, "flag update rejected");
		}
		if ("maxLength" in patch && patch.maxLength !== void 0) {
			if (!fn.EPDFForm_SetFieldMaxLen(docPtr, fieldObjectNumber, patch.maxLength ?? 0)) throw new EngineError(EngineErrorCode.InvalidArg, "maxLength rejected (current value exceeds it)");
		}
		if ("defaultValue" in patch && patch.defaultValue !== void 0) if (patch.defaultValue === null) {
			if (!fn.EPDFForm_RemoveFieldDefaultValue(docPtr, fieldObjectNumber)) throw new EngineError(EngineErrorCode.InvalidArg, "default value removal rejected");
		} else this.applyDefaultValues(fieldObjectNumber, [patch.defaultValue]);
		if (patch.alternateName !== void 0) this.applyWideSetter(fn.EPDFForm_SetFieldAlternateName, fieldObjectNumber, patch.alternateName ?? "", "alternate name rejected");
		if (patch.mappingName !== void 0) this.applyWideSetter(fn.EPDFForm_SetFieldMappingName, fieldObjectNumber, patch.mappingName ?? "", "mapping name rejected");
		if ("options" in patch && patch.options) this.applyOptions(fieldObjectNumber, patch.options);
		this.session.noteMutation();
		return { field: this.readBackField(fieldObjectNumber) };
	}
	/**
	* Delete a terminal field. Widgets are DETACHED here (they become inert
	* annotations); the worker host cascades their annotation deletion so
	* page /Annots bookkeeping flows through the annotation feature.
	*/
	deleteField(ref, signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const model = acquireFormModel(this.runtime, this.session);
		const resolved = resolveFieldRef(this.runtime, model, ref);
		this.assertWritable(resolved.fieldObjectNumber);
		const before = readFieldAt(this.runtime, model, resolved.fieldIndex);
		if (!withScratchN(mem, [256 * 4, 4], ([buf, countPtr]) => {
			mem.poke(countPtr, "i32", 0);
			return fn.EPDFForm_DeleteField(this.session.requireDocPtr(), resolved.fieldObjectNumber, buf, 256, countPtr);
		})) throw new EngineError(EngineErrorCode.InvalidArg, "field cannot be deleted");
		this.session.noteMutation();
		return {
			deletedFieldObjectNumber: resolved.fieldObjectNumber,
			detachedWidgets: before.widgets.map((w) => ({
				annotObjectNumber: w.annotObjectNumber,
				pageObjectNumber: w.pageObjectNumber
			}))
		};
	}
	attachWidget(ref, widget, onState, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const model = acquireFormModel(this.runtime, this.session);
		const resolved = resolveFieldRef(this.runtime, model, ref);
		this.assertWritable(resolved.fieldObjectNumber);
		const before = readFieldAt(this.runtime, model, resolved.fieldIndex);
		const toggle = before.family === "checkbox" || before.family === "radio";
		const state = toggle ? onState ?? (before.family === "checkbox" ? "Yes" : "") : "";
		if (toggle && (!state || state === "Off")) throw new EngineError(EngineErrorCode.InvalidArg, "attaching to a radio group needs a non-\"Off\" onState");
		if (!fn.EPDFForm_AttachWidget(this.session.requireDocPtr(), resolved.fieldObjectNumber, widget.annotObjectNumber, state)) throw new EngineError(EngineErrorCode.InvalidArg, "widget cannot be adopted (already attached, merged, or not a widget)");
		this.session.noteMutation();
		return { field: this.readBackField(resolved.fieldObjectNumber) };
	}
	detachWidget(ref, widget, signal) {
		throwIfAborted(signal);
		const { fn } = this.runtime;
		const model = acquireFormModel(this.runtime, this.session);
		const resolved = resolveFieldRef(this.runtime, model, ref);
		this.assertWritable(resolved.fieldObjectNumber);
		if (!fn.EPDFForm_DetachWidget(this.session.requireDocPtr(), resolved.fieldObjectNumber, widget.annotObjectNumber)) throw new EngineError(EngineErrorCode.InvalidArg, "widget is not attached to this field");
		this.session.noteMutation();
		return { field: this.readBackField(resolved.fieldObjectNumber) };
	}
	placementsOf(draft) {
		if (draft.family === "radio") return draft.widgets ?? [];
		return draft.widget ? [draft.widget] : [];
	}
	applyOptions(fieldObjectNumber, options) {
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		if (!withWideStringArray(this.runtime, options.map((o) => o.label), (labelsPtr) => withWideStringArray(this.runtime, options.map((o) => o.value), (exportsPtr, count) => fn.EPDFForm_SetFieldOptions(docPtr, fieldObjectNumber, labelsPtr, exportsPtr, count)))) throw new EngineError(EngineErrorCode.InvalidArg, "options rejected");
	}
	applyWideSetter(setter, fieldObjectNumber, value, errorMessage) {
		const { mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const valuePtr = mem.writeU16String(value);
		try {
			if (!setter(docPtr, fieldObjectNumber, valuePtr)) throw new EngineError(EngineErrorCode.InvalidArg, errorMessage);
		} finally {
			mem.free(valuePtr);
		}
	}
	applyDefaultValues(fieldObjectNumber, values) {
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		if (!withWideStringArray(this.runtime, values, (valuesPtr, count) => fn.EPDFForm_SetFieldDefaultValues(docPtr, fieldObjectNumber, valuesPtr, count))) throw new EngineError(EngineErrorCode.InvalidArg, "default value rejected");
	}
	readBackField(fieldObjectNumber) {
		const fresh = acquireFormModel(this.runtime, this.session);
		const fieldIndex = this.runtime.fn.EPDFForm_GetFieldIndexByObjNum(fresh, fieldObjectNumber);
		if (fieldIndex < 0) throw new EngineError(EngineErrorCode.Unknown, "form field vanished after write");
		return readFieldAt(this.runtime, fresh, fieldIndex);
	}
	assertWritable(fieldObjectNumber) {
		if (fieldObjectNumber === 0) throw new EngineError(EngineErrorCode.InvalidArg, "form field is stored as a direct object and cannot be written");
	}
	/** Dispatch the typed native write. Returns the changed widget objnums. */
	applyWrite(fieldObjectNumber, value) {
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const changed = this.withChangedWidgets((buf, cap, countPtr) => {
			switch (value.type) {
				default: throw new EngineError(EngineErrorCode.InvalidArg, "unknown form value type");
				case "text": {
					const textPtr = mem.writeU16String(value.value);
					try {
						return fn.EPDFForm_SetTextValue(docPtr, fieldObjectNumber, textPtr, buf, cap, countPtr);
					} finally {
						mem.free(textPtr);
					}
				}
				case "toggle": return fn.EPDFForm_SetToggle(docPtr, fieldObjectNumber, value.state ?? "", buf, cap, countPtr);
				case "choice": return withWideStringArray(this.runtime, value.values, (arrayPtr, count) => fn.EPDFForm_SetChoiceValues(docPtr, fieldObjectNumber, arrayPtr, count, buf, cap, countPtr));
			}
		});
		if (changed === null) throw new EngineError(EngineErrorCode.InvalidArg, "form value rejected (unknown toggle state, length limit, or non-option choice)");
		return changed;
	}
	/** Run a native write with the changed-widgets out buffer wired up. */
	withChangedWidgets(call) {
		const { mem } = this.runtime;
		return withScratchN(mem, [CHANGED_WIDGETS_CAPACITY$1 * 4, 4], ([buf, countPtr]) => {
			mem.poke(countPtr, "i32", 0);
			if (!call(buf, CHANGED_WIDGETS_CAPACITY$1, countPtr)) return null;
			const total = Number(mem.peek(countPtr, "i32"));
			const reported = Math.min(total, CHANGED_WIDGETS_CAPACITY$1);
			const changed = [];
			for (let i = 0; i < reported; i++) changed.push(Number(mem.peek(buf, "i32", i * 4)));
			return changed;
		});
	}
	/** Bump the session version, rebuild the model, and read the field back. */
	readBack(fieldObjectNumber, changedObjNums) {
		this.session.noteMutation();
		const fresh = acquireFormModel(this.runtime, this.session);
		const fieldIndex = this.runtime.fn.EPDFForm_GetFieldIndexByObjNum(fresh, fieldObjectNumber);
		if (fieldIndex < 0) throw new EngineError(EngineErrorCode.Unknown, "form field vanished after write");
		const field = readFieldAt(this.runtime, fresh, fieldIndex);
		const changedSet = new Set(changedObjNums);
		return {
			field,
			changedWidgets: field.widgets.filter((w) => changedSet.has(w.annotObjectNumber)).map((w) => ({
				annotObjectNumber: w.annotObjectNumber,
				pageObjectNumber: w.pageObjectNumber
			})),
			meta: EMPTY_META$1
		};
	}
};
/** `%FDF-…` payloads are FDF; anything starting with markup is XFDF. */
function sniffFormat(bytes) {
	for (let i = 0; i < Math.min(bytes.length, 64); i++) {
		const c = bytes[i];
		if (c === 239 || c === 187 || c === 191) continue;
		if (c === 32 || c === 9 || c === 10 || c === 13) continue;
		return c === 60 ? "xfdf" : "fdf";
	}
	return "fdf";
}
//#endregion
//#region src/features/forms/FormsEffectsApplier.ts
const CHANGED_WIDGETS_CAPACITY = 1024;
const EMPTY_META = {
	affectedPages: [],
	cacheDelta: null
};
const DISPLAY_CODE = {
	visible: 0,
	hidden: 1,
	noPrint: 2,
	noView: 3
};
/** Ordered, non-rollback-atomic sink for one committed client script run. */
var FormsEffectsApplier = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	apply(effects, signal) {
		throwIfAborted(signal);
		const preflightActionBudget = new ActionReadBudgetTracker();
		const resultActionBudget = new ActionReadBudgetTracker();
		const preflight = effects.map((effect) => this.preflight(effect, preflightActionBudget));
		const results = [];
		const allChangedWidgets = /* @__PURE__ */ new Map();
		let mustFinalize = false;
		let stop = false;
		for (let index = 0; index < preflight.length; index++) {
			const item = preflight[index];
			if (stop || signal.aborted && mustFinalize) {
				stop = true;
				results.push(emptyResult(index, "skipped"));
				continue;
			}
			if (signal.aborted) throwIfAborted(signal);
			if (item.error) {
				results.push({
					...emptyResult(index, "rejected"),
					fields: item.fields,
					error: item.error
				});
				continue;
			}
			let before;
			try {
				before = item.fieldObjectNumbers.map((objectNumber) => this.readField(objectNumber));
			} catch (error) {
				const status = mustFinalize ? "failed" : "rejected";
				results.push({
					...emptyResult(index, status),
					error: serializeError(error)
				});
				if (status === "failed") stop = true;
				continue;
			}
			if (isNoOp(item.effect, before)) {
				results.push({
					...emptyResult(index, "unchanged"),
					fields: before
				});
				continue;
			}
			try {
				const native = this.applyNative(item.effect, item.fieldObjectNumbers);
				if (!native.ok) {
					mustFinalize = true;
					this.session.noteMutation();
					const fields = this.readFieldsBestEffort(item.fieldObjectNumbers, resultActionBudget);
					const changedWidgets = widgetRefs(native.changedWidgetObjectNumbers, before, fields);
					rememberWidgets(allChangedWidgets, changedWidgets);
					results.push({
						index,
						status: "failed",
						fields,
						changedWidgets,
						error: serializeError(new EngineError(EngineErrorCode.Unknown, "native form effect failed after preflight"))
					});
					stop = true;
					continue;
				}
				if (!effectChangedState(item.effect, before, native.changedWidgetObjectNumbers)) {
					results.push({
						...emptyResult(index, "unchanged"),
						fields: before
					});
					continue;
				}
				mustFinalize = true;
				this.session.noteMutation();
				const fields = this.readFieldsBestEffort(item.fieldObjectNumbers, resultActionBudget);
				const changedWidgets = widgetRefs(native.changedWidgetObjectNumbers, before, fields);
				rememberWidgets(allChangedWidgets, changedWidgets);
				results.push({
					index,
					status: "applied",
					fields,
					changedWidgets
				});
			} catch (error) {
				mustFinalize = true;
				this.session.noteMutation();
				results.push({
					index,
					status: "failed",
					fields: this.readFieldsBestEffort(item.fieldObjectNumbers, resultActionBudget),
					changedWidgets: [],
					error: serializeError(error)
				});
				stop = true;
			}
		}
		return {
			results,
			changedWidgets: [...allChangedWidgets.values()],
			meta: mustFinalize ? EMPTY_META : null
		};
	}
	preflight(effect, actionBudget) {
		try {
			const refs = effect.kind === "reset" ? effect.refs : [effect.ref];
			if (refs.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "reset effect requires at least one field");
			const model = acquireFormModel(this.runtime, this.session);
			const resolved = refs.map((ref) => this.resolve(model, ref));
			const fields = resolved.map(({ fieldIndex }) => readFieldAt(this.runtime, model, fieldIndex, actionBudget));
			validateEffect(effect, fields);
			return {
				effect,
				fieldObjectNumbers: resolved.map(({ fieldObjectNumber }) => fieldObjectNumber),
				fields
			};
		} catch (error) {
			return {
				effect,
				fieldObjectNumbers: [],
				fields: [],
				error: serializeError(error)
			};
		}
	}
	resolve(model, ref) {
		const resolved = resolveFieldRef(this.runtime, model, ref);
		if (resolved.fieldObjectNumber <= 0) throw new EngineError(EngineErrorCode.InvalidReference, "direct-object form fields cannot be mutated");
		return resolved;
	}
	readField(fieldObjectNumber, actionBudget = new ActionReadBudgetTracker()) {
		const model = acquireFormModel(this.runtime, this.session);
		const index = this.runtime.fn.EPDFForm_GetFieldIndexByObjNum(model, fieldObjectNumber);
		if (index < 0) throw new EngineError(EngineErrorCode.InvalidReference, "form field disappeared");
		return readFieldAt(this.runtime, model, index, actionBudget);
	}
	readFieldsBestEffort(fieldObjectNumbers, actionBudget) {
		const fields = [];
		for (const objectNumber of fieldObjectNumbers) try {
			fields.push(this.readField(objectNumber, actionBudget));
		} catch {}
		return fields;
	}
	applyNative(effect, fieldObjectNumbers) {
		if (effect.kind === "reset") {
			const changed = [];
			for (const objectNumber of fieldObjectNumbers) {
				const result = this.withChangedWidgets((buf, cap, countPtr) => this.runtime.fn.EPDFForm_ResetField(this.session.requireDocPtr(), objectNumber, buf, cap, countPtr));
				changed.push(...result.changedWidgetObjectNumbers);
				if (!result.ok) return {
					ok: false,
					changedWidgetObjectNumbers: changed
				};
			}
			return {
				ok: true,
				changedWidgetObjectNumbers: changed
			};
		}
		const objectNumber = fieldObjectNumbers[0];
		switch (effect.kind) {
			case "setValue": return this.applyValue(objectNumber, effect.value);
			case "setDisplay": return this.withChangedWidgets((buf, cap, countPtr) => this.runtime.fn.EPDFForm_SetFieldDisplay(this.session.requireDocPtr(), objectNumber, DISPLAY_CODE[effect.display], buf, cap, countPtr));
			case "setAppearanceText": {
				const textPtr = this.runtime.mem.writeU16String(effect.text);
				try {
					return this.withChangedWidgets((buf, cap, countPtr) => this.runtime.fn.EPDFForm_SetFieldAppearanceText(this.session.requireDocPtr(), objectNumber, textPtr, buf, cap, countPtr));
				} finally {
					this.runtime.mem.free(textPtr);
				}
			}
		}
	}
	applyValue(fieldObjectNumber, value) {
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		return this.withChangedWidgets((buf, cap, countPtr) => {
			if (value.type === "toggle") return fn.EPDFForm_SetToggle(docPtr, fieldObjectNumber, value.state ?? "", buf, cap, countPtr);
			if (value.type === "choice") return withWideStringArray(this.runtime, value.values, (valuesPtr, count) => fn.EPDFForm_SetChoiceValues(docPtr, fieldObjectNumber, valuesPtr, count, buf, cap, countPtr));
			const valuePtr = mem.writeU16String(value.value);
			try {
				return fn.EPDFForm_SetTextValue(docPtr, fieldObjectNumber, valuePtr, buf, cap, countPtr);
			} finally {
				mem.free(valuePtr);
			}
		});
	}
	withChangedWidgets(call) {
		const { mem } = this.runtime;
		return withScratchN(mem, [CHANGED_WIDGETS_CAPACITY * 4, 4], ([buf, countPtr]) => {
			mem.poke(countPtr, "i32", 0);
			const ok = call(buf, CHANGED_WIDGETS_CAPACITY, countPtr);
			const count = Math.min(Math.max(0, Number(mem.peek(countPtr, "i32"))), CHANGED_WIDGETS_CAPACITY);
			const changedWidgetObjectNumbers = [];
			for (let index = 0; index < count; index++) changedWidgetObjectNumbers.push(Number(mem.peek(buf, "i32", index * 4)));
			return {
				ok,
				changedWidgetObjectNumbers
			};
		});
	}
};
function validateEffect(effect, fields) {
	if (effect.kind === "reset") {
		for (const field of fields) if (field.family === "pushbutton" || field.family === "signature") throw new EngineError(EngineErrorCode.InvalidArg, `${field.family} fields cannot be reset`);
		return;
	}
	const field = fields[0];
	if (effect.kind === "setAppearanceText") {
		if (field.family !== "text" && field.family !== "combobox") throw new EngineError(EngineErrorCode.InvalidArg, `appearance text does not apply to a ${field.family} field`);
		return;
	}
	if (effect.kind === "setDisplay") return;
	validateValue(field, effect.value);
}
function validateValue(field, value) {
	if (value.type === "text") {
		if (field.family !== "text") mismatch(field, value);
		return;
	}
	if (value.type === "toggle") {
		if (field.family !== "checkbox" && field.family !== "radio") mismatch(field, value);
		if (value.state !== null && !field.widgets.some((widget) => widget.onState === value.state)) throw new EngineError(EngineErrorCode.InvalidArg, "unknown toggle appearance state");
		return;
	}
	if (field.family !== "combobox" && field.family !== "listbox") mismatch(field, value);
	if (new Set(value.values).size !== value.values.length) throw new EngineError(EngineErrorCode.InvalidArg, "choice values must not contain duplicates");
	if (field.family === "combobox" && value.values.length > 1) throw new EngineError(EngineErrorCode.InvalidArg, "combo boxes accept at most one value");
	if (field.family === "listbox" && !field.multiSelect && value.values.length > 1) throw new EngineError(EngineErrorCode.InvalidArg, "single-select list boxes accept one value");
	const optionValues = new Set(field.options.map((option) => option.value));
	if (!(field.family === "combobox" && field.edit && value.values.length === 1) && value.values.some((entry) => !optionValues.has(entry))) throw new EngineError(EngineErrorCode.InvalidArg, "choice value is not a field option");
}
function mismatch(field, value) {
	throw new EngineError(EngineErrorCode.InvalidArg, `value type '${value.type}' does not apply to a '${field.family}' field`);
}
function isNoOp(effect, fields) {
	if (effect.kind === "setAppearanceText") return fields[0].widgets.length === 0;
	if (effect.kind === "setDisplay") return fields[0].widgets.length === 0;
	if (effect.kind === "reset") return fields.every((field) => valueEntriesEqual(field.valueEntry, field.defaultValueEntry));
	const field = fields[0];
	const value = effect.value;
	if (value.type === "text" && field.family === "text") return field.value === value.value;
	if (value.type === "toggle" && (field.family === "checkbox" || field.family === "radio")) return field.widgets.every((widget) => widget.checked === (widget.onState === value.state));
	if (value.type === "choice" && field.family === "combobox") return value.values.length === 0 ? field.value === "" : field.value === value.values[0];
	if (value.type === "choice" && field.family === "listbox") return arraysEqual(field.selectedValues, value.values);
	return false;
}
function effectChangedState(effect, before, changedWidgetObjectNumbers) {
	if (effect.kind === "setDisplay" || effect.kind === "setAppearanceText") return changedWidgetObjectNumbers.length > 0;
	return !isNoOp(effect, before) || changedWidgetObjectNumbers.length > 0;
}
function valueEntriesEqual(left, right) {
	if (left.kind !== right.kind) return false;
	if (left.kind === "scalar" && right.kind === "scalar") return left.value === right.value;
	if (left.kind === "array" && right.kind === "array") return arraysEqual(left.values, right.values);
	return true;
}
function arraysEqual(left, right) {
	return left.length === right.length && left.every((entry, index) => entry === right[index]);
}
function widgetRefs(objectNumbers, before, after) {
	const byObjectNumber = /* @__PURE__ */ new Map();
	for (const field of [...before, ...after]) for (const widget of field.widgets) byObjectNumber.set(widget.annotObjectNumber, widget);
	return [...new Set(objectNumbers)].map((objectNumber) => byObjectNumber.get(objectNumber)).filter((widget) => widget !== void 0).map(({ annotObjectNumber, pageObjectNumber }) => ({
		annotObjectNumber,
		pageObjectNumber
	}));
}
function rememberWidgets(target, widgets) {
	for (const widget of widgets) target.set(`${widget.pageObjectNumber}:${widget.annotObjectNumber}`, widget);
}
function emptyResult(index, status) {
	return {
		index,
		status,
		fields: [],
		changedWidgets: []
	};
}
//#endregion
//#region src/features/search/internal/pageCorpusCache.ts
/**
* Per-session, per-page search corpus: the page's extracted text plus its
* DEFAULT fold (the one literal queries with default options search).
* This is the local engine's in-memory equivalent of the server's corpus
* artifacts — same fold version, same shape, built lazily on first search
* and reused across slices and re-queries.
*
* Version-keyed on `DocumentSession.mutationSeq()` per PAGE (not per
* session): a form fill or annotation edit bumps the sequence, and only
* the pages actually re-read after that pay the re-extraction — untouched
* cache entries for other pages are refreshed lazily as they're revisited.
*
* Capped: search touches every page of arbitrarily large documents, so
* entries evict in insertion order past `MAX_CACHED_PAGES` (text of ~500
* pages ≈ a few MB — bounded regardless of document size).
*/
const MAX_CACHED_PAGES = 512;
const cache = /* @__PURE__ */ new WeakMap();
function acquirePageCorpus(runtime, session, pageObjectNumber, signal) {
	const seq = session.mutationSeq();
	let pages = cache.get(session);
	if (!pages) {
		pages = /* @__PURE__ */ new Map();
		cache.set(session, pages);
	}
	const hit = pages.get(pageObjectNumber);
	if (hit && hit.seq === seq) return hit.folded;
	const text = new PageTextReader(runtime, session).read(pageObjectNumber, signal).text;
	const folded = foldText(text);
	pages.delete(pageObjectNumber);
	pages.set(pageObjectNumber, {
		seq,
		folded
	});
	if (pages.size > MAX_CACHED_PAGES) {
		const oldest = pages.keys().next().value;
		if (oldest !== void 0) pages.delete(oldest);
	}
	return folded;
}
//#endregion
//#region src/features/search/internal/searchCursor.ts
/** Stable identity for query + mode (option order pinned by hand). */
function searchQueryKey(query, mode) {
	return JSON.stringify([
		query.regex ? "r" : "l",
		query.text,
		query.matchCase ? 1 : 0,
		query.matchDiacritics ? 1 : 0,
		query.wholeWord ? 1 : 0,
		mode
	]);
}
function encodeSearchCursor(state) {
	return JSON.stringify(state);
}
function decodeSearchCursor(request, cursor, expectedSeq, expectedKey) {
	let state;
	try {
		state = JSON.parse(cursor);
	} catch {
		throw new EngineError(EngineErrorCode.InvalidArg, "malformed search cursor");
	}
	if (state?.v !== 1 || typeof state.scanned !== "number" || typeof state.key !== "string") throw new EngineError(EngineErrorCode.InvalidArg, "malformed search cursor");
	if (state.key !== expectedKey) throw new EngineError(EngineErrorCode.InvalidArg, "search cursor belongs to a different query — restart the search");
	if (state.seq !== expectedSeq) throw new EngineError(EngineErrorCode.InvalidArg, "document changed since this search cursor was issued — restart the search", { details: {
		cursorSeq: state.seq,
		currentSeq: expectedSeq
	} });
	if (request.startPage !== void 0 && request.startPage !== state.start) throw new EngineError(EngineErrorCode.InvalidArg, "startPage conflicts with the cursor — omit startPage when resuming");
	return state;
}
//#endregion
//#region src/features/search/SearchReader.ts
/**
* Per-slice budget defaults and ceilings. The ceiling is the worker's
* self-defense: one `query()` call is one synchronous stretch of native
* work, and no request — whatever budget it asks for — may hold the
* worker longer than ~a few hundred pages. Callers wanting more issue
* more slices; the cursor makes that cheap.
*/
const DEFAULT_MAX_PAGES = 64;
const CEILING_MAX_PAGES = 256;
const DEFAULT_MAX_MATCHES = 256;
const CEILING_MAX_MATCHES = 1024;
/**
* The whole search pipeline for one budgeted slice, running inside the
* trust boundary (worker locally, server process in the cloud):
*
*   extract — page text via the session corpus cache (version-keyed on
*             `mutationSeq`, so results always reflect the CURRENT layer
*             view — text a redaction removed is unfindable),
*   match   — the pure engine-core matcher (identical code on every
*             engine; parity by construction),
*   anchor  — geometry read only for pages that actually matched, rects
*             via the selection line-merge (one rect per visual line).
*
* Scope gating (`doc.text.search`, `doc.text.copy` for `'full'`) is the
* caller's job — this reader assumes an authorized request.
*/
var SearchReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	query(request, signal) {
		throwIfAborted(signal);
		const query = request.query;
		const mode = request.mode ?? "full";
		const valid = validateSearchQuery(query);
		if (!valid.ok) throw new EngineError(EngineErrorCode.InvalidArg, `invalid search query (${valid.issue}): ${valid.message}`);
		const records = this.session.allRecords();
		const totalPages = records.length;
		if (!query.regex && foldText(query.text).folded.trim().length === 0) return {
			matches: [],
			nextCursor: null,
			scannedPages: 0,
			totalPages
		};
		const seq = this.session.mutationSeq();
		const key = searchQueryKey(query, mode);
		let start = 0;
		let scanned = 0;
		if (request.cursor !== void 0) {
			const state = decodeSearchCursor(request, request.cursor, seq, key);
			start = state.start;
			scanned = state.scanned;
		} else {
			if (request.startPage !== void 0) {
				this.session.recordByObjectNumber(request.startPage);
				start = request.startPage;
			}
			if (request.skip !== void 0) scanned = Math.min(Math.max(0, Math.floor(request.skip)), totalPages);
		}
		let order = records;
		if (start !== 0) {
			const at = records.findIndex((r) => r.pageObjectNumber === start);
			order = records.slice(at).concat(records.slice(0, at));
		}
		const maxPages = clamp(request.budget?.maxPages, DEFAULT_MAX_PAGES, CEILING_MAX_PAGES);
		const maxMatches = clamp(request.budget?.maxMatches, DEFAULT_MAX_MATCHES, CEILING_MAX_MATCHES);
		const matches = [];
		let pagesThisSlice = 0;
		while (scanned < order.length && pagesThisSlice < maxPages && matches.length < maxMatches) {
			throwIfAborted(signal);
			const pon = order[scanned].pageObjectNumber;
			const corpus = acquirePageCorpus(this.runtime, this.session, pon, signal);
			let ranges;
			if (query.regex) ranges = matchRegex(corpus.original, query);
			else if (query.matchCase || query.matchDiacritics) ranges = matchLiteral(foldText(corpus.original, foldOptionsFor(query)), query);
			else ranges = matchLiteral(corpus, query);
			if (ranges.length > 0) {
				const geometry = new PageGeometryReader(this.runtime, this.session).read(pon, signal);
				for (const range of ranges) matches.push({
					pageObjectNumber: pon,
					charStart: range.start,
					charCount: range.length,
					rects: searchRectsForRange(geometry, range.start, range.length),
					...mode === "full" ? { snippet: buildSnippet(corpus.original, range) } : {}
				});
			}
			scanned++;
			pagesThisSlice++;
		}
		return {
			matches,
			nextCursor: scanned < order.length ? encodeSearchCursor({
				v: 1,
				seq,
				key,
				start,
				scanned
			}) : null,
			scannedPages: scanned,
			totalPages
		};
	}
};
function clamp(requested, fallback, ceiling) {
	if (requested === void 0 || !Number.isFinite(requested)) return fallback;
	return Math.max(1, Math.min(Math.floor(requested), ceiling));
}
//#endregion
//#region src/features/actions/DocumentActionsReader.ts
const DOCUMENT_ACTION_EVENTS = [
	["willClose", 0],
	["willSave", 1],
	["didSave", 2],
	["willPrint", 3],
	["didPrint", 4]
];
/** Read catalog-owned actions. Extraction only; no action is executed. */
var DocumentActionsReader = class {
	constructor(runtime, session) {
		this.runtime = runtime;
		this.session = session;
	}
	read(signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const budget = new ActionReadBudgetTracker();
		const count = fn.FPDFDoc_GetJavaScriptActionCount(docPtr);
		if (count < 0) throw new EngineError(EngineErrorCode.MalformedPdf, "failed to enumerate PDF name-tree scripts");
		const nameTreeScripts = [];
		for (let index = 0; index < count; index++) {
			throwIfAborted(signal);
			const namedPtr = fn.FPDFDoc_GetJavaScriptAction(docPtr, index);
			if (namedPtr === NULL_PTR) throw new EngineError(EngineErrorCode.MalformedPdf, "invalid PDF name-tree script entry", { details: { index } });
			let name;
			try {
				name = readUtf16String(mem, (buf, capacity) => fn.FPDFJavaScriptAction_GetName(namedPtr, buf, capacity)) ?? "";
			} finally {
				fn.FPDFDoc_CloseJavaScriptAction(namedPtr);
			}
			const action = requireAction(readActionModel(fn, mem, fn.EPDFDoc_GetNamedJavaScriptActionModel(docPtr, index), budget), "name-tree script", index);
			nameTreeScripts.push({
				name,
				action
			});
		}
		const openAction = readActionModel(fn, mem, fn.EPDFDoc_GetOpenActionModel(docPtr), budget);
		const additional = {};
		for (const [key, event] of DOCUMENT_ACTION_EVENTS) {
			throwIfAborted(signal);
			const action = readActionModel(fn, mem, fn.EPDFDoc_GetAdditionalActionModel(docPtr, event), budget);
			if (action) additional[key] = action;
		}
		return {
			nameTreeScripts,
			openAction,
			...additional
		};
	}
};
function requireAction(action, owner, index) {
	if (action) return action;
	throw new EngineError(EngineErrorCode.MalformedPdf, `failed to read ${owner}`, { details: { index } });
}
//#endregion
//#region src/features/pieceinfo/PieceInfoAccessor.ts
const OBJ_BOOLEAN = 1;
const OBJ_NUMBER = 2;
const OBJ_STRING = 3;
const OBJ_NAME = 4;
const OBJ_ARRAY = 5;
/**
* `/PieceInfo` reads and writes for one holder — the document CATALOG
* (`pageObjectNumber` undefined) or one PAGE. Wraps the fork's symmetric
* `EPDFDoc_*PieceInfo*` / `EPDFDoc_*PagePieceInfo*` families behind one
* shape, so the worker host has a single code path for both levels.
*
* Writes stamp `content_last_modified` once per job (the worker is
* single-threaded, so every key written by one job gets one revision date);
* the native side fans it out to the application dict, the page dict,
* and — for catalog writes — /Info /ModDate.
*/
var PieceInfoAccessor = class {
	constructor(runtime, session, pageObjectNumber) {
		this.runtime = runtime;
		this.session = session;
		this.pageObjectNumber = pageObjectNumber;
		if (pageObjectNumber !== void 0) session.recordByObjectNumber(pageObjectNumber);
	}
	read(application, signal) {
		throwIfAborted(signal);
		requireApplication(application);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const pon = this.pageObjectNumber;
		if (!(pon === void 0 ? fn.EPDFDoc_HasPieceInfoEntry(docPtr, application) : fn.EPDFDoc_HasPagePieceInfoEntry(docPtr, pon, application))) return null;
		const keyCount = pon === void 0 ? fn.EPDFDoc_GetPieceInfoKeyCount(docPtr, application) : fn.EPDFDoc_GetPagePieceInfoKeyCount(docPtr, pon, application);
		const entries = {};
		for (let i = 0; i < keyCount; i++) {
			throwIfAborted(signal);
			const key = readUtf8String(mem, (buf, cap) => pon === void 0 ? fn.EPDFDoc_GetPieceInfoKeyAt(docPtr, application, i, buf, cap) : fn.EPDFDoc_GetPagePieceInfoKeyAt(docPtr, pon, application, i, buf, cap));
			if (key === null) continue;
			entries[key] = this.readEntry(application, key);
		}
		const lastModifiedPdf = readUtf16String(mem, (buf, cap) => pon === void 0 ? fn.EPDFDoc_GetPieceInfoLastModified(docPtr, application, buf, cap) : fn.EPDFDoc_GetPagePieceInfoLastModified(docPtr, pon, application, buf, cap), null);
		return {
			entries,
			lastModified: lastModifiedPdf ? pdfDateToIso(lastModifiedPdf) : null
		};
	}
	update(application, patch, signal) {
		throwIfAborted(signal);
		requireApplication(application);
		requirePatch(patch);
		const keys = Object.keys(patch);
		if (keys.length === 0) return;
		validatePatch(patch, keys);
		throwIfAborted(signal);
		const { mem } = this.runtime;
		const lastModified = formatPdfDate(/* @__PURE__ */ new Date());
		const lmPtr = mem.writeU16String(lastModified);
		try {
			for (const key of keys) {
				const value = patch[key];
				if (!this.writeEntry(application, key, value, lmPtr)) throw new EngineError(EngineErrorCode.Unknown, `pieceInfo write rejected for key '${key}' after validation`);
			}
		} finally {
			mem.free(lmPtr);
		}
	}
	applications(signal) {
		throwIfAborted(signal);
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const pon = this.pageObjectNumber;
		const count = pon === void 0 ? fn.EPDFDoc_GetPieceInfoEntryCount(docPtr) : fn.EPDFDoc_GetPagePieceInfoEntryCount(docPtr, pon);
		const out = [];
		for (let i = 0; i < count; i++) {
			const name = readUtf8String(mem, (buf, cap) => pon === void 0 ? fn.EPDFDoc_GetPieceInfoEntryAt(docPtr, i, buf, cap) : fn.EPDFDoc_GetPagePieceInfoEntryAt(docPtr, pon, i, buf, cap));
			if (name !== null) out.push(name);
		}
		return out;
	}
	clear(application, signal) {
		throwIfAborted(signal);
		requireApplication(application);
		const { fn } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const pon = this.pageObjectNumber;
		if (!(pon === void 0 ? fn.EPDFDoc_ClearPieceInfoEntry(docPtr, application) : fn.EPDFDoc_ClearPagePieceInfoEntry(docPtr, pon, application))) throw new EngineError(EngineErrorCode.Unknown, `pieceInfo clear rejected for application '${application}'`);
	}
	readEntry(application, key) {
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const pon = this.pageObjectNumber;
		switch (pon === void 0 ? fn.EPDFDoc_GetPieceInfoValueType(docPtr, application, key) : fn.EPDFDoc_GetPagePieceInfoValueType(docPtr, pon, application, key)) {
			case OBJ_STRING: {
				const value = readUtf16String(mem, (buf, cap) => pon === void 0 ? fn.EPDFDoc_GetPieceInfoString(docPtr, application, key, buf, cap) : fn.EPDFDoc_GetPagePieceInfoString(docPtr, pon, application, key, buf, cap), "");
				return value === null ? { type: "unknown" } : {
					type: "string",
					value
				};
			}
			case OBJ_NAME: {
				const value = readUtf8String(mem, (buf, cap) => pon === void 0 ? fn.EPDFDoc_GetPieceInfoName(docPtr, application, key, buf, cap) : fn.EPDFDoc_GetPagePieceInfoName(docPtr, pon, application, key, buf, cap));
				return value === null ? { type: "unknown" } : {
					type: "name",
					value
				};
			}
			case OBJ_NUMBER: {
				const outPtr = mem.alloc(4);
				try {
					if (!(pon === void 0 ? fn.EPDFDoc_GetPieceInfoNumber(docPtr, application, key, outPtr) : fn.EPDFDoc_GetPagePieceInfoNumber(docPtr, pon, application, key, outPtr))) return { type: "unknown" };
					return {
						type: "number",
						value: Number(mem.peek(outPtr, "f32"))
					};
				} finally {
					mem.free(outPtr);
				}
			}
			case OBJ_BOOLEAN: {
				const outPtr = mem.alloc(4);
				try {
					if (!(pon === void 0 ? fn.EPDFDoc_GetPieceInfoBoolean(docPtr, application, key, outPtr) : fn.EPDFDoc_GetPagePieceInfoBoolean(docPtr, pon, application, key, outPtr))) return { type: "unknown" };
					return {
						type: "boolean",
						value: Number(mem.peek(outPtr, "i32")) !== 0
					};
				} finally {
					mem.free(outPtr);
				}
			}
			case OBJ_ARRAY: {
				const count = pon === void 0 ? fn.EPDFDoc_GetPieceInfoStringArrayCount(docPtr, application, key) : fn.EPDFDoc_GetPagePieceInfoStringArrayCount(docPtr, pon, application, key);
				if (count < 0) return { type: "unknown" };
				const value = [];
				for (let i = 0; i < count; i++) {
					const item = readUtf16String(mem, (buf, cap) => pon === void 0 ? fn.EPDFDoc_GetPieceInfoStringArrayAt(docPtr, application, key, i, buf, cap) : fn.EPDFDoc_GetPagePieceInfoStringArrayAt(docPtr, pon, application, key, i, buf, cap), "");
					if (item === null) return { type: "unknown" };
					value.push(item);
				}
				return {
					type: "string-array",
					value
				};
			}
			default: return { type: "unknown" };
		}
	}
	writeEntry(application, key, value, lmPtr) {
		const { fn, mem } = this.runtime;
		const docPtr = this.session.requireDocPtr();
		const pon = this.pageObjectNumber;
		if (value === null) return pon === void 0 ? fn.EPDFDoc_ClearPieceInfoKey(docPtr, application, key, lmPtr) : fn.EPDFDoc_ClearPagePieceInfoKey(docPtr, pon, application, key, lmPtr);
		if (typeof value === "string") return writeUtf16String(mem, value, (ptr) => pon === void 0 ? fn.EPDFDoc_SetPieceInfoString(docPtr, application, key, ptr, lmPtr) : fn.EPDFDoc_SetPagePieceInfoString(docPtr, pon, application, key, ptr, lmPtr));
		if (typeof value === "number") return pon === void 0 ? fn.EPDFDoc_SetPieceInfoNumber(docPtr, application, key, value, lmPtr) : fn.EPDFDoc_SetPagePieceInfoNumber(docPtr, pon, application, key, value, lmPtr);
		if (typeof value === "boolean") return pon === void 0 ? fn.EPDFDoc_SetPieceInfoBoolean(docPtr, application, key, value, lmPtr) : fn.EPDFDoc_SetPagePieceInfoBoolean(docPtr, pon, application, key, value, lmPtr);
		if (Array.isArray(value)) return withWideStringArray(this.runtime, value, (arrayPtr, count) => pon === void 0 ? fn.EPDFDoc_SetPieceInfoStringArray(docPtr, application, key, arrayPtr, count, lmPtr) : fn.EPDFDoc_SetPagePieceInfoStringArray(docPtr, pon, application, key, arrayPtr, count, lmPtr));
		if (typeof value === "object" && value !== null && "name" in value) return pon === void 0 ? fn.EPDFDoc_SetPieceInfoName(docPtr, application, key, value.name, lmPtr) : fn.EPDFDoc_SetPagePieceInfoName(docPtr, pon, application, key, value.name, lmPtr);
		throw new EngineError(EngineErrorCode.Unknown, `unvalidated pieceInfo value for '${key}'`);
	}
};
function requireApplication(application) {
	if (typeof application !== "string" || application.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "pieceInfo application must be non-empty");
}
function requirePatch(patch) {
	if (typeof patch !== "object" || patch === null || Array.isArray(patch)) throw new EngineError(EngineErrorCode.InvalidArg, "pieceInfo patch must be an object");
}
function validatePatch(patch, keys) {
	for (const key of keys) {
		if (key.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, "pieceInfo keys must be non-empty");
		validatePatchValue(key, patch[key]);
	}
}
function validatePatchValue(key, value) {
	if (value === null || typeof value === "string" || typeof value === "boolean") return;
	if (typeof value === "number") {
		if (!Number.isFinite(value)) throw new EngineError(EngineErrorCode.InvalidArg, `pieceInfo number for '${key}' must be finite`);
		return;
	}
	if (Array.isArray(value)) {
		if (!value.every((item) => typeof item === "string")) throw new EngineError(EngineErrorCode.InvalidArg, `pieceInfo string array for '${key}' must contain only strings`);
		return;
	}
	if (typeof value === "object" && value !== null && "name" in value) {
		if (typeof value.name !== "string" || value.name.length === 0) throw new EngineError(EngineErrorCode.InvalidArg, `pieceInfo PDF name for '${key}' must be a non-empty string`);
		return;
	}
	throw new EngineError(EngineErrorCode.InvalidArg, `unsupported pieceInfo value for '${key}': string | number | boolean | string[] | { name } | null`);
}
//#endregion
//#region src/worker-host/WorkerHost.ts
/**
* The piece that runs "inside the worker": owns runtime, manages document
* sessions, dispatches requests to the engine-services synchronous code.
*
* Environment-agnostic. Wrap it with a Web Worker entry, a Node
* worker_thread entry, or an inline transport. The wrapper owns
* postMessage plumbing and any process/lifecycle concerns; the host only
* knows about PdfRuntimeModule, DocumentSession, AbortController, and the
* worker wire shape from @embedpdf/engine-core.
*/
var WorkerHost = class {
	constructor(runtime, post) {
		this.runtime = runtime;
		this.post = post;
		_defineProperty(this, "sessions", /* @__PURE__ */ new Map());
		_defineProperty(this, "baseDocuments", void 0);
		_defineProperty(this, "aborts", /* @__PURE__ */ new Map());
		_defineProperty(this, "fontIds", /* @__PURE__ */ new Map());
		_defineProperty(this, "fonts", void 0);
		_defineProperty(this, "destroyed", false);
		ensureInitialized(this.runtime);
		this.baseDocuments = new BaseDocumentRegistry(this.runtime);
		this.fonts = new FontRegistrar(this.runtime, this.fontIds);
	}
	/**
	* Register deployment-owned fonts on this worker thread, after init and
	* before serving requests. Server-only: the cloud engine never exposes font
	* configuration to clients, so the host (not a wire message) seeds the
	* thread's fallback fonts. Browser engines drive fonts through the
	* `fonts.*` wire path instead. Throws if any font fails to load.
	*/
	registerStartupFonts(specs) {
		this.fonts.registerStartup(specs);
	}
	receive(msg) {
		if (msg.kind === "abort") {
			this.aborts.get(msg.jobId)?.abort();
			return;
		}
		const ctrl = new AbortController();
		this.aborts.set(msg.jobId, ctrl);
		let resultPack;
		try {
			switch (msg.kind) {
				case "open.fatMem":
				case "open.layerMemBase":
				case "open.layerFileBase":
					resultPack = this.handleOpen(msg);
					break;
				case "metadata.read":
					resultPack = this.handleMetadataRead(msg, ctrl.signal);
					break;
				case "metadata.update":
					resultPack = this.handleMetadataUpdate(msg, ctrl.signal);
					break;
				case "actions.read":
					resultPack = this.handleActionsRead(msg, ctrl.signal);
					break;
				case "annotations.listRawAll":
					resultPack = this.handleAnnotationsListRawAll(msg, ctrl.signal);
					break;
				case "annotations.listRawPage":
					resultPack = this.handleAnnotationsListRawPage(msg, ctrl.signal);
					break;
				case "annotations.listFullPage":
					resultPack = this.handleAnnotationsListFullPage(msg, ctrl.signal);
					break;
				case "annotations.renderAppearances":
					resultPack = this.handleAnnotationsRenderAppearances(msg, ctrl.signal);
					break;
				case "annotations.create":
					resultPack = this.handleAnnotationsCreate(msg, ctrl.signal);
					break;
				case "annotations.update":
					resultPack = this.handleAnnotationsUpdate(msg, ctrl.signal);
					break;
				case "annotations.delete":
					resultPack = this.handleAnnotationsDelete(msg, ctrl.signal);
					break;
				case "annotations.move":
					resultPack = this.handleAnnotationsMove(msg, ctrl.signal);
					break;
				case "forms.list":
					resultPack = this.handleFormsList(msg, ctrl.signal);
					break;
				case "forms.setValue":
					resultPack = this.handleFormsSetValue(msg, ctrl.signal);
					break;
				case "forms.reset":
					resultPack = this.handleFormsReset(msg, ctrl.signal);
					break;
				case "forms.applyEffects":
					resultPack = this.handleFormsApplyEffects(msg, ctrl.signal);
					break;
				case "forms.export":
					resultPack = this.handleFormsExport(msg, ctrl.signal);
					break;
				case "forms.import":
					resultPack = this.handleFormsImport(msg, ctrl.signal);
					break;
				case "forms.repair":
					resultPack = this.handleFormsRepair(msg, ctrl.signal);
					break;
				case "forms.createField":
					resultPack = this.handleFormsCreateField(msg, ctrl.signal);
					break;
				case "forms.updateField":
					resultPack = this.handleFormsUpdateField(msg, ctrl.signal);
					break;
				case "forms.deleteField":
					resultPack = this.handleFormsDeleteField(msg, ctrl.signal);
					break;
				case "forms.attachWidget":
					resultPack = this.handleFormsAttachWidget(msg, ctrl.signal);
					break;
				case "forms.detachWidget":
					resultPack = this.handleFormsDetachWidget(msg, ctrl.signal);
					break;
				case "pages.list":
					resultPack = this.handlePagesList(msg, ctrl.signal);
					break;
				case "pages.move":
					resultPack = this.handlePagesMove(msg, ctrl.signal);
					break;
				case "pages.rotate":
					resultPack = this.handlePagesRotate(msg, ctrl.signal);
					break;
				case "pages.delete":
					resultPack = this.handlePagesDelete(msg, ctrl.signal);
					break;
				case "pages.flatten":
					resultPack = this.handlePagesFlatten(msg, ctrl.signal);
					break;
				case "redaction.apply":
					resultPack = this.handleRedactionApply(msg, ctrl.signal);
					break;
				case "pages.extract":
					resultPack = this.handlePagesExtract(msg, ctrl.signal);
					break;
				case "pages.insert":
					resultPack = this.handlePagesInsert(msg, ctrl.signal);
					break;
				case "attachments.list":
					resultPack = this.handleAttachmentsList(msg, ctrl.signal);
					break;
				case "attachments.readFile":
					resultPack = this.handleAttachmentsReadFile(msg, ctrl.signal);
					break;
				case "attachments.create":
					resultPack = this.handleAttachmentsCreate(msg, ctrl.signal);
					break;
				case "attachments.delete":
					resultPack = this.handleAttachmentsDelete(msg, ctrl.signal);
					break;
				case "annotations.readFile":
					resultPack = this.handleAnnotationsReadFile(msg, ctrl.signal);
					break;
				case "pieceInfo.read":
					resultPack = this.handlePieceInfoRead(msg, ctrl.signal);
					break;
				case "pieceInfo.update":
					resultPack = this.handlePieceInfoUpdate(msg, ctrl.signal);
					break;
				case "pieceInfo.applications":
					resultPack = this.handlePieceInfoApplications(msg, ctrl.signal);
					break;
				case "pieceInfo.clear":
					resultPack = this.handlePieceInfoClear(msg, ctrl.signal);
					break;
				case "pages.text":
					resultPack = this.handlePagesText(msg, ctrl.signal);
					break;
				case "pages.geometry":
					resultPack = this.handlePagesGeometry(msg, ctrl.signal);
					break;
				case "pages.render":
					resultPack = this.handlePagesRender(msg, ctrl.signal);
					break;
				case "search.query":
					resultPack = this.handleSearchQuery(msg, ctrl.signal);
					break;
				case "document.saveBuffer":
					resultPack = this.handleDocumentSaveBuffer(msg);
					break;
				case "document.saveLayerBuffer":
					resultPack = this.handleDocumentSaveLayerBuffer(msg);
					break;
				case "document.saveFile":
					resultPack = this.handleDocumentSaveFile(msg);
					break;
				case "document.probeSecurityFile":
					resultPack = this.handleDocumentProbeSecurityFile(msg);
					break;
				case "document.renderPageFile":
					resultPack = this.handleDocumentRenderPageFile(msg, ctrl.signal);
					break;
				case "document.checkPasswordPermissions":
					resultPack = this.handleDocumentCheckPasswordPermissions(msg);
					break;
				case "fonts.register":
					resultPack = this.handleFontsRegister(msg);
					break;
				case "fonts.addFallback":
					resultPack = this.handleFontsAddFallback(msg);
					break;
				case "fonts.clearFallbacks":
					resultPack = this.handleFontsClearFallbacks(msg);
					break;
				case "fonts.clear":
					resultPack = this.handleFontsClear(msg);
					break;
				case "layer.close":
					resultPack = this.handleLayerClose(msg);
					break;
				case "close":
					resultPack = this.handleClose(msg);
					break;
				case "shutdown":
					resultPack = this.handleShutdown(msg);
					break;
				default: throw new EngineError(EngineErrorCode.InvalidArg, `unknown request kind: ${msg.kind}`);
			}
			this.post(wirePack({
				kind: "resolve",
				jobId: msg.jobId,
				result: resultPack.payload
			}, resultPack.transfer));
		} catch (err) {
			const error = serializeError(err);
			this.post(wirePack({
				kind: "reject",
				jobId: msg.jobId,
				error
			}, EMPTY_TRANSFER));
		} finally {
			this.aborts.delete(msg.jobId);
		}
	}
	handleOpen(req) {
		const key = sessionKey(req.docId, req.kind === "open.fatMem" ? void 0 : req.layerName);
		if (this.sessions.has(key)) throw new EngineError(EngineErrorCode.InvalidArg, `document session already open: ${key}`);
		const session = new DocumentSession(this.runtime);
		if (req.kind === "open.fatMem") {
			const bytes = new Uint8Array(req.bytes);
			try {
				session.openFromHandle(openFatMemoryDocument(this.runtime, bytes, req.password));
			} catch (error) {
				if (!isPasswordOpenError(error)) throw error;
				session.parkLocked(bytes);
				this.sessions.set(key, session);
				return wirePack({
					tag: "open",
					docId: req.docId,
					security: passwordRequiredProbe()
				});
			}
		} else if (req.kind === "open.layerMemBase") {
			const base = this.baseDocuments.acquireMemoryBase({
				key: req.baseKey,
				bytes: new Uint8Array(req.baseBytes),
				password: req.password
			});
			session.openFromHandle(openLayerDocument(this.runtime, base, req.layer, req.password));
		} else {
			const base = this.baseDocuments.acquireFileBase({
				key: req.baseKey,
				path: req.basePath,
				password: req.password
			});
			session.openFromHandle(openLayerDocument(this.runtime, base, req.layer, req.password));
		}
		this.sessions.set(key, session);
		return wirePack({
			tag: "open",
			docId: req.docId,
			security: new SecurityReader(this.runtime).checkPasswordPermissions(session, req.password ?? "")
		});
	}
	handleMetadataRead(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "metadata.read",
			metadata: new MetadataReader(this.runtime, session).read(signal)
		});
	}
	handleMetadataUpdate(req, signal) {
		const session = this.requireSession(req);
		const result = new MetadataMutator(this.runtime, session).update(req.patch, signal);
		return this.finishMutation(session, {
			tag: "metadata.update",
			result
		}, req.artifactPath);
	}
	handleActionsRead(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "actions.read",
			snapshot: new DocumentActionsReader(this.runtime, session).read(signal)
		});
	}
	handleAnnotationsListRawAll(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "annotations.listRawAll",
			snapshot: new RawAnnotationReader(this.runtime, session).listAll(signal)
		});
	}
	handleAnnotationsListRawPage(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "annotations.listRawPage",
			snapshot: new RawAnnotationReader(this.runtime, session).listOne(req.pageObjectNumber, signal)
		});
	}
	handleAnnotationsListFullPage(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "annotations.listFullPage",
			snapshot: new AnnotationReader(this.runtime, session).list(req.pageObjectNumber, signal)
		});
	}
	handleAnnotationsRenderAppearances(req, signal) {
		const session = this.requireSession(req);
		const result = new AnnotationAppearanceReader(this.runtime, session).render(req.pageObjectNumber, req.options ?? {}, signal);
		const transfer = result.appearances.map((a) => a.raster.data);
		return wirePack({
			tag: "annotations.renderAppearances",
			result
		}, transfer);
	}
	handleAnnotationsCreate(req, signal) {
		const session = this.requireSession(req);
		const result = new AnnotationMutator(this.runtime, session, this.fonts).create(req.pageObjectNumber, req.draft, signal, req.actor, req.resources);
		return this.finishMutation(session, {
			tag: "annotations.create",
			result
		}, req.artifactPath);
	}
	handleAnnotationsUpdate(req, signal) {
		const session = this.requireSession(req);
		const result = new AnnotationMutator(this.runtime, session, this.fonts).update(req.ref, req.patch, signal, req.actor, req.resources);
		return this.finishMutation(session, {
			tag: "annotations.update",
			result
		}, req.artifactPath);
	}
	handleAnnotationsDelete(req, signal) {
		const session = this.requireSession(req);
		const result = new AnnotationMutator(this.runtime, session).delete(req.ref, signal);
		return this.finishMutation(session, {
			tag: "annotations.delete",
			result
		}, req.artifactPath);
	}
	handleAnnotationsMove(req, signal) {
		const session = this.requireSession(req);
		const result = new AnnotationMutator(this.runtime, session).move(req.pageObjectNumber, req.refs, req.toIndex, signal);
		return this.finishMutation(session, {
			tag: "annotations.move",
			result
		}, req.artifactPath);
	}
	handlePagesList(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "pages.list",
			snapshot: new PagesReader(this.runtime, session).read(signal)
		});
	}
	handlePagesMove(req, signal) {
		const session = this.requireSession(req);
		const result = new PagesMutator(this.runtime, session).move(req.pageObjectNumbers, req.destIndex, signal);
		return this.finishMutation(session, {
			tag: "pages.move",
			result
		}, req.artifactPath);
	}
	handlePagesRotate(req, signal) {
		const session = this.requireSession(req);
		const result = new PagesMutator(this.runtime, session).rotate(req.pageObjectNumbers, req.rotation, signal);
		return this.finishMutation(session, {
			tag: "pages.rotate",
			result
		}, req.artifactPath);
	}
	handlePagesDelete(req, signal) {
		const session = this.requireSession(req);
		const result = new PagesMutator(this.runtime, session).delete(req.pageObjectNumbers, signal);
		return this.finishMutation(session, {
			tag: "pages.delete",
			result
		}, req.artifactPath);
	}
	handlePagesFlatten(req, signal) {
		const session = this.requireSession(req);
		const result = new PagesFlattener(this.runtime, session).flatten(req.pageObjectNumbers, req.usage, signal);
		if (result.meta === null) return wirePack({
			tag: "pages.flatten",
			result
		});
		return this.finishMutation(session, {
			tag: "pages.flatten",
			result
		}, req.artifactPath);
	}
	handleRedactionApply(req, signal) {
		const session = this.requireSession(req);
		const result = new RedactionApplier(this.runtime, session).apply(req.scope, signal);
		if (result.meta === null) return wirePack({
			tag: "redaction.apply",
			result
		});
		return this.finishMutation(session, {
			tag: "redaction.apply",
			result
		}, req.artifactPath);
	}
	handlePagesExtract(req, signal) {
		const session = this.requireSession(req);
		const extracted = new PagesExtractor(this.runtime, session).extract(req.pageObjectNumbers, signal);
		return wirePack({
			tag: "pages.extract",
			bytes: extracted.bytes,
			size: extracted.size
		}, [extracted.bytes]);
	}
	handlePagesInsert(req, signal) {
		const session = this.requireSession(req);
		const result = new PagesInserter(this.runtime, session).insert(req.bytes, req.destIndex, signal);
		return this.finishMutation(session, {
			tag: "pages.insert",
			result
		}, req.artifactPath);
	}
	handleAttachmentsList(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "attachments.list",
			items: new AttachmentReader(this.runtime, session).list(signal)
		});
	}
	handleAttachmentsReadFile(req, signal) {
		const session = this.requireSession(req);
		const content = new AttachmentReader(this.runtime, session).readFile(req.ref, req.path, req.maxDecodedBytes, signal);
		return wirePack({
			tag: "attachments.readFile",
			content
		}, content.bytes !== void 0 ? [content.bytes] : []);
	}
	handleAttachmentsCreate(req, signal) {
		const session = this.requireSession(req);
		const result = new AttachmentMutator(this.runtime, session).create(req.file, req.resources, signal);
		return this.finishMutation(session, {
			tag: "attachments.create",
			result
		}, req.artifactPath);
	}
	handleAttachmentsDelete(req, signal) {
		const session = this.requireSession(req);
		const result = new AttachmentMutator(this.runtime, session).delete(req.ref, signal);
		return this.finishMutation(session, {
			tag: "attachments.delete",
			result
		}, req.artifactPath);
	}
	handleAnnotationsReadFile(req, signal) {
		const session = this.requireSession(req);
		const content = new AttachmentReader(this.runtime, session).readAnnotationFile(req.pageObjectNumber, req.ref, req.path, req.maxDecodedBytes, signal);
		return wirePack({
			tag: "annotations.readFile",
			content
		}, content.bytes !== void 0 ? [content.bytes] : []);
	}
	handlePieceInfoRead(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "pieceInfo.read",
			snapshot: new PieceInfoAccessor(this.runtime, session, req.pageObjectNumber).read(req.application, signal)
		});
	}
	handlePieceInfoUpdate(req, signal) {
		const session = this.requireSession(req);
		new PieceInfoAccessor(this.runtime, session, req.pageObjectNumber).update(req.application, req.patch, signal);
		return this.finishMutation(session, { tag: "pieceInfo.update" }, req.artifactPath);
	}
	handlePieceInfoApplications(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "pieceInfo.applications",
			applications: new PieceInfoAccessor(this.runtime, session, req.pageObjectNumber).applications(signal)
		});
	}
	handlePieceInfoClear(req, signal) {
		const session = this.requireSession(req);
		new PieceInfoAccessor(this.runtime, session, req.pageObjectNumber).clear(req.application, signal);
		return this.finishMutation(session, { tag: "pieceInfo.clear" }, req.artifactPath);
	}
	handlePagesText(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "pages.text",
			snapshot: new PageTextReader(this.runtime, session).read(req.pageObjectNumber, signal)
		});
	}
	handlePagesGeometry(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "pages.geometry",
			snapshot: new PageGeometryReader(this.runtime, session).read(req.pageObjectNumber, signal)
		});
	}
	handleSearchQuery(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "search.query",
			slice: new SearchReader(this.runtime, session).query(req.request, signal)
		});
	}
	handlePagesRender(req, signal) {
		const session = this.requireSession(req);
		const raster = new PageRenderReader(this.runtime, session).render(req.pageObjectNumber, req.options ?? {}, signal);
		return wirePack({
			tag: "pages.render",
			raster
		}, [raster.data]);
	}
	handleDocumentSaveBuffer(req) {
		const session = this.requireSession(req);
		const saved = new DocumentSaver(this.runtime, session).saveStandaloneToBuffer(req.mode);
		return wirePack({
			tag: "document.saveBuffer",
			bytes: saved.bytes,
			size: saved.size
		}, [saved.bytes]);
	}
	handleDocumentSaveFile(req) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "document.saveFile",
			path: new DocumentSaver(this.runtime, session).saveStandaloneToFile(req.path, req.mode).path
		});
	}
	handleDocumentSaveLayerBuffer(req) {
		const session = this.requireSession(req);
		if (session.kind !== "layer") throw new EngineError(EngineErrorCode.InvalidArg, "document has no layer to export (opened without a layer)");
		const artifact = new DocumentSaver(this.runtime, session).saveLayerArtifact();
		return wirePack({
			tag: "document.saveLayerBuffer",
			bytes: artifact.bytes,
			size: artifact.size
		}, [artifact.bytes]);
	}
	handleDocumentProbeSecurityFile(req) {
		return wirePack({
			tag: "document.probeSecurityFile",
			security: new SecurityReader(this.runtime).probeFile(req.path, req.password)
		});
	}
	/**
	* One-shot file render (the derived-artifact warmer's producer): open the
	* base from a file path into a TRANSIENT session — never stored in
	* `this.sessions`, so it can't collide with (or leak into) live document
	* sessions — resolve the display index to its durable page object number,
	* render, close. Shares the base parse with concurrent ad-hoc opens of
	* the same file via the registry refcount.
	*/
	handleDocumentRenderPageFile(req, signal) {
		const session = new DocumentSession(this.runtime);
		const base = this.baseDocuments.acquireFileBase({
			key: `adhoc-file:${req.path}`,
			path: req.path,
			password: req.password
		});
		try {
			session.openFromHandle(openLayerDocument(this.runtime, base, { kind: "fresh" }, req.password));
			const layout = new PagesReader(this.runtime, session).read(signal);
			const page = layout.pages[req.pageIndex];
			if (!page) throw new EngineError(EngineErrorCode.NotFound, `renderPageFile: no page at index ${req.pageIndex} (pageCount=${layout.pageCount})`);
			const raster = new PageRenderReader(this.runtime, session).render(page.pageObjectNumber, req.options ?? {}, signal);
			return wirePack({
				tag: "document.renderPageFile",
				pageObjectNumber: page.pageObjectNumber,
				pageCount: layout.pageCount,
				raster
			}, [raster.data]);
		} finally {
			session.close();
		}
	}
	handleDocumentCheckPasswordPermissions(req) {
		const parked = this.sessions.get(sessionKey(req.docId));
		let session;
		if (parked?.isLocked()) {
			parked.openFromHandle(openFatMemoryDocument(this.runtime, parked.lockedBytes(), req.password));
			session = parked;
		} else session = this.requireSession(req);
		return wirePack({
			tag: "document.checkPasswordPermissions",
			security: new SecurityReader(this.runtime).checkPasswordPermissions(session, req.password, req.mode ?? "any")
		});
	}
	handleFontsRegister(req) {
		this.fonts.register(req.fontKey, req.familyName, req.weight, req.italic, new Uint8Array(req.data));
		return wirePack({
			tag: "fonts.register",
			fontKey: req.fontKey
		});
	}
	handleFontsAddFallback(req) {
		this.fonts.addFallback(req.fontKey);
		return wirePack({ tag: "fonts.addFallback" });
	}
	handleFontsClearFallbacks(_req) {
		this.fonts.clearFallbacks();
		return wirePack({ tag: "fonts.clearFallbacks" });
	}
	handleFontsClear(_req) {
		this.fonts.clear();
		return wirePack({ tag: "fonts.clear" });
	}
	/**
	* Close exactly ONE layer session — the reload seam for layer-session
	* freshness. The base document's refcount releases through the session's
	* close stack, so sibling layer sessions (and the base session) are
	* untouched. Idempotent: closing an absent session is a no-op ack,
	* because the caller may be reloading a layer this worker never held
	* (e.g. after a pool eviction).
	*/
	handleLayerClose(req) {
		const key = sessionKey(req.docId, req.layerName);
		const session = this.sessions.get(key);
		if (session) {
			disposeFormModel(this.runtime, session);
			session.close();
			this.sessions.delete(key);
		}
		return wirePack({ tag: "close" });
	}
	handleClose(req) {
		for (const [key, session] of Array.from(this.sessions.entries())) {
			if (!sessionKeyBelongsToDoc(key, req.docId)) continue;
			disposeFormModel(this.runtime, session);
			session.close();
			this.sessions.delete(key);
		}
		return wirePack({ tag: "close" });
	}
	handleShutdown(_req) {
		if (!this.destroyed) {
			this.destroyed = true;
			for (const session of this.sessions.values()) {
				disposeFormModel(this.runtime, session);
				session.close();
			}
			this.sessions.clear();
			this.baseDocuments.releaseAll();
			destroyLibrary(this.runtime);
		}
		return wirePack({ tag: "shutdown" });
	}
	requireSession(req) {
		const key = sessionKey(req.docId, req.layerName);
		const session = this.sessions.get(key);
		if (session?.isLocked()) throw new EngineError(EngineErrorCode.DocPasswordRequired, `document session is password-locked: ${key}`);
		if (!session || !session.isOpen()) throw new EngineError(EngineErrorCode.DocNotOpen, `document session not open: ${key}`);
		return session;
	}
	/**
	* Finalize a mutation response. For a standalone session the mutation
	* result is returned as-is. For a layer session we additionally persist
	* the layer artifact (to file when `artifactPath` is given, otherwise to
	* a transferable buffer) and merge it onto the response envelope. This
	* is the one place the layer-vs-standalone branch lives, shared by every
	* annotation and page mutation handler.
	*/
	handleFormsList(req, signal) {
		const session = this.requireSession(req);
		return wirePack({
			tag: "forms.list",
			snapshot: new FormReader(this.runtime, session).snapshot(signal)
		});
	}
	handleFormsSetValue(req, signal) {
		const session = this.requireSession(req);
		const result = new FormMutator(this.runtime, session).setValue(req.ref, req.value, signal);
		return this.finishMutation(session, {
			tag: "forms.setValue",
			result
		}, req.artifactPath);
	}
	handleFormsReset(req, signal) {
		const session = this.requireSession(req);
		const result = new FormMutator(this.runtime, session).reset(req.ref, signal);
		return this.finishMutation(session, {
			tag: "forms.reset",
			result
		}, req.artifactPath);
	}
	handleFormsApplyEffects(req, signal) {
		const session = this.requireSession(req);
		const result = new FormsEffectsApplier(this.runtime, session).apply(req.effects, signal);
		if (result.meta === null) return wirePack({
			tag: "forms.applyEffects",
			result
		});
		return this.finishMutation(session, {
			tag: "forms.applyEffects",
			result
		}, req.artifactPath);
	}
	handleFormsExport(req, signal) {
		const session = this.requireSession(req);
		const exported = new FormReader(this.runtime, session).exportData(req.format, signal);
		return wirePack({
			tag: "forms.export",
			format: exported.format,
			bytes: exported.bytes
		}, [exported.bytes]);
	}
	handleFormsImport(req, signal) {
		const session = this.requireSession(req);
		const result = new FormMutator(this.runtime, session).importData(req.data, req.format, signal);
		return this.finishMutation(session, {
			tag: "forms.import",
			result
		}, req.artifactPath);
	}
	handleFormsRepair(req, signal) {
		const session = this.requireSession(req);
		const result = new FormMutator(this.runtime, session).repair(req.bakeAppearances ?? false, signal);
		return this.finishMutation(session, {
			tag: "forms.repair",
			result
		}, req.artifactPath);
	}
	handleFormsCreateField(req, signal) {
		const session = this.requireSession(req);
		const { field } = new FormMutator(this.runtime, session).createField(req.draft, signal);
		return this.finishMutation(session, {
			tag: "forms.createField",
			result: {
				field,
				meta: EMPTY_FORM_META
			}
		}, req.artifactPath);
	}
	handleFormsUpdateField(req, signal) {
		const session = this.requireSession(req);
		const { field } = new FormMutator(this.runtime, session).updateField(req.ref, req.patch, signal);
		return this.finishMutation(session, {
			tag: "forms.updateField",
			result: {
				field,
				meta: EMPTY_FORM_META
			}
		}, req.artifactPath);
	}
	handleFormsDeleteField(req, signal) {
		const session = this.requireSession(req);
		const { deletedFieldObjectNumber, detachedWidgets } = new FormMutator(this.runtime, session).deleteField(req.ref, signal);
		const annotations = new AnnotationMutator(this.runtime, session);
		for (const widget of detachedWidgets) {
			if (widget.annotObjectNumber <= 0 || widget.pageObjectNumber <= 0) continue;
			annotations.delete({
				kind: "objectNumber",
				pageObjectNumber: widget.pageObjectNumber,
				annotObjectNumber: widget.annotObjectNumber
			}, signal);
		}
		session.noteMutation();
		return this.finishMutation(session, {
			tag: "forms.deleteField",
			result: {
				deletedFieldObjectNumber,
				removedWidgets: detachedWidgets,
				meta: EMPTY_FORM_META
			}
		}, req.artifactPath);
	}
	handleFormsAttachWidget(req, signal) {
		const session = this.requireSession(req);
		const { field } = new FormMutator(this.runtime, session).attachWidget(req.ref, req.widget, req.onState, signal);
		return this.finishMutation(session, {
			tag: "forms.attachWidget",
			result: {
				field,
				meta: EMPTY_FORM_META
			}
		}, req.artifactPath);
	}
	handleFormsDetachWidget(req, signal) {
		const session = this.requireSession(req);
		const { field } = new FormMutator(this.runtime, session).detachWidget(req.ref, req.widget, signal);
		return this.finishMutation(session, {
			tag: "forms.detachWidget",
			result: {
				field,
				meta: EMPTY_FORM_META
			}
		}, req.artifactPath);
	}
	finishMutation(session, payload, artifactPath) {
		if (!payload.tag.startsWith("forms.") && payload.tag !== "pages.flatten" && payload.tag !== "redaction.apply") session.noteMutation();
		if (session.kind !== "layer") return wirePack(payload);
		const saved = this.saveLayerArtifact(session, artifactPath);
		return wirePack({
			...payload,
			...saved.payload
		}, saved.transfer);
	}
	saveLayerArtifact(session, artifactPath) {
		const saver = new DocumentSaver(this.runtime, session);
		if (artifactPath) return {
			payload: { artifactFile: saver.saveLayerArtifactToFile(artifactPath) },
			transfer: []
		};
		const artifact = saver.saveLayerArtifact();
		return {
			payload: { artifact },
			transfer: [artifact.bytes]
		};
	}
};
/** Form mutations are non-structural at the page-list level. */
const EMPTY_FORM_META = {
	affectedPages: [],
	cacheDelta: null
};
const BASE_SESSION_SUFFIX = "__base__";
function sessionKey(docId, layerName) {
	return `${docId}::${layerName ? `layer:${layerName}` : BASE_SESSION_SUFFIX}`;
}
function sessionKeyBelongsToDoc(key, docId) {
	return key === sessionKey(docId) || key.startsWith(`${docId}::layer:`);
}
function isPasswordOpenError(error) {
	return error instanceof EngineError && (error.code === EngineErrorCode.DocPasswordRequired || error.code === EngineErrorCode.DocPasswordIncorrect);
}
/**
* The security probe a locked open answers with — identical to what
* `SecurityReader.probeFile` reports for a password-protected file it
* couldn't read: encrypted, password required, permissions unknown.
* `securityStateFromProbe` + `passwordPromptFromState` on the client
* turn this into `passwordPrompt: { state: 'required' }`.
*/
function passwordRequiredProbe() {
	return {
		encryptionState: "encrypted",
		encryptionRequiresPassword: true,
		securityHandlerRevision: null,
		pdfPermissionsBits: null,
		pdfPermissionsAllAllowed: null,
		pdfOpenedAs: null,
		securityProbedAt: Date.now()
	};
}
//#endregion
export { ALL_STANDARD_SECURITY_PERMISSIONS, ActionReadBudgetTracker, AnnotationAppearanceReader, AnnotationMutator, AnnotationReader, AttachmentMutator, AttachmentReader, BaseDocumentRegistry, CloseStack, DocumentActionsReader, PagesMutator as DocumentPagesMutator, PagesMutator, DocumentSaver, DocumentSession, EventHub, FontRegistrar, FormMutator, FormReader, FormsEffectsApplier, LocalRevisionAuthority, MetadataMutator, MetadataReader, PageGeometryReader, PagesReader as PageLayoutReader, PagesReader, PagePtrPool, PageRenderReader as PageRasterReader, PageRenderReader, PageTextReader, PagesExtractor, PagesFlattener, PagesInserter, RawAnnotationReader, RedactionApplier, SearchReader, SecurityReader, SessionEventPublisher, WorkerHost, computeMutationImpact, destroyLibrary, disposeFormModel, ensureInitialized, formatPdfDate, generateUuid, hasAllStandardSecurityPermissions, normalizeU32, openFatMemoryDocument, openLayerDocument, pdfDateToIso, readActionModel, readAttachmentFileInfo, throwIfAborted, writeAttachmentFilePayload };

//# sourceMappingURL=index.js.map